const DEBUG_BUILD$3 = typeof __SENTRY_DEBUG__ === "undefined" || __SENTRY_DEBUG__;
const SDK_VERSION = "8.55.1";
const GLOBAL_OBJ = globalThis;
function getGlobalSingleton(name, creator, obj) {
  const gbl = obj || GLOBAL_OBJ;
  const __SENTRY__ = gbl.__SENTRY__ = gbl.__SENTRY__ || {};
  const versionedCarrier = __SENTRY__[SDK_VERSION] = __SENTRY__[SDK_VERSION] || {};
  return versionedCarrier[name] || (versionedCarrier[name] = creator());
}
const DEBUG_BUILD$2 = typeof __SENTRY_DEBUG__ === "undefined" || __SENTRY_DEBUG__;
const PREFIX = "Sentry Logger ";
const CONSOLE_LEVELS = [
  "debug",
  "info",
  "warn",
  "error",
  "log",
  "assert",
  "trace"
];
const originalConsoleMethods = {};
function consoleSandbox(callback) {
  if (!("console" in GLOBAL_OBJ)) {
    return callback();
  }
  const console2 = GLOBAL_OBJ.console;
  const wrappedFuncs = {};
  const wrappedLevels = Object.keys(originalConsoleMethods);
  wrappedLevels.forEach((level) => {
    const originalConsoleMethod = originalConsoleMethods[level];
    wrappedFuncs[level] = console2[level];
    console2[level] = originalConsoleMethod;
  });
  try {
    return callback();
  } finally {
    wrappedLevels.forEach((level) => {
      console2[level] = wrappedFuncs[level];
    });
  }
}
function makeLogger() {
  let enabled = false;
  const logger2 = {
    enable: () => {
      enabled = true;
    },
    disable: () => {
      enabled = false;
    },
    isEnabled: () => enabled
  };
  if (DEBUG_BUILD$2) {
    CONSOLE_LEVELS.forEach((name) => {
      logger2[name] = (...args) => {
        if (enabled) {
          consoleSandbox(() => {
            GLOBAL_OBJ.console[name](`${PREFIX}[${name}]:`, ...args);
          });
        }
      };
    });
  } else {
    CONSOLE_LEVELS.forEach((name) => {
      logger2[name] = () => void 0;
    });
  }
  return logger2;
}
const logger = getGlobalSingleton("logger", makeLogger);
const STACKTRACE_FRAME_LIMIT = 50;
const UNKNOWN_FUNCTION = "?";
const WEBPACK_ERROR_REGEXP = /\(error: (.*)\)/;
const STRIP_FRAME_REGEXP = /captureMessage|captureException/;
function createStackParser(...parsers) {
  const sortedParsers = parsers.sort((a, b) => a[0] - b[0]).map((p2) => p2[1]);
  return (stack2, skipFirstLines = 0, framesToPop = 0) => {
    const frames = [];
    const lines = stack2.split("\n");
    for (let i = skipFirstLines; i < lines.length; i++) {
      const line = lines[i];
      if (line.length > 1024) {
        continue;
      }
      const cleanedLine = WEBPACK_ERROR_REGEXP.test(line) ? line.replace(WEBPACK_ERROR_REGEXP, "$1") : line;
      if (cleanedLine.match(/\S*Error: /)) {
        continue;
      }
      for (const parser of sortedParsers) {
        const frame = parser(cleanedLine);
        if (frame) {
          frames.push(frame);
          break;
        }
      }
      if (frames.length >= STACKTRACE_FRAME_LIMIT + framesToPop) {
        break;
      }
    }
    return stripSentryFramesAndReverse(frames.slice(framesToPop));
  };
}
function stackParserFromStackParserOptions(stackParser) {
  if (Array.isArray(stackParser)) {
    return createStackParser(...stackParser);
  }
  return stackParser;
}
function stripSentryFramesAndReverse(stack2) {
  if (!stack2.length) {
    return [];
  }
  const localStack = Array.from(stack2);
  if (/sentryWrapped/.test(getLastStackFrame(localStack).function || "")) {
    localStack.pop();
  }
  localStack.reverse();
  if (STRIP_FRAME_REGEXP.test(getLastStackFrame(localStack).function || "")) {
    localStack.pop();
    if (STRIP_FRAME_REGEXP.test(getLastStackFrame(localStack).function || "")) {
      localStack.pop();
    }
  }
  return localStack.slice(0, STACKTRACE_FRAME_LIMIT).map((frame) => ({
    ...frame,
    filename: frame.filename || getLastStackFrame(localStack).filename,
    function: frame.function || UNKNOWN_FUNCTION
  }));
}
function getLastStackFrame(arr) {
  return arr[arr.length - 1] || {};
}
const defaultFunctionName = "<anonymous>";
function getFunctionName(fn) {
  try {
    if (!fn || typeof fn !== "function") {
      return defaultFunctionName;
    }
    return fn.name || defaultFunctionName;
  } catch (e) {
    return defaultFunctionName;
  }
}
function getFramesFromEvent(event) {
  const exception = event.exception;
  if (exception) {
    const frames = [];
    try {
      exception.values.forEach((value) => {
        if (value.stacktrace.frames) {
          frames.push(...value.stacktrace.frames);
        }
      });
      return frames;
    } catch (_oO) {
      return void 0;
    }
  }
  return void 0;
}
const handlers = {};
const instrumented = {};
function addHandler(type, handler) {
  handlers[type] = handlers[type] || [];
  handlers[type].push(handler);
}
function maybeInstrument(type, instrumentFn) {
  if (!instrumented[type]) {
    instrumented[type] = true;
    try {
      instrumentFn();
    } catch (e) {
      DEBUG_BUILD$2 && logger.error(`Error while instrumenting ${type}`, e);
    }
  }
}
function triggerHandlers(type, data) {
  const typeHandlers = type && handlers[type];
  if (!typeHandlers) {
    return;
  }
  for (const handler of typeHandlers) {
    try {
      handler(data);
    } catch (e) {
      DEBUG_BUILD$2 && logger.error(
        `Error while triggering instrumentation handler.
Type: ${type}
Name: ${getFunctionName(handler)}
Error:`,
        e
      );
    }
  }
}
let _oldOnErrorHandler = null;
function addGlobalErrorInstrumentationHandler(handler) {
  const type = "error";
  addHandler(type, handler);
  maybeInstrument(type, instrumentError);
}
function instrumentError() {
  _oldOnErrorHandler = GLOBAL_OBJ.onerror;
  GLOBAL_OBJ.onerror = function(msg, url, line, column, error) {
    const handlerData = {
      column,
      error,
      line,
      msg,
      url
    };
    triggerHandlers("error", handlerData);
    if (_oldOnErrorHandler) {
      return _oldOnErrorHandler.apply(this, arguments);
    }
    return false;
  };
  GLOBAL_OBJ.onerror.__SENTRY_INSTRUMENTED__ = true;
}
let _oldOnUnhandledRejectionHandler = null;
function addGlobalUnhandledRejectionInstrumentationHandler(handler) {
  const type = "unhandledrejection";
  addHandler(type, handler);
  maybeInstrument(type, instrumentUnhandledRejection);
}
function instrumentUnhandledRejection() {
  _oldOnUnhandledRejectionHandler = GLOBAL_OBJ.onunhandledrejection;
  GLOBAL_OBJ.onunhandledrejection = function(e) {
    const handlerData = e;
    triggerHandlers("unhandledrejection", handlerData);
    if (_oldOnUnhandledRejectionHandler) {
      return _oldOnUnhandledRejectionHandler.apply(this, arguments);
    }
    return true;
  };
  GLOBAL_OBJ.onunhandledrejection.__SENTRY_INSTRUMENTED__ = true;
}
function getMainCarrier() {
  getSentryCarrier(GLOBAL_OBJ);
  return GLOBAL_OBJ;
}
function getSentryCarrier(carrier) {
  const __SENTRY__ = carrier.__SENTRY__ = carrier.__SENTRY__ || {};
  __SENTRY__.version = __SENTRY__.version || SDK_VERSION;
  return __SENTRY__[SDK_VERSION] = __SENTRY__[SDK_VERSION] || {};
}
const objectToString$1 = Object.prototype.toString;
function isError(wat) {
  switch (objectToString$1.call(wat)) {
    case "[object Error]":
    case "[object Exception]":
    case "[object DOMException]":
    case "[object WebAssembly.Exception]":
      return true;
    default:
      return isInstanceOf(wat, Error);
  }
}
function isBuiltin(wat, className) {
  return objectToString$1.call(wat) === `[object ${className}]`;
}
function isErrorEvent$1(wat) {
  return isBuiltin(wat, "ErrorEvent");
}
function isDOMError(wat) {
  return isBuiltin(wat, "DOMError");
}
function isDOMException(wat) {
  return isBuiltin(wat, "DOMException");
}
function isString$1(wat) {
  return isBuiltin(wat, "String");
}
function isParameterizedString(wat) {
  return typeof wat === "object" && wat !== null && "__sentry_template_string__" in wat && "__sentry_template_values__" in wat;
}
function isPrimitive(wat) {
  return wat === null || isParameterizedString(wat) || typeof wat !== "object" && typeof wat !== "function";
}
function isPlainObject$1(wat) {
  return isBuiltin(wat, "Object");
}
function isEvent(wat) {
  return typeof Event !== "undefined" && isInstanceOf(wat, Event);
}
function isElement(wat) {
  return typeof Element !== "undefined" && isInstanceOf(wat, Element);
}
function isRegExp(wat) {
  return isBuiltin(wat, "RegExp");
}
function isThenable(wat) {
  return Boolean(wat && wat.then && typeof wat.then === "function");
}
function isSyntheticEvent(wat) {
  return isPlainObject$1(wat) && "nativeEvent" in wat && "preventDefault" in wat && "stopPropagation" in wat;
}
function isInstanceOf(wat, base) {
  try {
    return wat instanceof base;
  } catch (_e) {
    return false;
  }
}
function isVueViewModel(wat) {
  return !!(typeof wat === "object" && wat !== null && (wat.__isVue || wat._isVue));
}
const WINDOW$4 = GLOBAL_OBJ;
const DEFAULT_MAX_STRING_LENGTH = 80;
function htmlTreeAsString(elem, options = {}) {
  if (!elem) {
    return "<unknown>";
  }
  try {
    let currentElem = elem;
    const MAX_TRAVERSE_HEIGHT = 5;
    const out = [];
    let height = 0;
    let len = 0;
    const separator = " > ";
    const sepLength = separator.length;
    let nextStr;
    const keyAttrs = Array.isArray(options) ? options : options.keyAttrs;
    const maxStringLength = !Array.isArray(options) && options.maxStringLength || DEFAULT_MAX_STRING_LENGTH;
    while (currentElem && height++ < MAX_TRAVERSE_HEIGHT) {
      nextStr = _htmlElementAsString(currentElem, keyAttrs);
      if (nextStr === "html" || height > 1 && len + out.length * sepLength + nextStr.length >= maxStringLength) {
        break;
      }
      out.push(nextStr);
      len += nextStr.length;
      currentElem = currentElem.parentNode;
    }
    return out.reverse().join(separator);
  } catch (_oO) {
    return "<unknown>";
  }
}
function _htmlElementAsString(el, keyAttrs) {
  const elem = el;
  const out = [];
  if (!elem || !elem.tagName) {
    return "";
  }
  if (WINDOW$4.HTMLElement) {
    if (elem instanceof HTMLElement && elem.dataset) {
      if (elem.dataset["sentryComponent"]) {
        return elem.dataset["sentryComponent"];
      }
      if (elem.dataset["sentryElement"]) {
        return elem.dataset["sentryElement"];
      }
    }
  }
  out.push(elem.tagName.toLowerCase());
  const keyAttrPairs = keyAttrs && keyAttrs.length ? keyAttrs.filter((keyAttr) => elem.getAttribute(keyAttr)).map((keyAttr) => [keyAttr, elem.getAttribute(keyAttr)]) : null;
  if (keyAttrPairs && keyAttrPairs.length) {
    keyAttrPairs.forEach((keyAttrPair) => {
      out.push(`[${keyAttrPair[0]}="${keyAttrPair[1]}"]`);
    });
  } else {
    if (elem.id) {
      out.push(`#${elem.id}`);
    }
    const className = elem.className;
    if (className && isString$1(className)) {
      const classes = className.split(/\s+/);
      for (const c of classes) {
        out.push(`.${c}`);
      }
    }
  }
  const allowedAttrs = ["aria-label", "type", "name", "title", "alt"];
  for (const k of allowedAttrs) {
    const attr = elem.getAttribute(k);
    if (attr) {
      out.push(`[${k}="${attr}"]`);
    }
  }
  return out.join("");
}
function getLocationHref() {
  try {
    return WINDOW$4.document.location.href;
  } catch (oO) {
    return "";
  }
}
function getComponentName$1(elem) {
  if (!WINDOW$4.HTMLElement) {
    return null;
  }
  let currentElem = elem;
  const MAX_TRAVERSE_HEIGHT = 5;
  for (let i = 0; i < MAX_TRAVERSE_HEIGHT; i++) {
    if (!currentElem) {
      return null;
    }
    if (currentElem instanceof HTMLElement) {
      if (currentElem.dataset["sentryComponent"]) {
        return currentElem.dataset["sentryComponent"];
      }
      if (currentElem.dataset["sentryElement"]) {
        return currentElem.dataset["sentryElement"];
      }
    }
    currentElem = currentElem.parentNode;
  }
  return null;
}
function truncate(str, max = 0) {
  if (typeof str !== "string" || max === 0) {
    return str;
  }
  return str.length <= max ? str : `${str.slice(0, max)}...`;
}
function safeJoin(input, delimiter) {
  if (!Array.isArray(input)) {
    return "";
  }
  const output = [];
  for (let i = 0; i < input.length; i++) {
    const value = input[i];
    try {
      if (isVueViewModel(value)) {
        output.push("[VueViewModel]");
      } else {
        output.push(String(value));
      }
    } catch (e) {
      output.push("[value cannot be serialized]");
    }
  }
  return output.join(delimiter);
}
function isMatchingPattern(value, pattern, requireExactStringMatch = false) {
  if (!isString$1(value)) {
    return false;
  }
  if (isRegExp(pattern)) {
    return pattern.test(value);
  }
  if (isString$1(pattern)) {
    return requireExactStringMatch ? value === pattern : value.includes(pattern);
  }
  return false;
}
function stringMatchesSomePattern(testString, patterns = [], requireExactStringMatch = false) {
  return patterns.some((pattern) => isMatchingPattern(testString, pattern, requireExactStringMatch));
}
function fill(source, name, replacementFactory) {
  if (!(name in source)) {
    return;
  }
  const original = source[name];
  const wrapped = replacementFactory(original);
  if (typeof wrapped === "function") {
    markFunctionWrapped(wrapped, original);
  }
  try {
    source[name] = wrapped;
  } catch (e) {
    DEBUG_BUILD$2 && logger.log(`Failed to replace method "${name}" in object`, source);
  }
}
function addNonEnumerableProperty(obj, name, value) {
  try {
    Object.defineProperty(obj, name, {
      // enumerable: false, // the default, so we can save on bundle size by not explicitly setting it
      value,
      writable: true,
      configurable: true
    });
  } catch (o_O) {
    DEBUG_BUILD$2 && logger.log(`Failed to add non-enumerable property "${name}" to object`, obj);
  }
}
function markFunctionWrapped(wrapped, original) {
  try {
    const proto = original.prototype || {};
    wrapped.prototype = original.prototype = proto;
    addNonEnumerableProperty(wrapped, "__sentry_original__", original);
  } catch (o_O) {
  }
}
function getOriginalFunction(func) {
  return func.__sentry_original__;
}
function convertToPlainObject(value) {
  if (isError(value)) {
    return {
      message: value.message,
      name: value.name,
      stack: value.stack,
      ...getOwnProperties(value)
    };
  } else if (isEvent(value)) {
    const newObj = {
      type: value.type,
      target: serializeEventTarget(value.target),
      currentTarget: serializeEventTarget(value.currentTarget),
      ...getOwnProperties(value)
    };
    if (typeof CustomEvent !== "undefined" && isInstanceOf(value, CustomEvent)) {
      newObj.detail = value.detail;
    }
    return newObj;
  } else {
    return value;
  }
}
function serializeEventTarget(target) {
  try {
    return isElement(target) ? htmlTreeAsString(target) : Object.prototype.toString.call(target);
  } catch (_oO) {
    return "<unknown>";
  }
}
function getOwnProperties(obj) {
  if (typeof obj === "object" && obj !== null) {
    const extractedProps = {};
    for (const property in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, property)) {
        extractedProps[property] = obj[property];
      }
    }
    return extractedProps;
  } else {
    return {};
  }
}
function extractExceptionKeysForMessage(exception, maxLength = 40) {
  const keys = Object.keys(convertToPlainObject(exception));
  keys.sort();
  const firstKey = keys[0];
  if (!firstKey) {
    return "[object has no keys]";
  }
  if (firstKey.length >= maxLength) {
    return truncate(firstKey, maxLength);
  }
  for (let includedKeys = keys.length; includedKeys > 0; includedKeys--) {
    const serialized = keys.slice(0, includedKeys).join(", ");
    if (serialized.length > maxLength) {
      continue;
    }
    if (includedKeys === keys.length) {
      return serialized;
    }
    return truncate(serialized, maxLength);
  }
  return "";
}
function dropUndefinedKeys(inputValue) {
  const memoizationMap = /* @__PURE__ */ new Map();
  return _dropUndefinedKeys(inputValue, memoizationMap);
}
function _dropUndefinedKeys(inputValue, memoizationMap) {
  if (isPojo(inputValue)) {
    const memoVal = memoizationMap.get(inputValue);
    if (memoVal !== void 0) {
      return memoVal;
    }
    const returnValue = {};
    memoizationMap.set(inputValue, returnValue);
    for (const key of Object.getOwnPropertyNames(inputValue)) {
      if (typeof inputValue[key] !== "undefined") {
        returnValue[key] = _dropUndefinedKeys(inputValue[key], memoizationMap);
      }
    }
    return returnValue;
  }
  if (Array.isArray(inputValue)) {
    const memoVal = memoizationMap.get(inputValue);
    if (memoVal !== void 0) {
      return memoVal;
    }
    const returnValue = [];
    memoizationMap.set(inputValue, returnValue);
    inputValue.forEach((item) => {
      returnValue.push(_dropUndefinedKeys(item, memoizationMap));
    });
    return returnValue;
  }
  return inputValue;
}
function isPojo(input) {
  if (!isPlainObject$1(input)) {
    return false;
  }
  try {
    const name = Object.getPrototypeOf(input).constructor.name;
    return !name || name === "Object";
  } catch (e2) {
    return true;
  }
}
const ONE_SECOND_IN_MS = 1e3;
function dateTimestampInSeconds() {
  return Date.now() / ONE_SECOND_IN_MS;
}
function createUnixTimestampInSecondsFunc() {
  const { performance } = GLOBAL_OBJ;
  if (!performance || !performance.now) {
    return dateTimestampInSeconds;
  }
  const approxStartingTimeOrigin = Date.now() - performance.now();
  const timeOrigin = performance.timeOrigin == void 0 ? approxStartingTimeOrigin : performance.timeOrigin;
  return () => {
    return (timeOrigin + performance.now()) / ONE_SECOND_IN_MS;
  };
}
const timestampInSeconds = createUnixTimestampInSecondsFunc();
(() => {
  const { performance } = GLOBAL_OBJ;
  if (!performance || !performance.now) {
    return void 0;
  }
  const threshold = 3600 * 1e3;
  const performanceNow = performance.now();
  const dateNow = Date.now();
  const timeOriginDelta = performance.timeOrigin ? Math.abs(performance.timeOrigin + performanceNow - dateNow) : threshold;
  const timeOriginIsReliable = timeOriginDelta < threshold;
  const navigationStart = performance.timing && performance.timing.navigationStart;
  const hasNavigationStart = typeof navigationStart === "number";
  const navigationStartDelta = hasNavigationStart ? Math.abs(navigationStart + performanceNow - dateNow) : threshold;
  const navigationStartIsReliable = navigationStartDelta < threshold;
  if (timeOriginIsReliable || navigationStartIsReliable) {
    if (timeOriginDelta <= navigationStartDelta) {
      return performance.timeOrigin;
    } else {
      return navigationStart;
    }
  }
  return dateNow;
})();
function uuid4() {
  const gbl = GLOBAL_OBJ;
  const crypto = gbl.crypto || gbl.msCrypto;
  let getRandomByte = () => Math.random() * 16;
  try {
    if (crypto && crypto.randomUUID) {
      return crypto.randomUUID().replace(/-/g, "");
    }
    if (crypto && crypto.getRandomValues) {
      getRandomByte = () => {
        const typedArray = new Uint8Array(1);
        crypto.getRandomValues(typedArray);
        return typedArray[0];
      };
    }
  } catch (_) {
  }
  return ("10000000100040008000" + 1e11).replace(
    /[018]/g,
    (c) => (
      // eslint-disable-next-line no-bitwise
      (c ^ (getRandomByte() & 15) >> c / 4).toString(16)
    )
  );
}
function getFirstException(event) {
  return event.exception && event.exception.values ? event.exception.values[0] : void 0;
}
function getEventDescription(event) {
  const { message, event_id: eventId } = event;
  if (message) {
    return message;
  }
  const firstException = getFirstException(event);
  if (firstException) {
    if (firstException.type && firstException.value) {
      return `${firstException.type}: ${firstException.value}`;
    }
    return firstException.type || firstException.value || eventId || "<unknown>";
  }
  return eventId || "<unknown>";
}
function addExceptionTypeValue(event, value, type) {
  const exception = event.exception = event.exception || {};
  const values = exception.values = exception.values || [];
  const firstException = values[0] = values[0] || {};
  if (!firstException.value) {
    firstException.value = value || "";
  }
  if (!firstException.type) {
    firstException.type = "Error";
  }
}
function addExceptionMechanism(event, newMechanism) {
  const firstException = getFirstException(event);
  if (!firstException) {
    return;
  }
  const defaultMechanism = { type: "generic", handled: true };
  const currentMechanism = firstException.mechanism;
  firstException.mechanism = { ...defaultMechanism, ...currentMechanism, ...newMechanism };
  if (newMechanism && "data" in newMechanism) {
    const mergedData = { ...currentMechanism && currentMechanism.data, ...newMechanism.data };
    firstException.mechanism.data = mergedData;
  }
}
function checkOrSetAlreadyCaught(exception) {
  if (isAlreadyCaptured(exception)) {
    return true;
  }
  try {
    addNonEnumerableProperty(exception, "__sentry_captured__", true);
  } catch (err) {
  }
  return false;
}
function isAlreadyCaptured(exception) {
  try {
    return exception.__sentry_captured__;
  } catch (e) {
  }
}
var States;
(function(States2) {
  const PENDING = 0;
  States2[States2["PENDING"] = PENDING] = "PENDING";
  const RESOLVED = 1;
  States2[States2["RESOLVED"] = RESOLVED] = "RESOLVED";
  const REJECTED = 2;
  States2[States2["REJECTED"] = REJECTED] = "REJECTED";
})(States || (States = {}));
function resolvedSyncPromise(value) {
  return new SyncPromise((resolve2) => {
    resolve2(value);
  });
}
function rejectedSyncPromise(reason) {
  return new SyncPromise((_, reject) => {
    reject(reason);
  });
}
class SyncPromise {
  constructor(executor) {
    SyncPromise.prototype.__init.call(this);
    SyncPromise.prototype.__init2.call(this);
    SyncPromise.prototype.__init3.call(this);
    SyncPromise.prototype.__init4.call(this);
    this._state = States.PENDING;
    this._handlers = [];
    try {
      executor(this._resolve, this._reject);
    } catch (e) {
      this._reject(e);
    }
  }
  /** JSDoc */
  then(onfulfilled, onrejected) {
    return new SyncPromise((resolve2, reject) => {
      this._handlers.push([
        false,
        (result) => {
          if (!onfulfilled) {
            resolve2(result);
          } else {
            try {
              resolve2(onfulfilled(result));
            } catch (e) {
              reject(e);
            }
          }
        },
        (reason) => {
          if (!onrejected) {
            reject(reason);
          } else {
            try {
              resolve2(onrejected(reason));
            } catch (e) {
              reject(e);
            }
          }
        }
      ]);
      this._executeHandlers();
    });
  }
  /** JSDoc */
  catch(onrejected) {
    return this.then((val) => val, onrejected);
  }
  /** JSDoc */
  finally(onfinally) {
    return new SyncPromise((resolve2, reject) => {
      let val;
      let isRejected;
      return this.then(
        (value) => {
          isRejected = false;
          val = value;
          if (onfinally) {
            onfinally();
          }
        },
        (reason) => {
          isRejected = true;
          val = reason;
          if (onfinally) {
            onfinally();
          }
        }
      ).then(() => {
        if (isRejected) {
          reject(val);
          return;
        }
        resolve2(val);
      });
    });
  }
  /** JSDoc */
  __init() {
    this._resolve = (value) => {
      this._setResult(States.RESOLVED, value);
    };
  }
  /** JSDoc */
  __init2() {
    this._reject = (reason) => {
      this._setResult(States.REJECTED, reason);
    };
  }
  /** JSDoc */
  __init3() {
    this._setResult = (state, value) => {
      if (this._state !== States.PENDING) {
        return;
      }
      if (isThenable(value)) {
        void value.then(this._resolve, this._reject);
        return;
      }
      this._state = state;
      this._value = value;
      this._executeHandlers();
    };
  }
  /** JSDoc */
  __init4() {
    this._executeHandlers = () => {
      if (this._state === States.PENDING) {
        return;
      }
      const cachedHandlers = this._handlers.slice();
      this._handlers = [];
      cachedHandlers.forEach((handler) => {
        if (handler[0]) {
          return;
        }
        if (this._state === States.RESOLVED) {
          handler[1](this._value);
        }
        if (this._state === States.REJECTED) {
          handler[2](this._value);
        }
        handler[0] = true;
      });
    };
  }
}
function makeSession(context) {
  const startingTime = timestampInSeconds();
  const session = {
    sid: uuid4(),
    init: true,
    timestamp: startingTime,
    started: startingTime,
    duration: 0,
    status: "ok",
    errors: 0,
    ignoreDuration: false,
    toJSON: () => sessionToJSON(session)
  };
  if (context) {
    updateSession(session, context);
  }
  return session;
}
function updateSession(session, context = {}) {
  if (context.user) {
    if (!session.ipAddress && context.user.ip_address) {
      session.ipAddress = context.user.ip_address;
    }
    if (!session.did && !context.did) {
      session.did = context.user.id || context.user.email || context.user.username;
    }
  }
  session.timestamp = context.timestamp || timestampInSeconds();
  if (context.abnormal_mechanism) {
    session.abnormal_mechanism = context.abnormal_mechanism;
  }
  if (context.ignoreDuration) {
    session.ignoreDuration = context.ignoreDuration;
  }
  if (context.sid) {
    session.sid = context.sid.length === 32 ? context.sid : uuid4();
  }
  if (context.init !== void 0) {
    session.init = context.init;
  }
  if (!session.did && context.did) {
    session.did = `${context.did}`;
  }
  if (typeof context.started === "number") {
    session.started = context.started;
  }
  if (session.ignoreDuration) {
    session.duration = void 0;
  } else if (typeof context.duration === "number") {
    session.duration = context.duration;
  } else {
    const duration = session.timestamp - session.started;
    session.duration = duration >= 0 ? duration : 0;
  }
  if (context.release) {
    session.release = context.release;
  }
  if (context.environment) {
    session.environment = context.environment;
  }
  if (!session.ipAddress && context.ipAddress) {
    session.ipAddress = context.ipAddress;
  }
  if (!session.userAgent && context.userAgent) {
    session.userAgent = context.userAgent;
  }
  if (typeof context.errors === "number") {
    session.errors = context.errors;
  }
  if (context.status) {
    session.status = context.status;
  }
}
function closeSession(session, status) {
  let context = {};
  if (session.status === "ok") {
    context = { status: "exited" };
  }
  updateSession(session, context);
}
function sessionToJSON(session) {
  return dropUndefinedKeys({
    sid: `${session.sid}`,
    init: session.init,
    // Make sure that sec is converted to ms for date constructor
    started: new Date(session.started * 1e3).toISOString(),
    timestamp: new Date(session.timestamp * 1e3).toISOString(),
    status: session.status,
    errors: session.errors,
    did: typeof session.did === "number" || typeof session.did === "string" ? `${session.did}` : void 0,
    duration: session.duration,
    abnormal_mechanism: session.abnormal_mechanism,
    attrs: {
      release: session.release,
      environment: session.environment,
      ip_address: session.ipAddress,
      user_agent: session.userAgent
    }
  });
}
function generateTraceId() {
  return uuid4();
}
function generateSpanId() {
  return uuid4().substring(16);
}
function merge(initialObj, mergeObj, levels = 2) {
  if (!mergeObj || typeof mergeObj !== "object" || levels <= 0) {
    return mergeObj;
  }
  if (initialObj && mergeObj && Object.keys(mergeObj).length === 0) {
    return initialObj;
  }
  const output = { ...initialObj };
  for (const key in mergeObj) {
    if (Object.prototype.hasOwnProperty.call(mergeObj, key)) {
      output[key] = merge(output[key], mergeObj[key], levels - 1);
    }
  }
  return output;
}
const SCOPE_SPAN_FIELD = "_sentrySpan";
function _setSpanForScope(scope, span) {
  if (span) {
    addNonEnumerableProperty(scope, SCOPE_SPAN_FIELD, span);
  } else {
    delete scope[SCOPE_SPAN_FIELD];
  }
}
function _getSpanForScope(scope) {
  return scope[SCOPE_SPAN_FIELD];
}
const DEFAULT_MAX_BREADCRUMBS = 100;
class ScopeClass {
  /** Flag if notifying is happening. */
  /** Callback for client to receive scope changes. */
  /** Callback list that will be called during event processing. */
  /** Array of breadcrumbs. */
  /** User */
  /** Tags */
  /** Extra */
  /** Contexts */
  /** Attachments */
  /** Propagation Context for distributed tracing */
  /**
   * A place to stash data which is needed at some point in the SDK's event processing pipeline but which shouldn't get
   * sent to Sentry
   */
  /** Fingerprint */
  /** Severity */
  /**
   * Transaction Name
   *
   * IMPORTANT: The transaction name on the scope has nothing to do with root spans/transaction objects.
   * It's purpose is to assign a transaction to the scope that's added to non-transaction events.
   */
  /** Session */
  /** Request Mode Session Status */
  // eslint-disable-next-line deprecation/deprecation
  /** The client on this scope */
  /** Contains the last event id of a captured event.  */
  // NOTE: Any field which gets added here should get added not only to the constructor but also to the `clone` method.
  constructor() {
    this._notifyingListeners = false;
    this._scopeListeners = [];
    this._eventProcessors = [];
    this._breadcrumbs = [];
    this._attachments = [];
    this._user = {};
    this._tags = {};
    this._extra = {};
    this._contexts = {};
    this._sdkProcessingMetadata = {};
    this._propagationContext = {
      traceId: generateTraceId(),
      spanId: generateSpanId()
    };
  }
  /**
   * @inheritDoc
   */
  clone() {
    const newScope = new ScopeClass();
    newScope._breadcrumbs = [...this._breadcrumbs];
    newScope._tags = { ...this._tags };
    newScope._extra = { ...this._extra };
    newScope._contexts = { ...this._contexts };
    if (this._contexts.flags) {
      newScope._contexts.flags = {
        values: [...this._contexts.flags.values]
      };
    }
    newScope._user = this._user;
    newScope._level = this._level;
    newScope._session = this._session;
    newScope._transactionName = this._transactionName;
    newScope._fingerprint = this._fingerprint;
    newScope._eventProcessors = [...this._eventProcessors];
    newScope._requestSession = this._requestSession;
    newScope._attachments = [...this._attachments];
    newScope._sdkProcessingMetadata = { ...this._sdkProcessingMetadata };
    newScope._propagationContext = { ...this._propagationContext };
    newScope._client = this._client;
    newScope._lastEventId = this._lastEventId;
    _setSpanForScope(newScope, _getSpanForScope(this));
    return newScope;
  }
  /**
   * @inheritDoc
   */
  setClient(client) {
    this._client = client;
  }
  /**
   * @inheritDoc
   */
  setLastEventId(lastEventId) {
    this._lastEventId = lastEventId;
  }
  /**
   * @inheritDoc
   */
  getClient() {
    return this._client;
  }
  /**
   * @inheritDoc
   */
  lastEventId() {
    return this._lastEventId;
  }
  /**
   * @inheritDoc
   */
  addScopeListener(callback) {
    this._scopeListeners.push(callback);
  }
  /**
   * @inheritDoc
   */
  addEventProcessor(callback) {
    this._eventProcessors.push(callback);
    return this;
  }
  /**
   * @inheritDoc
   */
  setUser(user) {
    this._user = user || {
      email: void 0,
      id: void 0,
      ip_address: void 0,
      username: void 0
    };
    if (this._session) {
      updateSession(this._session, { user });
    }
    this._notifyScopeListeners();
    return this;
  }
  /**
   * @inheritDoc
   */
  getUser() {
    return this._user;
  }
  /**
   * @inheritDoc
   */
  // eslint-disable-next-line deprecation/deprecation
  getRequestSession() {
    return this._requestSession;
  }
  /**
   * @inheritDoc
   */
  // eslint-disable-next-line deprecation/deprecation
  setRequestSession(requestSession) {
    this._requestSession = requestSession;
    return this;
  }
  /**
   * @inheritDoc
   */
  setTags(tags) {
    this._tags = {
      ...this._tags,
      ...tags
    };
    this._notifyScopeListeners();
    return this;
  }
  /**
   * @inheritDoc
   */
  setTag(key, value) {
    this._tags = { ...this._tags, [key]: value };
    this._notifyScopeListeners();
    return this;
  }
  /**
   * @inheritDoc
   */
  setExtras(extras) {
    this._extra = {
      ...this._extra,
      ...extras
    };
    this._notifyScopeListeners();
    return this;
  }
  /**
   * @inheritDoc
   */
  setExtra(key, extra) {
    this._extra = { ...this._extra, [key]: extra };
    this._notifyScopeListeners();
    return this;
  }
  /**
   * @inheritDoc
   */
  setFingerprint(fingerprint) {
    this._fingerprint = fingerprint;
    this._notifyScopeListeners();
    return this;
  }
  /**
   * @inheritDoc
   */
  setLevel(level) {
    this._level = level;
    this._notifyScopeListeners();
    return this;
  }
  /**
   * Sets the transaction name on the scope so that the name of e.g. taken server route or
   * the page location is attached to future events.
   *
   * IMPORTANT: Calling this function does NOT change the name of the currently active
   * root span. If you want to change the name of the active root span, use
   * `Sentry.updateSpanName(rootSpan, 'new name')` instead.
   *
   * By default, the SDK updates the scope's transaction name automatically on sensible
   * occasions, such as a page navigation or when handling a new request on the server.
   */
  setTransactionName(name) {
    this._transactionName = name;
    this._notifyScopeListeners();
    return this;
  }
  /**
   * @inheritDoc
   */
  setContext(key, context) {
    if (context === null) {
      delete this._contexts[key];
    } else {
      this._contexts[key] = context;
    }
    this._notifyScopeListeners();
    return this;
  }
  /**
   * @inheritDoc
   */
  setSession(session) {
    if (!session) {
      delete this._session;
    } else {
      this._session = session;
    }
    this._notifyScopeListeners();
    return this;
  }
  /**
   * @inheritDoc
   */
  getSession() {
    return this._session;
  }
  /**
   * @inheritDoc
   */
  update(captureContext) {
    if (!captureContext) {
      return this;
    }
    const scopeToMerge = typeof captureContext === "function" ? captureContext(this) : captureContext;
    const [scopeInstance, requestSession] = scopeToMerge instanceof Scope ? (
      // eslint-disable-next-line deprecation/deprecation
      [scopeToMerge.getScopeData(), scopeToMerge.getRequestSession()]
    ) : isPlainObject$1(scopeToMerge) ? [captureContext, captureContext.requestSession] : [];
    const { tags, extra, user, contexts, level, fingerprint = [], propagationContext } = scopeInstance || {};
    this._tags = { ...this._tags, ...tags };
    this._extra = { ...this._extra, ...extra };
    this._contexts = { ...this._contexts, ...contexts };
    if (user && Object.keys(user).length) {
      this._user = user;
    }
    if (level) {
      this._level = level;
    }
    if (fingerprint.length) {
      this._fingerprint = fingerprint;
    }
    if (propagationContext) {
      this._propagationContext = propagationContext;
    }
    if (requestSession) {
      this._requestSession = requestSession;
    }
    return this;
  }
  /**
   * @inheritDoc
   */
  clear() {
    this._breadcrumbs = [];
    this._tags = {};
    this._extra = {};
    this._user = {};
    this._contexts = {};
    this._level = void 0;
    this._transactionName = void 0;
    this._fingerprint = void 0;
    this._requestSession = void 0;
    this._session = void 0;
    _setSpanForScope(this, void 0);
    this._attachments = [];
    this.setPropagationContext({ traceId: generateTraceId() });
    this._notifyScopeListeners();
    return this;
  }
  /**
   * @inheritDoc
   */
  addBreadcrumb(breadcrumb, maxBreadcrumbs) {
    const maxCrumbs = typeof maxBreadcrumbs === "number" ? maxBreadcrumbs : DEFAULT_MAX_BREADCRUMBS;
    if (maxCrumbs <= 0) {
      return this;
    }
    const mergedBreadcrumb = {
      timestamp: dateTimestampInSeconds(),
      ...breadcrumb
    };
    this._breadcrumbs.push(mergedBreadcrumb);
    if (this._breadcrumbs.length > maxCrumbs) {
      this._breadcrumbs = this._breadcrumbs.slice(-maxCrumbs);
      if (this._client) {
        this._client.recordDroppedEvent("buffer_overflow", "log_item");
      }
    }
    this._notifyScopeListeners();
    return this;
  }
  /**
   * @inheritDoc
   */
  getLastBreadcrumb() {
    return this._breadcrumbs[this._breadcrumbs.length - 1];
  }
  /**
   * @inheritDoc
   */
  clearBreadcrumbs() {
    this._breadcrumbs = [];
    this._notifyScopeListeners();
    return this;
  }
  /**
   * @inheritDoc
   */
  addAttachment(attachment) {
    this._attachments.push(attachment);
    return this;
  }
  /**
   * @inheritDoc
   */
  clearAttachments() {
    this._attachments = [];
    return this;
  }
  /** @inheritDoc */
  getScopeData() {
    return {
      breadcrumbs: this._breadcrumbs,
      attachments: this._attachments,
      contexts: this._contexts,
      tags: this._tags,
      extra: this._extra,
      user: this._user,
      level: this._level,
      fingerprint: this._fingerprint || [],
      eventProcessors: this._eventProcessors,
      propagationContext: this._propagationContext,
      sdkProcessingMetadata: this._sdkProcessingMetadata,
      transactionName: this._transactionName,
      span: _getSpanForScope(this)
    };
  }
  /**
   * @inheritDoc
   */
  setSDKProcessingMetadata(newData) {
    this._sdkProcessingMetadata = merge(this._sdkProcessingMetadata, newData, 2);
    return this;
  }
  /**
   * @inheritDoc
   */
  setPropagationContext(context) {
    this._propagationContext = {
      // eslint-disable-next-line deprecation/deprecation
      spanId: generateSpanId(),
      ...context
    };
    return this;
  }
  /**
   * @inheritDoc
   */
  getPropagationContext() {
    return this._propagationContext;
  }
  /**
   * @inheritDoc
   */
  captureException(exception, hint) {
    const eventId = hint && hint.event_id ? hint.event_id : uuid4();
    if (!this._client) {
      logger.warn("No client configured on scope - will not capture exception!");
      return eventId;
    }
    const syntheticException = new Error("Sentry syntheticException");
    this._client.captureException(
      exception,
      {
        originalException: exception,
        syntheticException,
        ...hint,
        event_id: eventId
      },
      this
    );
    return eventId;
  }
  /**
   * @inheritDoc
   */
  captureMessage(message, level, hint) {
    const eventId = hint && hint.event_id ? hint.event_id : uuid4();
    if (!this._client) {
      logger.warn("No client configured on scope - will not capture message!");
      return eventId;
    }
    const syntheticException = new Error(message);
    this._client.captureMessage(
      message,
      level,
      {
        originalException: message,
        syntheticException,
        ...hint,
        event_id: eventId
      },
      this
    );
    return eventId;
  }
  /**
   * @inheritDoc
   */
  captureEvent(event, hint) {
    const eventId = hint && hint.event_id ? hint.event_id : uuid4();
    if (!this._client) {
      logger.warn("No client configured on scope - will not capture event!");
      return eventId;
    }
    this._client.captureEvent(event, { ...hint, event_id: eventId }, this);
    return eventId;
  }
  /**
   * This will be called on every set call.
   */
  _notifyScopeListeners() {
    if (!this._notifyingListeners) {
      this._notifyingListeners = true;
      this._scopeListeners.forEach((callback) => {
        callback(this);
      });
      this._notifyingListeners = false;
    }
  }
}
const Scope = ScopeClass;
function getDefaultCurrentScope() {
  return getGlobalSingleton("defaultCurrentScope", () => new Scope());
}
function getDefaultIsolationScope() {
  return getGlobalSingleton("defaultIsolationScope", () => new Scope());
}
class AsyncContextStack {
  constructor(scope, isolationScope) {
    let assignedScope;
    if (!scope) {
      assignedScope = new Scope();
    } else {
      assignedScope = scope;
    }
    let assignedIsolationScope;
    if (!isolationScope) {
      assignedIsolationScope = new Scope();
    } else {
      assignedIsolationScope = isolationScope;
    }
    this._stack = [{ scope: assignedScope }];
    this._isolationScope = assignedIsolationScope;
  }
  /**
   * Fork a scope for the stack.
   */
  withScope(callback) {
    const scope = this._pushScope();
    let maybePromiseResult;
    try {
      maybePromiseResult = callback(scope);
    } catch (e) {
      this._popScope();
      throw e;
    }
    if (isThenable(maybePromiseResult)) {
      return maybePromiseResult.then(
        (res) => {
          this._popScope();
          return res;
        },
        (e) => {
          this._popScope();
          throw e;
        }
      );
    }
    this._popScope();
    return maybePromiseResult;
  }
  /**
   * Get the client of the stack.
   */
  getClient() {
    return this.getStackTop().client;
  }
  /**
   * Returns the scope of the top stack.
   */
  getScope() {
    return this.getStackTop().scope;
  }
  /**
   * Get the isolation scope for the stack.
   */
  getIsolationScope() {
    return this._isolationScope;
  }
  /**
   * Returns the topmost scope layer in the order domain > local > process.
   */
  getStackTop() {
    return this._stack[this._stack.length - 1];
  }
  /**
   * Push a scope to the stack.
   */
  _pushScope() {
    const scope = this.getScope().clone();
    this._stack.push({
      client: this.getClient(),
      scope
    });
    return scope;
  }
  /**
   * Pop a scope from the stack.
   */
  _popScope() {
    if (this._stack.length <= 1) return false;
    return !!this._stack.pop();
  }
}
function getAsyncContextStack() {
  const registry = getMainCarrier();
  const sentry = getSentryCarrier(registry);
  return sentry.stack = sentry.stack || new AsyncContextStack(getDefaultCurrentScope(), getDefaultIsolationScope());
}
function withScope$1(callback) {
  return getAsyncContextStack().withScope(callback);
}
function withSetScope(scope, callback) {
  const stack2 = getAsyncContextStack();
  return stack2.withScope(() => {
    stack2.getStackTop().scope = scope;
    return callback(scope);
  });
}
function withIsolationScope(callback) {
  return getAsyncContextStack().withScope(() => {
    return callback(getAsyncContextStack().getIsolationScope());
  });
}
function getStackAsyncContextStrategy() {
  return {
    withIsolationScope,
    withScope: withScope$1,
    withSetScope,
    withSetIsolationScope: (_isolationScope, callback) => {
      return withIsolationScope(callback);
    },
    getCurrentScope: () => getAsyncContextStack().getScope(),
    getIsolationScope: () => getAsyncContextStack().getIsolationScope()
  };
}
function getAsyncContextStrategy(carrier) {
  const sentry = getSentryCarrier(carrier);
  if (sentry.acs) {
    return sentry.acs;
  }
  return getStackAsyncContextStrategy();
}
function getCurrentScope$1() {
  const carrier = getMainCarrier();
  const acs = getAsyncContextStrategy(carrier);
  return acs.getCurrentScope();
}
function getIsolationScope() {
  const carrier = getMainCarrier();
  const acs = getAsyncContextStrategy(carrier);
  return acs.getIsolationScope();
}
function getGlobalScope() {
  return getGlobalSingleton("globalScope", () => new Scope());
}
function withScope(...rest) {
  const carrier = getMainCarrier();
  const acs = getAsyncContextStrategy(carrier);
  if (rest.length === 2) {
    const [scope, callback] = rest;
    if (!scope) {
      return acs.withScope(callback);
    }
    return acs.withSetScope(scope, callback);
  }
  return acs.withScope(rest[0]);
}
function getClient() {
  return getCurrentScope$1().getClient();
}
function getTraceContextFromScope(scope) {
  const propagationContext = scope.getPropagationContext();
  const { traceId, spanId, parentSpanId } = propagationContext;
  const traceContext = dropUndefinedKeys({
    trace_id: traceId,
    span_id: spanId,
    parent_span_id: parentSpanId
  });
  return traceContext;
}
const METRICS_SPAN_FIELD = "_sentryMetrics";
function getMetricSummaryJsonForSpan(span) {
  const storage = span[METRICS_SPAN_FIELD];
  if (!storage) {
    return void 0;
  }
  const output = {};
  for (const [, [exportKey, summary]] of storage) {
    const arr = output[exportKey] || (output[exportKey] = []);
    arr.push(dropUndefinedKeys(summary));
  }
  return output;
}
const SEMANTIC_ATTRIBUTE_SENTRY_SOURCE = "sentry.source";
const SEMANTIC_ATTRIBUTE_SENTRY_SAMPLE_RATE = "sentry.sample_rate";
const SEMANTIC_ATTRIBUTE_SENTRY_OP = "sentry.op";
const SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN = "sentry.origin";
const SPAN_STATUS_UNSET = 0;
const SPAN_STATUS_OK = 1;
const SENTRY_BAGGAGE_KEY_PREFIX = "sentry-";
const SENTRY_BAGGAGE_KEY_PREFIX_REGEX = /^sentry-/;
function baggageHeaderToDynamicSamplingContext(baggageHeader) {
  const baggageObject = parseBaggageHeader(baggageHeader);
  if (!baggageObject) {
    return void 0;
  }
  const dynamicSamplingContext = Object.entries(baggageObject).reduce((acc, [key, value]) => {
    if (key.match(SENTRY_BAGGAGE_KEY_PREFIX_REGEX)) {
      const nonPrefixedKey = key.slice(SENTRY_BAGGAGE_KEY_PREFIX.length);
      acc[nonPrefixedKey] = value;
    }
    return acc;
  }, {});
  if (Object.keys(dynamicSamplingContext).length > 0) {
    return dynamicSamplingContext;
  } else {
    return void 0;
  }
}
function parseBaggageHeader(baggageHeader) {
  if (!baggageHeader || !isString$1(baggageHeader) && !Array.isArray(baggageHeader)) {
    return void 0;
  }
  if (Array.isArray(baggageHeader)) {
    return baggageHeader.reduce((acc, curr) => {
      const currBaggageObject = baggageHeaderToObject(curr);
      Object.entries(currBaggageObject).forEach(([key, value]) => {
        acc[key] = value;
      });
      return acc;
    }, {});
  }
  return baggageHeaderToObject(baggageHeader);
}
function baggageHeaderToObject(baggageHeader) {
  return baggageHeader.split(",").map((baggageEntry) => baggageEntry.split("=").map((keyOrValue) => decodeURIComponent(keyOrValue.trim()))).reduce((acc, [key, value]) => {
    if (key && value) {
      acc[key] = value;
    }
    return acc;
  }, {});
}
const TRACE_FLAG_SAMPLED = 1;
let hasShownSpanDropWarning = false;
function spanToTraceContext(span) {
  const { spanId, traceId: trace_id, isRemote } = span.spanContext();
  const parent_span_id = isRemote ? spanId : spanToJSON(span).parent_span_id;
  const span_id = isRemote ? generateSpanId() : spanId;
  return dropUndefinedKeys({
    parent_span_id,
    span_id,
    trace_id
  });
}
function spanTimeInputToSeconds(input) {
  if (typeof input === "number") {
    return ensureTimestampInSeconds(input);
  }
  if (Array.isArray(input)) {
    return input[0] + input[1] / 1e9;
  }
  if (input instanceof Date) {
    return ensureTimestampInSeconds(input.getTime());
  }
  return timestampInSeconds();
}
function ensureTimestampInSeconds(timestamp) {
  const isMs = timestamp > 9999999999;
  return isMs ? timestamp / 1e3 : timestamp;
}
function spanToJSON(span) {
  if (spanIsSentrySpan(span)) {
    return span.getSpanJSON();
  }
  try {
    const { spanId: span_id, traceId: trace_id } = span.spanContext();
    if (spanIsOpenTelemetrySdkTraceBaseSpan(span)) {
      const { attributes, startTime, name, endTime, parentSpanId, status } = span;
      return dropUndefinedKeys({
        span_id,
        trace_id,
        data: attributes,
        description: name,
        parent_span_id: parentSpanId,
        start_timestamp: spanTimeInputToSeconds(startTime),
        // This is [0,0] by default in OTEL, in which case we want to interpret this as no end time
        timestamp: spanTimeInputToSeconds(endTime) || void 0,
        status: getStatusMessage(status),
        op: attributes[SEMANTIC_ATTRIBUTE_SENTRY_OP],
        origin: attributes[SEMANTIC_ATTRIBUTE_SENTRY_ORIGIN],
        _metrics_summary: getMetricSummaryJsonForSpan(span)
      });
    }
    return {
      span_id,
      trace_id
    };
  } catch (e) {
    return {};
  }
}
function spanIsOpenTelemetrySdkTraceBaseSpan(span) {
  const castSpan = span;
  return !!castSpan.attributes && !!castSpan.startTime && !!castSpan.name && !!castSpan.endTime && !!castSpan.status;
}
function spanIsSentrySpan(span) {
  return typeof span.getSpanJSON === "function";
}
function spanIsSampled(span) {
  const { traceFlags } = span.spanContext();
  return traceFlags === TRACE_FLAG_SAMPLED;
}
function getStatusMessage(status) {
  if (!status || status.code === SPAN_STATUS_UNSET) {
    return void 0;
  }
  if (status.code === SPAN_STATUS_OK) {
    return "ok";
  }
  return status.message || "unknown_error";
}
const ROOT_SPAN_FIELD = "_sentryRootSpan";
function getRootSpan(span) {
  return span[ROOT_SPAN_FIELD] || span;
}
function showSpanDropWarning() {
  if (!hasShownSpanDropWarning) {
    consoleSandbox(() => {
      console.warn(
        "[Sentry] Deprecation warning: Returning null from `beforeSendSpan` will be disallowed from SDK version 9.0.0 onwards. The callback will only support mutating spans. To drop certain spans, configure the respective integrations directly."
      );
    });
    hasShownSpanDropWarning = true;
  }
}
function hasTracingEnabled(maybeOptions) {
  if (typeof __SENTRY_TRACING__ === "boolean" && !__SENTRY_TRACING__) {
    return false;
  }
  const client = getClient();
  const options = client && client.getOptions();
  return !!options && (options.enableTracing || "tracesSampleRate" in options || "tracesSampler" in options);
}
const DEFAULT_ENVIRONMENT = "production";
const FROZEN_DSC_FIELD = "_frozenDsc";
function getDynamicSamplingContextFromClient(trace_id, client) {
  const options = client.getOptions();
  const { publicKey: public_key } = client.getDsn() || {};
  const dsc = dropUndefinedKeys({
    environment: options.environment || DEFAULT_ENVIRONMENT,
    release: options.release,
    public_key,
    trace_id
  });
  client.emit("createDsc", dsc);
  return dsc;
}
function getDynamicSamplingContextFromScope(client, scope) {
  const propagationContext = scope.getPropagationContext();
  return propagationContext.dsc || getDynamicSamplingContextFromClient(propagationContext.traceId, client);
}
function getDynamicSamplingContextFromSpan(span) {
  const client = getClient();
  if (!client) {
    return {};
  }
  const rootSpan = getRootSpan(span);
  const frozenDsc = rootSpan[FROZEN_DSC_FIELD];
  if (frozenDsc) {
    return frozenDsc;
  }
  const traceState = rootSpan.spanContext().traceState;
  const traceStateDsc = traceState && traceState.get("sentry.dsc");
  const dscOnTraceState = traceStateDsc && baggageHeaderToDynamicSamplingContext(traceStateDsc);
  if (dscOnTraceState) {
    return dscOnTraceState;
  }
  const dsc = getDynamicSamplingContextFromClient(span.spanContext().traceId, client);
  const jsonSpan = spanToJSON(rootSpan);
  const attributes = jsonSpan.data || {};
  const maybeSampleRate = attributes[SEMANTIC_ATTRIBUTE_SENTRY_SAMPLE_RATE];
  if (maybeSampleRate != null) {
    dsc.sample_rate = `${maybeSampleRate}`;
  }
  const source = attributes[SEMANTIC_ATTRIBUTE_SENTRY_SOURCE];
  const name = jsonSpan.description;
  if (source !== "url" && name) {
    dsc.transaction = name;
  }
  if (hasTracingEnabled()) {
    dsc.sampled = String(spanIsSampled(rootSpan));
  }
  client.emit("createDsc", dsc, rootSpan);
  return dsc;
}
function parseSampleRate(sampleRate) {
  if (typeof sampleRate === "boolean") {
    return Number(sampleRate);
  }
  const rate = typeof sampleRate === "string" ? parseFloat(sampleRate) : sampleRate;
  if (typeof rate !== "number" || isNaN(rate) || rate < 0 || rate > 1) {
    DEBUG_BUILD$3 && logger.warn(
      `[Tracing] Given sample rate is invalid. Sample rate must be a boolean or a number between 0 and 1. Got ${JSON.stringify(
        sampleRate
      )} of type ${JSON.stringify(typeof sampleRate)}.`
    );
    return void 0;
  }
  return rate;
}
const DSN_REGEX = /^(?:(\w+):)\/\/(?:(\w+)(?::(\w+)?)?@)([\w.-]+)(?::(\d+))?\/(.+)/;
function isValidProtocol(protocol) {
  return protocol === "http" || protocol === "https";
}
function dsnToString(dsn, withPassword = false) {
  const { host, path, pass, port, projectId, protocol, publicKey } = dsn;
  return `${protocol}://${publicKey}${withPassword && pass ? `:${pass}` : ""}@${host}${port ? `:${port}` : ""}/${path ? `${path}/` : path}${projectId}`;
}
function dsnFromString(str) {
  const match = DSN_REGEX.exec(str);
  if (!match) {
    consoleSandbox(() => {
      console.error(`Invalid Sentry Dsn: ${str}`);
    });
    return void 0;
  }
  const [protocol, publicKey, pass = "", host = "", port = "", lastPath = ""] = match.slice(1);
  let path = "";
  let projectId = lastPath;
  const split = projectId.split("/");
  if (split.length > 1) {
    path = split.slice(0, -1).join("/");
    projectId = split.pop();
  }
  if (projectId) {
    const projectMatch = projectId.match(/^\d+/);
    if (projectMatch) {
      projectId = projectMatch[0];
    }
  }
  return dsnFromComponents({ host, pass, path, projectId, port, protocol, publicKey });
}
function dsnFromComponents(components) {
  return {
    protocol: components.protocol,
    publicKey: components.publicKey || "",
    pass: components.pass || "",
    host: components.host,
    port: components.port || "",
    path: components.path || "",
    projectId: components.projectId
  };
}
function validateDsn(dsn) {
  if (!DEBUG_BUILD$2) {
    return true;
  }
  const { port, projectId, protocol } = dsn;
  const requiredComponents = ["protocol", "publicKey", "host", "projectId"];
  const hasMissingRequiredComponent = requiredComponents.find((component) => {
    if (!dsn[component]) {
      logger.error(`Invalid Sentry Dsn: ${component} missing`);
      return true;
    }
    return false;
  });
  if (hasMissingRequiredComponent) {
    return false;
  }
  if (!projectId.match(/^\d+$/)) {
    logger.error(`Invalid Sentry Dsn: Invalid projectId ${projectId}`);
    return false;
  }
  if (!isValidProtocol(protocol)) {
    logger.error(`Invalid Sentry Dsn: Invalid protocol ${protocol}`);
    return false;
  }
  if (port && isNaN(parseInt(port, 10))) {
    logger.error(`Invalid Sentry Dsn: Invalid port ${port}`);
    return false;
  }
  return true;
}
function makeDsn(from) {
  const components = typeof from === "string" ? dsnFromString(from) : dsnFromComponents(from);
  if (!components || !validateDsn(components)) {
    return void 0;
  }
  return components;
}
function memoBuilder() {
  const hasWeakSet = typeof WeakSet === "function";
  const inner = hasWeakSet ? /* @__PURE__ */ new WeakSet() : [];
  function memoize(obj) {
    if (hasWeakSet) {
      if (inner.has(obj)) {
        return true;
      }
      inner.add(obj);
      return false;
    }
    for (let i = 0; i < inner.length; i++) {
      const value = inner[i];
      if (value === obj) {
        return true;
      }
    }
    inner.push(obj);
    return false;
  }
  function unmemoize(obj) {
    if (hasWeakSet) {
      inner.delete(obj);
    } else {
      for (let i = 0; i < inner.length; i++) {
        if (inner[i] === obj) {
          inner.splice(i, 1);
          break;
        }
      }
    }
  }
  return [memoize, unmemoize];
}
function normalize(input, depth = 100, maxProperties = Infinity) {
  try {
    return visit("", input, depth, maxProperties);
  } catch (err) {
    return { ERROR: `**non-serializable** (${err})` };
  }
}
function normalizeToSize(object, depth = 3, maxSize = 100 * 1024) {
  const normalized = normalize(object, depth);
  if (jsonSize(normalized) > maxSize) {
    return normalizeToSize(object, depth - 1, maxSize);
  }
  return normalized;
}
function visit(key, value, depth = Infinity, maxProperties = Infinity, memo = memoBuilder()) {
  const [memoize, unmemoize] = memo;
  if (value == null || // this matches null and undefined -> eqeq not eqeqeq
  ["boolean", "string"].includes(typeof value) || typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  const stringified = stringifyValue(key, value);
  if (!stringified.startsWith("[object ")) {
    return stringified;
  }
  if (value["__sentry_skip_normalization__"]) {
    return value;
  }
  const remainingDepth = typeof value["__sentry_override_normalization_depth__"] === "number" ? value["__sentry_override_normalization_depth__"] : depth;
  if (remainingDepth === 0) {
    return stringified.replace("object ", "");
  }
  if (memoize(value)) {
    return "[Circular ~]";
  }
  const valueWithToJSON = value;
  if (valueWithToJSON && typeof valueWithToJSON.toJSON === "function") {
    try {
      const jsonValue = valueWithToJSON.toJSON();
      return visit("", jsonValue, remainingDepth - 1, maxProperties, memo);
    } catch (err) {
    }
  }
  const normalized = Array.isArray(value) ? [] : {};
  let numAdded = 0;
  const visitable = convertToPlainObject(value);
  for (const visitKey in visitable) {
    if (!Object.prototype.hasOwnProperty.call(visitable, visitKey)) {
      continue;
    }
    if (numAdded >= maxProperties) {
      normalized[visitKey] = "[MaxProperties ~]";
      break;
    }
    const visitValue = visitable[visitKey];
    normalized[visitKey] = visit(visitKey, visitValue, remainingDepth - 1, maxProperties, memo);
    numAdded++;
  }
  unmemoize(value);
  return normalized;
}
function stringifyValue(key, value) {
  try {
    if (key === "domain" && value && typeof value === "object" && value._events) {
      return "[Domain]";
    }
    if (key === "domainEmitter") {
      return "[DomainEmitter]";
    }
    if (typeof global !== "undefined" && value === global) {
      return "[Global]";
    }
    if (typeof window !== "undefined" && value === window) {
      return "[Window]";
    }
    if (typeof document !== "undefined" && value === document) {
      return "[Document]";
    }
    if (isVueViewModel(value)) {
      return "[VueViewModel]";
    }
    if (isSyntheticEvent(value)) {
      return "[SyntheticEvent]";
    }
    if (typeof value === "number" && !Number.isFinite(value)) {
      return `[${value}]`;
    }
    if (typeof value === "function") {
      return `[Function: ${getFunctionName(value)}]`;
    }
    if (typeof value === "symbol") {
      return `[${String(value)}]`;
    }
    if (typeof value === "bigint") {
      return `[BigInt: ${String(value)}]`;
    }
    const objName = getConstructorName(value);
    if (/^HTML(\w*)Element$/.test(objName)) {
      return `[HTMLElement: ${objName}]`;
    }
    return `[object ${objName}]`;
  } catch (err) {
    return `**non-serializable** (${err})`;
  }
}
function getConstructorName(value) {
  const prototype = Object.getPrototypeOf(value);
  return prototype ? prototype.constructor.name : "null prototype";
}
function utf8Length(value) {
  return ~-encodeURI(value).split(/%..|./).length;
}
function jsonSize(value) {
  return utf8Length(JSON.stringify(value));
}
function createEnvelope(headers, items = []) {
  return [headers, items];
}
function addItemToEnvelope(envelope, newItem) {
  const [headers, items] = envelope;
  return [headers, [...items, newItem]];
}
function forEachEnvelopeItem(envelope, callback) {
  const envelopeItems = envelope[1];
  for (const envelopeItem of envelopeItems) {
    const envelopeItemType = envelopeItem[0].type;
    const result = callback(envelopeItem, envelopeItemType);
    if (result) {
      return true;
    }
  }
  return false;
}
function encodeUTF8(input) {
  return GLOBAL_OBJ.__SENTRY__ && GLOBAL_OBJ.__SENTRY__.encodePolyfill ? GLOBAL_OBJ.__SENTRY__.encodePolyfill(input) : new TextEncoder().encode(input);
}
function serializeEnvelope(envelope) {
  const [envHeaders, items] = envelope;
  let parts = JSON.stringify(envHeaders);
  function append(next) {
    if (typeof parts === "string") {
      parts = typeof next === "string" ? parts + next : [encodeUTF8(parts), next];
    } else {
      parts.push(typeof next === "string" ? encodeUTF8(next) : next);
    }
  }
  for (const item of items) {
    const [itemHeaders, payload] = item;
    append(`
${JSON.stringify(itemHeaders)}
`);
    if (typeof payload === "string" || payload instanceof Uint8Array) {
      append(payload);
    } else {
      let stringifiedPayload;
      try {
        stringifiedPayload = JSON.stringify(payload);
      } catch (e) {
        stringifiedPayload = JSON.stringify(normalize(payload));
      }
      append(stringifiedPayload);
    }
  }
  return typeof parts === "string" ? parts : concatBuffers(parts);
}
function concatBuffers(buffers) {
  const totalLength = buffers.reduce((acc, buf) => acc + buf.length, 0);
  const merged = new Uint8Array(totalLength);
  let offset = 0;
  for (const buffer of buffers) {
    merged.set(buffer, offset);
    offset += buffer.length;
  }
  return merged;
}
function createAttachmentEnvelopeItem(attachment) {
  const buffer = typeof attachment.data === "string" ? encodeUTF8(attachment.data) : attachment.data;
  return [
    dropUndefinedKeys({
      type: "attachment",
      length: buffer.length,
      filename: attachment.filename,
      content_type: attachment.contentType,
      attachment_type: attachment.attachmentType
    }),
    buffer
  ];
}
const ITEM_TYPE_TO_DATA_CATEGORY_MAP = {
  session: "session",
  sessions: "session",
  attachment: "attachment",
  transaction: "transaction",
  event: "error",
  client_report: "internal",
  user_report: "default",
  profile: "profile",
  profile_chunk: "profile",
  replay_event: "replay",
  replay_recording: "replay",
  check_in: "monitor",
  feedback: "feedback",
  span: "span",
  statsd: "metric_bucket",
  raw_security: "security"
};
function envelopeItemTypeToDataCategory(type) {
  return ITEM_TYPE_TO_DATA_CATEGORY_MAP[type];
}
function getSdkMetadataForEnvelopeHeader(metadataOrEvent) {
  if (!metadataOrEvent || !metadataOrEvent.sdk) {
    return;
  }
  const { name, version: version2 } = metadataOrEvent.sdk;
  return { name, version: version2 };
}
function createEventEnvelopeHeaders(event, sdkInfo, tunnel, dsn) {
  const dynamicSamplingContext = event.sdkProcessingMetadata && event.sdkProcessingMetadata.dynamicSamplingContext;
  return {
    event_id: event.event_id,
    sent_at: (/* @__PURE__ */ new Date()).toISOString(),
    ...sdkInfo && { sdk: sdkInfo },
    ...!!tunnel && dsn && { dsn: dsnToString(dsn) },
    ...dynamicSamplingContext && {
      trace: dropUndefinedKeys({ ...dynamicSamplingContext })
    }
  };
}
function enhanceEventWithSdkInfo(event, sdkInfo) {
  if (!sdkInfo) {
    return event;
  }
  event.sdk = event.sdk || {};
  event.sdk.name = event.sdk.name || sdkInfo.name;
  event.sdk.version = event.sdk.version || sdkInfo.version;
  event.sdk.integrations = [...event.sdk.integrations || [], ...sdkInfo.integrations || []];
  event.sdk.packages = [...event.sdk.packages || [], ...sdkInfo.packages || []];
  return event;
}
function createSessionEnvelope(session, dsn, metadata, tunnel) {
  const sdkInfo = getSdkMetadataForEnvelopeHeader(metadata);
  const envelopeHeaders = {
    sent_at: (/* @__PURE__ */ new Date()).toISOString(),
    ...sdkInfo && { sdk: sdkInfo },
    ...!!tunnel && dsn && { dsn: dsnToString(dsn) }
  };
  const envelopeItem = "aggregates" in session ? [{ type: "sessions" }, session] : [{ type: "session" }, session.toJSON()];
  return createEnvelope(envelopeHeaders, [envelopeItem]);
}
function createEventEnvelope(event, dsn, metadata, tunnel) {
  const sdkInfo = getSdkMetadataForEnvelopeHeader(metadata);
  const eventType = event.type && event.type !== "replay_event" ? event.type : "event";
  enhanceEventWithSdkInfo(event, metadata && metadata.sdk);
  const envelopeHeaders = createEventEnvelopeHeaders(event, sdkInfo, tunnel, dsn);
  delete event.sdkProcessingMetadata;
  const eventItem = [{ type: eventType }, event];
  return createEnvelope(envelopeHeaders, [eventItem]);
}
function notifyEventProcessors(processors, event, hint, index = 0) {
  return new SyncPromise((resolve2, reject) => {
    const processor = processors[index];
    if (event === null || typeof processor !== "function") {
      resolve2(event);
    } else {
      const result = processor({ ...event }, hint);
      DEBUG_BUILD$3 && processor.id && result === null && logger.log(`Event processor "${processor.id}" dropped event`);
      if (isThenable(result)) {
        void result.then((final) => notifyEventProcessors(processors, final, hint, index + 1).then(resolve2)).then(null, reject);
      } else {
        void notifyEventProcessors(processors, result, hint, index + 1).then(resolve2).then(null, reject);
      }
    }
  });
}
let parsedStackResults;
let lastKeysCount;
let cachedFilenameDebugIds;
function getFilenameToDebugIdMap(stackParser) {
  const debugIdMap = GLOBAL_OBJ._sentryDebugIds;
  if (!debugIdMap) {
    return {};
  }
  const debugIdKeys = Object.keys(debugIdMap);
  if (cachedFilenameDebugIds && debugIdKeys.length === lastKeysCount) {
    return cachedFilenameDebugIds;
  }
  lastKeysCount = debugIdKeys.length;
  cachedFilenameDebugIds = debugIdKeys.reduce((acc, stackKey) => {
    if (!parsedStackResults) {
      parsedStackResults = {};
    }
    const result = parsedStackResults[stackKey];
    if (result) {
      acc[result[0]] = result[1];
    } else {
      const parsedStack = stackParser(stackKey);
      for (let i = parsedStack.length - 1; i >= 0; i--) {
        const stackFrame = parsedStack[i];
        const filename = stackFrame && stackFrame.filename;
        const debugId = debugIdMap[stackKey];
        if (filename && debugId) {
          acc[filename] = debugId;
          parsedStackResults[stackKey] = [filename, debugId];
          break;
        }
      }
    }
    return acc;
  }, {});
  return cachedFilenameDebugIds;
}
function applyScopeDataToEvent(event, data) {
  const { fingerprint, span, breadcrumbs, sdkProcessingMetadata } = data;
  applyDataToEvent(event, data);
  if (span) {
    applySpanToEvent(event, span);
  }
  applyFingerprintToEvent(event, fingerprint);
  applyBreadcrumbsToEvent(event, breadcrumbs);
  applySdkMetadataToEvent(event, sdkProcessingMetadata);
}
function mergeScopeData(data, mergeData) {
  const {
    extra,
    tags,
    user,
    contexts,
    level,
    sdkProcessingMetadata,
    breadcrumbs,
    fingerprint,
    eventProcessors,
    attachments,
    propagationContext,
    transactionName,
    span
  } = mergeData;
  mergeAndOverwriteScopeData(data, "extra", extra);
  mergeAndOverwriteScopeData(data, "tags", tags);
  mergeAndOverwriteScopeData(data, "user", user);
  mergeAndOverwriteScopeData(data, "contexts", contexts);
  data.sdkProcessingMetadata = merge(data.sdkProcessingMetadata, sdkProcessingMetadata, 2);
  if (level) {
    data.level = level;
  }
  if (transactionName) {
    data.transactionName = transactionName;
  }
  if (span) {
    data.span = span;
  }
  if (breadcrumbs.length) {
    data.breadcrumbs = [...data.breadcrumbs, ...breadcrumbs];
  }
  if (fingerprint.length) {
    data.fingerprint = [...data.fingerprint, ...fingerprint];
  }
  if (eventProcessors.length) {
    data.eventProcessors = [...data.eventProcessors, ...eventProcessors];
  }
  if (attachments.length) {
    data.attachments = [...data.attachments, ...attachments];
  }
  data.propagationContext = { ...data.propagationContext, ...propagationContext };
}
function mergeAndOverwriteScopeData(data, prop, mergeVal) {
  data[prop] = merge(data[prop], mergeVal, 1);
}
function applyDataToEvent(event, data) {
  const { extra, tags, user, contexts, level, transactionName } = data;
  const cleanedExtra = dropUndefinedKeys(extra);
  if (cleanedExtra && Object.keys(cleanedExtra).length) {
    event.extra = { ...cleanedExtra, ...event.extra };
  }
  const cleanedTags = dropUndefinedKeys(tags);
  if (cleanedTags && Object.keys(cleanedTags).length) {
    event.tags = { ...cleanedTags, ...event.tags };
  }
  const cleanedUser = dropUndefinedKeys(user);
  if (cleanedUser && Object.keys(cleanedUser).length) {
    event.user = { ...cleanedUser, ...event.user };
  }
  const cleanedContexts = dropUndefinedKeys(contexts);
  if (cleanedContexts && Object.keys(cleanedContexts).length) {
    event.contexts = { ...cleanedContexts, ...event.contexts };
  }
  if (level) {
    event.level = level;
  }
  if (transactionName && event.type !== "transaction") {
    event.transaction = transactionName;
  }
}
function applyBreadcrumbsToEvent(event, breadcrumbs) {
  const mergedBreadcrumbs = [...event.breadcrumbs || [], ...breadcrumbs];
  event.breadcrumbs = mergedBreadcrumbs.length ? mergedBreadcrumbs : void 0;
}
function applySdkMetadataToEvent(event, sdkProcessingMetadata) {
  event.sdkProcessingMetadata = {
    ...event.sdkProcessingMetadata,
    ...sdkProcessingMetadata
  };
}
function applySpanToEvent(event, span) {
  event.contexts = {
    trace: spanToTraceContext(span),
    ...event.contexts
  };
  event.sdkProcessingMetadata = {
    dynamicSamplingContext: getDynamicSamplingContextFromSpan(span),
    ...event.sdkProcessingMetadata
  };
  const rootSpan = getRootSpan(span);
  const transactionName = spanToJSON(rootSpan).description;
  if (transactionName && !event.transaction && event.type === "transaction") {
    event.transaction = transactionName;
  }
}
function applyFingerprintToEvent(event, fingerprint) {
  event.fingerprint = event.fingerprint ? Array.isArray(event.fingerprint) ? event.fingerprint : [event.fingerprint] : [];
  if (fingerprint) {
    event.fingerprint = event.fingerprint.concat(fingerprint);
  }
  if (event.fingerprint && !event.fingerprint.length) {
    delete event.fingerprint;
  }
}
function prepareEvent(options, event, hint, scope, client, isolationScope) {
  const { normalizeDepth = 3, normalizeMaxBreadth = 1e3 } = options;
  const prepared = {
    ...event,
    event_id: event.event_id || hint.event_id || uuid4(),
    timestamp: event.timestamp || dateTimestampInSeconds()
  };
  const integrations = hint.integrations || options.integrations.map((i) => i.name);
  applyClientOptions(prepared, options);
  applyIntegrationsMetadata(prepared, integrations);
  if (client) {
    client.emit("applyFrameMetadata", event);
  }
  if (event.type === void 0) {
    applyDebugIds(prepared, options.stackParser);
  }
  const finalScope = getFinalScope(scope, hint.captureContext);
  if (hint.mechanism) {
    addExceptionMechanism(prepared, hint.mechanism);
  }
  const clientEventProcessors = client ? client.getEventProcessors() : [];
  const data = getGlobalScope().getScopeData();
  if (isolationScope) {
    const isolationData = isolationScope.getScopeData();
    mergeScopeData(data, isolationData);
  }
  if (finalScope) {
    const finalScopeData = finalScope.getScopeData();
    mergeScopeData(data, finalScopeData);
  }
  const attachments = [...hint.attachments || [], ...data.attachments];
  if (attachments.length) {
    hint.attachments = attachments;
  }
  applyScopeDataToEvent(prepared, data);
  const eventProcessors = [
    ...clientEventProcessors,
    // Run scope event processors _after_ all other processors
    ...data.eventProcessors
  ];
  const result = notifyEventProcessors(eventProcessors, prepared, hint);
  return result.then((evt) => {
    if (evt) {
      applyDebugMeta(evt);
    }
    if (typeof normalizeDepth === "number" && normalizeDepth > 0) {
      return normalizeEvent(evt, normalizeDepth, normalizeMaxBreadth);
    }
    return evt;
  });
}
function applyClientOptions(event, options) {
  const { environment, release, dist, maxValueLength = 250 } = options;
  event.environment = event.environment || environment || DEFAULT_ENVIRONMENT;
  if (!event.release && release) {
    event.release = release;
  }
  if (!event.dist && dist) {
    event.dist = dist;
  }
  if (event.message) {
    event.message = truncate(event.message, maxValueLength);
  }
  const exception = event.exception && event.exception.values && event.exception.values[0];
  if (exception && exception.value) {
    exception.value = truncate(exception.value, maxValueLength);
  }
  const request = event.request;
  if (request && request.url) {
    request.url = truncate(request.url, maxValueLength);
  }
}
function applyDebugIds(event, stackParser) {
  const filenameDebugIdMap = getFilenameToDebugIdMap(stackParser);
  try {
    event.exception.values.forEach((exception) => {
      exception.stacktrace.frames.forEach((frame) => {
        if (filenameDebugIdMap && frame.filename) {
          frame.debug_id = filenameDebugIdMap[frame.filename];
        }
      });
    });
  } catch (e) {
  }
}
function applyDebugMeta(event) {
  const filenameDebugIdMap = {};
  try {
    event.exception.values.forEach((exception) => {
      exception.stacktrace.frames.forEach((frame) => {
        if (frame.debug_id) {
          if (frame.abs_path) {
            filenameDebugIdMap[frame.abs_path] = frame.debug_id;
          } else if (frame.filename) {
            filenameDebugIdMap[frame.filename] = frame.debug_id;
          }
          delete frame.debug_id;
        }
      });
    });
  } catch (e) {
  }
  if (Object.keys(filenameDebugIdMap).length === 0) {
    return;
  }
  event.debug_meta = event.debug_meta || {};
  event.debug_meta.images = event.debug_meta.images || [];
  const images = event.debug_meta.images;
  Object.entries(filenameDebugIdMap).forEach(([filename, debug_id]) => {
    images.push({
      type: "sourcemap",
      code_file: filename,
      debug_id
    });
  });
}
function applyIntegrationsMetadata(event, integrationNames) {
  if (integrationNames.length > 0) {
    event.sdk = event.sdk || {};
    event.sdk.integrations = [...event.sdk.integrations || [], ...integrationNames];
  }
}
function normalizeEvent(event, depth, maxBreadth) {
  if (!event) {
    return null;
  }
  const normalized = {
    ...event,
    ...event.breadcrumbs && {
      breadcrumbs: event.breadcrumbs.map((b) => ({
        ...b,
        ...b.data && {
          data: normalize(b.data, depth, maxBreadth)
        }
      }))
    },
    ...event.user && {
      user: normalize(event.user, depth, maxBreadth)
    },
    ...event.contexts && {
      contexts: normalize(event.contexts, depth, maxBreadth)
    },
    ...event.extra && {
      extra: normalize(event.extra, depth, maxBreadth)
    }
  };
  if (event.contexts && event.contexts.trace && normalized.contexts) {
    normalized.contexts.trace = event.contexts.trace;
    if (event.contexts.trace.data) {
      normalized.contexts.trace.data = normalize(event.contexts.trace.data, depth, maxBreadth);
    }
  }
  if (event.spans) {
    normalized.spans = event.spans.map((span) => {
      return {
        ...span,
        ...span.data && {
          data: normalize(span.data, depth, maxBreadth)
        }
      };
    });
  }
  if (event.contexts && event.contexts.flags && normalized.contexts) {
    normalized.contexts.flags = normalize(event.contexts.flags, 3, maxBreadth);
  }
  return normalized;
}
function getFinalScope(scope, captureContext) {
  if (!captureContext) {
    return scope;
  }
  const finalScope = scope ? scope.clone() : new Scope();
  finalScope.update(captureContext);
  return finalScope;
}
function parseEventHintOrCaptureContext(hint) {
  {
    return void 0;
  }
}
function captureException(exception, hint) {
  return getCurrentScope$1().captureException(exception, parseEventHintOrCaptureContext());
}
function captureMessage(message, captureContext) {
  const level = captureContext;
  const context = void 0;
  return getCurrentScope$1().captureMessage(message, level, context);
}
function captureEvent(event, hint) {
  return getCurrentScope$1().captureEvent(event, hint);
}
function startSession(context) {
  const client = getClient();
  const isolationScope = getIsolationScope();
  const currentScope = getCurrentScope$1();
  const { release, environment = DEFAULT_ENVIRONMENT } = client && client.getOptions() || {};
  const { userAgent } = GLOBAL_OBJ.navigator || {};
  const session = makeSession({
    release,
    environment,
    user: currentScope.getUser() || isolationScope.getUser(),
    ...userAgent && { userAgent },
    ...context
  });
  const currentSession = isolationScope.getSession();
  if (currentSession && currentSession.status === "ok") {
    updateSession(currentSession, { status: "exited" });
  }
  endSession();
  isolationScope.setSession(session);
  currentScope.setSession(session);
  return session;
}
function endSession() {
  const isolationScope = getIsolationScope();
  const currentScope = getCurrentScope$1();
  const session = currentScope.getSession() || isolationScope.getSession();
  if (session) {
    closeSession(session);
  }
  _sendSessionUpdate();
  isolationScope.setSession();
  currentScope.setSession();
}
function _sendSessionUpdate() {
  const isolationScope = getIsolationScope();
  const currentScope = getCurrentScope$1();
  const client = getClient();
  const session = currentScope.getSession() || isolationScope.getSession();
  if (session && client) {
    client.captureSession(session);
  }
}
function captureSession(end = false) {
  if (end) {
    endSession();
    return;
  }
  _sendSessionUpdate();
}
const SENTRY_API_VERSION = "7";
function getBaseApiEndpoint(dsn) {
  const protocol = dsn.protocol ? `${dsn.protocol}:` : "";
  const port = dsn.port ? `:${dsn.port}` : "";
  return `${protocol}//${dsn.host}${port}${dsn.path ? `/${dsn.path}` : ""}/api/`;
}
function _getIngestEndpoint(dsn) {
  return `${getBaseApiEndpoint(dsn)}${dsn.projectId}/envelope/`;
}
function _encodedAuth(dsn, sdkInfo) {
  const params = {
    sentry_version: SENTRY_API_VERSION
  };
  if (dsn.publicKey) {
    params.sentry_key = dsn.publicKey;
  }
  if (sdkInfo) {
    params.sentry_client = `${sdkInfo.name}/${sdkInfo.version}`;
  }
  return new URLSearchParams(params).toString();
}
function getEnvelopeEndpointWithUrlEncodedAuth(dsn, tunnel, sdkInfo) {
  return tunnel ? tunnel : `${_getIngestEndpoint(dsn)}?${_encodedAuth(dsn, sdkInfo)}`;
}
const installedIntegrations = [];
function filterDuplicates(integrations) {
  const integrationsByName = {};
  integrations.forEach((currentInstance2) => {
    const { name } = currentInstance2;
    const existingInstance = integrationsByName[name];
    if (existingInstance && !existingInstance.isDefaultInstance && currentInstance2.isDefaultInstance) {
      return;
    }
    integrationsByName[name] = currentInstance2;
  });
  return Object.values(integrationsByName);
}
function getIntegrationsToSetup(options) {
  const defaultIntegrations = options.defaultIntegrations || [];
  const userIntegrations = options.integrations;
  defaultIntegrations.forEach((integration) => {
    integration.isDefaultInstance = true;
  });
  let integrations;
  if (Array.isArray(userIntegrations)) {
    integrations = [...defaultIntegrations, ...userIntegrations];
  } else if (typeof userIntegrations === "function") {
    const resolvedUserIntegrations = userIntegrations(defaultIntegrations);
    integrations = Array.isArray(resolvedUserIntegrations) ? resolvedUserIntegrations : [resolvedUserIntegrations];
  } else {
    integrations = defaultIntegrations;
  }
  const finalIntegrations = filterDuplicates(integrations);
  const debugIndex = finalIntegrations.findIndex((integration) => integration.name === "Debug");
  if (debugIndex > -1) {
    const [debugInstance] = finalIntegrations.splice(debugIndex, 1);
    finalIntegrations.push(debugInstance);
  }
  return finalIntegrations;
}
function setupIntegrations(client, integrations) {
  const integrationIndex = {};
  integrations.forEach((integration) => {
    if (integration) {
      setupIntegration(client, integration, integrationIndex);
    }
  });
  return integrationIndex;
}
function afterSetupIntegrations(client, integrations) {
  for (const integration of integrations) {
    if (integration && integration.afterAllSetup) {
      integration.afterAllSetup(client);
    }
  }
}
function setupIntegration(client, integration, integrationIndex) {
  if (integrationIndex[integration.name]) {
    DEBUG_BUILD$3 && logger.log(`Integration skipped because it was already installed: ${integration.name}`);
    return;
  }
  integrationIndex[integration.name] = integration;
  if (installedIntegrations.indexOf(integration.name) === -1 && typeof integration.setupOnce === "function") {
    integration.setupOnce();
    installedIntegrations.push(integration.name);
  }
  if (integration.setup && typeof integration.setup === "function") {
    integration.setup(client);
  }
  if (typeof integration.preprocessEvent === "function") {
    const callback = integration.preprocessEvent.bind(integration);
    client.on("preprocessEvent", (event, hint) => callback(event, hint, client));
  }
  if (typeof integration.processEvent === "function") {
    const callback = integration.processEvent.bind(integration);
    const processor = Object.assign((event, hint) => callback(event, hint, client), {
      id: integration.name
    });
    client.addEventProcessor(processor);
  }
  DEBUG_BUILD$3 && logger.log(`Integration installed: ${integration.name}`);
}
function defineIntegration(fn) {
  return fn;
}
function createClientReportEnvelope(discarded_events, dsn, timestamp) {
  const clientReportItem = [
    { type: "client_report" },
    {
      timestamp: dateTimestampInSeconds(),
      discarded_events
    }
  ];
  return createEnvelope(dsn ? { dsn } : {}, [clientReportItem]);
}
class SentryError extends Error {
  constructor(message, logLevel = "warn") {
    super(message);
    this.message = message;
    this.logLevel = logLevel;
  }
}
const ALREADY_SEEN_ERROR = "Not capturing exception because it's already been captured.";
class BaseClient {
  /** Options passed to the SDK. */
  /** The client Dsn, if specified in options. Without this Dsn, the SDK will be disabled. */
  /** Array of set up integrations. */
  /** Number of calls being processed */
  /** Holds flushable  */
  // eslint-disable-next-line @typescript-eslint/ban-types
  /**
   * Initializes this client instance.
   *
   * @param options Options for the client.
   */
  constructor(options) {
    this._options = options;
    this._integrations = {};
    this._numProcessing = 0;
    this._outcomes = {};
    this._hooks = {};
    this._eventProcessors = [];
    if (options.dsn) {
      this._dsn = makeDsn(options.dsn);
    } else {
      DEBUG_BUILD$3 && logger.warn("No DSN provided, client will not send events.");
    }
    if (this._dsn) {
      const url = getEnvelopeEndpointWithUrlEncodedAuth(
        this._dsn,
        options.tunnel,
        options._metadata ? options._metadata.sdk : void 0
      );
      this._transport = options.transport({
        tunnel: this._options.tunnel,
        recordDroppedEvent: this.recordDroppedEvent.bind(this),
        ...options.transportOptions,
        url
      });
    }
    const tracingOptions = ["enableTracing", "tracesSampleRate", "tracesSampler"];
    const undefinedOption = tracingOptions.find((option) => option in options && options[option] == void 0);
    if (undefinedOption) {
      consoleSandbox(() => {
        console.warn(
          `[Sentry] Deprecation warning: \`${undefinedOption}\` is set to undefined, which leads to tracing being enabled. In v9, a value of \`undefined\` will result in tracing being disabled.`
        );
      });
    }
  }
  /**
   * @inheritDoc
   */
  captureException(exception, hint, scope) {
    const eventId = uuid4();
    if (checkOrSetAlreadyCaught(exception)) {
      DEBUG_BUILD$3 && logger.log(ALREADY_SEEN_ERROR);
      return eventId;
    }
    const hintWithEventId = {
      event_id: eventId,
      ...hint
    };
    this._process(
      this.eventFromException(exception, hintWithEventId).then(
        (event) => this._captureEvent(event, hintWithEventId, scope)
      )
    );
    return hintWithEventId.event_id;
  }
  /**
   * @inheritDoc
   */
  captureMessage(message, level, hint, currentScope) {
    const hintWithEventId = {
      event_id: uuid4(),
      ...hint
    };
    const eventMessage = isParameterizedString(message) ? message : String(message);
    const promisedEvent = isPrimitive(message) ? this.eventFromMessage(eventMessage, level, hintWithEventId) : this.eventFromException(message, hintWithEventId);
    this._process(promisedEvent.then((event) => this._captureEvent(event, hintWithEventId, currentScope)));
    return hintWithEventId.event_id;
  }
  /**
   * @inheritDoc
   */
  captureEvent(event, hint, currentScope) {
    const eventId = uuid4();
    if (hint && hint.originalException && checkOrSetAlreadyCaught(hint.originalException)) {
      DEBUG_BUILD$3 && logger.log(ALREADY_SEEN_ERROR);
      return eventId;
    }
    const hintWithEventId = {
      event_id: eventId,
      ...hint
    };
    const sdkProcessingMetadata = event.sdkProcessingMetadata || {};
    const capturedSpanScope = sdkProcessingMetadata.capturedSpanScope;
    this._process(this._captureEvent(event, hintWithEventId, capturedSpanScope || currentScope));
    return hintWithEventId.event_id;
  }
  /**
   * @inheritDoc
   */
  captureSession(session) {
    if (!(typeof session.release === "string")) {
      DEBUG_BUILD$3 && logger.warn("Discarded session because of missing or non-string release");
    } else {
      this.sendSession(session);
      updateSession(session, { init: false });
    }
  }
  /**
   * @inheritDoc
   */
  getDsn() {
    return this._dsn;
  }
  /**
   * @inheritDoc
   */
  getOptions() {
    return this._options;
  }
  /**
   * @see SdkMetadata
   *
   * @return The metadata of the SDK
   */
  getSdkMetadata() {
    return this._options._metadata;
  }
  /**
   * @inheritDoc
   */
  getTransport() {
    return this._transport;
  }
  /**
   * @inheritDoc
   */
  flush(timeout) {
    const transport = this._transport;
    if (transport) {
      this.emit("flush");
      return this._isClientDoneProcessing(timeout).then((clientFinished) => {
        return transport.flush(timeout).then((transportFlushed) => clientFinished && transportFlushed);
      });
    } else {
      return resolvedSyncPromise(true);
    }
  }
  /**
   * @inheritDoc
   */
  close(timeout) {
    return this.flush(timeout).then((result) => {
      this.getOptions().enabled = false;
      this.emit("close");
      return result;
    });
  }
  /** Get all installed event processors. */
  getEventProcessors() {
    return this._eventProcessors;
  }
  /** @inheritDoc */
  addEventProcessor(eventProcessor) {
    this._eventProcessors.push(eventProcessor);
  }
  /** @inheritdoc */
  init() {
    if (this._isEnabled() || // Force integrations to be setup even if no DSN was set when we have
    // Spotlight enabled. This is particularly important for browser as we
    // don't support the `spotlight` option there and rely on the users
    // adding the `spotlightBrowserIntegration()` to their integrations which
    // wouldn't get initialized with the check below when there's no DSN set.
    this._options.integrations.some(({ name }) => name.startsWith("Spotlight"))) {
      this._setupIntegrations();
    }
  }
  /**
   * Gets an installed integration by its name.
   *
   * @returns The installed integration or `undefined` if no integration with that `name` was installed.
   */
  getIntegrationByName(integrationName) {
    return this._integrations[integrationName];
  }
  /**
   * @inheritDoc
   */
  addIntegration(integration) {
    const isAlreadyInstalled = this._integrations[integration.name];
    setupIntegration(this, integration, this._integrations);
    if (!isAlreadyInstalled) {
      afterSetupIntegrations(this, [integration]);
    }
  }
  /**
   * @inheritDoc
   */
  sendEvent(event, hint = {}) {
    this.emit("beforeSendEvent", event, hint);
    let env = createEventEnvelope(event, this._dsn, this._options._metadata, this._options.tunnel);
    for (const attachment of hint.attachments || []) {
      env = addItemToEnvelope(env, createAttachmentEnvelopeItem(attachment));
    }
    const promise = this.sendEnvelope(env);
    if (promise) {
      promise.then((sendResponse) => this.emit("afterSendEvent", event, sendResponse), null);
    }
  }
  /**
   * @inheritDoc
   */
  sendSession(session) {
    const env = createSessionEnvelope(session, this._dsn, this._options._metadata, this._options.tunnel);
    this.sendEnvelope(env);
  }
  /**
   * @inheritDoc
   */
  recordDroppedEvent(reason, category, eventOrCount) {
    if (this._options.sendClientReports) {
      const count = typeof eventOrCount === "number" ? eventOrCount : 1;
      const key = `${reason}:${category}`;
      DEBUG_BUILD$3 && logger.log(`Recording outcome: "${key}"${count > 1 ? ` (${count} times)` : ""}`);
      this._outcomes[key] = (this._outcomes[key] || 0) + count;
    }
  }
  // Keep on() & emit() signatures in sync with types' client.ts interface
  /* eslint-disable @typescript-eslint/unified-signatures */
  /** @inheritdoc */
  /** @inheritdoc */
  on(hook, callback) {
    const hooks = this._hooks[hook] = this._hooks[hook] || [];
    hooks.push(callback);
    return () => {
      const cbIndex = hooks.indexOf(callback);
      if (cbIndex > -1) {
        hooks.splice(cbIndex, 1);
      }
    };
  }
  /** @inheritdoc */
  /** @inheritdoc */
  emit(hook, ...rest) {
    const callbacks = this._hooks[hook];
    if (callbacks) {
      callbacks.forEach((callback) => callback(...rest));
    }
  }
  /**
   * @inheritdoc
   */
  sendEnvelope(envelope) {
    this.emit("beforeEnvelope", envelope);
    if (this._isEnabled() && this._transport) {
      return this._transport.send(envelope).then(null, (reason) => {
        DEBUG_BUILD$3 && logger.error("Error while sending envelope:", reason);
        return reason;
      });
    }
    DEBUG_BUILD$3 && logger.error("Transport disabled");
    return resolvedSyncPromise({});
  }
  /* eslint-enable @typescript-eslint/unified-signatures */
  /** Setup integrations for this client. */
  _setupIntegrations() {
    const { integrations } = this._options;
    this._integrations = setupIntegrations(this, integrations);
    afterSetupIntegrations(this, integrations);
  }
  /** Updates existing session based on the provided event */
  _updateSessionFromEvent(session, event) {
    let crashed = event.level === "fatal";
    let errored = false;
    const exceptions = event.exception && event.exception.values;
    if (exceptions) {
      errored = true;
      for (const ex of exceptions) {
        const mechanism = ex.mechanism;
        if (mechanism && mechanism.handled === false) {
          crashed = true;
          break;
        }
      }
    }
    const sessionNonTerminal = session.status === "ok";
    const shouldUpdateAndSend = sessionNonTerminal && session.errors === 0 || sessionNonTerminal && crashed;
    if (shouldUpdateAndSend) {
      updateSession(session, {
        ...crashed && { status: "crashed" },
        errors: session.errors || Number(errored || crashed)
      });
      this.captureSession(session);
    }
  }
  /**
   * Determine if the client is finished processing. Returns a promise because it will wait `timeout` ms before saying
   * "no" (resolving to `false`) in order to give the client a chance to potentially finish first.
   *
   * @param timeout The time, in ms, after which to resolve to `false` if the client is still busy. Passing `0` (or not
   * passing anything) will make the promise wait as long as it takes for processing to finish before resolving to
   * `true`.
   * @returns A promise which will resolve to `true` if processing is already done or finishes before the timeout, and
   * `false` otherwise
   */
  _isClientDoneProcessing(timeout) {
    return new SyncPromise((resolve2) => {
      let ticked = 0;
      const tick = 1;
      const interval = setInterval(() => {
        if (this._numProcessing == 0) {
          clearInterval(interval);
          resolve2(true);
        } else {
          ticked += tick;
          if (timeout && ticked >= timeout) {
            clearInterval(interval);
            resolve2(false);
          }
        }
      }, tick);
    });
  }
  /** Determines whether this SDK is enabled and a transport is present. */
  _isEnabled() {
    return this.getOptions().enabled !== false && this._transport !== void 0;
  }
  /**
   * Adds common information to events.
   *
   * The information includes release and environment from `options`,
   * breadcrumbs and context (extra, tags and user) from the scope.
   *
   * Information that is already present in the event is never overwritten. For
   * nested objects, such as the context, keys are merged.
   *
   * @param event The original event.
   * @param hint May contain additional information about the original exception.
   * @param currentScope A scope containing event metadata.
   * @returns A new event with more information.
   */
  _prepareEvent(event, hint, currentScope = getCurrentScope$1(), isolationScope = getIsolationScope()) {
    const options = this.getOptions();
    const integrations = Object.keys(this._integrations);
    if (!hint.integrations && integrations.length > 0) {
      hint.integrations = integrations;
    }
    this.emit("preprocessEvent", event, hint);
    if (!event.type) {
      isolationScope.setLastEventId(event.event_id || hint.event_id);
    }
    return prepareEvent(options, event, hint, currentScope, this, isolationScope).then((evt) => {
      if (evt === null) {
        return evt;
      }
      evt.contexts = {
        trace: getTraceContextFromScope(currentScope),
        ...evt.contexts
      };
      const dynamicSamplingContext = getDynamicSamplingContextFromScope(this, currentScope);
      evt.sdkProcessingMetadata = {
        dynamicSamplingContext,
        ...evt.sdkProcessingMetadata
      };
      return evt;
    });
  }
  /**
   * Processes the event and logs an error in case of rejection
   * @param event
   * @param hint
   * @param scope
   */
  _captureEvent(event, hint = {}, scope) {
    return this._processEvent(event, hint, scope).then(
      (finalEvent) => {
        return finalEvent.event_id;
      },
      (reason) => {
        if (DEBUG_BUILD$3) {
          if (reason instanceof SentryError && reason.logLevel === "log") {
            logger.log(reason.message);
          } else {
            logger.warn(reason);
          }
        }
        return void 0;
      }
    );
  }
  /**
   * Processes an event (either error or message) and sends it to Sentry.
   *
   * This also adds breadcrumbs and context information to the event. However,
   * platform specific meta data (such as the User's IP address) must be added
   * by the SDK implementor.
   *
   *
   * @param event The event to send to Sentry.
   * @param hint May contain additional information about the original exception.
   * @param currentScope A scope containing event metadata.
   * @returns A SyncPromise that resolves with the event or rejects in case event was/will not be send.
   */
  _processEvent(event, hint, currentScope) {
    const options = this.getOptions();
    const { sampleRate } = options;
    const isTransaction = isTransactionEvent(event);
    const isError2 = isErrorEvent(event);
    const eventType = event.type || "error";
    const beforeSendLabel = `before send for type \`${eventType}\``;
    const parsedSampleRate = typeof sampleRate === "undefined" ? void 0 : parseSampleRate(sampleRate);
    if (isError2 && typeof parsedSampleRate === "number" && Math.random() > parsedSampleRate) {
      this.recordDroppedEvent("sample_rate", "error", event);
      return rejectedSyncPromise(
        new SentryError(
          `Discarding event because it's not included in the random sample (sampling rate = ${sampleRate})`,
          "log"
        )
      );
    }
    const dataCategory = eventType === "replay_event" ? "replay" : eventType;
    const sdkProcessingMetadata = event.sdkProcessingMetadata || {};
    const capturedSpanIsolationScope = sdkProcessingMetadata.capturedSpanIsolationScope;
    return this._prepareEvent(event, hint, currentScope, capturedSpanIsolationScope).then((prepared) => {
      if (prepared === null) {
        this.recordDroppedEvent("event_processor", dataCategory, event);
        throw new SentryError("An event processor returned `null`, will not send event.", "log");
      }
      const isInternalException = hint.data && hint.data.__sentry__ === true;
      if (isInternalException) {
        return prepared;
      }
      const result = processBeforeSend(this, options, prepared, hint);
      return _validateBeforeSendResult(result, beforeSendLabel);
    }).then((processedEvent) => {
      if (processedEvent === null) {
        this.recordDroppedEvent("before_send", dataCategory, event);
        if (isTransaction) {
          const spans = event.spans || [];
          const spanCount = 1 + spans.length;
          this.recordDroppedEvent("before_send", "span", spanCount);
        }
        throw new SentryError(`${beforeSendLabel} returned \`null\`, will not send event.`, "log");
      }
      const session = currentScope && currentScope.getSession();
      if (!isTransaction && session) {
        this._updateSessionFromEvent(session, processedEvent);
      }
      if (isTransaction) {
        const spanCountBefore = processedEvent.sdkProcessingMetadata && processedEvent.sdkProcessingMetadata.spanCountBeforeProcessing || 0;
        const spanCountAfter = processedEvent.spans ? processedEvent.spans.length : 0;
        const droppedSpanCount = spanCountBefore - spanCountAfter;
        if (droppedSpanCount > 0) {
          this.recordDroppedEvent("before_send", "span", droppedSpanCount);
        }
      }
      const transactionInfo = processedEvent.transaction_info;
      if (isTransaction && transactionInfo && processedEvent.transaction !== event.transaction) {
        const source = "custom";
        processedEvent.transaction_info = {
          ...transactionInfo,
          source
        };
      }
      this.sendEvent(processedEvent, hint);
      return processedEvent;
    }).then(null, (reason) => {
      if (reason instanceof SentryError) {
        throw reason;
      }
      this.captureException(reason, {
        data: {
          __sentry__: true
        },
        originalException: reason
      });
      throw new SentryError(
        `Event processing pipeline threw an error, original event will not be sent. Details have been sent as a new event.
Reason: ${reason}`
      );
    });
  }
  /**
   * Occupies the client with processing and event
   */
  _process(promise) {
    this._numProcessing++;
    void promise.then(
      (value) => {
        this._numProcessing--;
        return value;
      },
      (reason) => {
        this._numProcessing--;
        return reason;
      }
    );
  }
  /**
   * Clears outcomes on this client and returns them.
   */
  _clearOutcomes() {
    const outcomes = this._outcomes;
    this._outcomes = {};
    return Object.entries(outcomes).map(([key, quantity]) => {
      const [reason, category] = key.split(":");
      return {
        reason,
        category,
        quantity
      };
    });
  }
  /**
   * Sends client reports as an envelope.
   */
  _flushOutcomes() {
    DEBUG_BUILD$3 && logger.log("Flushing outcomes...");
    const outcomes = this._clearOutcomes();
    if (outcomes.length === 0) {
      DEBUG_BUILD$3 && logger.log("No outcomes to send");
      return;
    }
    if (!this._dsn) {
      DEBUG_BUILD$3 && logger.log("No dsn provided, will not send outcomes");
      return;
    }
    DEBUG_BUILD$3 && logger.log("Sending outcomes:", outcomes);
    const envelope = createClientReportEnvelope(outcomes, this._options.tunnel && dsnToString(this._dsn));
    this.sendEnvelope(envelope);
  }
  /**
   * @inheritDoc
   */
}
function _validateBeforeSendResult(beforeSendResult, beforeSendLabel) {
  const invalidValueError = `${beforeSendLabel} must return \`null\` or a valid event.`;
  if (isThenable(beforeSendResult)) {
    return beforeSendResult.then(
      (event) => {
        if (!isPlainObject$1(event) && event !== null) {
          throw new SentryError(invalidValueError);
        }
        return event;
      },
      (e) => {
        throw new SentryError(`${beforeSendLabel} rejected with ${e}`);
      }
    );
  } else if (!isPlainObject$1(beforeSendResult) && beforeSendResult !== null) {
    throw new SentryError(invalidValueError);
  }
  return beforeSendResult;
}
function processBeforeSend(client, options, event, hint) {
  const { beforeSend, beforeSendTransaction, beforeSendSpan } = options;
  if (isErrorEvent(event) && beforeSend) {
    return beforeSend(event, hint);
  }
  if (isTransactionEvent(event)) {
    if (event.spans && beforeSendSpan) {
      const processedSpans = [];
      for (const span of event.spans) {
        const processedSpan = beforeSendSpan(span);
        if (processedSpan) {
          processedSpans.push(processedSpan);
        } else {
          showSpanDropWarning();
          client.recordDroppedEvent("before_send", "span");
        }
      }
      event.spans = processedSpans;
    }
    if (beforeSendTransaction) {
      if (event.spans) {
        const spanCountBefore = event.spans.length;
        event.sdkProcessingMetadata = {
          ...event.sdkProcessingMetadata,
          spanCountBeforeProcessing: spanCountBefore
        };
      }
      return beforeSendTransaction(event, hint);
    }
  }
  return event;
}
function isErrorEvent(event) {
  return event.type === void 0;
}
function isTransactionEvent(event) {
  return event.type === "transaction";
}
function initAndBind(clientClass, options) {
  if (options.debug === true) {
    if (DEBUG_BUILD$3) {
      logger.enable();
    } else {
      consoleSandbox(() => {
        console.warn("[Sentry] Cannot initialize SDK with `debug` option using a non-debug bundle.");
      });
    }
  }
  const scope = getCurrentScope$1();
  scope.update(options.initialScope);
  const client = new clientClass(options);
  setCurrentClient(client);
  client.init();
  return client;
}
function setCurrentClient(client) {
  getCurrentScope$1().setClient(client);
}
function makePromiseBuffer(limit) {
  const buffer = [];
  function isReady() {
    return limit === void 0 || buffer.length < limit;
  }
  function remove2(task) {
    return buffer.splice(buffer.indexOf(task), 1)[0] || Promise.resolve(void 0);
  }
  function add(taskProducer) {
    if (!isReady()) {
      return rejectedSyncPromise(new SentryError("Not adding Promise because buffer limit was reached."));
    }
    const task = taskProducer();
    if (buffer.indexOf(task) === -1) {
      buffer.push(task);
    }
    void task.then(() => remove2(task)).then(
      null,
      () => remove2(task).then(null, () => {
      })
    );
    return task;
  }
  function drain(timeout) {
    return new SyncPromise((resolve2, reject) => {
      let counter = buffer.length;
      if (!counter) {
        return resolve2(true);
      }
      const capturedSetTimeout = setTimeout(() => {
        if (timeout && timeout > 0) {
          resolve2(false);
        }
      }, timeout);
      buffer.forEach((item) => {
        void resolvedSyncPromise(item).then(() => {
          if (!--counter) {
            clearTimeout(capturedSetTimeout);
            resolve2(true);
          }
        }, reject);
      });
    });
  }
  return {
    $: buffer,
    add,
    drain
  };
}
const DEFAULT_RETRY_AFTER = 60 * 1e3;
function parseRetryAfterHeader(header, now2 = Date.now()) {
  const headerDelay = parseInt(`${header}`, 10);
  if (!isNaN(headerDelay)) {
    return headerDelay * 1e3;
  }
  const headerDate = Date.parse(`${header}`);
  if (!isNaN(headerDate)) {
    return headerDate - now2;
  }
  return DEFAULT_RETRY_AFTER;
}
function disabledUntil(limits, dataCategory) {
  return limits[dataCategory] || limits.all || 0;
}
function isRateLimited(limits, dataCategory, now2 = Date.now()) {
  return disabledUntil(limits, dataCategory) > now2;
}
function updateRateLimits(limits, { statusCode, headers }, now2 = Date.now()) {
  const updatedRateLimits = {
    ...limits
  };
  const rateLimitHeader = headers && headers["x-sentry-rate-limits"];
  const retryAfterHeader = headers && headers["retry-after"];
  if (rateLimitHeader) {
    for (const limit of rateLimitHeader.trim().split(",")) {
      const [retryAfter, categories, , , namespaces] = limit.split(":", 5);
      const headerDelay = parseInt(retryAfter, 10);
      const delay = (!isNaN(headerDelay) ? headerDelay : 60) * 1e3;
      if (!categories) {
        updatedRateLimits.all = now2 + delay;
      } else {
        for (const category of categories.split(";")) {
          if (category === "metric_bucket") {
            if (!namespaces || namespaces.split(";").includes("custom")) {
              updatedRateLimits[category] = now2 + delay;
            }
          } else {
            updatedRateLimits[category] = now2 + delay;
          }
        }
      }
    }
  } else if (retryAfterHeader) {
    updatedRateLimits.all = now2 + parseRetryAfterHeader(retryAfterHeader, now2);
  } else if (statusCode === 429) {
    updatedRateLimits.all = now2 + 60 * 1e3;
  }
  return updatedRateLimits;
}
const DEFAULT_TRANSPORT_BUFFER_SIZE = 64;
function createTransport(options, makeRequest, buffer = makePromiseBuffer(
  options.bufferSize || DEFAULT_TRANSPORT_BUFFER_SIZE
)) {
  let rateLimits = {};
  const flush = (timeout) => buffer.drain(timeout);
  function send(envelope) {
    const filteredEnvelopeItems = [];
    forEachEnvelopeItem(envelope, (item, type) => {
      const dataCategory = envelopeItemTypeToDataCategory(type);
      if (isRateLimited(rateLimits, dataCategory)) {
        const event = getEventForEnvelopeItem(item, type);
        options.recordDroppedEvent("ratelimit_backoff", dataCategory, event);
      } else {
        filteredEnvelopeItems.push(item);
      }
    });
    if (filteredEnvelopeItems.length === 0) {
      return resolvedSyncPromise({});
    }
    const filteredEnvelope = createEnvelope(envelope[0], filteredEnvelopeItems);
    const recordEnvelopeLoss = (reason) => {
      forEachEnvelopeItem(filteredEnvelope, (item, type) => {
        const event = getEventForEnvelopeItem(item, type);
        options.recordDroppedEvent(reason, envelopeItemTypeToDataCategory(type), event);
      });
    };
    const requestTask = () => makeRequest({ body: serializeEnvelope(filteredEnvelope) }).then(
      (response) => {
        if (response.statusCode !== void 0 && (response.statusCode < 200 || response.statusCode >= 300)) {
          DEBUG_BUILD$3 && logger.warn(`Sentry responded with status code ${response.statusCode} to sent event.`);
        }
        rateLimits = updateRateLimits(rateLimits, response);
        return response;
      },
      (error) => {
        recordEnvelopeLoss("network_error");
        throw error;
      }
    );
    return buffer.add(requestTask).then(
      (result) => result,
      (error) => {
        if (error instanceof SentryError) {
          DEBUG_BUILD$3 && logger.error("Skipped sending event because buffer is full.");
          recordEnvelopeLoss("queue_overflow");
          return resolvedSyncPromise({});
        } else {
          throw error;
        }
      }
    );
  }
  return {
    send,
    flush
  };
}
function getEventForEnvelopeItem(item, type) {
  if (type !== "event" && type !== "transaction") {
    return void 0;
  }
  return Array.isArray(item) ? item[1] : void 0;
}
function applySdkMetadata(options, name, names = [name], source = "npm") {
  const metadata = options._metadata || {};
  if (!metadata.sdk) {
    metadata.sdk = {
      name: `sentry.javascript.${name}`,
      packages: names.map((name2) => ({
        name: `${source}:@sentry/${name2}`,
        version: SDK_VERSION
      })),
      version: SDK_VERSION
    };
  }
  options._metadata = metadata;
}
const DEFAULT_BREADCRUMBS = 100;
function addBreadcrumb(breadcrumb, hint) {
  const client = getClient();
  const isolationScope = getIsolationScope();
  if (!client) return;
  const { beforeBreadcrumb = null, maxBreadcrumbs = DEFAULT_BREADCRUMBS } = client.getOptions();
  if (maxBreadcrumbs <= 0) return;
  const timestamp = dateTimestampInSeconds();
  const mergedBreadcrumb = { timestamp, ...breadcrumb };
  const finalBreadcrumb = beforeBreadcrumb ? consoleSandbox(() => beforeBreadcrumb(mergedBreadcrumb, hint)) : mergedBreadcrumb;
  if (finalBreadcrumb === null) return;
  if (client.emit) {
    client.emit("beforeAddBreadcrumb", finalBreadcrumb, hint);
  }
  isolationScope.addBreadcrumb(finalBreadcrumb, maxBreadcrumbs);
}
let originalFunctionToString;
const INTEGRATION_NAME$6 = "FunctionToString";
const SETUP_CLIENTS = /* @__PURE__ */ new WeakMap();
const _functionToStringIntegration = () => {
  return {
    name: INTEGRATION_NAME$6,
    setupOnce() {
      originalFunctionToString = Function.prototype.toString;
      try {
        Function.prototype.toString = function(...args) {
          const originalFunction = getOriginalFunction(this);
          const context = SETUP_CLIENTS.has(getClient()) && originalFunction !== void 0 ? originalFunction : this;
          return originalFunctionToString.apply(context, args);
        };
      } catch (e) {
      }
    },
    setup(client) {
      SETUP_CLIENTS.set(client, true);
    }
  };
};
const functionToStringIntegration = defineIntegration(_functionToStringIntegration);
const DEFAULT_IGNORE_ERRORS = [
  /^Script error\.?$/,
  /^Javascript error: Script error\.? on line 0$/,
  /^ResizeObserver loop completed with undelivered notifications.$/,
  // The browser logs this when a ResizeObserver handler takes a bit longer. Usually this is not an actual issue though. It indicates slowness.
  /^Cannot redefine property: googletag$/,
  // This is thrown when google tag manager is used in combination with an ad blocker
  /^Can't find variable: gmo$/,
  // Error from Google Search App https://issuetracker.google.com/issues/396043331
  "undefined is not an object (evaluating 'a.L')",
  // Random error that happens but not actionable or noticeable to end-users.
  `can't redefine non-configurable property "solana"`,
  // Probably a browser extension or custom browser (Brave) throwing this error
  "vv().getRestrictions is not a function. (In 'vv().getRestrictions(1,a)', 'vv().getRestrictions' is undefined)",
  // Error thrown by GTM, seemingly not affecting end-users
  "Can't find variable: _AutofillCallbackHandler",
  // Unactionable error in instagram webview https://developers.facebook.com/community/threads/320013549791141/
  /^Non-Error promise rejection captured with value: Object Not Found Matching Id:\d+, MethodName:simulateEvent, ParamCount:\d+$/,
  // unactionable error from CEFSharp, a .NET library that embeds chromium in .NET apps
  /^Java exception was raised during method invocation$/
  // error from Facebook Mobile browser (https://github.com/getsentry/sentry-javascript/issues/15065)
];
const INTEGRATION_NAME$5 = "InboundFilters";
const _inboundFiltersIntegration = (options = {}) => {
  return {
    name: INTEGRATION_NAME$5,
    processEvent(event, _hint, client) {
      const clientOptions = client.getOptions();
      const mergedOptions = _mergeOptions(options, clientOptions);
      return _shouldDropEvent$1(event, mergedOptions) ? null : event;
    }
  };
};
const inboundFiltersIntegration = defineIntegration(_inboundFiltersIntegration);
function _mergeOptions(internalOptions = {}, clientOptions = {}) {
  return {
    allowUrls: [...internalOptions.allowUrls || [], ...clientOptions.allowUrls || []],
    denyUrls: [...internalOptions.denyUrls || [], ...clientOptions.denyUrls || []],
    ignoreErrors: [
      ...internalOptions.ignoreErrors || [],
      ...clientOptions.ignoreErrors || [],
      ...internalOptions.disableErrorDefaults ? [] : DEFAULT_IGNORE_ERRORS
    ],
    ignoreTransactions: [...internalOptions.ignoreTransactions || [], ...clientOptions.ignoreTransactions || []],
    ignoreInternal: internalOptions.ignoreInternal !== void 0 ? internalOptions.ignoreInternal : true
  };
}
function _shouldDropEvent$1(event, options) {
  if (options.ignoreInternal && _isSentryError(event)) {
    DEBUG_BUILD$3 && logger.warn(`Event dropped due to being internal Sentry Error.
Event: ${getEventDescription(event)}`);
    return true;
  }
  if (_isIgnoredError(event, options.ignoreErrors)) {
    DEBUG_BUILD$3 && logger.warn(
      `Event dropped due to being matched by \`ignoreErrors\` option.
Event: ${getEventDescription(event)}`
    );
    return true;
  }
  if (_isUselessError(event)) {
    DEBUG_BUILD$3 && logger.warn(
      `Event dropped due to not having an error message, error type or stacktrace.
Event: ${getEventDescription(
        event
      )}`
    );
    return true;
  }
  if (_isIgnoredTransaction(event, options.ignoreTransactions)) {
    DEBUG_BUILD$3 && logger.warn(
      `Event dropped due to being matched by \`ignoreTransactions\` option.
Event: ${getEventDescription(event)}`
    );
    return true;
  }
  if (_isDeniedUrl(event, options.denyUrls)) {
    DEBUG_BUILD$3 && logger.warn(
      `Event dropped due to being matched by \`denyUrls\` option.
Event: ${getEventDescription(
        event
      )}.
Url: ${_getEventFilterUrl(event)}`
    );
    return true;
  }
  if (!_isAllowedUrl(event, options.allowUrls)) {
    DEBUG_BUILD$3 && logger.warn(
      `Event dropped due to not being matched by \`allowUrls\` option.
Event: ${getEventDescription(
        event
      )}.
Url: ${_getEventFilterUrl(event)}`
    );
    return true;
  }
  return false;
}
function _isIgnoredError(event, ignoreErrors) {
  if (event.type || !ignoreErrors || !ignoreErrors.length) {
    return false;
  }
  return _getPossibleEventMessages(event).some((message) => stringMatchesSomePattern(message, ignoreErrors));
}
function _isIgnoredTransaction(event, ignoreTransactions) {
  if (event.type !== "transaction" || !ignoreTransactions || !ignoreTransactions.length) {
    return false;
  }
  const name = event.transaction;
  return name ? stringMatchesSomePattern(name, ignoreTransactions) : false;
}
function _isDeniedUrl(event, denyUrls) {
  if (!denyUrls || !denyUrls.length) {
    return false;
  }
  const url = _getEventFilterUrl(event);
  return !url ? false : stringMatchesSomePattern(url, denyUrls);
}
function _isAllowedUrl(event, allowUrls) {
  if (!allowUrls || !allowUrls.length) {
    return true;
  }
  const url = _getEventFilterUrl(event);
  return !url ? true : stringMatchesSomePattern(url, allowUrls);
}
function _getPossibleEventMessages(event) {
  const possibleMessages = [];
  if (event.message) {
    possibleMessages.push(event.message);
  }
  let lastException;
  try {
    lastException = event.exception.values[event.exception.values.length - 1];
  } catch (e) {
  }
  if (lastException) {
    if (lastException.value) {
      possibleMessages.push(lastException.value);
      if (lastException.type) {
        possibleMessages.push(`${lastException.type}: ${lastException.value}`);
      }
    }
  }
  return possibleMessages;
}
function _isSentryError(event) {
  try {
    return event.exception.values[0].type === "SentryError";
  } catch (e) {
  }
  return false;
}
function _getLastValidUrl(frames = []) {
  for (let i = frames.length - 1; i >= 0; i--) {
    const frame = frames[i];
    if (frame && frame.filename !== "<anonymous>" && frame.filename !== "[native code]") {
      return frame.filename || null;
    }
  }
  return null;
}
function _getEventFilterUrl(event) {
  try {
    let frames;
    try {
      frames = event.exception.values[0].stacktrace.frames;
    } catch (e) {
    }
    return frames ? _getLastValidUrl(frames) : null;
  } catch (oO) {
    DEBUG_BUILD$3 && logger.error(`Cannot extract url for event ${getEventDescription(event)}`);
    return null;
  }
}
function _isUselessError(event) {
  if (event.type) {
    return false;
  }
  if (!event.exception || !event.exception.values || event.exception.values.length === 0) {
    return false;
  }
  return (
    // No top-level message
    !event.message && // There are no exception values that have a stacktrace, a non-generic-Error type or value
    !event.exception.values.some((value) => value.stacktrace || value.type && value.type !== "Error" || value.value)
  );
}
function applyAggregateErrorsToEvent(exceptionFromErrorImplementation, parser, maxValueLimit = 250, key, limit, event, hint) {
  if (!event.exception || !event.exception.values || !hint || !isInstanceOf(hint.originalException, Error)) {
    return;
  }
  const originalException = event.exception.values.length > 0 ? event.exception.values[event.exception.values.length - 1] : void 0;
  if (originalException) {
    event.exception.values = truncateAggregateExceptions(
      aggregateExceptionsFromError(
        exceptionFromErrorImplementation,
        parser,
        limit,
        hint.originalException,
        key,
        event.exception.values,
        originalException,
        0
      ),
      maxValueLimit
    );
  }
}
function aggregateExceptionsFromError(exceptionFromErrorImplementation, parser, limit, error, key, prevExceptions, exception, exceptionId) {
  if (prevExceptions.length >= limit + 1) {
    return prevExceptions;
  }
  let newExceptions = [...prevExceptions];
  if (isInstanceOf(error[key], Error)) {
    applyExceptionGroupFieldsForParentException(exception, exceptionId);
    const newException = exceptionFromErrorImplementation(parser, error[key]);
    const newExceptionId = newExceptions.length;
    applyExceptionGroupFieldsForChildException(newException, key, newExceptionId, exceptionId);
    newExceptions = aggregateExceptionsFromError(
      exceptionFromErrorImplementation,
      parser,
      limit,
      error[key],
      key,
      [newException, ...newExceptions],
      newException,
      newExceptionId
    );
  }
  if (Array.isArray(error.errors)) {
    error.errors.forEach((childError, i) => {
      if (isInstanceOf(childError, Error)) {
        applyExceptionGroupFieldsForParentException(exception, exceptionId);
        const newException = exceptionFromErrorImplementation(parser, childError);
        const newExceptionId = newExceptions.length;
        applyExceptionGroupFieldsForChildException(newException, `errors[${i}]`, newExceptionId, exceptionId);
        newExceptions = aggregateExceptionsFromError(
          exceptionFromErrorImplementation,
          parser,
          limit,
          childError,
          key,
          [newException, ...newExceptions],
          newException,
          newExceptionId
        );
      }
    });
  }
  return newExceptions;
}
function applyExceptionGroupFieldsForParentException(exception, exceptionId) {
  exception.mechanism = exception.mechanism || { type: "generic", handled: true };
  exception.mechanism = {
    ...exception.mechanism,
    ...exception.type === "AggregateError" && { is_exception_group: true },
    exception_id: exceptionId
  };
}
function applyExceptionGroupFieldsForChildException(exception, source, exceptionId, parentId) {
  exception.mechanism = exception.mechanism || { type: "generic", handled: true };
  exception.mechanism = {
    ...exception.mechanism,
    type: "chained",
    source,
    exception_id: exceptionId,
    parent_id: parentId
  };
}
function truncateAggregateExceptions(exceptions, maxValueLength) {
  return exceptions.map((exception) => {
    if (exception.value) {
      exception.value = truncate(exception.value, maxValueLength);
    }
    return exception;
  });
}
function parseUrl$1(url) {
  if (!url) {
    return {};
  }
  const match = url.match(/^(([^:/?#]+):)?(\/\/([^/?#]*))?([^?#]*)(\?([^#]*))?(#(.*))?$/);
  if (!match) {
    return {};
  }
  const query = match[6] || "";
  const fragment = match[8] || "";
  return {
    host: match[4],
    path: match[5],
    protocol: match[2],
    search: query,
    hash: fragment,
    relative: match[5] + query + fragment
    // everything minus origin
  };
}
function addConsoleInstrumentationHandler(handler) {
  const type = "console";
  addHandler(type, handler);
  maybeInstrument(type, instrumentConsole);
}
function instrumentConsole() {
  if (!("console" in GLOBAL_OBJ)) {
    return;
  }
  CONSOLE_LEVELS.forEach(function(level) {
    if (!(level in GLOBAL_OBJ.console)) {
      return;
    }
    fill(GLOBAL_OBJ.console, level, function(originalConsoleMethod) {
      originalConsoleMethods[level] = originalConsoleMethod;
      return function(...args) {
        const handlerData = { args, level };
        triggerHandlers("console", handlerData);
        const log = originalConsoleMethods[level];
        log && log.apply(GLOBAL_OBJ.console, args);
      };
    });
  });
}
function severityLevelFromString(level) {
  return level === "warn" ? "warning" : ["fatal", "error", "warning", "log", "info", "debug"].includes(level) ? level : "log";
}
const INTEGRATION_NAME$4 = "Dedupe";
const _dedupeIntegration = () => {
  let previousEvent;
  return {
    name: INTEGRATION_NAME$4,
    processEvent(currentEvent) {
      if (currentEvent.type) {
        return currentEvent;
      }
      try {
        if (_shouldDropEvent(currentEvent, previousEvent)) {
          DEBUG_BUILD$3 && logger.warn("Event dropped due to being a duplicate of previously captured event.");
          return null;
        }
      } catch (_oO) {
      }
      return previousEvent = currentEvent;
    }
  };
};
const dedupeIntegration = defineIntegration(_dedupeIntegration);
function _shouldDropEvent(currentEvent, previousEvent) {
  if (!previousEvent) {
    return false;
  }
  if (_isSameMessageEvent(currentEvent, previousEvent)) {
    return true;
  }
  if (_isSameExceptionEvent(currentEvent, previousEvent)) {
    return true;
  }
  return false;
}
function _isSameMessageEvent(currentEvent, previousEvent) {
  const currentMessage = currentEvent.message;
  const previousMessage = previousEvent.message;
  if (!currentMessage && !previousMessage) {
    return false;
  }
  if (currentMessage && !previousMessage || !currentMessage && previousMessage) {
    return false;
  }
  if (currentMessage !== previousMessage) {
    return false;
  }
  if (!_isSameFingerprint(currentEvent, previousEvent)) {
    return false;
  }
  if (!_isSameStacktrace(currentEvent, previousEvent)) {
    return false;
  }
  return true;
}
function _isSameExceptionEvent(currentEvent, previousEvent) {
  const previousException = _getExceptionFromEvent(previousEvent);
  const currentException = _getExceptionFromEvent(currentEvent);
  if (!previousException || !currentException) {
    return false;
  }
  if (previousException.type !== currentException.type || previousException.value !== currentException.value) {
    return false;
  }
  if (!_isSameFingerprint(currentEvent, previousEvent)) {
    return false;
  }
  if (!_isSameStacktrace(currentEvent, previousEvent)) {
    return false;
  }
  return true;
}
function _isSameStacktrace(currentEvent, previousEvent) {
  let currentFrames = getFramesFromEvent(currentEvent);
  let previousFrames = getFramesFromEvent(previousEvent);
  if (!currentFrames && !previousFrames) {
    return true;
  }
  if (currentFrames && !previousFrames || !currentFrames && previousFrames) {
    return false;
  }
  currentFrames = currentFrames;
  previousFrames = previousFrames;
  if (previousFrames.length !== currentFrames.length) {
    return false;
  }
  for (let i = 0; i < previousFrames.length; i++) {
    const frameA = previousFrames[i];
    const frameB = currentFrames[i];
    if (frameA.filename !== frameB.filename || frameA.lineno !== frameB.lineno || frameA.colno !== frameB.colno || frameA.function !== frameB.function) {
      return false;
    }
  }
  return true;
}
function _isSameFingerprint(currentEvent, previousEvent) {
  let currentFingerprint = currentEvent.fingerprint;
  let previousFingerprint = previousEvent.fingerprint;
  if (!currentFingerprint && !previousFingerprint) {
    return true;
  }
  if (currentFingerprint && !previousFingerprint || !currentFingerprint && previousFingerprint) {
    return false;
  }
  currentFingerprint = currentFingerprint;
  previousFingerprint = previousFingerprint;
  try {
    return !!(currentFingerprint.join("") === previousFingerprint.join(""));
  } catch (_oO) {
    return false;
  }
}
function _getExceptionFromEvent(event) {
  return event.exception && event.exception.values && event.exception.values[0];
}
function getBreadcrumbLogLevelFromHttpStatusCode(statusCode) {
  if (statusCode === void 0) {
    return void 0;
  } else if (statusCode >= 400 && statusCode < 500) {
    return "warning";
  } else if (statusCode >= 500) {
    return "error";
  } else {
    return void 0;
  }
}
const WINDOW$3 = GLOBAL_OBJ;
function supportsFetch() {
  if (!("fetch" in WINDOW$3)) {
    return false;
  }
  try {
    new Headers();
    new Request("http://www.example.com");
    new Response();
    return true;
  } catch (e) {
    return false;
  }
}
function isNativeFunction(func) {
  return func && /^function\s+\w+\(\)\s+\{\s+\[native code\]\s+\}$/.test(func.toString());
}
function supportsNativeFetch() {
  if (typeof EdgeRuntime === "string") {
    return true;
  }
  if (!supportsFetch()) {
    return false;
  }
  if (isNativeFunction(WINDOW$3.fetch)) {
    return true;
  }
  let result = false;
  const doc2 = WINDOW$3.document;
  if (doc2 && typeof doc2.createElement === "function") {
    try {
      const sandbox = doc2.createElement("iframe");
      sandbox.hidden = true;
      doc2.head.appendChild(sandbox);
      if (sandbox.contentWindow && sandbox.contentWindow.fetch) {
        result = isNativeFunction(sandbox.contentWindow.fetch);
      }
      doc2.head.removeChild(sandbox);
    } catch (err) {
      DEBUG_BUILD$2 && logger.warn("Could not create sandbox iframe for pure fetch check, bailing to window.fetch: ", err);
    }
  }
  return result;
}
function addFetchInstrumentationHandler(handler, skipNativeFetchCheck) {
  const type = "fetch";
  addHandler(type, handler);
  maybeInstrument(type, () => instrumentFetch(void 0, skipNativeFetchCheck));
}
function instrumentFetch(onFetchResolved, skipNativeFetchCheck = false) {
  if (skipNativeFetchCheck && !supportsNativeFetch()) {
    return;
  }
  fill(GLOBAL_OBJ, "fetch", function(originalFetch) {
    return function(...args) {
      const virtualError = new Error();
      const { method, url } = parseFetchArgs(args);
      const handlerData = {
        args,
        fetchData: {
          method,
          url
        },
        startTimestamp: timestampInSeconds() * 1e3,
        // // Adding the error to be able to fingerprint the failed fetch event in HttpClient instrumentation
        virtualError
      };
      {
        triggerHandlers("fetch", {
          ...handlerData
        });
      }
      return originalFetch.apply(GLOBAL_OBJ, args).then(
        async (response) => {
          {
            triggerHandlers("fetch", {
              ...handlerData,
              endTimestamp: timestampInSeconds() * 1e3,
              response
            });
          }
          return response;
        },
        (error) => {
          triggerHandlers("fetch", {
            ...handlerData,
            endTimestamp: timestampInSeconds() * 1e3,
            error
          });
          if (isError(error) && error.stack === void 0) {
            error.stack = virtualError.stack;
            addNonEnumerableProperty(error, "framesToPop", 1);
          }
          throw error;
        }
      );
    };
  });
}
function hasProp(obj, prop) {
  return !!obj && typeof obj === "object" && !!obj[prop];
}
function getUrlFromResource(resource) {
  if (typeof resource === "string") {
    return resource;
  }
  if (!resource) {
    return "";
  }
  if (hasProp(resource, "url")) {
    return resource.url;
  }
  if (resource.toString) {
    return resource.toString();
  }
  return "";
}
function parseFetchArgs(fetchArgs) {
  if (fetchArgs.length === 0) {
    return { method: "GET", url: "" };
  }
  if (fetchArgs.length === 2) {
    const [url, options] = fetchArgs;
    return {
      url: getUrlFromResource(url),
      method: hasProp(options, "method") ? String(options.method).toUpperCase() : "GET"
    };
  }
  const arg = fetchArgs[0];
  return {
    url: getUrlFromResource(arg),
    method: hasProp(arg, "method") ? String(arg.method).toUpperCase() : "GET"
  };
}
function getSDKSource() {
  return "npm";
}
const WINDOW$2 = GLOBAL_OBJ;
function supportsHistory() {
  const chromeVar = WINDOW$2.chrome;
  const isChromePackagedApp = chromeVar && chromeVar.app && chromeVar.app.runtime;
  const hasHistoryApi = "history" in WINDOW$2 && !!WINDOW$2.history.pushState && !!WINDOW$2.history.replaceState;
  return !isChromePackagedApp && hasHistoryApi;
}
const WINDOW$1 = GLOBAL_OBJ;
let ignoreOnError = 0;
function shouldIgnoreOnError() {
  return ignoreOnError > 0;
}
function ignoreNextOnError() {
  ignoreOnError++;
  setTimeout(() => {
    ignoreOnError--;
  });
}
function wrap(fn, options = {}) {
  function isFunction2(fn2) {
    return typeof fn2 === "function";
  }
  if (!isFunction2(fn)) {
    return fn;
  }
  try {
    const wrapper = fn.__sentry_wrapped__;
    if (wrapper) {
      if (typeof wrapper === "function") {
        return wrapper;
      } else {
        return fn;
      }
    }
    if (getOriginalFunction(fn)) {
      return fn;
    }
  } catch (e) {
    return fn;
  }
  const sentryWrapped = function(...args) {
    try {
      const wrappedArguments = args.map((arg) => wrap(arg, options));
      return fn.apply(this, wrappedArguments);
    } catch (ex) {
      ignoreNextOnError();
      withScope((scope) => {
        scope.addEventProcessor((event) => {
          if (options.mechanism) {
            addExceptionTypeValue(event, void 0);
            addExceptionMechanism(event, options.mechanism);
          }
          event.extra = {
            ...event.extra,
            arguments: args
          };
          return event;
        });
        captureException(ex);
      });
      throw ex;
    }
  };
  try {
    for (const property in fn) {
      if (Object.prototype.hasOwnProperty.call(fn, property)) {
        sentryWrapped[property] = fn[property];
      }
    }
  } catch (e2) {
  }
  markFunctionWrapped(sentryWrapped, fn);
  addNonEnumerableProperty(fn, "__sentry_wrapped__", sentryWrapped);
  try {
    const descriptor = Object.getOwnPropertyDescriptor(sentryWrapped, "name");
    if (descriptor.configurable) {
      Object.defineProperty(sentryWrapped, "name", {
        get() {
          return fn.name;
        }
      });
    }
  } catch (e3) {
  }
  return sentryWrapped;
}
const DEBUG_BUILD$1 = typeof __SENTRY_DEBUG__ === "undefined" || __SENTRY_DEBUG__;
function exceptionFromError(stackParser, ex) {
  const frames = parseStackFrames(stackParser, ex);
  const exception = {
    type: extractType(ex),
    value: extractMessage(ex)
  };
  if (frames.length) {
    exception.stacktrace = { frames };
  }
  if (exception.type === void 0 && exception.value === "") {
    exception.value = "Unrecoverable error caught";
  }
  return exception;
}
function eventFromPlainObject(stackParser, exception, syntheticException, isUnhandledRejection) {
  const client = getClient();
  const normalizeDepth = client && client.getOptions().normalizeDepth;
  const errorFromProp = getErrorPropertyFromObject(exception);
  const extra = {
    __serialized__: normalizeToSize(exception, normalizeDepth)
  };
  if (errorFromProp) {
    return {
      exception: {
        values: [exceptionFromError(stackParser, errorFromProp)]
      },
      extra
    };
  }
  const event = {
    exception: {
      values: [
        {
          type: isEvent(exception) ? exception.constructor.name : isUnhandledRejection ? "UnhandledRejection" : "Error",
          value: getNonErrorObjectExceptionValue(exception, { isUnhandledRejection })
        }
      ]
    },
    extra
  };
  if (syntheticException) {
    const frames = parseStackFrames(stackParser, syntheticException);
    if (frames.length) {
      event.exception.values[0].stacktrace = { frames };
    }
  }
  return event;
}
function eventFromError(stackParser, ex) {
  return {
    exception: {
      values: [exceptionFromError(stackParser, ex)]
    }
  };
}
function parseStackFrames(stackParser, ex) {
  const stacktrace = ex.stacktrace || ex.stack || "";
  const skipLines = getSkipFirstStackStringLines(ex);
  const framesToPop = getPopFirstTopFrames(ex);
  try {
    return stackParser(stacktrace, skipLines, framesToPop);
  } catch (e) {
  }
  return [];
}
const reactMinifiedRegexp = /Minified React error #\d+;/i;
function getSkipFirstStackStringLines(ex) {
  if (ex && reactMinifiedRegexp.test(ex.message)) {
    return 1;
  }
  return 0;
}
function getPopFirstTopFrames(ex) {
  if (typeof ex.framesToPop === "number") {
    return ex.framesToPop;
  }
  return 0;
}
function isWebAssemblyException(exception) {
  if (typeof WebAssembly !== "undefined" && typeof WebAssembly.Exception !== "undefined") {
    return exception instanceof WebAssembly.Exception;
  } else {
    return false;
  }
}
function extractType(ex) {
  const name = ex && ex.name;
  if (!name && isWebAssemblyException(ex)) {
    const hasTypeInMessage = ex.message && Array.isArray(ex.message) && ex.message.length == 2;
    return hasTypeInMessage ? ex.message[0] : "WebAssembly.Exception";
  }
  return name;
}
function extractMessage(ex) {
  const message = ex && ex.message;
  if (!message) {
    return "No error message";
  }
  if (message.error && typeof message.error.message === "string") {
    return message.error.message;
  }
  if (isWebAssemblyException(ex) && Array.isArray(ex.message) && ex.message.length == 2) {
    return ex.message[1];
  }
  return message;
}
function eventFromException(stackParser, exception, hint, attachStacktrace) {
  const syntheticException = hint && hint.syntheticException || void 0;
  const event = eventFromUnknownInput(stackParser, exception, syntheticException, attachStacktrace);
  addExceptionMechanism(event);
  event.level = "error";
  if (hint && hint.event_id) {
    event.event_id = hint.event_id;
  }
  return resolvedSyncPromise(event);
}
function eventFromMessage(stackParser, message, level = "info", hint, attachStacktrace) {
  const syntheticException = hint && hint.syntheticException || void 0;
  const event = eventFromString(stackParser, message, syntheticException, attachStacktrace);
  event.level = level;
  if (hint && hint.event_id) {
    event.event_id = hint.event_id;
  }
  return resolvedSyncPromise(event);
}
function eventFromUnknownInput(stackParser, exception, syntheticException, attachStacktrace, isUnhandledRejection) {
  let event;
  if (isErrorEvent$1(exception) && exception.error) {
    const errorEvent = exception;
    return eventFromError(stackParser, errorEvent.error);
  }
  if (isDOMError(exception) || isDOMException(exception)) {
    const domException = exception;
    if ("stack" in exception) {
      event = eventFromError(stackParser, exception);
    } else {
      const name = domException.name || (isDOMError(domException) ? "DOMError" : "DOMException");
      const message = domException.message ? `${name}: ${domException.message}` : name;
      event = eventFromString(stackParser, message, syntheticException, attachStacktrace);
      addExceptionTypeValue(event, message);
    }
    if ("code" in domException) {
      event.tags = { ...event.tags, "DOMException.code": `${domException.code}` };
    }
    return event;
  }
  if (isError(exception)) {
    return eventFromError(stackParser, exception);
  }
  if (isPlainObject$1(exception) || isEvent(exception)) {
    const objectException = exception;
    event = eventFromPlainObject(stackParser, objectException, syntheticException, isUnhandledRejection);
    addExceptionMechanism(event, {
      synthetic: true
    });
    return event;
  }
  event = eventFromString(stackParser, exception, syntheticException, attachStacktrace);
  addExceptionTypeValue(event, `${exception}`);
  addExceptionMechanism(event, {
    synthetic: true
  });
  return event;
}
function eventFromString(stackParser, message, syntheticException, attachStacktrace) {
  const event = {};
  if (attachStacktrace && syntheticException) {
    const frames = parseStackFrames(stackParser, syntheticException);
    if (frames.length) {
      event.exception = {
        values: [{ value: message, stacktrace: { frames } }]
      };
    }
    addExceptionMechanism(event, { synthetic: true });
  }
  if (isParameterizedString(message)) {
    const { __sentry_template_string__, __sentry_template_values__ } = message;
    event.logentry = {
      message: __sentry_template_string__,
      params: __sentry_template_values__
    };
    return event;
  }
  event.message = message;
  return event;
}
function getNonErrorObjectExceptionValue(exception, { isUnhandledRejection }) {
  const keys = extractExceptionKeysForMessage(exception);
  const captureType = isUnhandledRejection ? "promise rejection" : "exception";
  if (isErrorEvent$1(exception)) {
    return `Event \`ErrorEvent\` captured as ${captureType} with message \`${exception.message}\``;
  }
  if (isEvent(exception)) {
    const className = getObjectClassName(exception);
    return `Event \`${className}\` (type=${exception.type}) captured as ${captureType}`;
  }
  return `Object captured as ${captureType} with keys: ${keys}`;
}
function getObjectClassName(obj) {
  try {
    const prototype = Object.getPrototypeOf(obj);
    return prototype ? prototype.constructor.name : void 0;
  } catch (e) {
  }
}
function getErrorPropertyFromObject(obj) {
  for (const prop in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, prop)) {
      const value = obj[prop];
      if (value instanceof Error) {
        return value;
      }
    }
  }
  return void 0;
}
function createUserFeedbackEnvelope(feedback, {
  metadata,
  tunnel,
  dsn
}) {
  const headers = {
    event_id: feedback.event_id,
    sent_at: (/* @__PURE__ */ new Date()).toISOString(),
    ...metadata && metadata.sdk && {
      sdk: {
        name: metadata.sdk.name,
        version: metadata.sdk.version
      }
    },
    ...!!tunnel && !!dsn && { dsn: dsnToString(dsn) }
  };
  const item = createUserFeedbackEnvelopeItem(feedback);
  return createEnvelope(headers, [item]);
}
function createUserFeedbackEnvelopeItem(feedback) {
  const feedbackHeaders = {
    type: "user_report"
  };
  return [feedbackHeaders, feedback];
}
class BrowserClient extends BaseClient {
  /**
   * Creates a new Browser SDK instance.
   *
   * @param options Configuration options for this SDK.
   */
  constructor(options) {
    const opts = {
      // We default this to true, as it is the safer scenario
      parentSpanIsAlwaysRootSpan: true,
      ...options
    };
    const sdkSource = WINDOW$1.SENTRY_SDK_SOURCE || getSDKSource();
    applySdkMetadata(opts, "browser", ["browser"], sdkSource);
    super(opts);
    if (opts.sendClientReports && WINDOW$1.document) {
      WINDOW$1.document.addEventListener("visibilitychange", () => {
        if (WINDOW$1.document.visibilityState === "hidden") {
          this._flushOutcomes();
        }
      });
    }
  }
  /**
   * @inheritDoc
   */
  eventFromException(exception, hint) {
    return eventFromException(this._options.stackParser, exception, hint, this._options.attachStacktrace);
  }
  /**
   * @inheritDoc
   */
  eventFromMessage(message, level = "info", hint) {
    return eventFromMessage(this._options.stackParser, message, level, hint, this._options.attachStacktrace);
  }
  /**
   * Sends user feedback to Sentry.
   *
   * @deprecated Use `captureFeedback` instead.
   */
  captureUserFeedback(feedback) {
    if (!this._isEnabled()) {
      DEBUG_BUILD$1 && logger.warn("SDK not enabled, will not capture user feedback.");
      return;
    }
    const envelope = createUserFeedbackEnvelope(feedback, {
      metadata: this.getSdkMetadata(),
      dsn: this.getDsn(),
      tunnel: this.getOptions().tunnel
    });
    this.sendEnvelope(envelope);
  }
  /**
   * @inheritDoc
   */
  _prepareEvent(event, hint, scope) {
    event.platform = event.platform || "javascript";
    return super._prepareEvent(event, hint, scope);
  }
}
const DEBUG_BUILD = typeof __SENTRY_DEBUG__ === "undefined" || __SENTRY_DEBUG__;
const WINDOW = GLOBAL_OBJ;
const DEBOUNCE_DURATION = 1e3;
let debounceTimerID;
let lastCapturedEventType;
let lastCapturedEventTargetId;
function addClickKeypressInstrumentationHandler(handler) {
  const type = "dom";
  addHandler(type, handler);
  maybeInstrument(type, instrumentDOM);
}
function instrumentDOM() {
  if (!WINDOW.document) {
    return;
  }
  const triggerDOMHandler = triggerHandlers.bind(null, "dom");
  const globalDOMEventHandler = makeDOMEventHandler(triggerDOMHandler, true);
  WINDOW.document.addEventListener("click", globalDOMEventHandler, false);
  WINDOW.document.addEventListener("keypress", globalDOMEventHandler, false);
  ["EventTarget", "Node"].forEach((target) => {
    const globalObject = WINDOW;
    const targetObj = globalObject[target];
    const proto = targetObj && targetObj.prototype;
    if (!proto || !proto.hasOwnProperty || !proto.hasOwnProperty("addEventListener")) {
      return;
    }
    fill(proto, "addEventListener", function(originalAddEventListener) {
      return function(type, listener, options) {
        if (type === "click" || type == "keypress") {
          try {
            const handlers2 = this.__sentry_instrumentation_handlers__ = this.__sentry_instrumentation_handlers__ || {};
            const handlerForType = handlers2[type] = handlers2[type] || { refCount: 0 };
            if (!handlerForType.handler) {
              const handler = makeDOMEventHandler(triggerDOMHandler);
              handlerForType.handler = handler;
              originalAddEventListener.call(this, type, handler, options);
            }
            handlerForType.refCount++;
          } catch (e) {
          }
        }
        return originalAddEventListener.call(this, type, listener, options);
      };
    });
    fill(
      proto,
      "removeEventListener",
      function(originalRemoveEventListener) {
        return function(type, listener, options) {
          if (type === "click" || type == "keypress") {
            try {
              const handlers2 = this.__sentry_instrumentation_handlers__ || {};
              const handlerForType = handlers2[type];
              if (handlerForType) {
                handlerForType.refCount--;
                if (handlerForType.refCount <= 0) {
                  originalRemoveEventListener.call(this, type, handlerForType.handler, options);
                  handlerForType.handler = void 0;
                  delete handlers2[type];
                }
                if (Object.keys(handlers2).length === 0) {
                  delete this.__sentry_instrumentation_handlers__;
                }
              }
            } catch (e) {
            }
          }
          return originalRemoveEventListener.call(this, type, listener, options);
        };
      }
    );
  });
}
function isSimilarToLastCapturedEvent(event) {
  if (event.type !== lastCapturedEventType) {
    return false;
  }
  try {
    if (!event.target || event.target._sentryId !== lastCapturedEventTargetId) {
      return false;
    }
  } catch (e) {
  }
  return true;
}
function shouldSkipDOMEvent(eventType, target) {
  if (eventType !== "keypress") {
    return false;
  }
  if (!target || !target.tagName) {
    return true;
  }
  if (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable) {
    return false;
  }
  return true;
}
function makeDOMEventHandler(handler, globalListener = false) {
  return (event) => {
    if (!event || event["_sentryCaptured"]) {
      return;
    }
    const target = getEventTarget(event);
    if (shouldSkipDOMEvent(event.type, target)) {
      return;
    }
    addNonEnumerableProperty(event, "_sentryCaptured", true);
    if (target && !target._sentryId) {
      addNonEnumerableProperty(target, "_sentryId", uuid4());
    }
    const name = event.type === "keypress" ? "input" : event.type;
    if (!isSimilarToLastCapturedEvent(event)) {
      const handlerData = { event, name, global: globalListener };
      handler(handlerData);
      lastCapturedEventType = event.type;
      lastCapturedEventTargetId = target ? target._sentryId : void 0;
    }
    clearTimeout(debounceTimerID);
    debounceTimerID = WINDOW.setTimeout(() => {
      lastCapturedEventTargetId = void 0;
      lastCapturedEventType = void 0;
    }, DEBOUNCE_DURATION);
  };
}
function getEventTarget(event) {
  try {
    return event.target;
  } catch (e) {
    return null;
  }
}
let lastHref;
function addHistoryInstrumentationHandler(handler) {
  const type = "history";
  addHandler(type, handler);
  maybeInstrument(type, instrumentHistory);
}
function instrumentHistory() {
  if (!supportsHistory()) {
    return;
  }
  const oldOnPopState = WINDOW.onpopstate;
  WINDOW.onpopstate = function(...args) {
    const to = WINDOW.location.href;
    const from = lastHref;
    lastHref = to;
    const handlerData = { from, to };
    triggerHandlers("history", handlerData);
    if (oldOnPopState) {
      try {
        return oldOnPopState.apply(this, args);
      } catch (_oO) {
      }
    }
  };
  function historyReplacementFunction(originalHistoryFunction) {
    return function(...args) {
      const url = args.length > 2 ? args[2] : void 0;
      if (url) {
        const from = lastHref;
        const to = String(url);
        lastHref = to;
        const handlerData = { from, to };
        triggerHandlers("history", handlerData);
      }
      return originalHistoryFunction.apply(this, args);
    };
  }
  fill(WINDOW.history, "pushState", historyReplacementFunction);
  fill(WINDOW.history, "replaceState", historyReplacementFunction);
}
const cachedImplementations = {};
function getNativeImplementation(name) {
  const cached = cachedImplementations[name];
  if (cached) {
    return cached;
  }
  let impl = WINDOW[name];
  if (isNativeFunction(impl)) {
    return cachedImplementations[name] = impl.bind(WINDOW);
  }
  const document2 = WINDOW.document;
  if (document2 && typeof document2.createElement === "function") {
    try {
      const sandbox = document2.createElement("iframe");
      sandbox.hidden = true;
      document2.head.appendChild(sandbox);
      const contentWindow = sandbox.contentWindow;
      if (contentWindow && contentWindow[name]) {
        impl = contentWindow[name];
      }
      document2.head.removeChild(sandbox);
    } catch (e) {
      DEBUG_BUILD && logger.warn(`Could not create sandbox iframe for ${name} check, bailing to window.${name}: `, e);
    }
  }
  if (!impl) {
    return impl;
  }
  return cachedImplementations[name] = impl.bind(WINDOW);
}
function clearCachedImplementation(name) {
  cachedImplementations[name] = void 0;
}
const SENTRY_XHR_DATA_KEY = "__sentry_xhr_v3__";
function addXhrInstrumentationHandler(handler) {
  const type = "xhr";
  addHandler(type, handler);
  maybeInstrument(type, instrumentXHR);
}
function instrumentXHR() {
  if (!WINDOW.XMLHttpRequest) {
    return;
  }
  const xhrproto = XMLHttpRequest.prototype;
  xhrproto.open = new Proxy(xhrproto.open, {
    apply(originalOpen, xhrOpenThisArg, xhrOpenArgArray) {
      const virtualError = new Error();
      const startTimestamp = timestampInSeconds() * 1e3;
      const method = isString$1(xhrOpenArgArray[0]) ? xhrOpenArgArray[0].toUpperCase() : void 0;
      const url = parseUrl(xhrOpenArgArray[1]);
      if (!method || !url) {
        return originalOpen.apply(xhrOpenThisArg, xhrOpenArgArray);
      }
      xhrOpenThisArg[SENTRY_XHR_DATA_KEY] = {
        method,
        url,
        request_headers: {}
      };
      if (method === "POST" && url.match(/sentry_key/)) {
        xhrOpenThisArg.__sentry_own_request__ = true;
      }
      const onreadystatechangeHandler = () => {
        const xhrInfo = xhrOpenThisArg[SENTRY_XHR_DATA_KEY];
        if (!xhrInfo) {
          return;
        }
        if (xhrOpenThisArg.readyState === 4) {
          try {
            xhrInfo.status_code = xhrOpenThisArg.status;
          } catch (e) {
          }
          const handlerData = {
            endTimestamp: timestampInSeconds() * 1e3,
            startTimestamp,
            xhr: xhrOpenThisArg,
            virtualError
          };
          triggerHandlers("xhr", handlerData);
        }
      };
      if ("onreadystatechange" in xhrOpenThisArg && typeof xhrOpenThisArg.onreadystatechange === "function") {
        xhrOpenThisArg.onreadystatechange = new Proxy(xhrOpenThisArg.onreadystatechange, {
          apply(originalOnreadystatechange, onreadystatechangeThisArg, onreadystatechangeArgArray) {
            onreadystatechangeHandler();
            return originalOnreadystatechange.apply(onreadystatechangeThisArg, onreadystatechangeArgArray);
          }
        });
      } else {
        xhrOpenThisArg.addEventListener("readystatechange", onreadystatechangeHandler);
      }
      xhrOpenThisArg.setRequestHeader = new Proxy(xhrOpenThisArg.setRequestHeader, {
        apply(originalSetRequestHeader, setRequestHeaderThisArg, setRequestHeaderArgArray) {
          const [header, value] = setRequestHeaderArgArray;
          const xhrInfo = setRequestHeaderThisArg[SENTRY_XHR_DATA_KEY];
          if (xhrInfo && isString$1(header) && isString$1(value)) {
            xhrInfo.request_headers[header.toLowerCase()] = value;
          }
          return originalSetRequestHeader.apply(setRequestHeaderThisArg, setRequestHeaderArgArray);
        }
      });
      return originalOpen.apply(xhrOpenThisArg, xhrOpenArgArray);
    }
  });
  xhrproto.send = new Proxy(xhrproto.send, {
    apply(originalSend, sendThisArg, sendArgArray) {
      const sentryXhrData = sendThisArg[SENTRY_XHR_DATA_KEY];
      if (!sentryXhrData) {
        return originalSend.apply(sendThisArg, sendArgArray);
      }
      if (sendArgArray[0] !== void 0) {
        sentryXhrData.body = sendArgArray[0];
      }
      const handlerData = {
        startTimestamp: timestampInSeconds() * 1e3,
        xhr: sendThisArg
      };
      triggerHandlers("xhr", handlerData);
      return originalSend.apply(sendThisArg, sendArgArray);
    }
  });
}
function parseUrl(url) {
  if (isString$1(url)) {
    return url;
  }
  try {
    return url.toString();
  } catch (e2) {
  }
  return void 0;
}
function makeFetchTransport(options, nativeFetch = getNativeImplementation("fetch")) {
  let pendingBodySize = 0;
  let pendingCount = 0;
  function makeRequest(request) {
    const requestSize = request.body.length;
    pendingBodySize += requestSize;
    pendingCount++;
    const requestOptions = {
      body: request.body,
      method: "POST",
      referrerPolicy: "origin",
      headers: options.headers,
      // Outgoing requests are usually cancelled when navigating to a different page, causing a "TypeError: Failed to
      // fetch" error and sending a "network_error" client-outcome - in Chrome, the request status shows "(cancelled)".
      // The `keepalive` flag keeps outgoing requests alive, even when switching pages. We want this since we're
      // frequently sending events right before the user is switching pages (eg. when finishing navigation transactions).
      // Gotchas:
      // - `keepalive` isn't supported by Firefox
      // - As per spec (https://fetch.spec.whatwg.org/#http-network-or-cache-fetch):
      //   If the sum of contentLength and inflightKeepaliveBytes is greater than 64 kibibytes, then return a network error.
      //   We will therefore only activate the flag when we're below that limit.
      // There is also a limit of requests that can be open at the same time, so we also limit this to 15
      // See https://github.com/getsentry/sentry-javascript/pull/7553 for details
      keepalive: pendingBodySize <= 6e4 && pendingCount < 15,
      ...options.fetchOptions
    };
    if (!nativeFetch) {
      clearCachedImplementation("fetch");
      return rejectedSyncPromise("No fetch implementation available");
    }
    try {
      return nativeFetch(options.url, requestOptions).then((response) => {
        pendingBodySize -= requestSize;
        pendingCount--;
        return {
          statusCode: response.status,
          headers: {
            "x-sentry-rate-limits": response.headers.get("X-Sentry-Rate-Limits"),
            "retry-after": response.headers.get("Retry-After")
          }
        };
      });
    } catch (e) {
      clearCachedImplementation("fetch");
      pendingBodySize -= requestSize;
      pendingCount--;
      return rejectedSyncPromise(e);
    }
  }
  return createTransport(options, makeRequest);
}
const CHROME_PRIORITY = 30;
const GECKO_PRIORITY = 50;
function createFrame(filename, func, lineno, colno) {
  const frame = {
    filename,
    function: func === "<anonymous>" ? UNKNOWN_FUNCTION : func,
    in_app: true
    // All browser frames are considered in_app
  };
  if (lineno !== void 0) {
    frame.lineno = lineno;
  }
  if (colno !== void 0) {
    frame.colno = colno;
  }
  return frame;
}
const chromeRegexNoFnName = /^\s*at (\S+?)(?::(\d+))(?::(\d+))\s*$/i;
const chromeRegex = /^\s*at (?:(.+?\)(?: \[.+\])?|.*?) ?\((?:address at )?)?(?:async )?((?:<anonymous>|[-a-z]+:|.*bundle|\/)?.*?)(?::(\d+))?(?::(\d+))?\)?\s*$/i;
const chromeEvalRegex = /\((\S*)(?::(\d+))(?::(\d+))\)/;
const chromeStackParserFn = (line) => {
  const noFnParts = chromeRegexNoFnName.exec(line);
  if (noFnParts) {
    const [, filename, line2, col] = noFnParts;
    return createFrame(filename, UNKNOWN_FUNCTION, +line2, +col);
  }
  const parts = chromeRegex.exec(line);
  if (parts) {
    const isEval = parts[2] && parts[2].indexOf("eval") === 0;
    if (isEval) {
      const subMatch = chromeEvalRegex.exec(parts[2]);
      if (subMatch) {
        parts[2] = subMatch[1];
        parts[3] = subMatch[2];
        parts[4] = subMatch[3];
      }
    }
    const [func, filename] = extractSafariExtensionDetails(parts[1] || UNKNOWN_FUNCTION, parts[2]);
    return createFrame(filename, func, parts[3] ? +parts[3] : void 0, parts[4] ? +parts[4] : void 0);
  }
  return;
};
const chromeStackLineParser = [CHROME_PRIORITY, chromeStackParserFn];
const geckoREgex = /^\s*(.*?)(?:\((.*?)\))?(?:^|@)?((?:[-a-z]+)?:\/.*?|\[native code\]|[^@]*(?:bundle|\d+\.js)|\/[\w\-. /=]+)(?::(\d+))?(?::(\d+))?\s*$/i;
const geckoEvalRegex = /(\S+) line (\d+)(?: > eval line \d+)* > eval/i;
const gecko = (line) => {
  const parts = geckoREgex.exec(line);
  if (parts) {
    const isEval = parts[3] && parts[3].indexOf(" > eval") > -1;
    if (isEval) {
      const subMatch = geckoEvalRegex.exec(parts[3]);
      if (subMatch) {
        parts[1] = parts[1] || "eval";
        parts[3] = subMatch[1];
        parts[4] = subMatch[2];
        parts[5] = "";
      }
    }
    let filename = parts[3];
    let func = parts[1] || UNKNOWN_FUNCTION;
    [func, filename] = extractSafariExtensionDetails(func, filename);
    return createFrame(filename, func, parts[4] ? +parts[4] : void 0, parts[5] ? +parts[5] : void 0);
  }
  return;
};
const geckoStackLineParser = [GECKO_PRIORITY, gecko];
const defaultStackLineParsers = [chromeStackLineParser, geckoStackLineParser];
const defaultStackParser = createStackParser(...defaultStackLineParsers);
const extractSafariExtensionDetails = (func, filename) => {
  const isSafariExtension = func.indexOf("safari-extension") !== -1;
  const isSafariWebExtension = func.indexOf("safari-web-extension") !== -1;
  return isSafariExtension || isSafariWebExtension ? [
    func.indexOf("@") !== -1 ? func.split("@")[0] : UNKNOWN_FUNCTION,
    isSafariExtension ? `safari-extension:${filename}` : `safari-web-extension:${filename}`
  ] : [func, filename];
};
const MAX_ALLOWED_STRING_LENGTH = 1024;
const INTEGRATION_NAME$3 = "Breadcrumbs";
const _breadcrumbsIntegration = (options = {}) => {
  const _options = {
    console: true,
    dom: true,
    fetch: true,
    history: true,
    sentry: true,
    xhr: true,
    ...options
  };
  return {
    name: INTEGRATION_NAME$3,
    setup(client) {
      if (_options.console) {
        addConsoleInstrumentationHandler(_getConsoleBreadcrumbHandler(client));
      }
      if (_options.dom) {
        addClickKeypressInstrumentationHandler(_getDomBreadcrumbHandler(client, _options.dom));
      }
      if (_options.xhr) {
        addXhrInstrumentationHandler(_getXhrBreadcrumbHandler(client));
      }
      if (_options.fetch) {
        addFetchInstrumentationHandler(_getFetchBreadcrumbHandler(client));
      }
      if (_options.history) {
        addHistoryInstrumentationHandler(_getHistoryBreadcrumbHandler(client));
      }
      if (_options.sentry) {
        client.on("beforeSendEvent", _getSentryBreadcrumbHandler(client));
      }
    }
  };
};
const breadcrumbsIntegration = defineIntegration(_breadcrumbsIntegration);
function _getSentryBreadcrumbHandler(client) {
  return function addSentryBreadcrumb(event) {
    if (getClient() !== client) {
      return;
    }
    addBreadcrumb(
      {
        category: `sentry.${event.type === "transaction" ? "transaction" : "event"}`,
        event_id: event.event_id,
        level: event.level,
        message: getEventDescription(event)
      },
      {
        event
      }
    );
  };
}
function _getDomBreadcrumbHandler(client, dom) {
  return function _innerDomBreadcrumb(handlerData) {
    if (getClient() !== client) {
      return;
    }
    let target;
    let componentName;
    let keyAttrs = typeof dom === "object" ? dom.serializeAttribute : void 0;
    let maxStringLength = typeof dom === "object" && typeof dom.maxStringLength === "number" ? dom.maxStringLength : void 0;
    if (maxStringLength && maxStringLength > MAX_ALLOWED_STRING_LENGTH) {
      DEBUG_BUILD$1 && logger.warn(
        `\`dom.maxStringLength\` cannot exceed ${MAX_ALLOWED_STRING_LENGTH}, but a value of ${maxStringLength} was configured. Sentry will use ${MAX_ALLOWED_STRING_LENGTH} instead.`
      );
      maxStringLength = MAX_ALLOWED_STRING_LENGTH;
    }
    if (typeof keyAttrs === "string") {
      keyAttrs = [keyAttrs];
    }
    try {
      const event = handlerData.event;
      const element = _isEvent(event) ? event.target : event;
      target = htmlTreeAsString(element, { keyAttrs, maxStringLength });
      componentName = getComponentName$1(element);
    } catch (e) {
      target = "<unknown>";
    }
    if (target.length === 0) {
      return;
    }
    const breadcrumb = {
      category: `ui.${handlerData.name}`,
      message: target
    };
    if (componentName) {
      breadcrumb.data = { "ui.component_name": componentName };
    }
    addBreadcrumb(breadcrumb, {
      event: handlerData.event,
      name: handlerData.name,
      global: handlerData.global
    });
  };
}
function _getConsoleBreadcrumbHandler(client) {
  return function _consoleBreadcrumb(handlerData) {
    if (getClient() !== client) {
      return;
    }
    const breadcrumb = {
      category: "console",
      data: {
        arguments: handlerData.args,
        logger: "console"
      },
      level: severityLevelFromString(handlerData.level),
      message: safeJoin(handlerData.args, " ")
    };
    if (handlerData.level === "assert") {
      if (handlerData.args[0] === false) {
        breadcrumb.message = `Assertion failed: ${safeJoin(handlerData.args.slice(1), " ") || "console.assert"}`;
        breadcrumb.data.arguments = handlerData.args.slice(1);
      } else {
        return;
      }
    }
    addBreadcrumb(breadcrumb, {
      input: handlerData.args,
      level: handlerData.level
    });
  };
}
function _getXhrBreadcrumbHandler(client) {
  return function _xhrBreadcrumb(handlerData) {
    if (getClient() !== client) {
      return;
    }
    const { startTimestamp, endTimestamp } = handlerData;
    const sentryXhrData = handlerData.xhr[SENTRY_XHR_DATA_KEY];
    if (!startTimestamp || !endTimestamp || !sentryXhrData) {
      return;
    }
    const { method, url, status_code, body } = sentryXhrData;
    const data = {
      method,
      url,
      status_code
    };
    const hint = {
      xhr: handlerData.xhr,
      input: body,
      startTimestamp,
      endTimestamp
    };
    const level = getBreadcrumbLogLevelFromHttpStatusCode(status_code);
    addBreadcrumb(
      {
        category: "xhr",
        data,
        type: "http",
        level
      },
      hint
    );
  };
}
function _getFetchBreadcrumbHandler(client) {
  return function _fetchBreadcrumb(handlerData) {
    if (getClient() !== client) {
      return;
    }
    const { startTimestamp, endTimestamp } = handlerData;
    if (!endTimestamp) {
      return;
    }
    if (handlerData.fetchData.url.match(/sentry_key/) && handlerData.fetchData.method === "POST") {
      return;
    }
    if (handlerData.error) {
      const data = handlerData.fetchData;
      const hint = {
        data: handlerData.error,
        input: handlerData.args,
        startTimestamp,
        endTimestamp
      };
      addBreadcrumb(
        {
          category: "fetch",
          data,
          level: "error",
          type: "http"
        },
        hint
      );
    } else {
      const response = handlerData.response;
      const data = {
        ...handlerData.fetchData,
        status_code: response && response.status
      };
      const hint = {
        input: handlerData.args,
        response,
        startTimestamp,
        endTimestamp
      };
      const level = getBreadcrumbLogLevelFromHttpStatusCode(data.status_code);
      addBreadcrumb(
        {
          category: "fetch",
          data,
          type: "http",
          level
        },
        hint
      );
    }
  };
}
function _getHistoryBreadcrumbHandler(client) {
  return function _historyBreadcrumb(handlerData) {
    if (getClient() !== client) {
      return;
    }
    let from = handlerData.from;
    let to = handlerData.to;
    const parsedLoc = parseUrl$1(WINDOW$1.location.href);
    let parsedFrom = from ? parseUrl$1(from) : void 0;
    const parsedTo = parseUrl$1(to);
    if (!parsedFrom || !parsedFrom.path) {
      parsedFrom = parsedLoc;
    }
    if (parsedLoc.protocol === parsedTo.protocol && parsedLoc.host === parsedTo.host) {
      to = parsedTo.relative;
    }
    if (parsedLoc.protocol === parsedFrom.protocol && parsedLoc.host === parsedFrom.host) {
      from = parsedFrom.relative;
    }
    addBreadcrumb({
      category: "navigation",
      data: {
        from,
        to
      }
    });
  };
}
function _isEvent(event) {
  return !!event && !!event.target;
}
const DEFAULT_EVENT_TARGET = [
  "EventTarget",
  "Window",
  "Node",
  "ApplicationCache",
  "AudioTrackList",
  "BroadcastChannel",
  "ChannelMergerNode",
  "CryptoOperation",
  "EventSource",
  "FileReader",
  "HTMLUnknownElement",
  "IDBDatabase",
  "IDBRequest",
  "IDBTransaction",
  "KeyOperation",
  "MediaController",
  "MessagePort",
  "ModalWindow",
  "Notification",
  "SVGElementInstance",
  "Screen",
  "SharedWorker",
  "TextTrack",
  "TextTrackCue",
  "TextTrackList",
  "WebSocket",
  "WebSocketWorker",
  "Worker",
  "XMLHttpRequest",
  "XMLHttpRequestEventTarget",
  "XMLHttpRequestUpload"
];
const INTEGRATION_NAME$2 = "BrowserApiErrors";
const _browserApiErrorsIntegration = (options = {}) => {
  const _options = {
    XMLHttpRequest: true,
    eventTarget: true,
    requestAnimationFrame: true,
    setInterval: true,
    setTimeout: true,
    ...options
  };
  return {
    name: INTEGRATION_NAME$2,
    // TODO: This currently only works for the first client this is setup
    // We may want to adjust this to check for client etc.
    setupOnce() {
      if (_options.setTimeout) {
        fill(WINDOW$1, "setTimeout", _wrapTimeFunction);
      }
      if (_options.setInterval) {
        fill(WINDOW$1, "setInterval", _wrapTimeFunction);
      }
      if (_options.requestAnimationFrame) {
        fill(WINDOW$1, "requestAnimationFrame", _wrapRAF);
      }
      if (_options.XMLHttpRequest && "XMLHttpRequest" in WINDOW$1) {
        fill(XMLHttpRequest.prototype, "send", _wrapXHR);
      }
      const eventTargetOption = _options.eventTarget;
      if (eventTargetOption) {
        const eventTarget = Array.isArray(eventTargetOption) ? eventTargetOption : DEFAULT_EVENT_TARGET;
        eventTarget.forEach(_wrapEventTarget);
      }
    }
  };
};
const browserApiErrorsIntegration = defineIntegration(_browserApiErrorsIntegration);
function _wrapTimeFunction(original) {
  return function(...args) {
    const originalCallback = args[0];
    args[0] = wrap(originalCallback, {
      mechanism: {
        data: { function: getFunctionName(original) },
        handled: false,
        type: "instrument"
      }
    });
    return original.apply(this, args);
  };
}
function _wrapRAF(original) {
  return function(callback) {
    return original.apply(this, [
      wrap(callback, {
        mechanism: {
          data: {
            function: "requestAnimationFrame",
            handler: getFunctionName(original)
          },
          handled: false,
          type: "instrument"
        }
      })
    ]);
  };
}
function _wrapXHR(originalSend) {
  return function(...args) {
    const xhr = this;
    const xmlHttpRequestProps = ["onload", "onerror", "onprogress", "onreadystatechange"];
    xmlHttpRequestProps.forEach((prop) => {
      if (prop in xhr && typeof xhr[prop] === "function") {
        fill(xhr, prop, function(original) {
          const wrapOptions = {
            mechanism: {
              data: {
                function: prop,
                handler: getFunctionName(original)
              },
              handled: false,
              type: "instrument"
            }
          };
          const originalFunction = getOriginalFunction(original);
          if (originalFunction) {
            wrapOptions.mechanism.data.handler = getFunctionName(originalFunction);
          }
          return wrap(original, wrapOptions);
        });
      }
    });
    return originalSend.apply(this, args);
  };
}
function _wrapEventTarget(target) {
  const globalObject = WINDOW$1;
  const targetObj = globalObject[target];
  const proto = targetObj && targetObj.prototype;
  if (!proto || !proto.hasOwnProperty || !proto.hasOwnProperty("addEventListener")) {
    return;
  }
  fill(proto, "addEventListener", function(original) {
    return function(eventName, fn, options) {
      try {
        if (isEventListenerObject(fn)) {
          fn.handleEvent = wrap(fn.handleEvent, {
            mechanism: {
              data: {
                function: "handleEvent",
                handler: getFunctionName(fn),
                target
              },
              handled: false,
              type: "instrument"
            }
          });
        }
      } catch (e2) {
      }
      return original.apply(this, [
        eventName,
        wrap(fn, {
          mechanism: {
            data: {
              function: "addEventListener",
              handler: getFunctionName(fn),
              target
            },
            handled: false,
            type: "instrument"
          }
        }),
        options
      ]);
    };
  });
  fill(proto, "removeEventListener", function(originalRemoveEventListener) {
    return function(eventName, fn, options) {
      try {
        const originalEventHandler = fn.__sentry_wrapped__;
        if (originalEventHandler) {
          originalRemoveEventListener.call(this, eventName, originalEventHandler, options);
        }
      } catch (e) {
      }
      return originalRemoveEventListener.call(this, eventName, fn, options);
    };
  });
}
function isEventListenerObject(obj) {
  return typeof obj.handleEvent === "function";
}
const browserSessionIntegration = defineIntegration(() => {
  return {
    name: "BrowserSession",
    setupOnce() {
      if (typeof WINDOW$1.document === "undefined") {
        DEBUG_BUILD$1 && logger.warn("Using the `browserSessionIntegration` in non-browser environments is not supported.");
        return;
      }
      startSession({ ignoreDuration: true });
      captureSession();
      addHistoryInstrumentationHandler(({ from, to }) => {
        if (from !== void 0 && from !== to) {
          startSession({ ignoreDuration: true });
          captureSession();
        }
      });
    }
  };
});
const INTEGRATION_NAME$1 = "GlobalHandlers";
const _globalHandlersIntegration = (options = {}) => {
  const _options = {
    onerror: true,
    onunhandledrejection: true,
    ...options
  };
  return {
    name: INTEGRATION_NAME$1,
    setupOnce() {
      Error.stackTraceLimit = 50;
    },
    setup(client) {
      if (_options.onerror) {
        _installGlobalOnErrorHandler(client);
        globalHandlerLog("onerror");
      }
      if (_options.onunhandledrejection) {
        _installGlobalOnUnhandledRejectionHandler(client);
        globalHandlerLog("onunhandledrejection");
      }
    }
  };
};
const globalHandlersIntegration = defineIntegration(_globalHandlersIntegration);
function _installGlobalOnErrorHandler(client) {
  addGlobalErrorInstrumentationHandler((data) => {
    const { stackParser, attachStacktrace } = getOptions();
    if (getClient() !== client || shouldIgnoreOnError()) {
      return;
    }
    const { msg, url, line, column, error } = data;
    const event = _enhanceEventWithInitialFrame(
      eventFromUnknownInput(stackParser, error || msg, void 0, attachStacktrace, false),
      url,
      line,
      column
    );
    event.level = "error";
    captureEvent(event, {
      originalException: error,
      mechanism: {
        handled: false,
        type: "onerror"
      }
    });
  });
}
function _installGlobalOnUnhandledRejectionHandler(client) {
  addGlobalUnhandledRejectionInstrumentationHandler((e) => {
    const { stackParser, attachStacktrace } = getOptions();
    if (getClient() !== client || shouldIgnoreOnError()) {
      return;
    }
    const error = _getUnhandledRejectionError(e);
    const event = isPrimitive(error) ? _eventFromRejectionWithPrimitive(error) : eventFromUnknownInput(stackParser, error, void 0, attachStacktrace, true);
    event.level = "error";
    captureEvent(event, {
      originalException: error,
      mechanism: {
        handled: false,
        type: "onunhandledrejection"
      }
    });
  });
}
function _getUnhandledRejectionError(error) {
  if (isPrimitive(error)) {
    return error;
  }
  try {
    if ("reason" in error) {
      return error.reason;
    }
    if ("detail" in error && "reason" in error.detail) {
      return error.detail.reason;
    }
  } catch (e2) {
  }
  return error;
}
function _eventFromRejectionWithPrimitive(reason) {
  return {
    exception: {
      values: [
        {
          type: "UnhandledRejection",
          // String() is needed because the Primitive type includes symbols (which can't be automatically stringified)
          value: `Non-Error promise rejection captured with value: ${String(reason)}`
        }
      ]
    }
  };
}
function _enhanceEventWithInitialFrame(event, url, line, column) {
  const e = event.exception = event.exception || {};
  const ev = e.values = e.values || [];
  const ev0 = ev[0] = ev[0] || {};
  const ev0s = ev0.stacktrace = ev0.stacktrace || {};
  const ev0sf = ev0s.frames = ev0s.frames || [];
  const colno = column;
  const lineno = line;
  const filename = isString$1(url) && url.length > 0 ? url : getLocationHref();
  if (ev0sf.length === 0) {
    ev0sf.push({
      colno,
      filename,
      function: UNKNOWN_FUNCTION,
      in_app: true,
      lineno
    });
  }
  return event;
}
function globalHandlerLog(type) {
  DEBUG_BUILD$1 && logger.log(`Global Handler attached: ${type}`);
}
function getOptions() {
  const client = getClient();
  const options = client && client.getOptions() || {
    stackParser: () => [],
    attachStacktrace: false
  };
  return options;
}
const httpContextIntegration = defineIntegration(() => {
  return {
    name: "HttpContext",
    preprocessEvent(event) {
      if (!WINDOW$1.navigator && !WINDOW$1.location && !WINDOW$1.document) {
        return;
      }
      const url = event.request && event.request.url || WINDOW$1.location && WINDOW$1.location.href;
      const { referrer } = WINDOW$1.document || {};
      const { userAgent } = WINDOW$1.navigator || {};
      const headers = {
        ...event.request && event.request.headers,
        ...referrer && { Referer: referrer },
        ...userAgent && { "User-Agent": userAgent }
      };
      const request = { ...event.request, ...url && { url }, headers };
      event.request = request;
    }
  };
});
const DEFAULT_KEY = "cause";
const DEFAULT_LIMIT = 5;
const INTEGRATION_NAME = "LinkedErrors";
const _linkedErrorsIntegration = (options = {}) => {
  const limit = options.limit || DEFAULT_LIMIT;
  const key = options.key || DEFAULT_KEY;
  return {
    name: INTEGRATION_NAME,
    preprocessEvent(event, hint, client) {
      const options2 = client.getOptions();
      applyAggregateErrorsToEvent(
        // This differs from the LinkedErrors integration in core by using a different exceptionFromError function
        exceptionFromError,
        options2.stackParser,
        options2.maxValueLength,
        key,
        limit,
        event,
        hint
      );
    }
  };
};
const linkedErrorsIntegration = defineIntegration(_linkedErrorsIntegration);
function getDefaultIntegrations(options) {
  const integrations = [
    inboundFiltersIntegration(),
    functionToStringIntegration(),
    browserApiErrorsIntegration(),
    breadcrumbsIntegration(),
    globalHandlersIntegration(),
    linkedErrorsIntegration(),
    dedupeIntegration(),
    httpContextIntegration()
  ];
  if (options.autoSessionTracking !== false) {
    integrations.push(browserSessionIntegration());
  }
  return integrations;
}
function applyDefaultOptions(optionsArg = {}) {
  const defaultOptions = {
    defaultIntegrations: getDefaultIntegrations(optionsArg),
    release: typeof __SENTRY_RELEASE__ === "string" ? __SENTRY_RELEASE__ : WINDOW$1.SENTRY_RELEASE && WINDOW$1.SENTRY_RELEASE.id ? WINDOW$1.SENTRY_RELEASE.id : void 0,
    autoSessionTracking: true,
    sendClientReports: true
  };
  if (optionsArg.defaultIntegrations == null) {
    delete optionsArg.defaultIntegrations;
  }
  return { ...defaultOptions, ...optionsArg };
}
function shouldShowBrowserExtensionError() {
  const windowWithMaybeExtension = typeof WINDOW$1.window !== "undefined" && WINDOW$1;
  if (!windowWithMaybeExtension) {
    return false;
  }
  const extensionKey = windowWithMaybeExtension.chrome ? "chrome" : "browser";
  const extensionObject = windowWithMaybeExtension[extensionKey];
  const runtimeId = extensionObject && extensionObject.runtime && extensionObject.runtime.id;
  const href = WINDOW$1.location && WINDOW$1.location.href || "";
  const extensionProtocols = ["chrome-extension:", "moz-extension:", "ms-browser-extension:", "safari-web-extension:"];
  const isDedicatedExtensionPage = !!runtimeId && WINDOW$1 === WINDOW$1.top && extensionProtocols.some((protocol) => href.startsWith(`${protocol}//`));
  const isNWjs = typeof windowWithMaybeExtension.nw !== "undefined";
  return !!runtimeId && !isDedicatedExtensionPage && !isNWjs;
}
function init(browserOptions = {}) {
  const options = applyDefaultOptions(browserOptions);
  if (!options.skipBrowserExtensionCheck && shouldShowBrowserExtensionError()) {
    consoleSandbox(() => {
      console.error(
        "[Sentry] You cannot run Sentry this way in a browser extension, check: https://docs.sentry.io/platforms/javascript/best-practices/browser-extensions/"
      );
    });
    return;
  }
  if (DEBUG_BUILD$1) {
    if (!supportsFetch()) {
      logger.warn(
        "No Fetch API detected. The Sentry SDK requires a Fetch API compatible environment to send events. Please add a Fetch API polyfill."
      );
    }
  }
  const clientOptions = {
    ...options,
    stackParser: stackParserFromStackParserOptions(options.stackParser || defaultStackParser),
    integrations: getIntegrationsToSetup(options),
    transport: options.transport || makeFetchTransport
  };
  return initAndBind(BrowserClient, clientOptions);
}
const TAG_FAILURE = "bundle_cart_add_failure";
const CART_RELATED_COOKIE_NAME = /cart|checkout|shopify|storefront|customer|session|token|digest|locale|currency/i;
let bundleSentryReady = false;
function truncateString(value, maxLen) {
  if (value == null || typeof value !== "string") {
    return value;
  }
  if (value.length <= maxLen) {
    return value;
  }
  return `${value.slice(0, maxLen)}… [truncated ${value.length - maxLen} chars]`;
}
function safeJsonClone(value) {
  try {
    return JSON.parse(JSON.stringify(value, (_k, v) => {
      if (typeof v === "function" || typeof v === "symbol") {
        return void 0;
      }
      return v;
    }));
  } catch {
    return { _serialization: "failed", value_type: typeof value };
  }
}
function gatherBrowserCookiesSnapshot(raw = typeof document !== "undefined" ? document.cookie : "", valueMaxLen = 320) {
  const parsed = {};
  if (typeof raw === "string" && raw.length > 0) {
    raw.split(";").forEach((part) => {
      const idx = part.indexOf("=");
      if (idx === -1) {
        return;
      }
      const k = part.slice(0, idx).trim();
      const v = part.slice(idx + 1).trim();
      parsed[k] = truncateString(decodeURIComponent(v.replace(/\+/g, " ")), valueMaxLen);
    });
  }
  const cart_related = {};
  Object.keys(parsed).forEach((name) => {
    if (CART_RELATED_COOKIE_NAME.test(name)) {
      cart_related[name] = parsed[name];
    }
  });
  return {
    parsed,
    cart_related,
    cookie_names: Object.keys(parsed).sort()
  };
}
function formatCartHumanReadable(cart) {
  if (!cart || typeof cart !== "object") {
    return "(cart JSON unavailable or empty)";
  }
  const lines = [];
  const itemCount = Array.isArray(cart.items) ? cart.items.length : 0;
  lines.push(`Cart item count: ${itemCount}`);
  if (cart.item_count != null) {
    lines.push(`Reported item_count: ${cart.item_count}`);
  }
  if (cart.total_price != null) {
    lines.push(`total_price (cents): ${cart.total_price}`);
  }
  if (cart.currency) {
    lines.push(`currency: ${cart.currency}`);
  }
  if (cart.note) {
    lines.push(`note: ${truncateString(String(cart.note), 500)}`);
  }
  if (!itemCount) {
    lines.push("Line items: (none)");
    return lines.join("\n");
  }
  lines.push("— Line items —");
  cart.items.forEach((it, index) => {
    const n = index + 1;
    const title = it.product_title || it.title || "(no title)";
    const vTitle = it.variant_title || "";
    const sku = it.sku != null && it.sku !== "" ? it.sku : "n/a";
    let segment = `${n}. ${title}`;
    if (vTitle) {
      segment += ` — ${vTitle}`;
    }
    segment += ` | qty ${it.quantity} | variant_id ${it.variant_id} | sku ${sku}`;
    if (it.properties && typeof it.properties === "object" && Object.keys(it.properties).length) {
      const props = Object.entries(it.properties).slice(0, 16).map(([k, val]) => `${k}=${typeof val === "object" ? JSON.stringify(val) : val}`).join("; ");
      segment += `
   properties: ${truncateString(props, 1200)}`;
    }
    lines.push(segment);
  });
  return lines.join("\n");
}
function summarizeCartForContext(cart) {
  if (!cart || typeof cart !== "object") {
    return { available: false };
  }
  const items = Array.isArray(cart.items) ? cart.items : [];
  return {
    available: true,
    item_count: cart.item_count,
    items_length: items.length,
    currency: cart.currency,
    total_price: cart.total_price,
    note: cart.note ? truncateString(String(cart.note), 400) : void 0,
    lines: items.map((it, i) => ({
      index: i + 1,
      product_title: it.product_title || it.title,
      variant_title: it.variant_title,
      quantity: it.quantity,
      variant_id: it.variant_id,
      product_id: it.product_id,
      sku: it.sku,
      line_price: it.line_price,
      final_line_price: it.final_line_price,
      key: it.key
    }))
  };
}
function getCartJsUrl() {
  var _a, _b, _c;
  if (typeof window === "undefined") {
    return "/cart.js";
  }
  if ((_a = window.routes) == null ? void 0 : _a.cart_url) {
    const base = String(window.routes.cart_url);
    return base.endsWith(".js") ? base : `${base}.js`;
  }
  const root = (_c = (_b = window.Shopify) == null ? void 0 : _b.routes) == null ? void 0 : _c.root;
  if (root) {
    return `${root}cart.js`;
  }
  return "/cart.js";
}
async function fetchCartStateAtError() {
  try {
    const res = await fetch(getCartJsUrl(), {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    });
    const text = await res.text();
    let cart = null;
    try {
      cart = text ? JSON.parse(text) : null;
    } catch {
      return {
        ok: res.ok,
        status: res.status,
        cart: null,
        body_preview: truncateString(text, 2500)
      };
    }
    return { ok: res.ok, status: res.status, cart };
  } catch (e) {
    return {
      ok: false,
      status: 0,
      cart: null,
      fetch_error: e instanceof Error ? e.message : String(e)
    };
  }
}
function initBundleCartSentry() {
  var _a, _b;
  if (bundleSentryReady || typeof window === "undefined") {
    return;
  }
  const dsn = typeof ((_a = window.theme) == null ? void 0 : _a.bundleCartSentryDsn) === "string" ? window.theme.bundleCartSentryDsn.trim() : "";
  if (!dsn) {
    return;
  }
  try {
    init({
      dsn,
      environment: typeof ((_b = window.Shopify) == null ? void 0 : _b.shop) === "string" ? window.Shopify.shop : void 0,
      tracesSampleRate: 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      // Manual captureException/captureMessage only. Wrapping addEventListener because it breaks third-party scripts (e.g. Klaviyo onsite.js expects jQuery-like events).
      integrations(defaults) {
        return defaults.map((integration) => {
          if (integration.name !== "BrowserApiErrors") {
            return integration;
          }
          return browserApiErrorsIntegration({ eventTarget: false });
        });
      },
      beforeSend(event) {
        var _a2;
        if (((_a2 = event.tags) == null ? void 0 : _a2[TAG_FAILURE]) !== "true") {
          return null;
        }
        return event;
      }
    });
    bundleSentryReady = true;
  } catch (e) {
    console.error("Bundle cart Sentry init failed:", e);
  }
}
async function reportBundleCartAddFailure(params) {
  initBundleCartSentry();
  if (!bundleSentryReady) {
    return;
  }
  const {
    feature,
    httpStatus,
    responseBody,
    requestPayload,
    error,
    humanReadableBundle,
    additionalContext
  } = params;
  const cartFetch = await fetchCartStateAtError();
  const cookieSnapshot = gatherBrowserCookiesSnapshot();
  const baseMessage = typeof httpStatus === "number" ? `Bundle add-to-cart failed (HTTP ${httpStatus})` : "Bundle add-to-cart failed";
  const err = error instanceof Error ? error : new Error(baseMessage);
  const cartHuman = formatCartHumanReadable(cartFetch.cart);
  const bundleHuman = typeof humanReadableBundle === "string" && humanReadableBundle.trim() ? humanReadableBundle.trim() : "(No bundle composition text was provided for this event.)";
  withScope((scope) => {
    var _a, _b, _c, _d, _e, _f;
    scope.setTag(TAG_FAILURE, "true");
    scope.setTag("bundle_feature", feature);
    if (typeof httpStatus === "number") {
      scope.setTag("http_status", String(httpStatus));
    }
    const bundleRequest = {
      page_url: (_a = window.location) == null ? void 0 : _a.href,
      template: (_b = window.theme) == null ? void 0 : _b.template,
      page_type: (_c = window.theme) == null ? void 0 : _c.pageType,
      market: (_d = window.theme) == null ? void 0 : _d.market,
      shop: (_e = window.Shopify) == null ? void 0 : _e.shop,
      cart_add_url: ((_f = window.routes) == null ? void 0 : _f.cart_add_url) || "/cart/add.js"
    };
    if (requestPayload !== void 0) {
      bundleRequest.request_payload = safeJsonClone(requestPayload);
    }
    if (typeof httpStatus === "number") {
      bundleRequest.http_status = httpStatus;
    }
    if (responseBody != null) {
      bundleRequest.response_body = truncateString(String(responseBody), 8e3);
    }
    scope.setContext("bundle_cart_request", bundleRequest);
    scope.setContext("shopify_cart_at_error", {
      cart_js_fetch_ok: cartFetch.ok,
      cart_js_http_status: cartFetch.status,
      fetch_error: cartFetch.fetch_error,
      body_preview: cartFetch.body_preview,
      ...summarizeCartForContext(cartFetch.cart)
    });
    scope.setContext("browser_cookies", {
      raw_document_cookie_truncated: truncateString(
        typeof document !== "undefined" ? document.cookie : "",
        6e3
      ),
      cookie_name_list: cookieSnapshot.cookie_names,
      cart_related_cookies: cookieSnapshot.cart_related,
      total_cookie_count: cookieSnapshot.cookie_names.length
    });
    if (additionalContext && typeof additionalContext === "object") {
      scope.setContext("additional_information", safeJsonClone(additionalContext));
    }
    scope.setExtra("bundle_components_human_readable", truncateString(bundleHuman, 12e3));
    scope.setExtra("shopify_cart_contents_human_readable", truncateString(cartHuman, 12e3));
    scope.setExtra(
      "browser_cookies_cart_related_key_values",
      truncateString(JSON.stringify(cookieSnapshot.cart_related, null, 2), 8e3)
    );
    scope.setExtra(
      "browser_cookie_names",
      cookieSnapshot.cookie_names.join(", ")
    );
    if (additionalContext && typeof additionalContext === "object") {
      scope.setExtra(
        "additional_information_json",
        truncateString(JSON.stringify(safeJsonClone(additionalContext), null, 2), 12e3)
      );
    }
    captureException(err);
  });
}
async function reportBundleCartEvent(params) {
  initBundleCartSentry();
  if (!bundleSentryReady) {
    return;
  }
  const { feature, eventName, additionalContext, requestPayload, humanReadableBundle } = params;
  const cartFetch = await fetchCartStateAtError();
  const cookieSnapshot = gatherBrowserCookiesSnapshot();
  const cartHuman = formatCartHumanReadable(cartFetch.cart);
  const bundleHuman = typeof humanReadableBundle === "string" && humanReadableBundle.trim() ? humanReadableBundle.trim() : "(No bundle composition text was provided for this event.)";
  withScope((scope) => {
    var _a, _b, _c, _d, _e, _f;
    scope.setTag(TAG_FAILURE, "true");
    scope.setTag("bundle_feature", feature);
    scope.setTag("bundle_event_name", eventName);
    scope.setContext("bundle_cart_request", {
      page_url: (_a = window.location) == null ? void 0 : _a.href,
      template: (_b = window.theme) == null ? void 0 : _b.template,
      page_type: (_c = window.theme) == null ? void 0 : _c.pageType,
      market: (_d = window.theme) == null ? void 0 : _d.market,
      shop: (_e = window.Shopify) == null ? void 0 : _e.shop,
      cart_add_url: ((_f = window.routes) == null ? void 0 : _f.cart_add_url) || "/cart/add.js",
      request_payload: requestPayload !== void 0 ? safeJsonClone(requestPayload) : void 0
    });
    scope.setContext("shopify_cart_at_error", {
      cart_js_fetch_ok: cartFetch.ok,
      cart_js_http_status: cartFetch.status,
      fetch_error: cartFetch.fetch_error,
      body_preview: cartFetch.body_preview,
      ...summarizeCartForContext(cartFetch.cart)
    });
    scope.setContext("browser_cookies", {
      raw_document_cookie_truncated: truncateString(
        typeof document !== "undefined" ? document.cookie : "",
        6e3
      ),
      cookie_name_list: cookieSnapshot.cookie_names,
      cart_related_cookies: cookieSnapshot.cart_related,
      total_cookie_count: cookieSnapshot.cookie_names.length
    });
    if (additionalContext && typeof additionalContext === "object") {
      scope.setContext("additional_information", safeJsonClone(additionalContext));
    }
    scope.setExtra("bundle_components_human_readable", truncateString(bundleHuman, 12e3));
    scope.setExtra("shopify_cart_contents_human_readable", truncateString(cartHuman, 12e3));
    scope.setExtra(
      "browser_cookies_cart_related_key_values",
      truncateString(JSON.stringify(cookieSnapshot.cart_related, null, 2), 8e3)
    );
    scope.setExtra(
      "browser_cookie_names",
      cookieSnapshot.cookie_names.join(", ")
    );
    if (additionalContext && typeof additionalContext === "object") {
      scope.setExtra(
        "additional_information_json",
        truncateString(JSON.stringify(safeJsonClone(additionalContext), null, 2), 12e3)
      );
    }
    captureMessage(`Bundle cart event: ${eventName}`, "info");
  });
}
function parseCartAddJsonResponse(responseText) {
  if (typeof responseText !== "string" || !responseText.trim()) {
    return { isFailure: false, parsed: null, httpStatusFromBody: null };
  }
  try {
    const parsed = JSON.parse(responseText);
    if (!parsed || typeof parsed !== "object") {
      return { isFailure: false, parsed: null, httpStatusFromBody: null };
    }
    const statusNum = Number(parsed.status);
    const httpStatusFromBody = Number.isFinite(statusNum) ? statusNum : null;
    const hasDescription = typeof parsed.description === "string" && parsed.description.length > 0;
    const hasMessage = typeof parsed.message === "string" && parsed.message.length > 0;
    const hasErrors = parsed.errors != null && typeof parsed.errors === "object" && Object.keys(parsed.errors).length > 0;
    const isFailure = httpStatusFromBody != null && httpStatusFromBody >= 400 || hasDescription || hasMessage || hasErrors;
    return { isFailure, parsed, httpStatusFromBody };
  } catch {
    return { isFailure: false, parsed: null, httpStatusFromBody: null };
  }
}
/**
* @vue/shared v3.5.13
* (c) 2018-present Yuxi (Evan) You and Vue contributors
* @license MIT
**/
/*! #__NO_SIDE_EFFECTS__ */
// @__NO_SIDE_EFFECTS__
function makeMap(str) {
  const map = /* @__PURE__ */ Object.create(null);
  for (const key of str.split(",")) map[key] = 1;
  return (val) => val in map;
}
const EMPTY_OBJ = {};
const EMPTY_ARR = [];
const NOOP = () => {
};
const NO = () => false;
const isOn = (key) => key.charCodeAt(0) === 111 && key.charCodeAt(1) === 110 && // uppercase letter
(key.charCodeAt(2) > 122 || key.charCodeAt(2) < 97);
const isModelListener = (key) => key.startsWith("onUpdate:");
const extend = Object.assign;
const remove = (arr, el) => {
  const i = arr.indexOf(el);
  if (i > -1) {
    arr.splice(i, 1);
  }
};
const hasOwnProperty$1 = Object.prototype.hasOwnProperty;
const hasOwn = (val, key) => hasOwnProperty$1.call(val, key);
const isArray = Array.isArray;
const isMap = (val) => toTypeString(val) === "[object Map]";
const isSet = (val) => toTypeString(val) === "[object Set]";
const isFunction = (val) => typeof val === "function";
const isString = (val) => typeof val === "string";
const isSymbol = (val) => typeof val === "symbol";
const isObject$1 = (val) => val !== null && typeof val === "object";
const isPromise$1 = (val) => {
  return (isObject$1(val) || isFunction(val)) && isFunction(val.then) && isFunction(val.catch);
};
const objectToString = Object.prototype.toString;
const toTypeString = (value) => objectToString.call(value);
const toRawType = (value) => {
  return toTypeString(value).slice(8, -1);
};
const isPlainObject = (val) => toTypeString(val) === "[object Object]";
const isIntegerKey = (key) => isString(key) && key !== "NaN" && key[0] !== "-" && "" + parseInt(key, 10) === key;
const isReservedProp = /* @__PURE__ */ makeMap(
  // the leading comma is intentional so empty string "" is also included
  ",key,ref,ref_for,ref_key,onVnodeBeforeMount,onVnodeMounted,onVnodeBeforeUpdate,onVnodeUpdated,onVnodeBeforeUnmount,onVnodeUnmounted"
);
const cacheStringFunction = (fn) => {
  const cache = /* @__PURE__ */ Object.create(null);
  return (str) => {
    const hit = cache[str];
    return hit || (cache[str] = fn(str));
  };
};
const camelizeRE = /-(\w)/g;
const camelize = cacheStringFunction(
  (str) => {
    return str.replace(camelizeRE, (_, c) => c ? c.toUpperCase() : "");
  }
);
const hyphenateRE = /\B([A-Z])/g;
const hyphenate = cacheStringFunction(
  (str) => str.replace(hyphenateRE, "-$1").toLowerCase()
);
const capitalize = cacheStringFunction((str) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
});
const toHandlerKey = cacheStringFunction(
  (str) => {
    const s = str ? `on${capitalize(str)}` : ``;
    return s;
  }
);
const hasChanged = (value, oldValue) => !Object.is(value, oldValue);
const invokeArrayFns = (fns, ...arg) => {
  for (let i = 0; i < fns.length; i++) {
    fns[i](...arg);
  }
};
const def = (obj, key, value, writable = false) => {
  Object.defineProperty(obj, key, {
    configurable: true,
    enumerable: false,
    writable,
    value
  });
};
const looseToNumber = (val) => {
  const n = parseFloat(val);
  return isNaN(n) ? val : n;
};
let _globalThis;
const getGlobalThis = () => {
  return _globalThis || (_globalThis = typeof globalThis !== "undefined" ? globalThis : typeof self !== "undefined" ? self : typeof window !== "undefined" ? window : typeof global !== "undefined" ? global : {});
};
function normalizeStyle(value) {
  if (isArray(value)) {
    const res = {};
    for (let i = 0; i < value.length; i++) {
      const item = value[i];
      const normalized = isString(item) ? parseStringStyle(item) : normalizeStyle(item);
      if (normalized) {
        for (const key in normalized) {
          res[key] = normalized[key];
        }
      }
    }
    return res;
  } else if (isString(value) || isObject$1(value)) {
    return value;
  }
}
const listDelimiterRE = /;(?![^(]*\))/g;
const propertyDelimiterRE = /:([^]+)/;
const styleCommentRE = /\/\*[^]*?\*\//g;
function parseStringStyle(cssText) {
  const ret = {};
  cssText.replace(styleCommentRE, "").split(listDelimiterRE).forEach((item) => {
    if (item) {
      const tmp = item.split(propertyDelimiterRE);
      tmp.length > 1 && (ret[tmp[0].trim()] = tmp[1].trim());
    }
  });
  return ret;
}
function normalizeClass(value) {
  let res = "";
  if (isString(value)) {
    res = value;
  } else if (isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      const normalized = normalizeClass(value[i]);
      if (normalized) {
        res += normalized + " ";
      }
    }
  } else if (isObject$1(value)) {
    for (const name in value) {
      if (value[name]) {
        res += name + " ";
      }
    }
  }
  return res.trim();
}
const specialBooleanAttrs = `itemscope,allowfullscreen,formnovalidate,ismap,nomodule,novalidate,readonly`;
const isSpecialBooleanAttr = /* @__PURE__ */ makeMap(specialBooleanAttrs);
function includeBooleanAttr(value) {
  return !!value || value === "";
}
const isRef$1 = (val) => {
  return !!(val && val["__v_isRef"] === true);
};
const toDisplayString = (val) => {
  return isString(val) ? val : val == null ? "" : isArray(val) || isObject$1(val) && (val.toString === objectToString || !isFunction(val.toString)) ? isRef$1(val) ? toDisplayString(val.value) : JSON.stringify(val, replacer, 2) : String(val);
};
const replacer = (_key, val) => {
  if (isRef$1(val)) {
    return replacer(_key, val.value);
  } else if (isMap(val)) {
    return {
      [`Map(${val.size})`]: [...val.entries()].reduce(
        (entries, [key, val2], i) => {
          entries[stringifySymbol(key, i) + " =>"] = val2;
          return entries;
        },
        {}
      )
    };
  } else if (isSet(val)) {
    return {
      [`Set(${val.size})`]: [...val.values()].map((v) => stringifySymbol(v))
    };
  } else if (isSymbol(val)) {
    return stringifySymbol(val);
  } else if (isObject$1(val) && !isArray(val) && !isPlainObject(val)) {
    return String(val);
  }
  return val;
};
const stringifySymbol = (v, i = "") => {
  var _a;
  return (
    // Symbol.description in es2019+ so we need to cast here to pass
    // the lib: es2016 check
    isSymbol(v) ? `Symbol(${(_a = v.description) != null ? _a : i})` : v
  );
};
/**
* @vue/reactivity v3.5.13
* (c) 2018-present Yuxi (Evan) You and Vue contributors
* @license MIT
**/
let activeEffectScope;
class EffectScope {
  constructor(detached = false) {
    this.detached = detached;
    this._active = true;
    this.effects = [];
    this.cleanups = [];
    this._isPaused = false;
    this.parent = activeEffectScope;
    if (!detached && activeEffectScope) {
      this.index = (activeEffectScope.scopes || (activeEffectScope.scopes = [])).push(
        this
      ) - 1;
    }
  }
  get active() {
    return this._active;
  }
  pause() {
    if (this._active) {
      this._isPaused = true;
      let i, l;
      if (this.scopes) {
        for (i = 0, l = this.scopes.length; i < l; i++) {
          this.scopes[i].pause();
        }
      }
      for (i = 0, l = this.effects.length; i < l; i++) {
        this.effects[i].pause();
      }
    }
  }
  /**
   * Resumes the effect scope, including all child scopes and effects.
   */
  resume() {
    if (this._active) {
      if (this._isPaused) {
        this._isPaused = false;
        let i, l;
        if (this.scopes) {
          for (i = 0, l = this.scopes.length; i < l; i++) {
            this.scopes[i].resume();
          }
        }
        for (i = 0, l = this.effects.length; i < l; i++) {
          this.effects[i].resume();
        }
      }
    }
  }
  run(fn) {
    if (this._active) {
      const currentEffectScope = activeEffectScope;
      try {
        activeEffectScope = this;
        return fn();
      } finally {
        activeEffectScope = currentEffectScope;
      }
    }
  }
  /**
   * This should only be called on non-detached scopes
   * @internal
   */
  on() {
    activeEffectScope = this;
  }
  /**
   * This should only be called on non-detached scopes
   * @internal
   */
  off() {
    activeEffectScope = this.parent;
  }
  stop(fromParent) {
    if (this._active) {
      this._active = false;
      let i, l;
      for (i = 0, l = this.effects.length; i < l; i++) {
        this.effects[i].stop();
      }
      this.effects.length = 0;
      for (i = 0, l = this.cleanups.length; i < l; i++) {
        this.cleanups[i]();
      }
      this.cleanups.length = 0;
      if (this.scopes) {
        for (i = 0, l = this.scopes.length; i < l; i++) {
          this.scopes[i].stop(true);
        }
        this.scopes.length = 0;
      }
      if (!this.detached && this.parent && !fromParent) {
        const last = this.parent.scopes.pop();
        if (last && last !== this) {
          this.parent.scopes[this.index] = last;
          last.index = this.index;
        }
      }
      this.parent = void 0;
    }
  }
}
function effectScope(detached) {
  return new EffectScope(detached);
}
function getCurrentScope() {
  return activeEffectScope;
}
let activeSub;
const pausedQueueEffects = /* @__PURE__ */ new WeakSet();
class ReactiveEffect {
  constructor(fn) {
    this.fn = fn;
    this.deps = void 0;
    this.depsTail = void 0;
    this.flags = 1 | 4;
    this.next = void 0;
    this.cleanup = void 0;
    this.scheduler = void 0;
    if (activeEffectScope && activeEffectScope.active) {
      activeEffectScope.effects.push(this);
    }
  }
  pause() {
    this.flags |= 64;
  }
  resume() {
    if (this.flags & 64) {
      this.flags &= -65;
      if (pausedQueueEffects.has(this)) {
        pausedQueueEffects.delete(this);
        this.trigger();
      }
    }
  }
  /**
   * @internal
   */
  notify() {
    if (this.flags & 2 && !(this.flags & 32)) {
      return;
    }
    if (!(this.flags & 8)) {
      batch(this);
    }
  }
  run() {
    if (!(this.flags & 1)) {
      return this.fn();
    }
    this.flags |= 2;
    cleanupEffect(this);
    prepareDeps(this);
    const prevEffect = activeSub;
    const prevShouldTrack = shouldTrack;
    activeSub = this;
    shouldTrack = true;
    try {
      return this.fn();
    } finally {
      cleanupDeps(this);
      activeSub = prevEffect;
      shouldTrack = prevShouldTrack;
      this.flags &= -3;
    }
  }
  stop() {
    if (this.flags & 1) {
      for (let link = this.deps; link; link = link.nextDep) {
        removeSub(link);
      }
      this.deps = this.depsTail = void 0;
      cleanupEffect(this);
      this.onStop && this.onStop();
      this.flags &= -2;
    }
  }
  trigger() {
    if (this.flags & 64) {
      pausedQueueEffects.add(this);
    } else if (this.scheduler) {
      this.scheduler();
    } else {
      this.runIfDirty();
    }
  }
  /**
   * @internal
   */
  runIfDirty() {
    if (isDirty(this)) {
      this.run();
    }
  }
  get dirty() {
    return isDirty(this);
  }
}
let batchDepth = 0;
let batchedSub;
let batchedComputed;
function batch(sub, isComputed = false) {
  sub.flags |= 8;
  if (isComputed) {
    sub.next = batchedComputed;
    batchedComputed = sub;
    return;
  }
  sub.next = batchedSub;
  batchedSub = sub;
}
function startBatch() {
  batchDepth++;
}
function endBatch() {
  if (--batchDepth > 0) {
    return;
  }
  if (batchedComputed) {
    let e = batchedComputed;
    batchedComputed = void 0;
    while (e) {
      const next = e.next;
      e.next = void 0;
      e.flags &= -9;
      e = next;
    }
  }
  let error;
  while (batchedSub) {
    let e = batchedSub;
    batchedSub = void 0;
    while (e) {
      const next = e.next;
      e.next = void 0;
      e.flags &= -9;
      if (e.flags & 1) {
        try {
          ;
          e.trigger();
        } catch (err) {
          if (!error) error = err;
        }
      }
      e = next;
    }
  }
  if (error) throw error;
}
function prepareDeps(sub) {
  for (let link = sub.deps; link; link = link.nextDep) {
    link.version = -1;
    link.prevActiveLink = link.dep.activeLink;
    link.dep.activeLink = link;
  }
}
function cleanupDeps(sub) {
  let head;
  let tail = sub.depsTail;
  let link = tail;
  while (link) {
    const prev = link.prevDep;
    if (link.version === -1) {
      if (link === tail) tail = prev;
      removeSub(link);
      removeDep(link);
    } else {
      head = link;
    }
    link.dep.activeLink = link.prevActiveLink;
    link.prevActiveLink = void 0;
    link = prev;
  }
  sub.deps = head;
  sub.depsTail = tail;
}
function isDirty(sub) {
  for (let link = sub.deps; link; link = link.nextDep) {
    if (link.dep.version !== link.version || link.dep.computed && (refreshComputed(link.dep.computed) || link.dep.version !== link.version)) {
      return true;
    }
  }
  if (sub._dirty) {
    return true;
  }
  return false;
}
function refreshComputed(computed2) {
  if (computed2.flags & 4 && !(computed2.flags & 16)) {
    return;
  }
  computed2.flags &= -17;
  if (computed2.globalVersion === globalVersion) {
    return;
  }
  computed2.globalVersion = globalVersion;
  const dep = computed2.dep;
  computed2.flags |= 2;
  if (dep.version > 0 && !computed2.isSSR && computed2.deps && !isDirty(computed2)) {
    computed2.flags &= -3;
    return;
  }
  const prevSub = activeSub;
  const prevShouldTrack = shouldTrack;
  activeSub = computed2;
  shouldTrack = true;
  try {
    prepareDeps(computed2);
    const value = computed2.fn(computed2._value);
    if (dep.version === 0 || hasChanged(value, computed2._value)) {
      computed2._value = value;
      dep.version++;
    }
  } catch (err) {
    dep.version++;
    throw err;
  } finally {
    activeSub = prevSub;
    shouldTrack = prevShouldTrack;
    cleanupDeps(computed2);
    computed2.flags &= -3;
  }
}
function removeSub(link, soft = false) {
  const { dep, prevSub, nextSub } = link;
  if (prevSub) {
    prevSub.nextSub = nextSub;
    link.prevSub = void 0;
  }
  if (nextSub) {
    nextSub.prevSub = prevSub;
    link.nextSub = void 0;
  }
  if (dep.subs === link) {
    dep.subs = prevSub;
    if (!prevSub && dep.computed) {
      dep.computed.flags &= -5;
      for (let l = dep.computed.deps; l; l = l.nextDep) {
        removeSub(l, true);
      }
    }
  }
  if (!soft && !--dep.sc && dep.map) {
    dep.map.delete(dep.key);
  }
}
function removeDep(link) {
  const { prevDep, nextDep } = link;
  if (prevDep) {
    prevDep.nextDep = nextDep;
    link.prevDep = void 0;
  }
  if (nextDep) {
    nextDep.prevDep = prevDep;
    link.nextDep = void 0;
  }
}
let shouldTrack = true;
const trackStack = [];
function pauseTracking() {
  trackStack.push(shouldTrack);
  shouldTrack = false;
}
function resetTracking() {
  const last = trackStack.pop();
  shouldTrack = last === void 0 ? true : last;
}
function cleanupEffect(e) {
  const { cleanup } = e;
  e.cleanup = void 0;
  if (cleanup) {
    const prevSub = activeSub;
    activeSub = void 0;
    try {
      cleanup();
    } finally {
      activeSub = prevSub;
    }
  }
}
let globalVersion = 0;
class Link {
  constructor(sub, dep) {
    this.sub = sub;
    this.dep = dep;
    this.version = dep.version;
    this.nextDep = this.prevDep = this.nextSub = this.prevSub = this.prevActiveLink = void 0;
  }
}
class Dep {
  constructor(computed2) {
    this.computed = computed2;
    this.version = 0;
    this.activeLink = void 0;
    this.subs = void 0;
    this.map = void 0;
    this.key = void 0;
    this.sc = 0;
  }
  track(debugInfo) {
    if (!activeSub || !shouldTrack || activeSub === this.computed) {
      return;
    }
    let link = this.activeLink;
    if (link === void 0 || link.sub !== activeSub) {
      link = this.activeLink = new Link(activeSub, this);
      if (!activeSub.deps) {
        activeSub.deps = activeSub.depsTail = link;
      } else {
        link.prevDep = activeSub.depsTail;
        activeSub.depsTail.nextDep = link;
        activeSub.depsTail = link;
      }
      addSub(link);
    } else if (link.version === -1) {
      link.version = this.version;
      if (link.nextDep) {
        const next = link.nextDep;
        next.prevDep = link.prevDep;
        if (link.prevDep) {
          link.prevDep.nextDep = next;
        }
        link.prevDep = activeSub.depsTail;
        link.nextDep = void 0;
        activeSub.depsTail.nextDep = link;
        activeSub.depsTail = link;
        if (activeSub.deps === link) {
          activeSub.deps = next;
        }
      }
    }
    return link;
  }
  trigger(debugInfo) {
    this.version++;
    globalVersion++;
    this.notify(debugInfo);
  }
  notify(debugInfo) {
    startBatch();
    try {
      if (false) ;
      for (let link = this.subs; link; link = link.prevSub) {
        if (link.sub.notify()) {
          ;
          link.sub.dep.notify();
        }
      }
    } finally {
      endBatch();
    }
  }
}
function addSub(link) {
  link.dep.sc++;
  if (link.sub.flags & 4) {
    const computed2 = link.dep.computed;
    if (computed2 && !link.dep.subs) {
      computed2.flags |= 4 | 16;
      for (let l = computed2.deps; l; l = l.nextDep) {
        addSub(l);
      }
    }
    const currentTail = link.dep.subs;
    if (currentTail !== link) {
      link.prevSub = currentTail;
      if (currentTail) currentTail.nextSub = link;
    }
    link.dep.subs = link;
  }
}
const targetMap = /* @__PURE__ */ new WeakMap();
const ITERATE_KEY = Symbol(
  ""
);
const MAP_KEY_ITERATE_KEY = Symbol(
  ""
);
const ARRAY_ITERATE_KEY = Symbol(
  ""
);
function track(target, type, key) {
  if (shouldTrack && activeSub) {
    let depsMap = targetMap.get(target);
    if (!depsMap) {
      targetMap.set(target, depsMap = /* @__PURE__ */ new Map());
    }
    let dep = depsMap.get(key);
    if (!dep) {
      depsMap.set(key, dep = new Dep());
      dep.map = depsMap;
      dep.key = key;
    }
    {
      dep.track();
    }
  }
}
function trigger(target, type, key, newValue, oldValue, oldTarget) {
  const depsMap = targetMap.get(target);
  if (!depsMap) {
    globalVersion++;
    return;
  }
  const run = (dep) => {
    if (dep) {
      {
        dep.trigger();
      }
    }
  };
  startBatch();
  if (type === "clear") {
    depsMap.forEach(run);
  } else {
    const targetIsArray = isArray(target);
    const isArrayIndex = targetIsArray && isIntegerKey(key);
    if (targetIsArray && key === "length") {
      const newLength = Number(newValue);
      depsMap.forEach((dep, key2) => {
        if (key2 === "length" || key2 === ARRAY_ITERATE_KEY || !isSymbol(key2) && key2 >= newLength) {
          run(dep);
        }
      });
    } else {
      if (key !== void 0 || depsMap.has(void 0)) {
        run(depsMap.get(key));
      }
      if (isArrayIndex) {
        run(depsMap.get(ARRAY_ITERATE_KEY));
      }
      switch (type) {
        case "add":
          if (!targetIsArray) {
            run(depsMap.get(ITERATE_KEY));
            if (isMap(target)) {
              run(depsMap.get(MAP_KEY_ITERATE_KEY));
            }
          } else if (isArrayIndex) {
            run(depsMap.get("length"));
          }
          break;
        case "delete":
          if (!targetIsArray) {
            run(depsMap.get(ITERATE_KEY));
            if (isMap(target)) {
              run(depsMap.get(MAP_KEY_ITERATE_KEY));
            }
          }
          break;
        case "set":
          if (isMap(target)) {
            run(depsMap.get(ITERATE_KEY));
          }
          break;
      }
    }
  }
  endBatch();
}
function reactiveReadArray(array) {
  const raw = toRaw(array);
  if (raw === array) return raw;
  track(raw, "iterate", ARRAY_ITERATE_KEY);
  return isShallow(array) ? raw : raw.map(toReactive);
}
function shallowReadArray(arr) {
  track(arr = toRaw(arr), "iterate", ARRAY_ITERATE_KEY);
  return arr;
}
const arrayInstrumentations = {
  __proto__: null,
  [Symbol.iterator]() {
    return iterator(this, Symbol.iterator, toReactive);
  },
  concat(...args) {
    return reactiveReadArray(this).concat(
      ...args.map((x) => isArray(x) ? reactiveReadArray(x) : x)
    );
  },
  entries() {
    return iterator(this, "entries", (value) => {
      value[1] = toReactive(value[1]);
      return value;
    });
  },
  every(fn, thisArg) {
    return apply(this, "every", fn, thisArg, void 0, arguments);
  },
  filter(fn, thisArg) {
    return apply(this, "filter", fn, thisArg, (v) => v.map(toReactive), arguments);
  },
  find(fn, thisArg) {
    return apply(this, "find", fn, thisArg, toReactive, arguments);
  },
  findIndex(fn, thisArg) {
    return apply(this, "findIndex", fn, thisArg, void 0, arguments);
  },
  findLast(fn, thisArg) {
    return apply(this, "findLast", fn, thisArg, toReactive, arguments);
  },
  findLastIndex(fn, thisArg) {
    return apply(this, "findLastIndex", fn, thisArg, void 0, arguments);
  },
  // flat, flatMap could benefit from ARRAY_ITERATE but are not straight-forward to implement
  forEach(fn, thisArg) {
    return apply(this, "forEach", fn, thisArg, void 0, arguments);
  },
  includes(...args) {
    return searchProxy(this, "includes", args);
  },
  indexOf(...args) {
    return searchProxy(this, "indexOf", args);
  },
  join(separator) {
    return reactiveReadArray(this).join(separator);
  },
  // keys() iterator only reads `length`, no optimisation required
  lastIndexOf(...args) {
    return searchProxy(this, "lastIndexOf", args);
  },
  map(fn, thisArg) {
    return apply(this, "map", fn, thisArg, void 0, arguments);
  },
  pop() {
    return noTracking(this, "pop");
  },
  push(...args) {
    return noTracking(this, "push", args);
  },
  reduce(fn, ...args) {
    return reduce(this, "reduce", fn, args);
  },
  reduceRight(fn, ...args) {
    return reduce(this, "reduceRight", fn, args);
  },
  shift() {
    return noTracking(this, "shift");
  },
  // slice could use ARRAY_ITERATE but also seems to beg for range tracking
  some(fn, thisArg) {
    return apply(this, "some", fn, thisArg, void 0, arguments);
  },
  splice(...args) {
    return noTracking(this, "splice", args);
  },
  toReversed() {
    return reactiveReadArray(this).toReversed();
  },
  toSorted(comparer) {
    return reactiveReadArray(this).toSorted(comparer);
  },
  toSpliced(...args) {
    return reactiveReadArray(this).toSpliced(...args);
  },
  unshift(...args) {
    return noTracking(this, "unshift", args);
  },
  values() {
    return iterator(this, "values", toReactive);
  }
};
function iterator(self2, method, wrapValue) {
  const arr = shallowReadArray(self2);
  const iter = arr[method]();
  if (arr !== self2 && !isShallow(self2)) {
    iter._next = iter.next;
    iter.next = () => {
      const result = iter._next();
      if (result.value) {
        result.value = wrapValue(result.value);
      }
      return result;
    };
  }
  return iter;
}
const arrayProto = Array.prototype;
function apply(self2, method, fn, thisArg, wrappedRetFn, args) {
  const arr = shallowReadArray(self2);
  const needsWrap = arr !== self2 && !isShallow(self2);
  const methodFn = arr[method];
  if (methodFn !== arrayProto[method]) {
    const result2 = methodFn.apply(self2, args);
    return needsWrap ? toReactive(result2) : result2;
  }
  let wrappedFn = fn;
  if (arr !== self2) {
    if (needsWrap) {
      wrappedFn = function(item, index) {
        return fn.call(this, toReactive(item), index, self2);
      };
    } else if (fn.length > 2) {
      wrappedFn = function(item, index) {
        return fn.call(this, item, index, self2);
      };
    }
  }
  const result = methodFn.call(arr, wrappedFn, thisArg);
  return needsWrap && wrappedRetFn ? wrappedRetFn(result) : result;
}
function reduce(self2, method, fn, args) {
  const arr = shallowReadArray(self2);
  let wrappedFn = fn;
  if (arr !== self2) {
    if (!isShallow(self2)) {
      wrappedFn = function(acc, item, index) {
        return fn.call(this, acc, toReactive(item), index, self2);
      };
    } else if (fn.length > 3) {
      wrappedFn = function(acc, item, index) {
        return fn.call(this, acc, item, index, self2);
      };
    }
  }
  return arr[method](wrappedFn, ...args);
}
function searchProxy(self2, method, args) {
  const arr = toRaw(self2);
  track(arr, "iterate", ARRAY_ITERATE_KEY);
  const res = arr[method](...args);
  if ((res === -1 || res === false) && isProxy(args[0])) {
    args[0] = toRaw(args[0]);
    return arr[method](...args);
  }
  return res;
}
function noTracking(self2, method, args = []) {
  pauseTracking();
  startBatch();
  const res = toRaw(self2)[method].apply(self2, args);
  endBatch();
  resetTracking();
  return res;
}
const isNonTrackableKeys = /* @__PURE__ */ makeMap(`__proto__,__v_isRef,__isVue`);
const builtInSymbols = new Set(
  /* @__PURE__ */ Object.getOwnPropertyNames(Symbol).filter((key) => key !== "arguments" && key !== "caller").map((key) => Symbol[key]).filter(isSymbol)
);
function hasOwnProperty(key) {
  if (!isSymbol(key)) key = String(key);
  const obj = toRaw(this);
  track(obj, "has", key);
  return obj.hasOwnProperty(key);
}
class BaseReactiveHandler {
  constructor(_isReadonly = false, _isShallow = false) {
    this._isReadonly = _isReadonly;
    this._isShallow = _isShallow;
  }
  get(target, key, receiver) {
    if (key === "__v_skip") return target["__v_skip"];
    const isReadonly2 = this._isReadonly, isShallow2 = this._isShallow;
    if (key === "__v_isReactive") {
      return !isReadonly2;
    } else if (key === "__v_isReadonly") {
      return isReadonly2;
    } else if (key === "__v_isShallow") {
      return isShallow2;
    } else if (key === "__v_raw") {
      if (receiver === (isReadonly2 ? isShallow2 ? shallowReadonlyMap : readonlyMap : isShallow2 ? shallowReactiveMap : reactiveMap).get(target) || // receiver is not the reactive proxy, but has the same prototype
      // this means the receiver is a user proxy of the reactive proxy
      Object.getPrototypeOf(target) === Object.getPrototypeOf(receiver)) {
        return target;
      }
      return;
    }
    const targetIsArray = isArray(target);
    if (!isReadonly2) {
      let fn;
      if (targetIsArray && (fn = arrayInstrumentations[key])) {
        return fn;
      }
      if (key === "hasOwnProperty") {
        return hasOwnProperty;
      }
    }
    const res = Reflect.get(
      target,
      key,
      // if this is a proxy wrapping a ref, return methods using the raw ref
      // as receiver so that we don't have to call `toRaw` on the ref in all
      // its class methods
      isRef(target) ? target : receiver
    );
    if (isSymbol(key) ? builtInSymbols.has(key) : isNonTrackableKeys(key)) {
      return res;
    }
    if (!isReadonly2) {
      track(target, "get", key);
    }
    if (isShallow2) {
      return res;
    }
    if (isRef(res)) {
      return targetIsArray && isIntegerKey(key) ? res : res.value;
    }
    if (isObject$1(res)) {
      return isReadonly2 ? readonly(res) : reactive(res);
    }
    return res;
  }
}
class MutableReactiveHandler extends BaseReactiveHandler {
  constructor(isShallow2 = false) {
    super(false, isShallow2);
  }
  set(target, key, value, receiver) {
    let oldValue = target[key];
    if (!this._isShallow) {
      const isOldValueReadonly = isReadonly(oldValue);
      if (!isShallow(value) && !isReadonly(value)) {
        oldValue = toRaw(oldValue);
        value = toRaw(value);
      }
      if (!isArray(target) && isRef(oldValue) && !isRef(value)) {
        if (isOldValueReadonly) {
          return false;
        } else {
          oldValue.value = value;
          return true;
        }
      }
    }
    const hadKey = isArray(target) && isIntegerKey(key) ? Number(key) < target.length : hasOwn(target, key);
    const result = Reflect.set(
      target,
      key,
      value,
      isRef(target) ? target : receiver
    );
    if (target === toRaw(receiver)) {
      if (!hadKey) {
        trigger(target, "add", key, value);
      } else if (hasChanged(value, oldValue)) {
        trigger(target, "set", key, value);
      }
    }
    return result;
  }
  deleteProperty(target, key) {
    const hadKey = hasOwn(target, key);
    target[key];
    const result = Reflect.deleteProperty(target, key);
    if (result && hadKey) {
      trigger(target, "delete", key, void 0);
    }
    return result;
  }
  has(target, key) {
    const result = Reflect.has(target, key);
    if (!isSymbol(key) || !builtInSymbols.has(key)) {
      track(target, "has", key);
    }
    return result;
  }
  ownKeys(target) {
    track(
      target,
      "iterate",
      isArray(target) ? "length" : ITERATE_KEY
    );
    return Reflect.ownKeys(target);
  }
}
class ReadonlyReactiveHandler extends BaseReactiveHandler {
  constructor(isShallow2 = false) {
    super(true, isShallow2);
  }
  set(target, key) {
    return true;
  }
  deleteProperty(target, key) {
    return true;
  }
}
const mutableHandlers = /* @__PURE__ */ new MutableReactiveHandler();
const readonlyHandlers = /* @__PURE__ */ new ReadonlyReactiveHandler();
const shallowReactiveHandlers = /* @__PURE__ */ new MutableReactiveHandler(true);
const shallowReadonlyHandlers = /* @__PURE__ */ new ReadonlyReactiveHandler(true);
const toShallow = (value) => value;
const getProto = (v) => Reflect.getPrototypeOf(v);
function createIterableMethod(method, isReadonly2, isShallow2) {
  return function(...args) {
    const target = this["__v_raw"];
    const rawTarget = toRaw(target);
    const targetIsMap = isMap(rawTarget);
    const isPair = method === "entries" || method === Symbol.iterator && targetIsMap;
    const isKeyOnly = method === "keys" && targetIsMap;
    const innerIterator = target[method](...args);
    const wrap2 = isShallow2 ? toShallow : isReadonly2 ? toReadonly : toReactive;
    !isReadonly2 && track(
      rawTarget,
      "iterate",
      isKeyOnly ? MAP_KEY_ITERATE_KEY : ITERATE_KEY
    );
    return {
      // iterator protocol
      next() {
        const { value, done } = innerIterator.next();
        return done ? { value, done } : {
          value: isPair ? [wrap2(value[0]), wrap2(value[1])] : wrap2(value),
          done
        };
      },
      // iterable protocol
      [Symbol.iterator]() {
        return this;
      }
    };
  };
}
function createReadonlyMethod(type) {
  return function(...args) {
    return type === "delete" ? false : type === "clear" ? void 0 : this;
  };
}
function createInstrumentations(readonly2, shallow) {
  const instrumentations = {
    get(key) {
      const target = this["__v_raw"];
      const rawTarget = toRaw(target);
      const rawKey = toRaw(key);
      if (!readonly2) {
        if (hasChanged(key, rawKey)) {
          track(rawTarget, "get", key);
        }
        track(rawTarget, "get", rawKey);
      }
      const { has } = getProto(rawTarget);
      const wrap2 = shallow ? toShallow : readonly2 ? toReadonly : toReactive;
      if (has.call(rawTarget, key)) {
        return wrap2(target.get(key));
      } else if (has.call(rawTarget, rawKey)) {
        return wrap2(target.get(rawKey));
      } else if (target !== rawTarget) {
        target.get(key);
      }
    },
    get size() {
      const target = this["__v_raw"];
      !readonly2 && track(toRaw(target), "iterate", ITERATE_KEY);
      return Reflect.get(target, "size", target);
    },
    has(key) {
      const target = this["__v_raw"];
      const rawTarget = toRaw(target);
      const rawKey = toRaw(key);
      if (!readonly2) {
        if (hasChanged(key, rawKey)) {
          track(rawTarget, "has", key);
        }
        track(rawTarget, "has", rawKey);
      }
      return key === rawKey ? target.has(key) : target.has(key) || target.has(rawKey);
    },
    forEach(callback, thisArg) {
      const observed = this;
      const target = observed["__v_raw"];
      const rawTarget = toRaw(target);
      const wrap2 = shallow ? toShallow : readonly2 ? toReadonly : toReactive;
      !readonly2 && track(rawTarget, "iterate", ITERATE_KEY);
      return target.forEach((value, key) => {
        return callback.call(thisArg, wrap2(value), wrap2(key), observed);
      });
    }
  };
  extend(
    instrumentations,
    readonly2 ? {
      add: createReadonlyMethod("add"),
      set: createReadonlyMethod("set"),
      delete: createReadonlyMethod("delete"),
      clear: createReadonlyMethod("clear")
    } : {
      add(value) {
        if (!shallow && !isShallow(value) && !isReadonly(value)) {
          value = toRaw(value);
        }
        const target = toRaw(this);
        const proto = getProto(target);
        const hadKey = proto.has.call(target, value);
        if (!hadKey) {
          target.add(value);
          trigger(target, "add", value, value);
        }
        return this;
      },
      set(key, value) {
        if (!shallow && !isShallow(value) && !isReadonly(value)) {
          value = toRaw(value);
        }
        const target = toRaw(this);
        const { has, get: get2 } = getProto(target);
        let hadKey = has.call(target, key);
        if (!hadKey) {
          key = toRaw(key);
          hadKey = has.call(target, key);
        }
        const oldValue = get2.call(target, key);
        target.set(key, value);
        if (!hadKey) {
          trigger(target, "add", key, value);
        } else if (hasChanged(value, oldValue)) {
          trigger(target, "set", key, value);
        }
        return this;
      },
      delete(key) {
        const target = toRaw(this);
        const { has, get: get2 } = getProto(target);
        let hadKey = has.call(target, key);
        if (!hadKey) {
          key = toRaw(key);
          hadKey = has.call(target, key);
        }
        get2 ? get2.call(target, key) : void 0;
        const result = target.delete(key);
        if (hadKey) {
          trigger(target, "delete", key, void 0);
        }
        return result;
      },
      clear() {
        const target = toRaw(this);
        const hadItems = target.size !== 0;
        const result = target.clear();
        if (hadItems) {
          trigger(
            target,
            "clear",
            void 0,
            void 0
          );
        }
        return result;
      }
    }
  );
  const iteratorMethods = [
    "keys",
    "values",
    "entries",
    Symbol.iterator
  ];
  iteratorMethods.forEach((method) => {
    instrumentations[method] = createIterableMethod(method, readonly2, shallow);
  });
  return instrumentations;
}
function createInstrumentationGetter(isReadonly2, shallow) {
  const instrumentations = createInstrumentations(isReadonly2, shallow);
  return (target, key, receiver) => {
    if (key === "__v_isReactive") {
      return !isReadonly2;
    } else if (key === "__v_isReadonly") {
      return isReadonly2;
    } else if (key === "__v_raw") {
      return target;
    }
    return Reflect.get(
      hasOwn(instrumentations, key) && key in target ? instrumentations : target,
      key,
      receiver
    );
  };
}
const mutableCollectionHandlers = {
  get: /* @__PURE__ */ createInstrumentationGetter(false, false)
};
const shallowCollectionHandlers = {
  get: /* @__PURE__ */ createInstrumentationGetter(false, true)
};
const readonlyCollectionHandlers = {
  get: /* @__PURE__ */ createInstrumentationGetter(true, false)
};
const shallowReadonlyCollectionHandlers = {
  get: /* @__PURE__ */ createInstrumentationGetter(true, true)
};
const reactiveMap = /* @__PURE__ */ new WeakMap();
const shallowReactiveMap = /* @__PURE__ */ new WeakMap();
const readonlyMap = /* @__PURE__ */ new WeakMap();
const shallowReadonlyMap = /* @__PURE__ */ new WeakMap();
function targetTypeMap(rawType) {
  switch (rawType) {
    case "Object":
    case "Array":
      return 1;
    case "Map":
    case "Set":
    case "WeakMap":
    case "WeakSet":
      return 2;
    default:
      return 0;
  }
}
function getTargetType(value) {
  return value["__v_skip"] || !Object.isExtensible(value) ? 0 : targetTypeMap(toRawType(value));
}
function reactive(target) {
  if (isReadonly(target)) {
    return target;
  }
  return createReactiveObject(
    target,
    false,
    mutableHandlers,
    mutableCollectionHandlers,
    reactiveMap
  );
}
function shallowReactive(target) {
  return createReactiveObject(
    target,
    false,
    shallowReactiveHandlers,
    shallowCollectionHandlers,
    shallowReactiveMap
  );
}
function readonly(target) {
  return createReactiveObject(
    target,
    true,
    readonlyHandlers,
    readonlyCollectionHandlers,
    readonlyMap
  );
}
function shallowReadonly(target) {
  return createReactiveObject(
    target,
    true,
    shallowReadonlyHandlers,
    shallowReadonlyCollectionHandlers,
    shallowReadonlyMap
  );
}
function createReactiveObject(target, isReadonly2, baseHandlers, collectionHandlers, proxyMap) {
  if (!isObject$1(target)) {
    return target;
  }
  if (target["__v_raw"] && !(isReadonly2 && target["__v_isReactive"])) {
    return target;
  }
  const existingProxy = proxyMap.get(target);
  if (existingProxy) {
    return existingProxy;
  }
  const targetType = getTargetType(target);
  if (targetType === 0) {
    return target;
  }
  const proxy = new Proxy(
    target,
    targetType === 2 ? collectionHandlers : baseHandlers
  );
  proxyMap.set(target, proxy);
  return proxy;
}
function isReactive(value) {
  if (isReadonly(value)) {
    return isReactive(value["__v_raw"]);
  }
  return !!(value && value["__v_isReactive"]);
}
function isReadonly(value) {
  return !!(value && value["__v_isReadonly"]);
}
function isShallow(value) {
  return !!(value && value["__v_isShallow"]);
}
function isProxy(value) {
  return value ? !!value["__v_raw"] : false;
}
function toRaw(observed) {
  const raw = observed && observed["__v_raw"];
  return raw ? toRaw(raw) : observed;
}
function markRaw(value) {
  if (!hasOwn(value, "__v_skip") && Object.isExtensible(value)) {
    def(value, "__v_skip", true);
  }
  return value;
}
const toReactive = (value) => isObject$1(value) ? reactive(value) : value;
const toReadonly = (value) => isObject$1(value) ? readonly(value) : value;
function isRef(r) {
  return r ? r["__v_isRef"] === true : false;
}
function unref(ref2) {
  return isRef(ref2) ? ref2.value : ref2;
}
const shallowUnwrapHandlers = {
  get: (target, key, receiver) => key === "__v_raw" ? target : unref(Reflect.get(target, key, receiver)),
  set: (target, key, value, receiver) => {
    const oldValue = target[key];
    if (isRef(oldValue) && !isRef(value)) {
      oldValue.value = value;
      return true;
    } else {
      return Reflect.set(target, key, value, receiver);
    }
  }
};
function proxyRefs(objectWithRefs) {
  return isReactive(objectWithRefs) ? objectWithRefs : new Proxy(objectWithRefs, shallowUnwrapHandlers);
}
class ComputedRefImpl {
  constructor(fn, setter, isSSR) {
    this.fn = fn;
    this.setter = setter;
    this._value = void 0;
    this.dep = new Dep(this);
    this.__v_isRef = true;
    this.deps = void 0;
    this.depsTail = void 0;
    this.flags = 16;
    this.globalVersion = globalVersion - 1;
    this.next = void 0;
    this.effect = this;
    this["__v_isReadonly"] = !setter;
    this.isSSR = isSSR;
  }
  /**
   * @internal
   */
  notify() {
    this.flags |= 16;
    if (!(this.flags & 8) && // avoid infinite self recursion
    activeSub !== this) {
      batch(this, true);
      return true;
    }
  }
  get value() {
    const link = this.dep.track();
    refreshComputed(this);
    if (link) {
      link.version = this.dep.version;
    }
    return this._value;
  }
  set value(newValue) {
    if (this.setter) {
      this.setter(newValue);
    }
  }
}
function computed$1(getterOrOptions, debugOptions, isSSR = false) {
  let getter;
  let setter;
  if (isFunction(getterOrOptions)) {
    getter = getterOrOptions;
  } else {
    getter = getterOrOptions.get;
    setter = getterOrOptions.set;
  }
  const cRef = new ComputedRefImpl(getter, setter, isSSR);
  return cRef;
}
const INITIAL_WATCHER_VALUE = {};
const cleanupMap = /* @__PURE__ */ new WeakMap();
let activeWatcher = void 0;
function onWatcherCleanup(cleanupFn, failSilently = false, owner = activeWatcher) {
  if (owner) {
    let cleanups = cleanupMap.get(owner);
    if (!cleanups) cleanupMap.set(owner, cleanups = []);
    cleanups.push(cleanupFn);
  }
}
function watch$1(source, cb, options = EMPTY_OBJ) {
  const { immediate, deep, once, scheduler, augmentJob, call } = options;
  const reactiveGetter = (source2) => {
    if (deep) return source2;
    if (isShallow(source2) || deep === false || deep === 0)
      return traverse(source2, 1);
    return traverse(source2);
  };
  let effect2;
  let getter;
  let cleanup;
  let boundCleanup;
  let forceTrigger = false;
  let isMultiSource = false;
  if (isRef(source)) {
    getter = () => source.value;
    forceTrigger = isShallow(source);
  } else if (isReactive(source)) {
    getter = () => reactiveGetter(source);
    forceTrigger = true;
  } else if (isArray(source)) {
    isMultiSource = true;
    forceTrigger = source.some((s) => isReactive(s) || isShallow(s));
    getter = () => source.map((s) => {
      if (isRef(s)) {
        return s.value;
      } else if (isReactive(s)) {
        return reactiveGetter(s);
      } else if (isFunction(s)) {
        return call ? call(s, 2) : s();
      } else ;
    });
  } else if (isFunction(source)) {
    if (cb) {
      getter = call ? () => call(source, 2) : source;
    } else {
      getter = () => {
        if (cleanup) {
          pauseTracking();
          try {
            cleanup();
          } finally {
            resetTracking();
          }
        }
        const currentEffect = activeWatcher;
        activeWatcher = effect2;
        try {
          return call ? call(source, 3, [boundCleanup]) : source(boundCleanup);
        } finally {
          activeWatcher = currentEffect;
        }
      };
    }
  } else {
    getter = NOOP;
  }
  if (cb && deep) {
    const baseGetter = getter;
    const depth = deep === true ? Infinity : deep;
    getter = () => traverse(baseGetter(), depth);
  }
  const scope = getCurrentScope();
  const watchHandle = () => {
    effect2.stop();
    if (scope && scope.active) {
      remove(scope.effects, effect2);
    }
  };
  if (once && cb) {
    const _cb = cb;
    cb = (...args) => {
      _cb(...args);
      watchHandle();
    };
  }
  let oldValue = isMultiSource ? new Array(source.length).fill(INITIAL_WATCHER_VALUE) : INITIAL_WATCHER_VALUE;
  const job = (immediateFirstRun) => {
    if (!(effect2.flags & 1) || !effect2.dirty && !immediateFirstRun) {
      return;
    }
    if (cb) {
      const newValue = effect2.run();
      if (deep || forceTrigger || (isMultiSource ? newValue.some((v, i) => hasChanged(v, oldValue[i])) : hasChanged(newValue, oldValue))) {
        if (cleanup) {
          cleanup();
        }
        const currentWatcher = activeWatcher;
        activeWatcher = effect2;
        try {
          const args = [
            newValue,
            // pass undefined as the old value when it's changed for the first time
            oldValue === INITIAL_WATCHER_VALUE ? void 0 : isMultiSource && oldValue[0] === INITIAL_WATCHER_VALUE ? [] : oldValue,
            boundCleanup
          ];
          call ? call(cb, 3, args) : (
            // @ts-expect-error
            cb(...args)
          );
          oldValue = newValue;
        } finally {
          activeWatcher = currentWatcher;
        }
      }
    } else {
      effect2.run();
    }
  };
  if (augmentJob) {
    augmentJob(job);
  }
  effect2 = new ReactiveEffect(getter);
  effect2.scheduler = scheduler ? () => scheduler(job, false) : job;
  boundCleanup = (fn) => onWatcherCleanup(fn, false, effect2);
  cleanup = effect2.onStop = () => {
    const cleanups = cleanupMap.get(effect2);
    if (cleanups) {
      if (call) {
        call(cleanups, 4);
      } else {
        for (const cleanup2 of cleanups) cleanup2();
      }
      cleanupMap.delete(effect2);
    }
  };
  if (cb) {
    if (immediate) {
      job(true);
    } else {
      oldValue = effect2.run();
    }
  } else if (scheduler) {
    scheduler(job.bind(null, true), true);
  } else {
    effect2.run();
  }
  watchHandle.pause = effect2.pause.bind(effect2);
  watchHandle.resume = effect2.resume.bind(effect2);
  watchHandle.stop = watchHandle;
  return watchHandle;
}
function traverse(value, depth = Infinity, seen) {
  if (depth <= 0 || !isObject$1(value) || value["__v_skip"]) {
    return value;
  }
  seen = seen || /* @__PURE__ */ new Set();
  if (seen.has(value)) {
    return value;
  }
  seen.add(value);
  depth--;
  if (isRef(value)) {
    traverse(value.value, depth, seen);
  } else if (isArray(value)) {
    for (let i = 0; i < value.length; i++) {
      traverse(value[i], depth, seen);
    }
  } else if (isSet(value) || isMap(value)) {
    value.forEach((v) => {
      traverse(v, depth, seen);
    });
  } else if (isPlainObject(value)) {
    for (const key in value) {
      traverse(value[key], depth, seen);
    }
    for (const key of Object.getOwnPropertySymbols(value)) {
      if (Object.prototype.propertyIsEnumerable.call(value, key)) {
        traverse(value[key], depth, seen);
      }
    }
  }
  return value;
}
/**
* @vue/runtime-core v3.5.13
* (c) 2018-present Yuxi (Evan) You and Vue contributors
* @license MIT
**/
const stack = [];
let isWarning = false;
function warn$1(msg, ...args) {
  if (isWarning) return;
  isWarning = true;
  pauseTracking();
  const instance = stack.length ? stack[stack.length - 1].component : null;
  const appWarnHandler = instance && instance.appContext.config.warnHandler;
  const trace = getComponentTrace();
  if (appWarnHandler) {
    callWithErrorHandling(
      appWarnHandler,
      instance,
      11,
      [
        // eslint-disable-next-line no-restricted-syntax
        msg + args.map((a) => {
          var _a, _b;
          return (_b = (_a = a.toString) == null ? void 0 : _a.call(a)) != null ? _b : JSON.stringify(a);
        }).join(""),
        instance && instance.proxy,
        trace.map(
          ({ vnode }) => `at <${formatComponentName(instance, vnode.type)}>`
        ).join("\n"),
        trace
      ]
    );
  } else {
    const warnArgs = [`[Vue warn]: ${msg}`, ...args];
    if (trace.length && // avoid spamming console during tests
    true) {
      warnArgs.push(`
`, ...formatTrace(trace));
    }
    console.warn(...warnArgs);
  }
  resetTracking();
  isWarning = false;
}
function getComponentTrace() {
  let currentVNode = stack[stack.length - 1];
  if (!currentVNode) {
    return [];
  }
  const normalizedStack = [];
  while (currentVNode) {
    const last = normalizedStack[0];
    if (last && last.vnode === currentVNode) {
      last.recurseCount++;
    } else {
      normalizedStack.push({
        vnode: currentVNode,
        recurseCount: 0
      });
    }
    const parentInstance = currentVNode.component && currentVNode.component.parent;
    currentVNode = parentInstance && parentInstance.vnode;
  }
  return normalizedStack;
}
function formatTrace(trace) {
  const logs = [];
  trace.forEach((entry, i) => {
    logs.push(...i === 0 ? [] : [`
`], ...formatTraceEntry(entry));
  });
  return logs;
}
function formatTraceEntry({ vnode, recurseCount }) {
  const postfix = recurseCount > 0 ? `... (${recurseCount} recursive calls)` : ``;
  const isRoot = vnode.component ? vnode.component.parent == null : false;
  const open = ` at <${formatComponentName(
    vnode.component,
    vnode.type,
    isRoot
  )}`;
  const close = `>` + postfix;
  return vnode.props ? [open, ...formatProps(vnode.props), close] : [open + close];
}
function formatProps(props) {
  const res = [];
  const keys = Object.keys(props);
  keys.slice(0, 3).forEach((key) => {
    res.push(...formatProp(key, props[key]));
  });
  if (keys.length > 3) {
    res.push(` ...`);
  }
  return res;
}
function formatProp(key, value, raw) {
  if (isString(value)) {
    value = JSON.stringify(value);
    return raw ? value : [`${key}=${value}`];
  } else if (typeof value === "number" || typeof value === "boolean" || value == null) {
    return raw ? value : [`${key}=${value}`];
  } else if (isRef(value)) {
    value = formatProp(key, toRaw(value.value), true);
    return raw ? value : [`${key}=Ref<`, value, `>`];
  } else if (isFunction(value)) {
    return [`${key}=fn${value.name ? `<${value.name}>` : ``}`];
  } else {
    value = toRaw(value);
    return raw ? value : [`${key}=`, value];
  }
}
function callWithErrorHandling(fn, instance, type, args) {
  try {
    return args ? fn(...args) : fn();
  } catch (err) {
    handleError(err, instance, type);
  }
}
function callWithAsyncErrorHandling(fn, instance, type, args) {
  if (isFunction(fn)) {
    const res = callWithErrorHandling(fn, instance, type, args);
    if (res && isPromise$1(res)) {
      res.catch((err) => {
        handleError(err, instance, type);
      });
    }
    return res;
  }
  if (isArray(fn)) {
    const values = [];
    for (let i = 0; i < fn.length; i++) {
      values.push(callWithAsyncErrorHandling(fn[i], instance, type, args));
    }
    return values;
  }
}
function handleError(err, instance, type, throwInDev = true) {
  const contextVNode = instance ? instance.vnode : null;
  const { errorHandler, throwUnhandledErrorInProduction } = instance && instance.appContext.config || EMPTY_OBJ;
  if (instance) {
    let cur = instance.parent;
    const exposedInstance = instance.proxy;
    const errorInfo = `https://vuejs.org/error-reference/#runtime-${type}`;
    while (cur) {
      const errorCapturedHooks = cur.ec;
      if (errorCapturedHooks) {
        for (let i = 0; i < errorCapturedHooks.length; i++) {
          if (errorCapturedHooks[i](err, exposedInstance, errorInfo) === false) {
            return;
          }
        }
      }
      cur = cur.parent;
    }
    if (errorHandler) {
      pauseTracking();
      callWithErrorHandling(errorHandler, null, 10, [
        err,
        exposedInstance,
        errorInfo
      ]);
      resetTracking();
      return;
    }
  }
  logError(err, type, contextVNode, throwInDev, throwUnhandledErrorInProduction);
}
function logError(err, type, contextVNode, throwInDev = true, throwInProd = false) {
  if (throwInProd) {
    throw err;
  } else {
    console.error(err);
  }
}
const queue = [];
let flushIndex = -1;
const pendingPostFlushCbs = [];
let activePostFlushCbs = null;
let postFlushIndex = 0;
const resolvedPromise = /* @__PURE__ */ Promise.resolve();
let currentFlushPromise = null;
function nextTick(fn) {
  const p2 = currentFlushPromise || resolvedPromise;
  return fn ? p2.then(this ? fn.bind(this) : fn) : p2;
}
function findInsertionIndex(id) {
  let start = flushIndex + 1;
  let end = queue.length;
  while (start < end) {
    const middle = start + end >>> 1;
    const middleJob = queue[middle];
    const middleJobId = getId(middleJob);
    if (middleJobId < id || middleJobId === id && middleJob.flags & 2) {
      start = middle + 1;
    } else {
      end = middle;
    }
  }
  return start;
}
function queueJob(job) {
  if (!(job.flags & 1)) {
    const jobId = getId(job);
    const lastJob = queue[queue.length - 1];
    if (!lastJob || // fast path when the job id is larger than the tail
    !(job.flags & 2) && jobId >= getId(lastJob)) {
      queue.push(job);
    } else {
      queue.splice(findInsertionIndex(jobId), 0, job);
    }
    job.flags |= 1;
    queueFlush();
  }
}
function queueFlush() {
  if (!currentFlushPromise) {
    currentFlushPromise = resolvedPromise.then(flushJobs);
  }
}
function queuePostFlushCb(cb) {
  if (!isArray(cb)) {
    if (activePostFlushCbs && cb.id === -1) {
      activePostFlushCbs.splice(postFlushIndex + 1, 0, cb);
    } else if (!(cb.flags & 1)) {
      pendingPostFlushCbs.push(cb);
      cb.flags |= 1;
    }
  } else {
    pendingPostFlushCbs.push(...cb);
  }
  queueFlush();
}
function flushPreFlushCbs(instance, seen, i = flushIndex + 1) {
  for (; i < queue.length; i++) {
    const cb = queue[i];
    if (cb && cb.flags & 2) {
      if (instance && cb.id !== instance.uid) {
        continue;
      }
      queue.splice(i, 1);
      i--;
      if (cb.flags & 4) {
        cb.flags &= -2;
      }
      cb();
      if (!(cb.flags & 4)) {
        cb.flags &= -2;
      }
    }
  }
}
function flushPostFlushCbs(seen) {
  if (pendingPostFlushCbs.length) {
    const deduped = [...new Set(pendingPostFlushCbs)].sort(
      (a, b) => getId(a) - getId(b)
    );
    pendingPostFlushCbs.length = 0;
    if (activePostFlushCbs) {
      activePostFlushCbs.push(...deduped);
      return;
    }
    activePostFlushCbs = deduped;
    for (postFlushIndex = 0; postFlushIndex < activePostFlushCbs.length; postFlushIndex++) {
      const cb = activePostFlushCbs[postFlushIndex];
      if (cb.flags & 4) {
        cb.flags &= -2;
      }
      if (!(cb.flags & 8)) cb();
      cb.flags &= -2;
    }
    activePostFlushCbs = null;
    postFlushIndex = 0;
  }
}
const getId = (job) => job.id == null ? job.flags & 2 ? -1 : Infinity : job.id;
function flushJobs(seen) {
  try {
    for (flushIndex = 0; flushIndex < queue.length; flushIndex++) {
      const job = queue[flushIndex];
      if (job && !(job.flags & 8)) {
        if (false) ;
        if (job.flags & 4) {
          job.flags &= ~1;
        }
        callWithErrorHandling(
          job,
          job.i,
          job.i ? 15 : 14
        );
        if (!(job.flags & 4)) {
          job.flags &= ~1;
        }
      }
    }
  } finally {
    for (; flushIndex < queue.length; flushIndex++) {
      const job = queue[flushIndex];
      if (job) {
        job.flags &= -2;
      }
    }
    flushIndex = -1;
    queue.length = 0;
    flushPostFlushCbs();
    currentFlushPromise = null;
    if (queue.length || pendingPostFlushCbs.length) {
      flushJobs();
    }
  }
}
let currentRenderingInstance = null;
let currentScopeId = null;
function setCurrentRenderingInstance(instance) {
  const prev = currentRenderingInstance;
  currentRenderingInstance = instance;
  currentScopeId = instance && instance.type.__scopeId || null;
  return prev;
}
function withCtx(fn, ctx = currentRenderingInstance, isNonScopedSlot) {
  if (!ctx) return fn;
  if (fn._n) {
    return fn;
  }
  const renderFnWithContext = (...args) => {
    if (renderFnWithContext._d) {
      setBlockTracking(-1);
    }
    const prevInstance = setCurrentRenderingInstance(ctx);
    let res;
    try {
      res = fn(...args);
    } finally {
      setCurrentRenderingInstance(prevInstance);
      if (renderFnWithContext._d) {
        setBlockTracking(1);
      }
    }
    return res;
  };
  renderFnWithContext._n = true;
  renderFnWithContext._c = true;
  renderFnWithContext._d = true;
  return renderFnWithContext;
}
function withDirectives(vnode, directives) {
  if (currentRenderingInstance === null) {
    return vnode;
  }
  const instance = getComponentPublicInstance(currentRenderingInstance);
  const bindings = vnode.dirs || (vnode.dirs = []);
  for (let i = 0; i < directives.length; i++) {
    let [dir, value, arg, modifiers = EMPTY_OBJ] = directives[i];
    if (dir) {
      if (isFunction(dir)) {
        dir = {
          mounted: dir,
          updated: dir
        };
      }
      if (dir.deep) {
        traverse(value);
      }
      bindings.push({
        dir,
        instance,
        value,
        oldValue: void 0,
        arg,
        modifiers
      });
    }
  }
  return vnode;
}
function invokeDirectiveHook(vnode, prevVNode, instance, name) {
  const bindings = vnode.dirs;
  const oldBindings = prevVNode && prevVNode.dirs;
  for (let i = 0; i < bindings.length; i++) {
    const binding = bindings[i];
    if (oldBindings) {
      binding.oldValue = oldBindings[i].value;
    }
    let hook = binding.dir[name];
    if (hook) {
      pauseTracking();
      callWithAsyncErrorHandling(hook, instance, 8, [
        vnode.el,
        binding,
        vnode,
        prevVNode
      ]);
      resetTracking();
    }
  }
}
const TeleportEndKey = Symbol("_vte");
const isTeleport = (type) => type.__isTeleport;
const isTeleportDisabled = (props) => props && (props.disabled || props.disabled === "");
const isTeleportDeferred = (props) => props && (props.defer || props.defer === "");
const isTargetSVG = (target) => typeof SVGElement !== "undefined" && target instanceof SVGElement;
const isTargetMathML = (target) => typeof MathMLElement === "function" && target instanceof MathMLElement;
const resolveTarget = (props, select) => {
  const targetSelector = props && props.to;
  if (isString(targetSelector)) {
    if (!select) {
      return null;
    } else {
      const target = select(targetSelector);
      return target;
    }
  } else {
    return targetSelector;
  }
};
const TeleportImpl = {
  name: "Teleport",
  __isTeleport: true,
  process(n1, n2, container, anchor, parentComponent, parentSuspense, namespace, slotScopeIds, optimized, internals) {
    const {
      mc: mountChildren,
      pc: patchChildren,
      pbc: patchBlockChildren,
      o: { insert, querySelector, createText, createComment }
    } = internals;
    const disabled = isTeleportDisabled(n2.props);
    let { shapeFlag, children, dynamicChildren } = n2;
    if (n1 == null) {
      const placeholder = n2.el = createText("");
      const mainAnchor = n2.anchor = createText("");
      insert(placeholder, container, anchor);
      insert(mainAnchor, container, anchor);
      const mount = (container2, anchor2) => {
        if (shapeFlag & 16) {
          if (parentComponent && parentComponent.isCE) {
            parentComponent.ce._teleportTarget = container2;
          }
          mountChildren(
            children,
            container2,
            anchor2,
            parentComponent,
            parentSuspense,
            namespace,
            slotScopeIds,
            optimized
          );
        }
      };
      const mountToTarget = () => {
        const target = n2.target = resolveTarget(n2.props, querySelector);
        const targetAnchor = prepareAnchor(target, n2, createText, insert);
        if (target) {
          if (namespace !== "svg" && isTargetSVG(target)) {
            namespace = "svg";
          } else if (namespace !== "mathml" && isTargetMathML(target)) {
            namespace = "mathml";
          }
          if (!disabled) {
            mount(target, targetAnchor);
            updateCssVars(n2, false);
          }
        }
      };
      if (disabled) {
        mount(container, mainAnchor);
        updateCssVars(n2, true);
      }
      if (isTeleportDeferred(n2.props)) {
        queuePostRenderEffect(() => {
          mountToTarget();
          n2.el.__isMounted = true;
        }, parentSuspense);
      } else {
        mountToTarget();
      }
    } else {
      if (isTeleportDeferred(n2.props) && !n1.el.__isMounted) {
        queuePostRenderEffect(() => {
          TeleportImpl.process(
            n1,
            n2,
            container,
            anchor,
            parentComponent,
            parentSuspense,
            namespace,
            slotScopeIds,
            optimized,
            internals
          );
          delete n1.el.__isMounted;
        }, parentSuspense);
        return;
      }
      n2.el = n1.el;
      n2.targetStart = n1.targetStart;
      const mainAnchor = n2.anchor = n1.anchor;
      const target = n2.target = n1.target;
      const targetAnchor = n2.targetAnchor = n1.targetAnchor;
      const wasDisabled = isTeleportDisabled(n1.props);
      const currentContainer = wasDisabled ? container : target;
      const currentAnchor = wasDisabled ? mainAnchor : targetAnchor;
      if (namespace === "svg" || isTargetSVG(target)) {
        namespace = "svg";
      } else if (namespace === "mathml" || isTargetMathML(target)) {
        namespace = "mathml";
      }
      if (dynamicChildren) {
        patchBlockChildren(
          n1.dynamicChildren,
          dynamicChildren,
          currentContainer,
          parentComponent,
          parentSuspense,
          namespace,
          slotScopeIds
        );
        traverseStaticChildren(n1, n2, true);
      } else if (!optimized) {
        patchChildren(
          n1,
          n2,
          currentContainer,
          currentAnchor,
          parentComponent,
          parentSuspense,
          namespace,
          slotScopeIds,
          false
        );
      }
      if (disabled) {
        if (!wasDisabled) {
          moveTeleport(
            n2,
            container,
            mainAnchor,
            internals,
            1
          );
        } else {
          if (n2.props && n1.props && n2.props.to !== n1.props.to) {
            n2.props.to = n1.props.to;
          }
        }
      } else {
        if ((n2.props && n2.props.to) !== (n1.props && n1.props.to)) {
          const nextTarget = n2.target = resolveTarget(
            n2.props,
            querySelector
          );
          if (nextTarget) {
            moveTeleport(
              n2,
              nextTarget,
              null,
              internals,
              0
            );
          }
        } else if (wasDisabled) {
          moveTeleport(
            n2,
            target,
            targetAnchor,
            internals,
            1
          );
        }
      }
      updateCssVars(n2, disabled);
    }
  },
  remove(vnode, parentComponent, parentSuspense, { um: unmount, o: { remove: hostRemove } }, doRemove) {
    const {
      shapeFlag,
      children,
      anchor,
      targetStart,
      targetAnchor,
      target,
      props
    } = vnode;
    if (target) {
      hostRemove(targetStart);
      hostRemove(targetAnchor);
    }
    doRemove && hostRemove(anchor);
    if (shapeFlag & 16) {
      const shouldRemove = doRemove || !isTeleportDisabled(props);
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        unmount(
          child,
          parentComponent,
          parentSuspense,
          shouldRemove,
          !!child.dynamicChildren
        );
      }
    }
  },
  move: moveTeleport,
  hydrate: hydrateTeleport
};
function moveTeleport(vnode, container, parentAnchor, { o: { insert }, m: move }, moveType = 2) {
  if (moveType === 0) {
    insert(vnode.targetAnchor, container, parentAnchor);
  }
  const { el, anchor, shapeFlag, children, props } = vnode;
  const isReorder = moveType === 2;
  if (isReorder) {
    insert(el, container, parentAnchor);
  }
  if (!isReorder || isTeleportDisabled(props)) {
    if (shapeFlag & 16) {
      for (let i = 0; i < children.length; i++) {
        move(
          children[i],
          container,
          parentAnchor,
          2
        );
      }
    }
  }
  if (isReorder) {
    insert(anchor, container, parentAnchor);
  }
}
function hydrateTeleport(node, vnode, parentComponent, parentSuspense, slotScopeIds, optimized, {
  o: { nextSibling, parentNode, querySelector, insert, createText }
}, hydrateChildren) {
  const target = vnode.target = resolveTarget(
    vnode.props,
    querySelector
  );
  if (target) {
    const disabled = isTeleportDisabled(vnode.props);
    const targetNode = target._lpa || target.firstChild;
    if (vnode.shapeFlag & 16) {
      if (disabled) {
        vnode.anchor = hydrateChildren(
          nextSibling(node),
          vnode,
          parentNode(node),
          parentComponent,
          parentSuspense,
          slotScopeIds,
          optimized
        );
        vnode.targetStart = targetNode;
        vnode.targetAnchor = targetNode && nextSibling(targetNode);
      } else {
        vnode.anchor = nextSibling(node);
        let targetAnchor = targetNode;
        while (targetAnchor) {
          if (targetAnchor && targetAnchor.nodeType === 8) {
            if (targetAnchor.data === "teleport start anchor") {
              vnode.targetStart = targetAnchor;
            } else if (targetAnchor.data === "teleport anchor") {
              vnode.targetAnchor = targetAnchor;
              target._lpa = vnode.targetAnchor && nextSibling(vnode.targetAnchor);
              break;
            }
          }
          targetAnchor = nextSibling(targetAnchor);
        }
        if (!vnode.targetAnchor) {
          prepareAnchor(target, vnode, createText, insert);
        }
        hydrateChildren(
          targetNode && nextSibling(targetNode),
          vnode,
          target,
          parentComponent,
          parentSuspense,
          slotScopeIds,
          optimized
        );
      }
    }
    updateCssVars(vnode, disabled);
  }
  return vnode.anchor && nextSibling(vnode.anchor);
}
const Teleport = TeleportImpl;
function updateCssVars(vnode, isDisabled) {
  const ctx = vnode.ctx;
  if (ctx && ctx.ut) {
    let node, anchor;
    if (isDisabled) {
      node = vnode.el;
      anchor = vnode.anchor;
    } else {
      node = vnode.targetStart;
      anchor = vnode.targetAnchor;
    }
    while (node && node !== anchor) {
      if (node.nodeType === 1) node.setAttribute("data-v-owner", ctx.uid);
      node = node.nextSibling;
    }
    ctx.ut();
  }
}
function prepareAnchor(target, vnode, createText, insert) {
  const targetStart = vnode.targetStart = createText("");
  const targetAnchor = vnode.targetAnchor = createText("");
  targetStart[TeleportEndKey] = targetAnchor;
  if (target) {
    insert(targetStart, target);
    insert(targetAnchor, target);
  }
  return targetAnchor;
}
function setTransitionHooks(vnode, hooks) {
  if (vnode.shapeFlag & 6 && vnode.component) {
    vnode.transition = hooks;
    setTransitionHooks(vnode.component.subTree, hooks);
  } else if (vnode.shapeFlag & 128) {
    vnode.ssContent.transition = hooks.clone(vnode.ssContent);
    vnode.ssFallback.transition = hooks.clone(vnode.ssFallback);
  } else {
    vnode.transition = hooks;
  }
}
function markAsyncBoundary(instance) {
  instance.ids = [instance.ids[0] + instance.ids[2]++ + "-", 0, 0];
}
function setRef(rawRef, oldRawRef, parentSuspense, vnode, isUnmount = false) {
  if (isArray(rawRef)) {
    rawRef.forEach(
      (r, i) => setRef(
        r,
        oldRawRef && (isArray(oldRawRef) ? oldRawRef[i] : oldRawRef),
        parentSuspense,
        vnode,
        isUnmount
      )
    );
    return;
  }
  if (isAsyncWrapper(vnode) && !isUnmount) {
    if (vnode.shapeFlag & 512 && vnode.type.__asyncResolved && vnode.component.subTree.component) {
      setRef(rawRef, oldRawRef, parentSuspense, vnode.component.subTree);
    }
    return;
  }
  const refValue = vnode.shapeFlag & 4 ? getComponentPublicInstance(vnode.component) : vnode.el;
  const value = isUnmount ? null : refValue;
  const { i: owner, r: ref3 } = rawRef;
  const oldRef = oldRawRef && oldRawRef.r;
  const refs = owner.refs === EMPTY_OBJ ? owner.refs = {} : owner.refs;
  const setupState = owner.setupState;
  const rawSetupState = toRaw(setupState);
  const canSetSetupRef = setupState === EMPTY_OBJ ? () => false : (key) => {
    return hasOwn(rawSetupState, key);
  };
  if (oldRef != null && oldRef !== ref3) {
    if (isString(oldRef)) {
      refs[oldRef] = null;
      if (canSetSetupRef(oldRef)) {
        setupState[oldRef] = null;
      }
    } else if (isRef(oldRef)) {
      oldRef.value = null;
    }
  }
  if (isFunction(ref3)) {
    callWithErrorHandling(ref3, owner, 12, [value, refs]);
  } else {
    const _isString = isString(ref3);
    const _isRef = isRef(ref3);
    if (_isString || _isRef) {
      const doSet = () => {
        if (rawRef.f) {
          const existing = _isString ? canSetSetupRef(ref3) ? setupState[ref3] : refs[ref3] : ref3.value;
          if (isUnmount) {
            isArray(existing) && remove(existing, refValue);
          } else {
            if (!isArray(existing)) {
              if (_isString) {
                refs[ref3] = [refValue];
                if (canSetSetupRef(ref3)) {
                  setupState[ref3] = refs[ref3];
                }
              } else {
                ref3.value = [refValue];
                if (rawRef.k) refs[rawRef.k] = ref3.value;
              }
            } else if (!existing.includes(refValue)) {
              existing.push(refValue);
            }
          }
        } else if (_isString) {
          refs[ref3] = value;
          if (canSetSetupRef(ref3)) {
            setupState[ref3] = value;
          }
        } else if (_isRef) {
          ref3.value = value;
          if (rawRef.k) refs[rawRef.k] = value;
        } else ;
      };
      if (value) {
        doSet.id = -1;
        queuePostRenderEffect(doSet, parentSuspense);
      } else {
        doSet();
      }
    }
  }
}
getGlobalThis().requestIdleCallback || ((cb) => setTimeout(cb, 1));
getGlobalThis().cancelIdleCallback || ((id) => clearTimeout(id));
const isAsyncWrapper = (i) => !!i.type.__asyncLoader;
const isKeepAlive = (vnode) => vnode.type.__isKeepAlive;
function onActivated(hook, target) {
  registerKeepAliveHook(hook, "a", target);
}
function onDeactivated(hook, target) {
  registerKeepAliveHook(hook, "da", target);
}
function registerKeepAliveHook(hook, type, target = currentInstance) {
  const wrappedHook = hook.__wdc || (hook.__wdc = () => {
    let current = target;
    while (current) {
      if (current.isDeactivated) {
        return;
      }
      current = current.parent;
    }
    return hook();
  });
  injectHook(type, wrappedHook, target);
  if (target) {
    let current = target.parent;
    while (current && current.parent) {
      if (isKeepAlive(current.parent.vnode)) {
        injectToKeepAliveRoot(wrappedHook, type, target, current);
      }
      current = current.parent;
    }
  }
}
function injectToKeepAliveRoot(hook, type, target, keepAliveRoot) {
  const injected = injectHook(
    type,
    hook,
    keepAliveRoot,
    true
    /* prepend */
  );
  onUnmounted(() => {
    remove(keepAliveRoot[type], injected);
  }, target);
}
function injectHook(type, hook, target = currentInstance, prepend = false) {
  if (target) {
    const hooks = target[type] || (target[type] = []);
    const wrappedHook = hook.__weh || (hook.__weh = (...args) => {
      pauseTracking();
      const reset = setCurrentInstance(target);
      const res = callWithAsyncErrorHandling(hook, target, type, args);
      reset();
      resetTracking();
      return res;
    });
    if (prepend) {
      hooks.unshift(wrappedHook);
    } else {
      hooks.push(wrappedHook);
    }
    return wrappedHook;
  }
}
const createHook = (lifecycle) => (hook, target = currentInstance) => {
  if (!isInSSRComponentSetup || lifecycle === "sp") {
    injectHook(lifecycle, (...args) => hook(...args), target);
  }
};
const onBeforeMount = createHook("bm");
const onMounted = createHook("m");
const onBeforeUpdate = createHook(
  "bu"
);
const onUpdated = createHook("u");
const onBeforeUnmount = createHook(
  "bum"
);
const onUnmounted = createHook("um");
const onServerPrefetch = createHook(
  "sp"
);
const onRenderTriggered = createHook("rtg");
const onRenderTracked = createHook("rtc");
function onErrorCaptured(hook, target = currentInstance) {
  injectHook("ec", hook, target);
}
const COMPONENTS = "components";
function resolveComponent(name, maybeSelfReference) {
  return resolveAsset(COMPONENTS, name, true, maybeSelfReference) || name;
}
const NULL_DYNAMIC_COMPONENT = Symbol.for("v-ndc");
function resolveAsset(type, name, warnMissing = true, maybeSelfReference = false) {
  const instance = currentRenderingInstance || currentInstance;
  if (instance) {
    const Component = instance.type;
    {
      const selfName = getComponentName(
        Component,
        false
      );
      if (selfName && (selfName === name || selfName === camelize(name) || selfName === capitalize(camelize(name)))) {
        return Component;
      }
    }
    const res = (
      // local registration
      // check instance[type] first which is resolved for options API
      resolve(instance[type] || Component[type], name) || // global registration
      resolve(instance.appContext[type], name)
    );
    if (!res && maybeSelfReference) {
      return Component;
    }
    return res;
  }
}
function resolve(registry, name) {
  return registry && (registry[name] || registry[camelize(name)] || registry[capitalize(camelize(name))]);
}
function renderList(source, renderItem, cache, index) {
  let ret;
  const cached = cache;
  const sourceIsArray = isArray(source);
  if (sourceIsArray || isString(source)) {
    const sourceIsReactiveArray = sourceIsArray && isReactive(source);
    let needsWrap = false;
    if (sourceIsReactiveArray) {
      needsWrap = !isShallow(source);
      source = shallowReadArray(source);
    }
    ret = new Array(source.length);
    for (let i = 0, l = source.length; i < l; i++) {
      ret[i] = renderItem(
        needsWrap ? toReactive(source[i]) : source[i],
        i,
        void 0,
        cached
      );
    }
  } else if (typeof source === "number") {
    ret = new Array(source);
    for (let i = 0; i < source; i++) {
      ret[i] = renderItem(i + 1, i, void 0, cached);
    }
  } else if (isObject$1(source)) {
    if (source[Symbol.iterator]) {
      ret = Array.from(
        source,
        (item, i) => renderItem(item, i, void 0, cached)
      );
    } else {
      const keys = Object.keys(source);
      ret = new Array(keys.length);
      for (let i = 0, l = keys.length; i < l; i++) {
        const key = keys[i];
        ret[i] = renderItem(source[key], key, i, cached);
      }
    }
  } else {
    ret = [];
  }
  return ret;
}
const getPublicInstance = (i) => {
  if (!i) return null;
  if (isStatefulComponent(i)) return getComponentPublicInstance(i);
  return getPublicInstance(i.parent);
};
const publicPropertiesMap = (
  // Move PURE marker to new line to workaround compiler discarding it
  // due to type annotation
  /* @__PURE__ */ extend(/* @__PURE__ */ Object.create(null), {
    $: (i) => i,
    $el: (i) => i.vnode.el,
    $data: (i) => i.data,
    $props: (i) => i.props,
    $attrs: (i) => i.attrs,
    $slots: (i) => i.slots,
    $refs: (i) => i.refs,
    $parent: (i) => getPublicInstance(i.parent),
    $root: (i) => getPublicInstance(i.root),
    $host: (i) => i.ce,
    $emit: (i) => i.emit,
    $options: (i) => resolveMergedOptions(i),
    $forceUpdate: (i) => i.f || (i.f = () => {
      queueJob(i.update);
    }),
    $nextTick: (i) => i.n || (i.n = nextTick.bind(i.proxy)),
    $watch: (i) => instanceWatch.bind(i)
  })
);
const hasSetupBinding = (state, key) => state !== EMPTY_OBJ && !state.__isScriptSetup && hasOwn(state, key);
const PublicInstanceProxyHandlers = {
  get({ _: instance }, key) {
    if (key === "__v_skip") {
      return true;
    }
    const { ctx, setupState, data, props, accessCache, type, appContext } = instance;
    let normalizedProps;
    if (key[0] !== "$") {
      const n = accessCache[key];
      if (n !== void 0) {
        switch (n) {
          case 1:
            return setupState[key];
          case 2:
            return data[key];
          case 4:
            return ctx[key];
          case 3:
            return props[key];
        }
      } else if (hasSetupBinding(setupState, key)) {
        accessCache[key] = 1;
        return setupState[key];
      } else if (data !== EMPTY_OBJ && hasOwn(data, key)) {
        accessCache[key] = 2;
        return data[key];
      } else if (
        // only cache other properties when instance has declared (thus stable)
        // props
        (normalizedProps = instance.propsOptions[0]) && hasOwn(normalizedProps, key)
      ) {
        accessCache[key] = 3;
        return props[key];
      } else if (ctx !== EMPTY_OBJ && hasOwn(ctx, key)) {
        accessCache[key] = 4;
        return ctx[key];
      } else if (shouldCacheAccess) {
        accessCache[key] = 0;
      }
    }
    const publicGetter = publicPropertiesMap[key];
    let cssModule, globalProperties;
    if (publicGetter) {
      if (key === "$attrs") {
        track(instance.attrs, "get", "");
      }
      return publicGetter(instance);
    } else if (
      // css module (injected by vue-loader)
      (cssModule = type.__cssModules) && (cssModule = cssModule[key])
    ) {
      return cssModule;
    } else if (ctx !== EMPTY_OBJ && hasOwn(ctx, key)) {
      accessCache[key] = 4;
      return ctx[key];
    } else if (
      // global properties
      globalProperties = appContext.config.globalProperties, hasOwn(globalProperties, key)
    ) {
      {
        return globalProperties[key];
      }
    } else ;
  },
  set({ _: instance }, key, value) {
    const { data, setupState, ctx } = instance;
    if (hasSetupBinding(setupState, key)) {
      setupState[key] = value;
      return true;
    } else if (data !== EMPTY_OBJ && hasOwn(data, key)) {
      data[key] = value;
      return true;
    } else if (hasOwn(instance.props, key)) {
      return false;
    }
    if (key[0] === "$" && key.slice(1) in instance) {
      return false;
    } else {
      {
        ctx[key] = value;
      }
    }
    return true;
  },
  has({
    _: { data, setupState, accessCache, ctx, appContext, propsOptions }
  }, key) {
    let normalizedProps;
    return !!accessCache[key] || data !== EMPTY_OBJ && hasOwn(data, key) || hasSetupBinding(setupState, key) || (normalizedProps = propsOptions[0]) && hasOwn(normalizedProps, key) || hasOwn(ctx, key) || hasOwn(publicPropertiesMap, key) || hasOwn(appContext.config.globalProperties, key);
  },
  defineProperty(target, key, descriptor) {
    if (descriptor.get != null) {
      target._.accessCache[key] = 0;
    } else if (hasOwn(descriptor, "value")) {
      this.set(target, key, descriptor.value, null);
    }
    return Reflect.defineProperty(target, key, descriptor);
  }
};
function normalizePropsOrEmits(props) {
  return isArray(props) ? props.reduce(
    (normalized, p2) => (normalized[p2] = null, normalized),
    {}
  ) : props;
}
let shouldCacheAccess = true;
function applyOptions(instance) {
  const options = resolveMergedOptions(instance);
  const publicThis = instance.proxy;
  const ctx = instance.ctx;
  shouldCacheAccess = false;
  if (options.beforeCreate) {
    callHook(options.beforeCreate, instance, "bc");
  }
  const {
    // state
    data: dataOptions,
    computed: computedOptions,
    methods,
    watch: watchOptions,
    provide: provideOptions,
    inject: injectOptions,
    // lifecycle
    created,
    beforeMount,
    mounted,
    beforeUpdate,
    updated,
    activated,
    deactivated,
    beforeDestroy,
    beforeUnmount,
    destroyed,
    unmounted,
    render,
    renderTracked,
    renderTriggered,
    errorCaptured,
    serverPrefetch,
    // public API
    expose,
    inheritAttrs,
    // assets
    components,
    directives,
    filters
  } = options;
  const checkDuplicateProperties = null;
  if (injectOptions) {
    resolveInjections(injectOptions, ctx, checkDuplicateProperties);
  }
  if (methods) {
    for (const key in methods) {
      const methodHandler = methods[key];
      if (isFunction(methodHandler)) {
        {
          ctx[key] = methodHandler.bind(publicThis);
        }
      }
    }
  }
  if (dataOptions) {
    const data = dataOptions.call(publicThis, publicThis);
    if (!isObject$1(data)) ;
    else {
      instance.data = reactive(data);
    }
  }
  shouldCacheAccess = true;
  if (computedOptions) {
    for (const key in computedOptions) {
      const opt = computedOptions[key];
      const get2 = isFunction(opt) ? opt.bind(publicThis, publicThis) : isFunction(opt.get) ? opt.get.bind(publicThis, publicThis) : NOOP;
      const set = !isFunction(opt) && isFunction(opt.set) ? opt.set.bind(publicThis) : NOOP;
      const c = computed({
        get: get2,
        set
      });
      Object.defineProperty(ctx, key, {
        enumerable: true,
        configurable: true,
        get: () => c.value,
        set: (v) => c.value = v
      });
    }
  }
  if (watchOptions) {
    for (const key in watchOptions) {
      createWatcher(watchOptions[key], ctx, publicThis, key);
    }
  }
  if (provideOptions) {
    const provides = isFunction(provideOptions) ? provideOptions.call(publicThis) : provideOptions;
    Reflect.ownKeys(provides).forEach((key) => {
      provide(key, provides[key]);
    });
  }
  if (created) {
    callHook(created, instance, "c");
  }
  function registerLifecycleHook(register2, hook) {
    if (isArray(hook)) {
      hook.forEach((_hook) => register2(_hook.bind(publicThis)));
    } else if (hook) {
      register2(hook.bind(publicThis));
    }
  }
  registerLifecycleHook(onBeforeMount, beforeMount);
  registerLifecycleHook(onMounted, mounted);
  registerLifecycleHook(onBeforeUpdate, beforeUpdate);
  registerLifecycleHook(onUpdated, updated);
  registerLifecycleHook(onActivated, activated);
  registerLifecycleHook(onDeactivated, deactivated);
  registerLifecycleHook(onErrorCaptured, errorCaptured);
  registerLifecycleHook(onRenderTracked, renderTracked);
  registerLifecycleHook(onRenderTriggered, renderTriggered);
  registerLifecycleHook(onBeforeUnmount, beforeUnmount);
  registerLifecycleHook(onUnmounted, unmounted);
  registerLifecycleHook(onServerPrefetch, serverPrefetch);
  if (isArray(expose)) {
    if (expose.length) {
      const exposed = instance.exposed || (instance.exposed = {});
      expose.forEach((key) => {
        Object.defineProperty(exposed, key, {
          get: () => publicThis[key],
          set: (val) => publicThis[key] = val
        });
      });
    } else if (!instance.exposed) {
      instance.exposed = {};
    }
  }
  if (render && instance.render === NOOP) {
    instance.render = render;
  }
  if (inheritAttrs != null) {
    instance.inheritAttrs = inheritAttrs;
  }
  if (components) instance.components = components;
  if (directives) instance.directives = directives;
  if (serverPrefetch) {
    markAsyncBoundary(instance);
  }
}
function resolveInjections(injectOptions, ctx, checkDuplicateProperties = NOOP) {
  if (isArray(injectOptions)) {
    injectOptions = normalizeInject(injectOptions);
  }
  for (const key in injectOptions) {
    const opt = injectOptions[key];
    let injected;
    if (isObject$1(opt)) {
      if ("default" in opt) {
        injected = inject(
          opt.from || key,
          opt.default,
          true
        );
      } else {
        injected = inject(opt.from || key);
      }
    } else {
      injected = inject(opt);
    }
    if (isRef(injected)) {
      Object.defineProperty(ctx, key, {
        enumerable: true,
        configurable: true,
        get: () => injected.value,
        set: (v) => injected.value = v
      });
    } else {
      ctx[key] = injected;
    }
  }
}
function callHook(hook, instance, type) {
  callWithAsyncErrorHandling(
    isArray(hook) ? hook.map((h2) => h2.bind(instance.proxy)) : hook.bind(instance.proxy),
    instance,
    type
  );
}
function createWatcher(raw, ctx, publicThis, key) {
  let getter = key.includes(".") ? createPathGetter(publicThis, key) : () => publicThis[key];
  if (isString(raw)) {
    const handler = ctx[raw];
    if (isFunction(handler)) {
      {
        watch(getter, handler);
      }
    }
  } else if (isFunction(raw)) {
    {
      watch(getter, raw.bind(publicThis));
    }
  } else if (isObject$1(raw)) {
    if (isArray(raw)) {
      raw.forEach((r) => createWatcher(r, ctx, publicThis, key));
    } else {
      const handler = isFunction(raw.handler) ? raw.handler.bind(publicThis) : ctx[raw.handler];
      if (isFunction(handler)) {
        watch(getter, handler, raw);
      }
    }
  } else ;
}
function resolveMergedOptions(instance) {
  const base = instance.type;
  const { mixins, extends: extendsOptions } = base;
  const {
    mixins: globalMixins,
    optionsCache: cache,
    config: { optionMergeStrategies }
  } = instance.appContext;
  const cached = cache.get(base);
  let resolved;
  if (cached) {
    resolved = cached;
  } else if (!globalMixins.length && !mixins && !extendsOptions) {
    {
      resolved = base;
    }
  } else {
    resolved = {};
    if (globalMixins.length) {
      globalMixins.forEach(
        (m) => mergeOptions(resolved, m, optionMergeStrategies, true)
      );
    }
    mergeOptions(resolved, base, optionMergeStrategies);
  }
  if (isObject$1(base)) {
    cache.set(base, resolved);
  }
  return resolved;
}
function mergeOptions(to, from, strats, asMixin = false) {
  const { mixins, extends: extendsOptions } = from;
  if (extendsOptions) {
    mergeOptions(to, extendsOptions, strats, true);
  }
  if (mixins) {
    mixins.forEach(
      (m) => mergeOptions(to, m, strats, true)
    );
  }
  for (const key in from) {
    if (asMixin && key === "expose") ;
    else {
      const strat = internalOptionMergeStrats[key] || strats && strats[key];
      to[key] = strat ? strat(to[key], from[key]) : from[key];
    }
  }
  return to;
}
const internalOptionMergeStrats = {
  data: mergeDataFn,
  props: mergeEmitsOrPropsOptions,
  emits: mergeEmitsOrPropsOptions,
  // objects
  methods: mergeObjectOptions,
  computed: mergeObjectOptions,
  // lifecycle
  beforeCreate: mergeAsArray,
  created: mergeAsArray,
  beforeMount: mergeAsArray,
  mounted: mergeAsArray,
  beforeUpdate: mergeAsArray,
  updated: mergeAsArray,
  beforeDestroy: mergeAsArray,
  beforeUnmount: mergeAsArray,
  destroyed: mergeAsArray,
  unmounted: mergeAsArray,
  activated: mergeAsArray,
  deactivated: mergeAsArray,
  errorCaptured: mergeAsArray,
  serverPrefetch: mergeAsArray,
  // assets
  components: mergeObjectOptions,
  directives: mergeObjectOptions,
  // watch
  watch: mergeWatchOptions,
  // provide / inject
  provide: mergeDataFn,
  inject: mergeInject
};
function mergeDataFn(to, from) {
  if (!from) {
    return to;
  }
  if (!to) {
    return from;
  }
  return function mergedDataFn() {
    return extend(
      isFunction(to) ? to.call(this, this) : to,
      isFunction(from) ? from.call(this, this) : from
    );
  };
}
function mergeInject(to, from) {
  return mergeObjectOptions(normalizeInject(to), normalizeInject(from));
}
function normalizeInject(raw) {
  if (isArray(raw)) {
    const res = {};
    for (let i = 0; i < raw.length; i++) {
      res[raw[i]] = raw[i];
    }
    return res;
  }
  return raw;
}
function mergeAsArray(to, from) {
  return to ? [...new Set([].concat(to, from))] : from;
}
function mergeObjectOptions(to, from) {
  return to ? extend(/* @__PURE__ */ Object.create(null), to, from) : from;
}
function mergeEmitsOrPropsOptions(to, from) {
  if (to) {
    if (isArray(to) && isArray(from)) {
      return [.../* @__PURE__ */ new Set([...to, ...from])];
    }
    return extend(
      /* @__PURE__ */ Object.create(null),
      normalizePropsOrEmits(to),
      normalizePropsOrEmits(from != null ? from : {})
    );
  } else {
    return from;
  }
}
function mergeWatchOptions(to, from) {
  if (!to) return from;
  if (!from) return to;
  const merged = extend(/* @__PURE__ */ Object.create(null), to);
  for (const key in from) {
    merged[key] = mergeAsArray(to[key], from[key]);
  }
  return merged;
}
function createAppContext() {
  return {
    app: null,
    config: {
      isNativeTag: NO,
      performance: false,
      globalProperties: {},
      optionMergeStrategies: {},
      errorHandler: void 0,
      warnHandler: void 0,
      compilerOptions: {}
    },
    mixins: [],
    components: {},
    directives: {},
    provides: /* @__PURE__ */ Object.create(null),
    optionsCache: /* @__PURE__ */ new WeakMap(),
    propsCache: /* @__PURE__ */ new WeakMap(),
    emitsCache: /* @__PURE__ */ new WeakMap()
  };
}
let uid$1 = 0;
function createAppAPI(render, hydrate) {
  return function createApp2(rootComponent, rootProps = null) {
    if (!isFunction(rootComponent)) {
      rootComponent = extend({}, rootComponent);
    }
    if (rootProps != null && !isObject$1(rootProps)) {
      rootProps = null;
    }
    const context = createAppContext();
    const installedPlugins = /* @__PURE__ */ new WeakSet();
    const pluginCleanupFns = [];
    let isMounted = false;
    const app = context.app = {
      _uid: uid$1++,
      _component: rootComponent,
      _props: rootProps,
      _container: null,
      _context: context,
      _instance: null,
      version,
      get config() {
        return context.config;
      },
      set config(v) {
      },
      use(plugin, ...options) {
        if (installedPlugins.has(plugin)) ;
        else if (plugin && isFunction(plugin.install)) {
          installedPlugins.add(plugin);
          plugin.install(app, ...options);
        } else if (isFunction(plugin)) {
          installedPlugins.add(plugin);
          plugin(app, ...options);
        } else ;
        return app;
      },
      mixin(mixin) {
        {
          if (!context.mixins.includes(mixin)) {
            context.mixins.push(mixin);
          }
        }
        return app;
      },
      component(name, component) {
        if (!component) {
          return context.components[name];
        }
        context.components[name] = component;
        return app;
      },
      directive(name, directive) {
        if (!directive) {
          return context.directives[name];
        }
        context.directives[name] = directive;
        return app;
      },
      mount(rootContainer, isHydrate, namespace) {
        if (!isMounted) {
          const vnode = app._ceVNode || createVNode(rootComponent, rootProps);
          vnode.appContext = context;
          if (namespace === true) {
            namespace = "svg";
          } else if (namespace === false) {
            namespace = void 0;
          }
          {
            render(vnode, rootContainer, namespace);
          }
          isMounted = true;
          app._container = rootContainer;
          rootContainer.__vue_app__ = app;
          return getComponentPublicInstance(vnode.component);
        }
      },
      onUnmount(cleanupFn) {
        pluginCleanupFns.push(cleanupFn);
      },
      unmount() {
        if (isMounted) {
          callWithAsyncErrorHandling(
            pluginCleanupFns,
            app._instance,
            16
          );
          render(null, app._container);
          delete app._container.__vue_app__;
        }
      },
      provide(key, value) {
        context.provides[key] = value;
        return app;
      },
      runWithContext(fn) {
        const lastApp = currentApp;
        currentApp = app;
        try {
          return fn();
        } finally {
          currentApp = lastApp;
        }
      }
    };
    return app;
  };
}
let currentApp = null;
function provide(key, value) {
  if (!currentInstance) ;
  else {
    let provides = currentInstance.provides;
    const parentProvides = currentInstance.parent && currentInstance.parent.provides;
    if (parentProvides === provides) {
      provides = currentInstance.provides = Object.create(parentProvides);
    }
    provides[key] = value;
  }
}
function inject(key, defaultValue, treatDefaultAsFactory = false) {
  const instance = currentInstance || currentRenderingInstance;
  if (instance || currentApp) {
    const provides = currentApp ? currentApp._context.provides : instance ? instance.parent == null ? instance.vnode.appContext && instance.vnode.appContext.provides : instance.parent.provides : void 0;
    if (provides && key in provides) {
      return provides[key];
    } else if (arguments.length > 1) {
      return treatDefaultAsFactory && isFunction(defaultValue) ? defaultValue.call(instance && instance.proxy) : defaultValue;
    } else ;
  }
}
const internalObjectProto = {};
const createInternalObject = () => Object.create(internalObjectProto);
const isInternalObject = (obj) => Object.getPrototypeOf(obj) === internalObjectProto;
function initProps(instance, rawProps, isStateful, isSSR = false) {
  const props = {};
  const attrs = createInternalObject();
  instance.propsDefaults = /* @__PURE__ */ Object.create(null);
  setFullProps(instance, rawProps, props, attrs);
  for (const key in instance.propsOptions[0]) {
    if (!(key in props)) {
      props[key] = void 0;
    }
  }
  if (isStateful) {
    instance.props = isSSR ? props : shallowReactive(props);
  } else {
    if (!instance.type.props) {
      instance.props = attrs;
    } else {
      instance.props = props;
    }
  }
  instance.attrs = attrs;
}
function updateProps(instance, rawProps, rawPrevProps, optimized) {
  const {
    props,
    attrs,
    vnode: { patchFlag }
  } = instance;
  const rawCurrentProps = toRaw(props);
  const [options] = instance.propsOptions;
  let hasAttrsChanged = false;
  if (
    // always force full diff in dev
    // - #1942 if hmr is enabled with sfc component
    // - vite#872 non-sfc component used by sfc component
    (optimized || patchFlag > 0) && !(patchFlag & 16)
  ) {
    if (patchFlag & 8) {
      const propsToUpdate = instance.vnode.dynamicProps;
      for (let i = 0; i < propsToUpdate.length; i++) {
        let key = propsToUpdate[i];
        if (isEmitListener(instance.emitsOptions, key)) {
          continue;
        }
        const value = rawProps[key];
        if (options) {
          if (hasOwn(attrs, key)) {
            if (value !== attrs[key]) {
              attrs[key] = value;
              hasAttrsChanged = true;
            }
          } else {
            const camelizedKey = camelize(key);
            props[camelizedKey] = resolvePropValue(
              options,
              rawCurrentProps,
              camelizedKey,
              value,
              instance,
              false
            );
          }
        } else {
          if (value !== attrs[key]) {
            attrs[key] = value;
            hasAttrsChanged = true;
          }
        }
      }
    }
  } else {
    if (setFullProps(instance, rawProps, props, attrs)) {
      hasAttrsChanged = true;
    }
    let kebabKey;
    for (const key in rawCurrentProps) {
      if (!rawProps || // for camelCase
      !hasOwn(rawProps, key) && // it's possible the original props was passed in as kebab-case
      // and converted to camelCase (#955)
      ((kebabKey = hyphenate(key)) === key || !hasOwn(rawProps, kebabKey))) {
        if (options) {
          if (rawPrevProps && // for camelCase
          (rawPrevProps[key] !== void 0 || // for kebab-case
          rawPrevProps[kebabKey] !== void 0)) {
            props[key] = resolvePropValue(
              options,
              rawCurrentProps,
              key,
              void 0,
              instance,
              true
            );
          }
        } else {
          delete props[key];
        }
      }
    }
    if (attrs !== rawCurrentProps) {
      for (const key in attrs) {
        if (!rawProps || !hasOwn(rawProps, key) && true) {
          delete attrs[key];
          hasAttrsChanged = true;
        }
      }
    }
  }
  if (hasAttrsChanged) {
    trigger(instance.attrs, "set", "");
  }
}
function setFullProps(instance, rawProps, props, attrs) {
  const [options, needCastKeys] = instance.propsOptions;
  let hasAttrsChanged = false;
  let rawCastValues;
  if (rawProps) {
    for (let key in rawProps) {
      if (isReservedProp(key)) {
        continue;
      }
      const value = rawProps[key];
      let camelKey;
      if (options && hasOwn(options, camelKey = camelize(key))) {
        if (!needCastKeys || !needCastKeys.includes(camelKey)) {
          props[camelKey] = value;
        } else {
          (rawCastValues || (rawCastValues = {}))[camelKey] = value;
        }
      } else if (!isEmitListener(instance.emitsOptions, key)) {
        if (!(key in attrs) || value !== attrs[key]) {
          attrs[key] = value;
          hasAttrsChanged = true;
        }
      }
    }
  }
  if (needCastKeys) {
    const rawCurrentProps = toRaw(props);
    const castValues = rawCastValues || EMPTY_OBJ;
    for (let i = 0; i < needCastKeys.length; i++) {
      const key = needCastKeys[i];
      props[key] = resolvePropValue(
        options,
        rawCurrentProps,
        key,
        castValues[key],
        instance,
        !hasOwn(castValues, key)
      );
    }
  }
  return hasAttrsChanged;
}
function resolvePropValue(options, props, key, value, instance, isAbsent) {
  const opt = options[key];
  if (opt != null) {
    const hasDefault = hasOwn(opt, "default");
    if (hasDefault && value === void 0) {
      const defaultValue = opt.default;
      if (opt.type !== Function && !opt.skipFactory && isFunction(defaultValue)) {
        const { propsDefaults } = instance;
        if (key in propsDefaults) {
          value = propsDefaults[key];
        } else {
          const reset = setCurrentInstance(instance);
          value = propsDefaults[key] = defaultValue.call(
            null,
            props
          );
          reset();
        }
      } else {
        value = defaultValue;
      }
      if (instance.ce) {
        instance.ce._setProp(key, value);
      }
    }
    if (opt[
      0
      /* shouldCast */
    ]) {
      if (isAbsent && !hasDefault) {
        value = false;
      } else if (opt[
        1
        /* shouldCastTrue */
      ] && (value === "" || value === hyphenate(key))) {
        value = true;
      }
    }
  }
  return value;
}
const mixinPropsCache = /* @__PURE__ */ new WeakMap();
function normalizePropsOptions(comp, appContext, asMixin = false) {
  const cache = asMixin ? mixinPropsCache : appContext.propsCache;
  const cached = cache.get(comp);
  if (cached) {
    return cached;
  }
  const raw = comp.props;
  const normalized = {};
  const needCastKeys = [];
  let hasExtends = false;
  if (!isFunction(comp)) {
    const extendProps = (raw2) => {
      hasExtends = true;
      const [props, keys] = normalizePropsOptions(raw2, appContext, true);
      extend(normalized, props);
      if (keys) needCastKeys.push(...keys);
    };
    if (!asMixin && appContext.mixins.length) {
      appContext.mixins.forEach(extendProps);
    }
    if (comp.extends) {
      extendProps(comp.extends);
    }
    if (comp.mixins) {
      comp.mixins.forEach(extendProps);
    }
  }
  if (!raw && !hasExtends) {
    if (isObject$1(comp)) {
      cache.set(comp, EMPTY_ARR);
    }
    return EMPTY_ARR;
  }
  if (isArray(raw)) {
    for (let i = 0; i < raw.length; i++) {
      const normalizedKey = camelize(raw[i]);
      if (validatePropName(normalizedKey)) {
        normalized[normalizedKey] = EMPTY_OBJ;
      }
    }
  } else if (raw) {
    for (const key in raw) {
      const normalizedKey = camelize(key);
      if (validatePropName(normalizedKey)) {
        const opt = raw[key];
        const prop = normalized[normalizedKey] = isArray(opt) || isFunction(opt) ? { type: opt } : extend({}, opt);
        const propType = prop.type;
        let shouldCast = false;
        let shouldCastTrue = true;
        if (isArray(propType)) {
          for (let index = 0; index < propType.length; ++index) {
            const type = propType[index];
            const typeName = isFunction(type) && type.name;
            if (typeName === "Boolean") {
              shouldCast = true;
              break;
            } else if (typeName === "String") {
              shouldCastTrue = false;
            }
          }
        } else {
          shouldCast = isFunction(propType) && propType.name === "Boolean";
        }
        prop[
          0
          /* shouldCast */
        ] = shouldCast;
        prop[
          1
          /* shouldCastTrue */
        ] = shouldCastTrue;
        if (shouldCast || hasOwn(prop, "default")) {
          needCastKeys.push(normalizedKey);
        }
      }
    }
  }
  const res = [normalized, needCastKeys];
  if (isObject$1(comp)) {
    cache.set(comp, res);
  }
  return res;
}
function validatePropName(key) {
  if (key[0] !== "$" && !isReservedProp(key)) {
    return true;
  }
  return false;
}
const isInternalKey = (key) => key[0] === "_" || key === "$stable";
const normalizeSlotValue = (value) => isArray(value) ? value.map(normalizeVNode) : [normalizeVNode(value)];
const normalizeSlot = (key, rawSlot, ctx) => {
  if (rawSlot._n) {
    return rawSlot;
  }
  const normalized = withCtx((...args) => {
    if (false) ;
    return normalizeSlotValue(rawSlot(...args));
  }, ctx);
  normalized._c = false;
  return normalized;
};
const normalizeObjectSlots = (rawSlots, slots, instance) => {
  const ctx = rawSlots._ctx;
  for (const key in rawSlots) {
    if (isInternalKey(key)) continue;
    const value = rawSlots[key];
    if (isFunction(value)) {
      slots[key] = normalizeSlot(key, value, ctx);
    } else if (value != null) {
      const normalized = normalizeSlotValue(value);
      slots[key] = () => normalized;
    }
  }
};
const normalizeVNodeSlots = (instance, children) => {
  const normalized = normalizeSlotValue(children);
  instance.slots.default = () => normalized;
};
const assignSlots = (slots, children, optimized) => {
  for (const key in children) {
    if (optimized || key !== "_") {
      slots[key] = children[key];
    }
  }
};
const initSlots = (instance, children, optimized) => {
  const slots = instance.slots = createInternalObject();
  if (instance.vnode.shapeFlag & 32) {
    const type = children._;
    if (type) {
      assignSlots(slots, children, optimized);
      if (optimized) {
        def(slots, "_", type, true);
      }
    } else {
      normalizeObjectSlots(children, slots);
    }
  } else if (children) {
    normalizeVNodeSlots(instance, children);
  }
};
const updateSlots = (instance, children, optimized) => {
  const { vnode, slots } = instance;
  let needDeletionCheck = true;
  let deletionComparisonTarget = EMPTY_OBJ;
  if (vnode.shapeFlag & 32) {
    const type = children._;
    if (type) {
      if (optimized && type === 1) {
        needDeletionCheck = false;
      } else {
        assignSlots(slots, children, optimized);
      }
    } else {
      needDeletionCheck = !children.$stable;
      normalizeObjectSlots(children, slots);
    }
    deletionComparisonTarget = children;
  } else if (children) {
    normalizeVNodeSlots(instance, children);
    deletionComparisonTarget = { default: 1 };
  }
  if (needDeletionCheck) {
    for (const key in slots) {
      if (!isInternalKey(key) && deletionComparisonTarget[key] == null) {
        delete slots[key];
      }
    }
  }
};
const queuePostRenderEffect = queueEffectWithSuspense;
function createRenderer(options) {
  return baseCreateRenderer(options);
}
function baseCreateRenderer(options, createHydrationFns) {
  const target = getGlobalThis();
  target.__VUE__ = true;
  const {
    insert: hostInsert,
    remove: hostRemove,
    patchProp: hostPatchProp,
    createElement: hostCreateElement,
    createText: hostCreateText,
    createComment: hostCreateComment,
    setText: hostSetText,
    setElementText: hostSetElementText,
    parentNode: hostParentNode,
    nextSibling: hostNextSibling,
    setScopeId: hostSetScopeId = NOOP,
    insertStaticContent: hostInsertStaticContent
  } = options;
  const patch = (n1, n2, container, anchor = null, parentComponent = null, parentSuspense = null, namespace = void 0, slotScopeIds = null, optimized = !!n2.dynamicChildren) => {
    if (n1 === n2) {
      return;
    }
    if (n1 && !isSameVNodeType(n1, n2)) {
      anchor = getNextHostNode(n1);
      unmount(n1, parentComponent, parentSuspense, true);
      n1 = null;
    }
    if (n2.patchFlag === -2) {
      optimized = false;
      n2.dynamicChildren = null;
    }
    const { type, ref: ref3, shapeFlag } = n2;
    switch (type) {
      case Text:
        processText(n1, n2, container, anchor);
        break;
      case Comment:
        processCommentNode(n1, n2, container, anchor);
        break;
      case Static:
        if (n1 == null) {
          mountStaticNode(n2, container, anchor, namespace);
        }
        break;
      case Fragment:
        processFragment(
          n1,
          n2,
          container,
          anchor,
          parentComponent,
          parentSuspense,
          namespace,
          slotScopeIds,
          optimized
        );
        break;
      default:
        if (shapeFlag & 1) {
          processElement(
            n1,
            n2,
            container,
            anchor,
            parentComponent,
            parentSuspense,
            namespace,
            slotScopeIds,
            optimized
          );
        } else if (shapeFlag & 6) {
          processComponent(
            n1,
            n2,
            container,
            anchor,
            parentComponent,
            parentSuspense,
            namespace,
            slotScopeIds,
            optimized
          );
        } else if (shapeFlag & 64) {
          type.process(
            n1,
            n2,
            container,
            anchor,
            parentComponent,
            parentSuspense,
            namespace,
            slotScopeIds,
            optimized,
            internals
          );
        } else if (shapeFlag & 128) {
          type.process(
            n1,
            n2,
            container,
            anchor,
            parentComponent,
            parentSuspense,
            namespace,
            slotScopeIds,
            optimized,
            internals
          );
        } else ;
    }
    if (ref3 != null && parentComponent) {
      setRef(ref3, n1 && n1.ref, parentSuspense, n2 || n1, !n2);
    }
  };
  const processText = (n1, n2, container, anchor) => {
    if (n1 == null) {
      hostInsert(
        n2.el = hostCreateText(n2.children),
        container,
        anchor
      );
    } else {
      const el = n2.el = n1.el;
      if (n2.children !== n1.children) {
        hostSetText(el, n2.children);
      }
    }
  };
  const processCommentNode = (n1, n2, container, anchor) => {
    if (n1 == null) {
      hostInsert(
        n2.el = hostCreateComment(n2.children || ""),
        container,
        anchor
      );
    } else {
      n2.el = n1.el;
    }
  };
  const mountStaticNode = (n2, container, anchor, namespace) => {
    [n2.el, n2.anchor] = hostInsertStaticContent(
      n2.children,
      container,
      anchor,
      namespace,
      n2.el,
      n2.anchor
    );
  };
  const moveStaticNode = ({ el, anchor }, container, nextSibling) => {
    let next;
    while (el && el !== anchor) {
      next = hostNextSibling(el);
      hostInsert(el, container, nextSibling);
      el = next;
    }
    hostInsert(anchor, container, nextSibling);
  };
  const removeStaticNode = ({ el, anchor }) => {
    let next;
    while (el && el !== anchor) {
      next = hostNextSibling(el);
      hostRemove(el);
      el = next;
    }
    hostRemove(anchor);
  };
  const processElement = (n1, n2, container, anchor, parentComponent, parentSuspense, namespace, slotScopeIds, optimized) => {
    if (n2.type === "svg") {
      namespace = "svg";
    } else if (n2.type === "math") {
      namespace = "mathml";
    }
    if (n1 == null) {
      mountElement(
        n2,
        container,
        anchor,
        parentComponent,
        parentSuspense,
        namespace,
        slotScopeIds,
        optimized
      );
    } else {
      patchElement(
        n1,
        n2,
        parentComponent,
        parentSuspense,
        namespace,
        slotScopeIds,
        optimized
      );
    }
  };
  const mountElement = (vnode, container, anchor, parentComponent, parentSuspense, namespace, slotScopeIds, optimized) => {
    let el;
    let vnodeHook;
    const { props, shapeFlag, transition, dirs } = vnode;
    el = vnode.el = hostCreateElement(
      vnode.type,
      namespace,
      props && props.is,
      props
    );
    if (shapeFlag & 8) {
      hostSetElementText(el, vnode.children);
    } else if (shapeFlag & 16) {
      mountChildren(
        vnode.children,
        el,
        null,
        parentComponent,
        parentSuspense,
        resolveChildrenNamespace(vnode, namespace),
        slotScopeIds,
        optimized
      );
    }
    if (dirs) {
      invokeDirectiveHook(vnode, null, parentComponent, "created");
    }
    setScopeId(el, vnode, vnode.scopeId, slotScopeIds, parentComponent);
    if (props) {
      for (const key in props) {
        if (key !== "value" && !isReservedProp(key)) {
          hostPatchProp(el, key, null, props[key], namespace, parentComponent);
        }
      }
      if ("value" in props) {
        hostPatchProp(el, "value", null, props.value, namespace);
      }
      if (vnodeHook = props.onVnodeBeforeMount) {
        invokeVNodeHook(vnodeHook, parentComponent, vnode);
      }
    }
    if (dirs) {
      invokeDirectiveHook(vnode, null, parentComponent, "beforeMount");
    }
    const needCallTransitionHooks = needTransition(parentSuspense, transition);
    if (needCallTransitionHooks) {
      transition.beforeEnter(el);
    }
    hostInsert(el, container, anchor);
    if ((vnodeHook = props && props.onVnodeMounted) || needCallTransitionHooks || dirs) {
      queuePostRenderEffect(() => {
        vnodeHook && invokeVNodeHook(vnodeHook, parentComponent, vnode);
        needCallTransitionHooks && transition.enter(el);
        dirs && invokeDirectiveHook(vnode, null, parentComponent, "mounted");
      }, parentSuspense);
    }
  };
  const setScopeId = (el, vnode, scopeId, slotScopeIds, parentComponent) => {
    if (scopeId) {
      hostSetScopeId(el, scopeId);
    }
    if (slotScopeIds) {
      for (let i = 0; i < slotScopeIds.length; i++) {
        hostSetScopeId(el, slotScopeIds[i]);
      }
    }
    if (parentComponent) {
      let subTree = parentComponent.subTree;
      if (vnode === subTree || isSuspense(subTree.type) && (subTree.ssContent === vnode || subTree.ssFallback === vnode)) {
        const parentVNode = parentComponent.vnode;
        setScopeId(
          el,
          parentVNode,
          parentVNode.scopeId,
          parentVNode.slotScopeIds,
          parentComponent.parent
        );
      }
    }
  };
  const mountChildren = (children, container, anchor, parentComponent, parentSuspense, namespace, slotScopeIds, optimized, start = 0) => {
    for (let i = start; i < children.length; i++) {
      const child = children[i] = optimized ? cloneIfMounted(children[i]) : normalizeVNode(children[i]);
      patch(
        null,
        child,
        container,
        anchor,
        parentComponent,
        parentSuspense,
        namespace,
        slotScopeIds,
        optimized
      );
    }
  };
  const patchElement = (n1, n2, parentComponent, parentSuspense, namespace, slotScopeIds, optimized) => {
    const el = n2.el = n1.el;
    let { patchFlag, dynamicChildren, dirs } = n2;
    patchFlag |= n1.patchFlag & 16;
    const oldProps = n1.props || EMPTY_OBJ;
    const newProps = n2.props || EMPTY_OBJ;
    let vnodeHook;
    parentComponent && toggleRecurse(parentComponent, false);
    if (vnodeHook = newProps.onVnodeBeforeUpdate) {
      invokeVNodeHook(vnodeHook, parentComponent, n2, n1);
    }
    if (dirs) {
      invokeDirectiveHook(n2, n1, parentComponent, "beforeUpdate");
    }
    parentComponent && toggleRecurse(parentComponent, true);
    if (oldProps.innerHTML && newProps.innerHTML == null || oldProps.textContent && newProps.textContent == null) {
      hostSetElementText(el, "");
    }
    if (dynamicChildren) {
      patchBlockChildren(
        n1.dynamicChildren,
        dynamicChildren,
        el,
        parentComponent,
        parentSuspense,
        resolveChildrenNamespace(n2, namespace),
        slotScopeIds
      );
    } else if (!optimized) {
      patchChildren(
        n1,
        n2,
        el,
        null,
        parentComponent,
        parentSuspense,
        resolveChildrenNamespace(n2, namespace),
        slotScopeIds,
        false
      );
    }
    if (patchFlag > 0) {
      if (patchFlag & 16) {
        patchProps(el, oldProps, newProps, parentComponent, namespace);
      } else {
        if (patchFlag & 2) {
          if (oldProps.class !== newProps.class) {
            hostPatchProp(el, "class", null, newProps.class, namespace);
          }
        }
        if (patchFlag & 4) {
          hostPatchProp(el, "style", oldProps.style, newProps.style, namespace);
        }
        if (patchFlag & 8) {
          const propsToUpdate = n2.dynamicProps;
          for (let i = 0; i < propsToUpdate.length; i++) {
            const key = propsToUpdate[i];
            const prev = oldProps[key];
            const next = newProps[key];
            if (next !== prev || key === "value") {
              hostPatchProp(el, key, prev, next, namespace, parentComponent);
            }
          }
        }
      }
      if (patchFlag & 1) {
        if (n1.children !== n2.children) {
          hostSetElementText(el, n2.children);
        }
      }
    } else if (!optimized && dynamicChildren == null) {
      patchProps(el, oldProps, newProps, parentComponent, namespace);
    }
    if ((vnodeHook = newProps.onVnodeUpdated) || dirs) {
      queuePostRenderEffect(() => {
        vnodeHook && invokeVNodeHook(vnodeHook, parentComponent, n2, n1);
        dirs && invokeDirectiveHook(n2, n1, parentComponent, "updated");
      }, parentSuspense);
    }
  };
  const patchBlockChildren = (oldChildren, newChildren, fallbackContainer, parentComponent, parentSuspense, namespace, slotScopeIds) => {
    for (let i = 0; i < newChildren.length; i++) {
      const oldVNode = oldChildren[i];
      const newVNode = newChildren[i];
      const container = (
        // oldVNode may be an errored async setup() component inside Suspense
        // which will not have a mounted element
        oldVNode.el && // - In the case of a Fragment, we need to provide the actual parent
        // of the Fragment itself so it can move its children.
        (oldVNode.type === Fragment || // - In the case of different nodes, there is going to be a replacement
        // which also requires the correct parent container
        !isSameVNodeType(oldVNode, newVNode) || // - In the case of a component, it could contain anything.
        oldVNode.shapeFlag & (6 | 64)) ? hostParentNode(oldVNode.el) : (
          // In other cases, the parent container is not actually used so we
          // just pass the block element here to avoid a DOM parentNode call.
          fallbackContainer
        )
      );
      patch(
        oldVNode,
        newVNode,
        container,
        null,
        parentComponent,
        parentSuspense,
        namespace,
        slotScopeIds,
        true
      );
    }
  };
  const patchProps = (el, oldProps, newProps, parentComponent, namespace) => {
    if (oldProps !== newProps) {
      if (oldProps !== EMPTY_OBJ) {
        for (const key in oldProps) {
          if (!isReservedProp(key) && !(key in newProps)) {
            hostPatchProp(
              el,
              key,
              oldProps[key],
              null,
              namespace,
              parentComponent
            );
          }
        }
      }
      for (const key in newProps) {
        if (isReservedProp(key)) continue;
        const next = newProps[key];
        const prev = oldProps[key];
        if (next !== prev && key !== "value") {
          hostPatchProp(el, key, prev, next, namespace, parentComponent);
        }
      }
      if ("value" in newProps) {
        hostPatchProp(el, "value", oldProps.value, newProps.value, namespace);
      }
    }
  };
  const processFragment = (n1, n2, container, anchor, parentComponent, parentSuspense, namespace, slotScopeIds, optimized) => {
    const fragmentStartAnchor = n2.el = n1 ? n1.el : hostCreateText("");
    const fragmentEndAnchor = n2.anchor = n1 ? n1.anchor : hostCreateText("");
    let { patchFlag, dynamicChildren, slotScopeIds: fragmentSlotScopeIds } = n2;
    if (fragmentSlotScopeIds) {
      slotScopeIds = slotScopeIds ? slotScopeIds.concat(fragmentSlotScopeIds) : fragmentSlotScopeIds;
    }
    if (n1 == null) {
      hostInsert(fragmentStartAnchor, container, anchor);
      hostInsert(fragmentEndAnchor, container, anchor);
      mountChildren(
        // #10007
        // such fragment like `<></>` will be compiled into
        // a fragment which doesn't have a children.
        // In this case fallback to an empty array
        n2.children || [],
        container,
        fragmentEndAnchor,
        parentComponent,
        parentSuspense,
        namespace,
        slotScopeIds,
        optimized
      );
    } else {
      if (patchFlag > 0 && patchFlag & 64 && dynamicChildren && // #2715 the previous fragment could've been a BAILed one as a result
      // of renderSlot() with no valid children
      n1.dynamicChildren) {
        patchBlockChildren(
          n1.dynamicChildren,
          dynamicChildren,
          container,
          parentComponent,
          parentSuspense,
          namespace,
          slotScopeIds
        );
        if (
          // #2080 if the stable fragment has a key, it's a <template v-for> that may
          //  get moved around. Make sure all root level vnodes inherit el.
          // #2134 or if it's a component root, it may also get moved around
          // as the component is being moved.
          n2.key != null || parentComponent && n2 === parentComponent.subTree
        ) {
          traverseStaticChildren(
            n1,
            n2,
            true
            /* shallow */
          );
        }
      } else {
        patchChildren(
          n1,
          n2,
          container,
          fragmentEndAnchor,
          parentComponent,
          parentSuspense,
          namespace,
          slotScopeIds,
          optimized
        );
      }
    }
  };
  const processComponent = (n1, n2, container, anchor, parentComponent, parentSuspense, namespace, slotScopeIds, optimized) => {
    n2.slotScopeIds = slotScopeIds;
    if (n1 == null) {
      if (n2.shapeFlag & 512) {
        parentComponent.ctx.activate(
          n2,
          container,
          anchor,
          namespace,
          optimized
        );
      } else {
        mountComponent(
          n2,
          container,
          anchor,
          parentComponent,
          parentSuspense,
          namespace,
          optimized
        );
      }
    } else {
      updateComponent(n1, n2, optimized);
    }
  };
  const mountComponent = (initialVNode, container, anchor, parentComponent, parentSuspense, namespace, optimized) => {
    const instance = initialVNode.component = createComponentInstance(
      initialVNode,
      parentComponent,
      parentSuspense
    );
    if (isKeepAlive(initialVNode)) {
      instance.ctx.renderer = internals;
    }
    {
      setupComponent(instance, false, optimized);
    }
    if (instance.asyncDep) {
      parentSuspense && parentSuspense.registerDep(instance, setupRenderEffect, optimized);
      if (!initialVNode.el) {
        const placeholder = instance.subTree = createVNode(Comment);
        processCommentNode(null, placeholder, container, anchor);
      }
    } else {
      setupRenderEffect(
        instance,
        initialVNode,
        container,
        anchor,
        parentSuspense,
        namespace,
        optimized
      );
    }
  };
  const updateComponent = (n1, n2, optimized) => {
    const instance = n2.component = n1.component;
    if (shouldUpdateComponent(n1, n2, optimized)) {
      if (instance.asyncDep && !instance.asyncResolved) {
        updateComponentPreRender(instance, n2, optimized);
        return;
      } else {
        instance.next = n2;
        instance.update();
      }
    } else {
      n2.el = n1.el;
      instance.vnode = n2;
    }
  };
  const setupRenderEffect = (instance, initialVNode, container, anchor, parentSuspense, namespace, optimized) => {
    const componentUpdateFn = () => {
      if (!instance.isMounted) {
        let vnodeHook;
        const { el, props } = initialVNode;
        const { bm, m, parent, root, type } = instance;
        const isAsyncWrapperVNode = isAsyncWrapper(initialVNode);
        toggleRecurse(instance, false);
        if (bm) {
          invokeArrayFns(bm);
        }
        if (!isAsyncWrapperVNode && (vnodeHook = props && props.onVnodeBeforeMount)) {
          invokeVNodeHook(vnodeHook, parent, initialVNode);
        }
        toggleRecurse(instance, true);
        {
          if (root.ce) {
            root.ce._injectChildStyle(type);
          }
          const subTree = instance.subTree = renderComponentRoot(instance);
          patch(
            null,
            subTree,
            container,
            anchor,
            instance,
            parentSuspense,
            namespace
          );
          initialVNode.el = subTree.el;
        }
        if (m) {
          queuePostRenderEffect(m, parentSuspense);
        }
        if (!isAsyncWrapperVNode && (vnodeHook = props && props.onVnodeMounted)) {
          const scopedInitialVNode = initialVNode;
          queuePostRenderEffect(
            () => invokeVNodeHook(vnodeHook, parent, scopedInitialVNode),
            parentSuspense
          );
        }
        if (initialVNode.shapeFlag & 256 || parent && isAsyncWrapper(parent.vnode) && parent.vnode.shapeFlag & 256) {
          instance.a && queuePostRenderEffect(instance.a, parentSuspense);
        }
        instance.isMounted = true;
        initialVNode = container = anchor = null;
      } else {
        let { next, bu, u, parent, vnode } = instance;
        {
          const nonHydratedAsyncRoot = locateNonHydratedAsyncRoot(instance);
          if (nonHydratedAsyncRoot) {
            if (next) {
              next.el = vnode.el;
              updateComponentPreRender(instance, next, optimized);
            }
            nonHydratedAsyncRoot.asyncDep.then(() => {
              if (!instance.isUnmounted) {
                componentUpdateFn();
              }
            });
            return;
          }
        }
        let originNext = next;
        let vnodeHook;
        toggleRecurse(instance, false);
        if (next) {
          next.el = vnode.el;
          updateComponentPreRender(instance, next, optimized);
        } else {
          next = vnode;
        }
        if (bu) {
          invokeArrayFns(bu);
        }
        if (vnodeHook = next.props && next.props.onVnodeBeforeUpdate) {
          invokeVNodeHook(vnodeHook, parent, next, vnode);
        }
        toggleRecurse(instance, true);
        const nextTree = renderComponentRoot(instance);
        const prevTree = instance.subTree;
        instance.subTree = nextTree;
        patch(
          prevTree,
          nextTree,
          // parent may have changed if it's in a teleport
          hostParentNode(prevTree.el),
          // anchor may have changed if it's in a fragment
          getNextHostNode(prevTree),
          instance,
          parentSuspense,
          namespace
        );
        next.el = nextTree.el;
        if (originNext === null) {
          updateHOCHostEl(instance, nextTree.el);
        }
        if (u) {
          queuePostRenderEffect(u, parentSuspense);
        }
        if (vnodeHook = next.props && next.props.onVnodeUpdated) {
          queuePostRenderEffect(
            () => invokeVNodeHook(vnodeHook, parent, next, vnode),
            parentSuspense
          );
        }
      }
    };
    instance.scope.on();
    const effect2 = instance.effect = new ReactiveEffect(componentUpdateFn);
    instance.scope.off();
    const update3 = instance.update = effect2.run.bind(effect2);
    const job = instance.job = effect2.runIfDirty.bind(effect2);
    job.i = instance;
    job.id = instance.uid;
    effect2.scheduler = () => queueJob(job);
    toggleRecurse(instance, true);
    update3();
  };
  const updateComponentPreRender = (instance, nextVNode, optimized) => {
    nextVNode.component = instance;
    const prevProps = instance.vnode.props;
    instance.vnode = nextVNode;
    instance.next = null;
    updateProps(instance, nextVNode.props, prevProps, optimized);
    updateSlots(instance, nextVNode.children, optimized);
    pauseTracking();
    flushPreFlushCbs(instance);
    resetTracking();
  };
  const patchChildren = (n1, n2, container, anchor, parentComponent, parentSuspense, namespace, slotScopeIds, optimized = false) => {
    const c1 = n1 && n1.children;
    const prevShapeFlag = n1 ? n1.shapeFlag : 0;
    const c2 = n2.children;
    const { patchFlag, shapeFlag } = n2;
    if (patchFlag > 0) {
      if (patchFlag & 128) {
        patchKeyedChildren(
          c1,
          c2,
          container,
          anchor,
          parentComponent,
          parentSuspense,
          namespace,
          slotScopeIds,
          optimized
        );
        return;
      } else if (patchFlag & 256) {
        patchUnkeyedChildren(
          c1,
          c2,
          container,
          anchor,
          parentComponent,
          parentSuspense,
          namespace,
          slotScopeIds,
          optimized
        );
        return;
      }
    }
    if (shapeFlag & 8) {
      if (prevShapeFlag & 16) {
        unmountChildren(c1, parentComponent, parentSuspense);
      }
      if (c2 !== c1) {
        hostSetElementText(container, c2);
      }
    } else {
      if (prevShapeFlag & 16) {
        if (shapeFlag & 16) {
          patchKeyedChildren(
            c1,
            c2,
            container,
            anchor,
            parentComponent,
            parentSuspense,
            namespace,
            slotScopeIds,
            optimized
          );
        } else {
          unmountChildren(c1, parentComponent, parentSuspense, true);
        }
      } else {
        if (prevShapeFlag & 8) {
          hostSetElementText(container, "");
        }
        if (shapeFlag & 16) {
          mountChildren(
            c2,
            container,
            anchor,
            parentComponent,
            parentSuspense,
            namespace,
            slotScopeIds,
            optimized
          );
        }
      }
    }
  };
  const patchUnkeyedChildren = (c1, c2, container, anchor, parentComponent, parentSuspense, namespace, slotScopeIds, optimized) => {
    c1 = c1 || EMPTY_ARR;
    c2 = c2 || EMPTY_ARR;
    const oldLength = c1.length;
    const newLength = c2.length;
    const commonLength = Math.min(oldLength, newLength);
    let i;
    for (i = 0; i < commonLength; i++) {
      const nextChild = c2[i] = optimized ? cloneIfMounted(c2[i]) : normalizeVNode(c2[i]);
      patch(
        c1[i],
        nextChild,
        container,
        null,
        parentComponent,
        parentSuspense,
        namespace,
        slotScopeIds,
        optimized
      );
    }
    if (oldLength > newLength) {
      unmountChildren(
        c1,
        parentComponent,
        parentSuspense,
        true,
        false,
        commonLength
      );
    } else {
      mountChildren(
        c2,
        container,
        anchor,
        parentComponent,
        parentSuspense,
        namespace,
        slotScopeIds,
        optimized,
        commonLength
      );
    }
  };
  const patchKeyedChildren = (c1, c2, container, parentAnchor, parentComponent, parentSuspense, namespace, slotScopeIds, optimized) => {
    let i = 0;
    const l2 = c2.length;
    let e1 = c1.length - 1;
    let e2 = l2 - 1;
    while (i <= e1 && i <= e2) {
      const n1 = c1[i];
      const n2 = c2[i] = optimized ? cloneIfMounted(c2[i]) : normalizeVNode(c2[i]);
      if (isSameVNodeType(n1, n2)) {
        patch(
          n1,
          n2,
          container,
          null,
          parentComponent,
          parentSuspense,
          namespace,
          slotScopeIds,
          optimized
        );
      } else {
        break;
      }
      i++;
    }
    while (i <= e1 && i <= e2) {
      const n1 = c1[e1];
      const n2 = c2[e2] = optimized ? cloneIfMounted(c2[e2]) : normalizeVNode(c2[e2]);
      if (isSameVNodeType(n1, n2)) {
        patch(
          n1,
          n2,
          container,
          null,
          parentComponent,
          parentSuspense,
          namespace,
          slotScopeIds,
          optimized
        );
      } else {
        break;
      }
      e1--;
      e2--;
    }
    if (i > e1) {
      if (i <= e2) {
        const nextPos = e2 + 1;
        const anchor = nextPos < l2 ? c2[nextPos].el : parentAnchor;
        while (i <= e2) {
          patch(
            null,
            c2[i] = optimized ? cloneIfMounted(c2[i]) : normalizeVNode(c2[i]),
            container,
            anchor,
            parentComponent,
            parentSuspense,
            namespace,
            slotScopeIds,
            optimized
          );
          i++;
        }
      }
    } else if (i > e2) {
      while (i <= e1) {
        unmount(c1[i], parentComponent, parentSuspense, true);
        i++;
      }
    } else {
      const s1 = i;
      const s2 = i;
      const keyToNewIndexMap = /* @__PURE__ */ new Map();
      for (i = s2; i <= e2; i++) {
        const nextChild = c2[i] = optimized ? cloneIfMounted(c2[i]) : normalizeVNode(c2[i]);
        if (nextChild.key != null) {
          keyToNewIndexMap.set(nextChild.key, i);
        }
      }
      let j;
      let patched = 0;
      const toBePatched = e2 - s2 + 1;
      let moved = false;
      let maxNewIndexSoFar = 0;
      const newIndexToOldIndexMap = new Array(toBePatched);
      for (i = 0; i < toBePatched; i++) newIndexToOldIndexMap[i] = 0;
      for (i = s1; i <= e1; i++) {
        const prevChild = c1[i];
        if (patched >= toBePatched) {
          unmount(prevChild, parentComponent, parentSuspense, true);
          continue;
        }
        let newIndex;
        if (prevChild.key != null) {
          newIndex = keyToNewIndexMap.get(prevChild.key);
        } else {
          for (j = s2; j <= e2; j++) {
            if (newIndexToOldIndexMap[j - s2] === 0 && isSameVNodeType(prevChild, c2[j])) {
              newIndex = j;
              break;
            }
          }
        }
        if (newIndex === void 0) {
          unmount(prevChild, parentComponent, parentSuspense, true);
        } else {
          newIndexToOldIndexMap[newIndex - s2] = i + 1;
          if (newIndex >= maxNewIndexSoFar) {
            maxNewIndexSoFar = newIndex;
          } else {
            moved = true;
          }
          patch(
            prevChild,
            c2[newIndex],
            container,
            null,
            parentComponent,
            parentSuspense,
            namespace,
            slotScopeIds,
            optimized
          );
          patched++;
        }
      }
      const increasingNewIndexSequence = moved ? getSequence(newIndexToOldIndexMap) : EMPTY_ARR;
      j = increasingNewIndexSequence.length - 1;
      for (i = toBePatched - 1; i >= 0; i--) {
        const nextIndex = s2 + i;
        const nextChild = c2[nextIndex];
        const anchor = nextIndex + 1 < l2 ? c2[nextIndex + 1].el : parentAnchor;
        if (newIndexToOldIndexMap[i] === 0) {
          patch(
            null,
            nextChild,
            container,
            anchor,
            parentComponent,
            parentSuspense,
            namespace,
            slotScopeIds,
            optimized
          );
        } else if (moved) {
          if (j < 0 || i !== increasingNewIndexSequence[j]) {
            move(nextChild, container, anchor, 2);
          } else {
            j--;
          }
        }
      }
    }
  };
  const move = (vnode, container, anchor, moveType, parentSuspense = null) => {
    const { el, type, transition, children, shapeFlag } = vnode;
    if (shapeFlag & 6) {
      move(vnode.component.subTree, container, anchor, moveType);
      return;
    }
    if (shapeFlag & 128) {
      vnode.suspense.move(container, anchor, moveType);
      return;
    }
    if (shapeFlag & 64) {
      type.move(vnode, container, anchor, internals);
      return;
    }
    if (type === Fragment) {
      hostInsert(el, container, anchor);
      for (let i = 0; i < children.length; i++) {
        move(children[i], container, anchor, moveType);
      }
      hostInsert(vnode.anchor, container, anchor);
      return;
    }
    if (type === Static) {
      moveStaticNode(vnode, container, anchor);
      return;
    }
    const needTransition2 = moveType !== 2 && shapeFlag & 1 && transition;
    if (needTransition2) {
      if (moveType === 0) {
        transition.beforeEnter(el);
        hostInsert(el, container, anchor);
        queuePostRenderEffect(() => transition.enter(el), parentSuspense);
      } else {
        const { leave, delayLeave, afterLeave } = transition;
        const remove22 = () => hostInsert(el, container, anchor);
        const performLeave = () => {
          leave(el, () => {
            remove22();
            afterLeave && afterLeave();
          });
        };
        if (delayLeave) {
          delayLeave(el, remove22, performLeave);
        } else {
          performLeave();
        }
      }
    } else {
      hostInsert(el, container, anchor);
    }
  };
  const unmount = (vnode, parentComponent, parentSuspense, doRemove = false, optimized = false) => {
    const {
      type,
      props,
      ref: ref3,
      children,
      dynamicChildren,
      shapeFlag,
      patchFlag,
      dirs,
      cacheIndex
    } = vnode;
    if (patchFlag === -2) {
      optimized = false;
    }
    if (ref3 != null) {
      setRef(ref3, null, parentSuspense, vnode, true);
    }
    if (cacheIndex != null) {
      parentComponent.renderCache[cacheIndex] = void 0;
    }
    if (shapeFlag & 256) {
      parentComponent.ctx.deactivate(vnode);
      return;
    }
    const shouldInvokeDirs = shapeFlag & 1 && dirs;
    const shouldInvokeVnodeHook = !isAsyncWrapper(vnode);
    let vnodeHook;
    if (shouldInvokeVnodeHook && (vnodeHook = props && props.onVnodeBeforeUnmount)) {
      invokeVNodeHook(vnodeHook, parentComponent, vnode);
    }
    if (shapeFlag & 6) {
      unmountComponent(vnode.component, parentSuspense, doRemove);
    } else {
      if (shapeFlag & 128) {
        vnode.suspense.unmount(parentSuspense, doRemove);
        return;
      }
      if (shouldInvokeDirs) {
        invokeDirectiveHook(vnode, null, parentComponent, "beforeUnmount");
      }
      if (shapeFlag & 64) {
        vnode.type.remove(
          vnode,
          parentComponent,
          parentSuspense,
          internals,
          doRemove
        );
      } else if (dynamicChildren && // #5154
      // when v-once is used inside a block, setBlockTracking(-1) marks the
      // parent block with hasOnce: true
      // so that it doesn't take the fast path during unmount - otherwise
      // components nested in v-once are never unmounted.
      !dynamicChildren.hasOnce && // #1153: fast path should not be taken for non-stable (v-for) fragments
      (type !== Fragment || patchFlag > 0 && patchFlag & 64)) {
        unmountChildren(
          dynamicChildren,
          parentComponent,
          parentSuspense,
          false,
          true
        );
      } else if (type === Fragment && patchFlag & (128 | 256) || !optimized && shapeFlag & 16) {
        unmountChildren(children, parentComponent, parentSuspense);
      }
      if (doRemove) {
        remove2(vnode);
      }
    }
    if (shouldInvokeVnodeHook && (vnodeHook = props && props.onVnodeUnmounted) || shouldInvokeDirs) {
      queuePostRenderEffect(() => {
        vnodeHook && invokeVNodeHook(vnodeHook, parentComponent, vnode);
        shouldInvokeDirs && invokeDirectiveHook(vnode, null, parentComponent, "unmounted");
      }, parentSuspense);
    }
  };
  const remove2 = (vnode) => {
    const { type, el, anchor, transition } = vnode;
    if (type === Fragment) {
      {
        removeFragment(el, anchor);
      }
      return;
    }
    if (type === Static) {
      removeStaticNode(vnode);
      return;
    }
    const performRemove = () => {
      hostRemove(el);
      if (transition && !transition.persisted && transition.afterLeave) {
        transition.afterLeave();
      }
    };
    if (vnode.shapeFlag & 1 && transition && !transition.persisted) {
      const { leave, delayLeave } = transition;
      const performLeave = () => leave(el, performRemove);
      if (delayLeave) {
        delayLeave(vnode.el, performRemove, performLeave);
      } else {
        performLeave();
      }
    } else {
      performRemove();
    }
  };
  const removeFragment = (cur, end) => {
    let next;
    while (cur !== end) {
      next = hostNextSibling(cur);
      hostRemove(cur);
      cur = next;
    }
    hostRemove(end);
  };
  const unmountComponent = (instance, parentSuspense, doRemove) => {
    const { bum, scope, job, subTree, um, m, a } = instance;
    invalidateMount(m);
    invalidateMount(a);
    if (bum) {
      invokeArrayFns(bum);
    }
    scope.stop();
    if (job) {
      job.flags |= 8;
      unmount(subTree, instance, parentSuspense, doRemove);
    }
    if (um) {
      queuePostRenderEffect(um, parentSuspense);
    }
    queuePostRenderEffect(() => {
      instance.isUnmounted = true;
    }, parentSuspense);
    if (parentSuspense && parentSuspense.pendingBranch && !parentSuspense.isUnmounted && instance.asyncDep && !instance.asyncResolved && instance.suspenseId === parentSuspense.pendingId) {
      parentSuspense.deps--;
      if (parentSuspense.deps === 0) {
        parentSuspense.resolve();
      }
    }
  };
  const unmountChildren = (children, parentComponent, parentSuspense, doRemove = false, optimized = false, start = 0) => {
    for (let i = start; i < children.length; i++) {
      unmount(children[i], parentComponent, parentSuspense, doRemove, optimized);
    }
  };
  const getNextHostNode = (vnode) => {
    if (vnode.shapeFlag & 6) {
      return getNextHostNode(vnode.component.subTree);
    }
    if (vnode.shapeFlag & 128) {
      return vnode.suspense.next();
    }
    const el = hostNextSibling(vnode.anchor || vnode.el);
    const teleportEnd = el && el[TeleportEndKey];
    return teleportEnd ? hostNextSibling(teleportEnd) : el;
  };
  let isFlushing = false;
  const render = (vnode, container, namespace) => {
    if (vnode == null) {
      if (container._vnode) {
        unmount(container._vnode, null, null, true);
      }
    } else {
      patch(
        container._vnode || null,
        vnode,
        container,
        null,
        null,
        null,
        namespace
      );
    }
    container._vnode = vnode;
    if (!isFlushing) {
      isFlushing = true;
      flushPreFlushCbs();
      flushPostFlushCbs();
      isFlushing = false;
    }
  };
  const internals = {
    p: patch,
    um: unmount,
    m: move,
    r: remove2,
    mt: mountComponent,
    mc: mountChildren,
    pc: patchChildren,
    pbc: patchBlockChildren,
    n: getNextHostNode,
    o: options
  };
  let hydrate;
  return {
    render,
    hydrate,
    createApp: createAppAPI(render)
  };
}
function resolveChildrenNamespace({ type, props }, currentNamespace) {
  return currentNamespace === "svg" && type === "foreignObject" || currentNamespace === "mathml" && type === "annotation-xml" && props && props.encoding && props.encoding.includes("html") ? void 0 : currentNamespace;
}
function toggleRecurse({ effect: effect2, job }, allowed) {
  if (allowed) {
    effect2.flags |= 32;
    job.flags |= 4;
  } else {
    effect2.flags &= -33;
    job.flags &= -5;
  }
}
function needTransition(parentSuspense, transition) {
  return (!parentSuspense || parentSuspense && !parentSuspense.pendingBranch) && transition && !transition.persisted;
}
function traverseStaticChildren(n1, n2, shallow = false) {
  const ch1 = n1.children;
  const ch2 = n2.children;
  if (isArray(ch1) && isArray(ch2)) {
    for (let i = 0; i < ch1.length; i++) {
      const c1 = ch1[i];
      let c2 = ch2[i];
      if (c2.shapeFlag & 1 && !c2.dynamicChildren) {
        if (c2.patchFlag <= 0 || c2.patchFlag === 32) {
          c2 = ch2[i] = cloneIfMounted(ch2[i]);
          c2.el = c1.el;
        }
        if (!shallow && c2.patchFlag !== -2)
          traverseStaticChildren(c1, c2);
      }
      if (c2.type === Text) {
        c2.el = c1.el;
      }
    }
  }
}
function getSequence(arr) {
  const p2 = arr.slice();
  const result = [0];
  let i, j, u, v, c;
  const len = arr.length;
  for (i = 0; i < len; i++) {
    const arrI = arr[i];
    if (arrI !== 0) {
      j = result[result.length - 1];
      if (arr[j] < arrI) {
        p2[i] = j;
        result.push(i);
        continue;
      }
      u = 0;
      v = result.length - 1;
      while (u < v) {
        c = u + v >> 1;
        if (arr[result[c]] < arrI) {
          u = c + 1;
        } else {
          v = c;
        }
      }
      if (arrI < arr[result[u]]) {
        if (u > 0) {
          p2[i] = result[u - 1];
        }
        result[u] = i;
      }
    }
  }
  u = result.length;
  v = result[u - 1];
  while (u-- > 0) {
    result[u] = v;
    v = p2[v];
  }
  return result;
}
function locateNonHydratedAsyncRoot(instance) {
  const subComponent = instance.subTree.component;
  if (subComponent) {
    if (subComponent.asyncDep && !subComponent.asyncResolved) {
      return subComponent;
    } else {
      return locateNonHydratedAsyncRoot(subComponent);
    }
  }
}
function invalidateMount(hooks) {
  if (hooks) {
    for (let i = 0; i < hooks.length; i++)
      hooks[i].flags |= 8;
  }
}
const ssrContextKey = Symbol.for("v-scx");
const useSSRContext = () => {
  {
    const ctx = inject(ssrContextKey);
    return ctx;
  }
};
function watch(source, cb, options) {
  return doWatch(source, cb, options);
}
function doWatch(source, cb, options = EMPTY_OBJ) {
  const { immediate, deep, flush, once } = options;
  const baseWatchOptions = extend({}, options);
  const runsImmediately = cb && immediate || !cb && flush !== "post";
  let ssrCleanup;
  if (isInSSRComponentSetup) {
    if (flush === "sync") {
      const ctx = useSSRContext();
      ssrCleanup = ctx.__watcherHandles || (ctx.__watcherHandles = []);
    } else if (!runsImmediately) {
      const watchStopHandle = () => {
      };
      watchStopHandle.stop = NOOP;
      watchStopHandle.resume = NOOP;
      watchStopHandle.pause = NOOP;
      return watchStopHandle;
    }
  }
  const instance = currentInstance;
  baseWatchOptions.call = (fn, type, args) => callWithAsyncErrorHandling(fn, instance, type, args);
  let isPre = false;
  if (flush === "post") {
    baseWatchOptions.scheduler = (job) => {
      queuePostRenderEffect(job, instance && instance.suspense);
    };
  } else if (flush !== "sync") {
    isPre = true;
    baseWatchOptions.scheduler = (job, isFirstRun) => {
      if (isFirstRun) {
        job();
      } else {
        queueJob(job);
      }
    };
  }
  baseWatchOptions.augmentJob = (job) => {
    if (cb) {
      job.flags |= 4;
    }
    if (isPre) {
      job.flags |= 2;
      if (instance) {
        job.id = instance.uid;
        job.i = instance;
      }
    }
  };
  const watchHandle = watch$1(source, cb, baseWatchOptions);
  if (isInSSRComponentSetup) {
    if (ssrCleanup) {
      ssrCleanup.push(watchHandle);
    } else if (runsImmediately) {
      watchHandle();
    }
  }
  return watchHandle;
}
function instanceWatch(source, value, options) {
  const publicThis = this.proxy;
  const getter = isString(source) ? source.includes(".") ? createPathGetter(publicThis, source) : () => publicThis[source] : source.bind(publicThis, publicThis);
  let cb;
  if (isFunction(value)) {
    cb = value;
  } else {
    cb = value.handler;
    options = value;
  }
  const reset = setCurrentInstance(this);
  const res = doWatch(getter, cb.bind(publicThis), options);
  reset();
  return res;
}
function createPathGetter(ctx, path) {
  const segments = path.split(".");
  return () => {
    let cur = ctx;
    for (let i = 0; i < segments.length && cur; i++) {
      cur = cur[segments[i]];
    }
    return cur;
  };
}
const getModelModifiers = (props, modelName) => {
  return modelName === "modelValue" || modelName === "model-value" ? props.modelModifiers : props[`${modelName}Modifiers`] || props[`${camelize(modelName)}Modifiers`] || props[`${hyphenate(modelName)}Modifiers`];
};
function emit(instance, event, ...rawArgs) {
  if (instance.isUnmounted) return;
  const props = instance.vnode.props || EMPTY_OBJ;
  let args = rawArgs;
  const isModelListener2 = event.startsWith("update:");
  const modifiers = isModelListener2 && getModelModifiers(props, event.slice(7));
  if (modifiers) {
    if (modifiers.trim) {
      args = rawArgs.map((a) => isString(a) ? a.trim() : a);
    }
    if (modifiers.number) {
      args = rawArgs.map(looseToNumber);
    }
  }
  let handlerName;
  let handler = props[handlerName = toHandlerKey(event)] || // also try camelCase event handler (#2249)
  props[handlerName = toHandlerKey(camelize(event))];
  if (!handler && isModelListener2) {
    handler = props[handlerName = toHandlerKey(hyphenate(event))];
  }
  if (handler) {
    callWithAsyncErrorHandling(
      handler,
      instance,
      6,
      args
    );
  }
  const onceHandler = props[handlerName + `Once`];
  if (onceHandler) {
    if (!instance.emitted) {
      instance.emitted = {};
    } else if (instance.emitted[handlerName]) {
      return;
    }
    instance.emitted[handlerName] = true;
    callWithAsyncErrorHandling(
      onceHandler,
      instance,
      6,
      args
    );
  }
}
function normalizeEmitsOptions(comp, appContext, asMixin = false) {
  const cache = appContext.emitsCache;
  const cached = cache.get(comp);
  if (cached !== void 0) {
    return cached;
  }
  const raw = comp.emits;
  let normalized = {};
  let hasExtends = false;
  if (!isFunction(comp)) {
    const extendEmits = (raw2) => {
      const normalizedFromExtend = normalizeEmitsOptions(raw2, appContext, true);
      if (normalizedFromExtend) {
        hasExtends = true;
        extend(normalized, normalizedFromExtend);
      }
    };
    if (!asMixin && appContext.mixins.length) {
      appContext.mixins.forEach(extendEmits);
    }
    if (comp.extends) {
      extendEmits(comp.extends);
    }
    if (comp.mixins) {
      comp.mixins.forEach(extendEmits);
    }
  }
  if (!raw && !hasExtends) {
    if (isObject$1(comp)) {
      cache.set(comp, null);
    }
    return null;
  }
  if (isArray(raw)) {
    raw.forEach((key) => normalized[key] = null);
  } else {
    extend(normalized, raw);
  }
  if (isObject$1(comp)) {
    cache.set(comp, normalized);
  }
  return normalized;
}
function isEmitListener(options, key) {
  if (!options || !isOn(key)) {
    return false;
  }
  key = key.slice(2).replace(/Once$/, "");
  return hasOwn(options, key[0].toLowerCase() + key.slice(1)) || hasOwn(options, hyphenate(key)) || hasOwn(options, key);
}
function markAttrsAccessed() {
}
function renderComponentRoot(instance) {
  const {
    type: Component,
    vnode,
    proxy,
    withProxy,
    propsOptions: [propsOptions],
    slots,
    attrs,
    emit: emit2,
    render,
    renderCache,
    props,
    data,
    setupState,
    ctx,
    inheritAttrs
  } = instance;
  const prev = setCurrentRenderingInstance(instance);
  let result;
  let fallthroughAttrs;
  try {
    if (vnode.shapeFlag & 4) {
      const proxyToUse = withProxy || proxy;
      const thisProxy = false ? new Proxy(proxyToUse, {
        get(target, key, receiver) {
          warn$1(
            `Property '${String(
              key
            )}' was accessed via 'this'. Avoid using 'this' in templates.`
          );
          return Reflect.get(target, key, receiver);
        }
      }) : proxyToUse;
      result = normalizeVNode(
        render.call(
          thisProxy,
          proxyToUse,
          renderCache,
          false ? shallowReadonly(props) : props,
          setupState,
          data,
          ctx
        )
      );
      fallthroughAttrs = attrs;
    } else {
      const render2 = Component;
      if (false) ;
      result = normalizeVNode(
        render2.length > 1 ? render2(
          false ? shallowReadonly(props) : props,
          false ? {
            get attrs() {
              markAttrsAccessed();
              return shallowReadonly(attrs);
            },
            slots,
            emit: emit2
          } : { attrs, slots, emit: emit2 }
        ) : render2(
          false ? shallowReadonly(props) : props,
          null
        )
      );
      fallthroughAttrs = Component.props ? attrs : getFunctionalFallthrough(attrs);
    }
  } catch (err) {
    blockStack.length = 0;
    handleError(err, instance, 1);
    result = createVNode(Comment);
  }
  let root = result;
  if (fallthroughAttrs && inheritAttrs !== false) {
    const keys = Object.keys(fallthroughAttrs);
    const { shapeFlag } = root;
    if (keys.length) {
      if (shapeFlag & (1 | 6)) {
        if (propsOptions && keys.some(isModelListener)) {
          fallthroughAttrs = filterModelListeners(
            fallthroughAttrs,
            propsOptions
          );
        }
        root = cloneVNode(root, fallthroughAttrs, false, true);
      }
    }
  }
  if (vnode.dirs) {
    root = cloneVNode(root, null, false, true);
    root.dirs = root.dirs ? root.dirs.concat(vnode.dirs) : vnode.dirs;
  }
  if (vnode.transition) {
    setTransitionHooks(root, vnode.transition);
  }
  {
    result = root;
  }
  setCurrentRenderingInstance(prev);
  return result;
}
const getFunctionalFallthrough = (attrs) => {
  let res;
  for (const key in attrs) {
    if (key === "class" || key === "style" || isOn(key)) {
      (res || (res = {}))[key] = attrs[key];
    }
  }
  return res;
};
const filterModelListeners = (attrs, props) => {
  const res = {};
  for (const key in attrs) {
    if (!isModelListener(key) || !(key.slice(9) in props)) {
      res[key] = attrs[key];
    }
  }
  return res;
};
function shouldUpdateComponent(prevVNode, nextVNode, optimized) {
  const { props: prevProps, children: prevChildren, component } = prevVNode;
  const { props: nextProps, children: nextChildren, patchFlag } = nextVNode;
  const emits = component.emitsOptions;
  if (nextVNode.dirs || nextVNode.transition) {
    return true;
  }
  if (optimized && patchFlag >= 0) {
    if (patchFlag & 1024) {
      return true;
    }
    if (patchFlag & 16) {
      if (!prevProps) {
        return !!nextProps;
      }
      return hasPropsChanged(prevProps, nextProps, emits);
    } else if (patchFlag & 8) {
      const dynamicProps = nextVNode.dynamicProps;
      for (let i = 0; i < dynamicProps.length; i++) {
        const key = dynamicProps[i];
        if (nextProps[key] !== prevProps[key] && !isEmitListener(emits, key)) {
          return true;
        }
      }
    }
  } else {
    if (prevChildren || nextChildren) {
      if (!nextChildren || !nextChildren.$stable) {
        return true;
      }
    }
    if (prevProps === nextProps) {
      return false;
    }
    if (!prevProps) {
      return !!nextProps;
    }
    if (!nextProps) {
      return true;
    }
    return hasPropsChanged(prevProps, nextProps, emits);
  }
  return false;
}
function hasPropsChanged(prevProps, nextProps, emitsOptions) {
  const nextKeys = Object.keys(nextProps);
  if (nextKeys.length !== Object.keys(prevProps).length) {
    return true;
  }
  for (let i = 0; i < nextKeys.length; i++) {
    const key = nextKeys[i];
    if (nextProps[key] !== prevProps[key] && !isEmitListener(emitsOptions, key)) {
      return true;
    }
  }
  return false;
}
function updateHOCHostEl({ vnode, parent }, el) {
  while (parent) {
    const root = parent.subTree;
    if (root.suspense && root.suspense.activeBranch === vnode) {
      root.el = vnode.el;
    }
    if (root === vnode) {
      (vnode = parent.vnode).el = el;
      parent = parent.parent;
    } else {
      break;
    }
  }
}
const isSuspense = (type) => type.__isSuspense;
function queueEffectWithSuspense(fn, suspense) {
  if (suspense && suspense.pendingBranch) {
    if (isArray(fn)) {
      suspense.effects.push(...fn);
    } else {
      suspense.effects.push(fn);
    }
  } else {
    queuePostFlushCb(fn);
  }
}
const Fragment = Symbol.for("v-fgt");
const Text = Symbol.for("v-txt");
const Comment = Symbol.for("v-cmt");
const Static = Symbol.for("v-stc");
const blockStack = [];
let currentBlock = null;
function openBlock(disableTracking = false) {
  blockStack.push(currentBlock = disableTracking ? null : []);
}
function closeBlock() {
  blockStack.pop();
  currentBlock = blockStack[blockStack.length - 1] || null;
}
let isBlockTreeEnabled = 1;
function setBlockTracking(value, inVOnce = false) {
  isBlockTreeEnabled += value;
  if (value < 0 && currentBlock && inVOnce) {
    currentBlock.hasOnce = true;
  }
}
function setupBlock(vnode) {
  vnode.dynamicChildren = isBlockTreeEnabled > 0 ? currentBlock || EMPTY_ARR : null;
  closeBlock();
  if (isBlockTreeEnabled > 0 && currentBlock) {
    currentBlock.push(vnode);
  }
  return vnode;
}
function createElementBlock(type, props, children, patchFlag, dynamicProps, shapeFlag) {
  return setupBlock(
    createBaseVNode(
      type,
      props,
      children,
      patchFlag,
      dynamicProps,
      shapeFlag,
      true
    )
  );
}
function createBlock(type, props, children, patchFlag, dynamicProps) {
  return setupBlock(
    createVNode(
      type,
      props,
      children,
      patchFlag,
      dynamicProps,
      true
    )
  );
}
function isVNode(value) {
  return value ? value.__v_isVNode === true : false;
}
function isSameVNodeType(n1, n2) {
  return n1.type === n2.type && n1.key === n2.key;
}
const normalizeKey = ({ key }) => key != null ? key : null;
const normalizeRef = ({
  ref: ref3,
  ref_key,
  ref_for
}) => {
  if (typeof ref3 === "number") {
    ref3 = "" + ref3;
  }
  return ref3 != null ? isString(ref3) || isRef(ref3) || isFunction(ref3) ? { i: currentRenderingInstance, r: ref3, k: ref_key, f: !!ref_for } : ref3 : null;
};
function createBaseVNode(type, props = null, children = null, patchFlag = 0, dynamicProps = null, shapeFlag = type === Fragment ? 0 : 1, isBlockNode = false, needFullChildrenNormalization = false) {
  const vnode = {
    __v_isVNode: true,
    __v_skip: true,
    type,
    props,
    key: props && normalizeKey(props),
    ref: props && normalizeRef(props),
    scopeId: currentScopeId,
    slotScopeIds: null,
    children,
    component: null,
    suspense: null,
    ssContent: null,
    ssFallback: null,
    dirs: null,
    transition: null,
    el: null,
    anchor: null,
    target: null,
    targetStart: null,
    targetAnchor: null,
    staticCount: 0,
    shapeFlag,
    patchFlag,
    dynamicProps,
    dynamicChildren: null,
    appContext: null,
    ctx: currentRenderingInstance
  };
  if (needFullChildrenNormalization) {
    normalizeChildren(vnode, children);
    if (shapeFlag & 128) {
      type.normalize(vnode);
    }
  } else if (children) {
    vnode.shapeFlag |= isString(children) ? 8 : 16;
  }
  if (isBlockTreeEnabled > 0 && // avoid a block node from tracking itself
  !isBlockNode && // has current parent block
  currentBlock && // presence of a patch flag indicates this node needs patching on updates.
  // component nodes also should always be patched, because even if the
  // component doesn't need to update, it needs to persist the instance on to
  // the next vnode so that it can be properly unmounted later.
  (vnode.patchFlag > 0 || shapeFlag & 6) && // the EVENTS flag is only for hydration and if it is the only flag, the
  // vnode should not be considered dynamic due to handler caching.
  vnode.patchFlag !== 32) {
    currentBlock.push(vnode);
  }
  return vnode;
}
const createVNode = _createVNode;
function _createVNode(type, props = null, children = null, patchFlag = 0, dynamicProps = null, isBlockNode = false) {
  if (!type || type === NULL_DYNAMIC_COMPONENT) {
    type = Comment;
  }
  if (isVNode(type)) {
    const cloned = cloneVNode(
      type,
      props,
      true
      /* mergeRef: true */
    );
    if (children) {
      normalizeChildren(cloned, children);
    }
    if (isBlockTreeEnabled > 0 && !isBlockNode && currentBlock) {
      if (cloned.shapeFlag & 6) {
        currentBlock[currentBlock.indexOf(type)] = cloned;
      } else {
        currentBlock.push(cloned);
      }
    }
    cloned.patchFlag = -2;
    return cloned;
  }
  if (isClassComponent(type)) {
    type = type.__vccOpts;
  }
  if (props) {
    props = guardReactiveProps(props);
    let { class: klass, style } = props;
    if (klass && !isString(klass)) {
      props.class = normalizeClass(klass);
    }
    if (isObject$1(style)) {
      if (isProxy(style) && !isArray(style)) {
        style = extend({}, style);
      }
      props.style = normalizeStyle(style);
    }
  }
  const shapeFlag = isString(type) ? 1 : isSuspense(type) ? 128 : isTeleport(type) ? 64 : isObject$1(type) ? 4 : isFunction(type) ? 2 : 0;
  return createBaseVNode(
    type,
    props,
    children,
    patchFlag,
    dynamicProps,
    shapeFlag,
    isBlockNode,
    true
  );
}
function guardReactiveProps(props) {
  if (!props) return null;
  return isProxy(props) || isInternalObject(props) ? extend({}, props) : props;
}
function cloneVNode(vnode, extraProps, mergeRef = false, cloneTransition = false) {
  const { props, ref: ref3, patchFlag, children, transition } = vnode;
  const mergedProps = extraProps ? mergeProps(props || {}, extraProps) : props;
  const cloned = {
    __v_isVNode: true,
    __v_skip: true,
    type: vnode.type,
    props: mergedProps,
    key: mergedProps && normalizeKey(mergedProps),
    ref: extraProps && extraProps.ref ? (
      // #2078 in the case of <component :is="vnode" ref="extra"/>
      // if the vnode itself already has a ref, cloneVNode will need to merge
      // the refs so the single vnode can be set on multiple refs
      mergeRef && ref3 ? isArray(ref3) ? ref3.concat(normalizeRef(extraProps)) : [ref3, normalizeRef(extraProps)] : normalizeRef(extraProps)
    ) : ref3,
    scopeId: vnode.scopeId,
    slotScopeIds: vnode.slotScopeIds,
    children,
    target: vnode.target,
    targetStart: vnode.targetStart,
    targetAnchor: vnode.targetAnchor,
    staticCount: vnode.staticCount,
    shapeFlag: vnode.shapeFlag,
    // if the vnode is cloned with extra props, we can no longer assume its
    // existing patch flag to be reliable and need to add the FULL_PROPS flag.
    // note: preserve flag for fragments since they use the flag for children
    // fast paths only.
    patchFlag: extraProps && vnode.type !== Fragment ? patchFlag === -1 ? 16 : patchFlag | 16 : patchFlag,
    dynamicProps: vnode.dynamicProps,
    dynamicChildren: vnode.dynamicChildren,
    appContext: vnode.appContext,
    dirs: vnode.dirs,
    transition,
    // These should technically only be non-null on mounted VNodes. However,
    // they *should* be copied for kept-alive vnodes. So we just always copy
    // them since them being non-null during a mount doesn't affect the logic as
    // they will simply be overwritten.
    component: vnode.component,
    suspense: vnode.suspense,
    ssContent: vnode.ssContent && cloneVNode(vnode.ssContent),
    ssFallback: vnode.ssFallback && cloneVNode(vnode.ssFallback),
    el: vnode.el,
    anchor: vnode.anchor,
    ctx: vnode.ctx,
    ce: vnode.ce
  };
  if (transition && cloneTransition) {
    setTransitionHooks(
      cloned,
      transition.clone(cloned)
    );
  }
  return cloned;
}
function createTextVNode(text = " ", flag = 0) {
  return createVNode(Text, null, text, flag);
}
function createCommentVNode(text = "", asBlock = false) {
  return asBlock ? (openBlock(), createBlock(Comment, null, text)) : createVNode(Comment, null, text);
}
function normalizeVNode(child) {
  if (child == null || typeof child === "boolean") {
    return createVNode(Comment);
  } else if (isArray(child)) {
    return createVNode(
      Fragment,
      null,
      // #3666, avoid reference pollution when reusing vnode
      child.slice()
    );
  } else if (isVNode(child)) {
    return cloneIfMounted(child);
  } else {
    return createVNode(Text, null, String(child));
  }
}
function cloneIfMounted(child) {
  return child.el === null && child.patchFlag !== -1 || child.memo ? child : cloneVNode(child);
}
function normalizeChildren(vnode, children) {
  let type = 0;
  const { shapeFlag } = vnode;
  if (children == null) {
    children = null;
  } else if (isArray(children)) {
    type = 16;
  } else if (typeof children === "object") {
    if (shapeFlag & (1 | 64)) {
      const slot = children.default;
      if (slot) {
        slot._c && (slot._d = false);
        normalizeChildren(vnode, slot());
        slot._c && (slot._d = true);
      }
      return;
    } else {
      type = 32;
      const slotFlag = children._;
      if (!slotFlag && !isInternalObject(children)) {
        children._ctx = currentRenderingInstance;
      } else if (slotFlag === 3 && currentRenderingInstance) {
        if (currentRenderingInstance.slots._ === 1) {
          children._ = 1;
        } else {
          children._ = 2;
          vnode.patchFlag |= 1024;
        }
      }
    }
  } else if (isFunction(children)) {
    children = { default: children, _ctx: currentRenderingInstance };
    type = 32;
  } else {
    children = String(children);
    if (shapeFlag & 64) {
      type = 16;
      children = [createTextVNode(children)];
    } else {
      type = 8;
    }
  }
  vnode.children = children;
  vnode.shapeFlag |= type;
}
function mergeProps(...args) {
  const ret = {};
  for (let i = 0; i < args.length; i++) {
    const toMerge = args[i];
    for (const key in toMerge) {
      if (key === "class") {
        if (ret.class !== toMerge.class) {
          ret.class = normalizeClass([ret.class, toMerge.class]);
        }
      } else if (key === "style") {
        ret.style = normalizeStyle([ret.style, toMerge.style]);
      } else if (isOn(key)) {
        const existing = ret[key];
        const incoming = toMerge[key];
        if (incoming && existing !== incoming && !(isArray(existing) && existing.includes(incoming))) {
          ret[key] = existing ? [].concat(existing, incoming) : incoming;
        }
      } else if (key !== "") {
        ret[key] = toMerge[key];
      }
    }
  }
  return ret;
}
function invokeVNodeHook(hook, instance, vnode, prevVNode = null) {
  callWithAsyncErrorHandling(hook, instance, 7, [
    vnode,
    prevVNode
  ]);
}
const emptyAppContext = createAppContext();
let uid = 0;
function createComponentInstance(vnode, parent, suspense) {
  const type = vnode.type;
  const appContext = (parent ? parent.appContext : vnode.appContext) || emptyAppContext;
  const instance = {
    uid: uid++,
    vnode,
    type,
    parent,
    appContext,
    root: null,
    // to be immediately set
    next: null,
    subTree: null,
    // will be set synchronously right after creation
    effect: null,
    update: null,
    // will be set synchronously right after creation
    job: null,
    scope: new EffectScope(
      true
      /* detached */
    ),
    render: null,
    proxy: null,
    exposed: null,
    exposeProxy: null,
    withProxy: null,
    provides: parent ? parent.provides : Object.create(appContext.provides),
    ids: parent ? parent.ids : ["", 0, 0],
    accessCache: null,
    renderCache: [],
    // local resolved assets
    components: null,
    directives: null,
    // resolved props and emits options
    propsOptions: normalizePropsOptions(type, appContext),
    emitsOptions: normalizeEmitsOptions(type, appContext),
    // emit
    emit: null,
    // to be set immediately
    emitted: null,
    // props default value
    propsDefaults: EMPTY_OBJ,
    // inheritAttrs
    inheritAttrs: type.inheritAttrs,
    // state
    ctx: EMPTY_OBJ,
    data: EMPTY_OBJ,
    props: EMPTY_OBJ,
    attrs: EMPTY_OBJ,
    slots: EMPTY_OBJ,
    refs: EMPTY_OBJ,
    setupState: EMPTY_OBJ,
    setupContext: null,
    // suspense related
    suspense,
    suspenseId: suspense ? suspense.pendingId : 0,
    asyncDep: null,
    asyncResolved: false,
    // lifecycle hooks
    // not using enums here because it results in computed properties
    isMounted: false,
    isUnmounted: false,
    isDeactivated: false,
    bc: null,
    c: null,
    bm: null,
    m: null,
    bu: null,
    u: null,
    um: null,
    bum: null,
    da: null,
    a: null,
    rtg: null,
    rtc: null,
    ec: null,
    sp: null
  };
  {
    instance.ctx = { _: instance };
  }
  instance.root = parent ? parent.root : instance;
  instance.emit = emit.bind(null, instance);
  if (vnode.ce) {
    vnode.ce(instance);
  }
  return instance;
}
let currentInstance = null;
let internalSetCurrentInstance;
let setInSSRSetupState;
{
  const g = getGlobalThis();
  const registerGlobalSetter = (key, setter) => {
    let setters;
    if (!(setters = g[key])) setters = g[key] = [];
    setters.push(setter);
    return (v) => {
      if (setters.length > 1) setters.forEach((set) => set(v));
      else setters[0](v);
    };
  };
  internalSetCurrentInstance = registerGlobalSetter(
    `__VUE_INSTANCE_SETTERS__`,
    (v) => currentInstance = v
  );
  setInSSRSetupState = registerGlobalSetter(
    `__VUE_SSR_SETTERS__`,
    (v) => isInSSRComponentSetup = v
  );
}
const setCurrentInstance = (instance) => {
  const prev = currentInstance;
  internalSetCurrentInstance(instance);
  instance.scope.on();
  return () => {
    instance.scope.off();
    internalSetCurrentInstance(prev);
  };
};
const unsetCurrentInstance = () => {
  currentInstance && currentInstance.scope.off();
  internalSetCurrentInstance(null);
};
function isStatefulComponent(instance) {
  return instance.vnode.shapeFlag & 4;
}
let isInSSRComponentSetup = false;
function setupComponent(instance, isSSR = false, optimized = false) {
  isSSR && setInSSRSetupState(isSSR);
  const { props, children } = instance.vnode;
  const isStateful = isStatefulComponent(instance);
  initProps(instance, props, isStateful, isSSR);
  initSlots(instance, children, optimized);
  const setupResult = isStateful ? setupStatefulComponent(instance, isSSR) : void 0;
  isSSR && setInSSRSetupState(false);
  return setupResult;
}
function setupStatefulComponent(instance, isSSR) {
  const Component = instance.type;
  instance.accessCache = /* @__PURE__ */ Object.create(null);
  instance.proxy = new Proxy(instance.ctx, PublicInstanceProxyHandlers);
  const { setup } = Component;
  if (setup) {
    pauseTracking();
    const setupContext = instance.setupContext = setup.length > 1 ? createSetupContext(instance) : null;
    const reset = setCurrentInstance(instance);
    const setupResult = callWithErrorHandling(
      setup,
      instance,
      0,
      [
        instance.props,
        setupContext
      ]
    );
    const isAsyncSetup = isPromise$1(setupResult);
    resetTracking();
    reset();
    if ((isAsyncSetup || instance.sp) && !isAsyncWrapper(instance)) {
      markAsyncBoundary(instance);
    }
    if (isAsyncSetup) {
      setupResult.then(unsetCurrentInstance, unsetCurrentInstance);
      if (isSSR) {
        return setupResult.then((resolvedResult) => {
          handleSetupResult(instance, resolvedResult);
        }).catch((e) => {
          handleError(e, instance, 0);
        });
      } else {
        instance.asyncDep = setupResult;
      }
    } else {
      handleSetupResult(instance, setupResult);
    }
  } else {
    finishComponentSetup(instance);
  }
}
function handleSetupResult(instance, setupResult, isSSR) {
  if (isFunction(setupResult)) {
    if (instance.type.__ssrInlineRender) {
      instance.ssrRender = setupResult;
    } else {
      instance.render = setupResult;
    }
  } else if (isObject$1(setupResult)) {
    instance.setupState = proxyRefs(setupResult);
  } else ;
  finishComponentSetup(instance);
}
function finishComponentSetup(instance, isSSR, skipOptions) {
  const Component = instance.type;
  if (!instance.render) {
    instance.render = Component.render || NOOP;
  }
  {
    const reset = setCurrentInstance(instance);
    pauseTracking();
    try {
      applyOptions(instance);
    } finally {
      resetTracking();
      reset();
    }
  }
}
const attrsProxyHandlers = {
  get(target, key) {
    track(target, "get", "");
    return target[key];
  }
};
function createSetupContext(instance) {
  const expose = (exposed) => {
    instance.exposed = exposed || {};
  };
  {
    return {
      attrs: new Proxy(instance.attrs, attrsProxyHandlers),
      slots: instance.slots,
      emit: instance.emit,
      expose
    };
  }
}
function getComponentPublicInstance(instance) {
  if (instance.exposed) {
    return instance.exposeProxy || (instance.exposeProxy = new Proxy(proxyRefs(markRaw(instance.exposed)), {
      get(target, key) {
        if (key in target) {
          return target[key];
        } else if (key in publicPropertiesMap) {
          return publicPropertiesMap[key](instance);
        }
      },
      has(target, key) {
        return key in target || key in publicPropertiesMap;
      }
    }));
  } else {
    return instance.proxy;
  }
}
const classifyRE = /(?:^|[-_])(\w)/g;
const classify = (str) => str.replace(classifyRE, (c) => c.toUpperCase()).replace(/[-_]/g, "");
function getComponentName(Component, includeInferred = true) {
  return isFunction(Component) ? Component.displayName || Component.name : Component.name || includeInferred && Component.__name;
}
function formatComponentName(instance, Component, isRoot = false) {
  let name = getComponentName(Component);
  if (!name && Component.__file) {
    const match = Component.__file.match(/([^/\\]+)\.\w+$/);
    if (match) {
      name = match[1];
    }
  }
  if (!name && instance && instance.parent) {
    const inferFromRegistry = (registry) => {
      for (const key in registry) {
        if (registry[key] === Component) {
          return key;
        }
      }
    };
    name = inferFromRegistry(
      instance.components || instance.parent.type.components
    ) || inferFromRegistry(instance.appContext.components);
  }
  return name ? classify(name) : isRoot ? `App` : `Anonymous`;
}
function isClassComponent(value) {
  return isFunction(value) && "__vccOpts" in value;
}
const computed = (getterOrOptions, debugOptions) => {
  const c = computed$1(getterOrOptions, debugOptions, isInSSRComponentSetup);
  return c;
};
const version = "3.5.13";
/**
* @vue/runtime-dom v3.5.13
* (c) 2018-present Yuxi (Evan) You and Vue contributors
* @license MIT
**/
let policy = void 0;
const tt = typeof window !== "undefined" && window.trustedTypes;
if (tt) {
  try {
    policy = /* @__PURE__ */ tt.createPolicy("vue", {
      createHTML: (val) => val
    });
  } catch (e) {
  }
}
const unsafeToTrustedHTML = policy ? (val) => policy.createHTML(val) : (val) => val;
const svgNS = "http://www.w3.org/2000/svg";
const mathmlNS = "http://www.w3.org/1998/Math/MathML";
const doc = typeof document !== "undefined" ? document : null;
const templateContainer = doc && /* @__PURE__ */ doc.createElement("template");
const nodeOps = {
  insert: (child, parent, anchor) => {
    parent.insertBefore(child, anchor || null);
  },
  remove: (child) => {
    const parent = child.parentNode;
    if (parent) {
      parent.removeChild(child);
    }
  },
  createElement: (tag, namespace, is, props) => {
    const el = namespace === "svg" ? doc.createElementNS(svgNS, tag) : namespace === "mathml" ? doc.createElementNS(mathmlNS, tag) : is ? doc.createElement(tag, { is }) : doc.createElement(tag);
    if (tag === "select" && props && props.multiple != null) {
      el.setAttribute("multiple", props.multiple);
    }
    return el;
  },
  createText: (text) => doc.createTextNode(text),
  createComment: (text) => doc.createComment(text),
  setText: (node, text) => {
    node.nodeValue = text;
  },
  setElementText: (el, text) => {
    el.textContent = text;
  },
  parentNode: (node) => node.parentNode,
  nextSibling: (node) => node.nextSibling,
  querySelector: (selector) => doc.querySelector(selector),
  setScopeId(el, id) {
    el.setAttribute(id, "");
  },
  // __UNSAFE__
  // Reason: innerHTML.
  // Static content here can only come from compiled templates.
  // As long as the user only uses trusted templates, this is safe.
  insertStaticContent(content, parent, anchor, namespace, start, end) {
    const before = anchor ? anchor.previousSibling : parent.lastChild;
    if (start && (start === end || start.nextSibling)) {
      while (true) {
        parent.insertBefore(start.cloneNode(true), anchor);
        if (start === end || !(start = start.nextSibling)) break;
      }
    } else {
      templateContainer.innerHTML = unsafeToTrustedHTML(
        namespace === "svg" ? `<svg>${content}</svg>` : namespace === "mathml" ? `<math>${content}</math>` : content
      );
      const template2 = templateContainer.content;
      if (namespace === "svg" || namespace === "mathml") {
        const wrapper = template2.firstChild;
        while (wrapper.firstChild) {
          template2.appendChild(wrapper.firstChild);
        }
        template2.removeChild(wrapper);
      }
      parent.insertBefore(template2, anchor);
    }
    return [
      // first
      before ? before.nextSibling : parent.firstChild,
      // last
      anchor ? anchor.previousSibling : parent.lastChild
    ];
  }
};
const vtcKey = Symbol("_vtc");
function patchClass(el, value, isSVG) {
  const transitionClasses = el[vtcKey];
  if (transitionClasses) {
    value = (value ? [value, ...transitionClasses] : [...transitionClasses]).join(" ");
  }
  if (value == null) {
    el.removeAttribute("class");
  } else if (isSVG) {
    el.setAttribute("class", value);
  } else {
    el.className = value;
  }
}
const vShowOriginalDisplay = Symbol("_vod");
const vShowHidden = Symbol("_vsh");
const vShow = {
  beforeMount(el, { value }, { transition }) {
    el[vShowOriginalDisplay] = el.style.display === "none" ? "" : el.style.display;
    if (transition && value) {
      transition.beforeEnter(el);
    } else {
      setDisplay(el, value);
    }
  },
  mounted(el, { value }, { transition }) {
    if (transition && value) {
      transition.enter(el);
    }
  },
  updated(el, { value, oldValue }, { transition }) {
    if (!value === !oldValue) return;
    if (transition) {
      if (value) {
        transition.beforeEnter(el);
        setDisplay(el, true);
        transition.enter(el);
      } else {
        transition.leave(el, () => {
          setDisplay(el, false);
        });
      }
    } else {
      setDisplay(el, value);
    }
  },
  beforeUnmount(el, { value }) {
    setDisplay(el, value);
  }
};
function setDisplay(el, value) {
  el.style.display = value ? el[vShowOriginalDisplay] : "none";
  el[vShowHidden] = !value;
}
const CSS_VAR_TEXT = Symbol("");
const displayRE = /(^|;)\s*display\s*:/;
function patchStyle(el, prev, next) {
  const style = el.style;
  const isCssString = isString(next);
  let hasControlledDisplay = false;
  if (next && !isCssString) {
    if (prev) {
      if (!isString(prev)) {
        for (const key in prev) {
          if (next[key] == null) {
            setStyle(style, key, "");
          }
        }
      } else {
        for (const prevStyle of prev.split(";")) {
          const key = prevStyle.slice(0, prevStyle.indexOf(":")).trim();
          if (next[key] == null) {
            setStyle(style, key, "");
          }
        }
      }
    }
    for (const key in next) {
      if (key === "display") {
        hasControlledDisplay = true;
      }
      setStyle(style, key, next[key]);
    }
  } else {
    if (isCssString) {
      if (prev !== next) {
        const cssVarText = style[CSS_VAR_TEXT];
        if (cssVarText) {
          next += ";" + cssVarText;
        }
        style.cssText = next;
        hasControlledDisplay = displayRE.test(next);
      }
    } else if (prev) {
      el.removeAttribute("style");
    }
  }
  if (vShowOriginalDisplay in el) {
    el[vShowOriginalDisplay] = hasControlledDisplay ? style.display : "";
    if (el[vShowHidden]) {
      style.display = "none";
    }
  }
}
const importantRE = /\s*!important$/;
function setStyle(style, name, val) {
  if (isArray(val)) {
    val.forEach((v) => setStyle(style, name, v));
  } else {
    if (val == null) val = "";
    if (name.startsWith("--")) {
      style.setProperty(name, val);
    } else {
      const prefixed = autoPrefix(style, name);
      if (importantRE.test(val)) {
        style.setProperty(
          hyphenate(prefixed),
          val.replace(importantRE, ""),
          "important"
        );
      } else {
        style[prefixed] = val;
      }
    }
  }
}
const prefixes = ["Webkit", "Moz", "ms"];
const prefixCache = {};
function autoPrefix(style, rawName) {
  const cached = prefixCache[rawName];
  if (cached) {
    return cached;
  }
  let name = camelize(rawName);
  if (name !== "filter" && name in style) {
    return prefixCache[rawName] = name;
  }
  name = capitalize(name);
  for (let i = 0; i < prefixes.length; i++) {
    const prefixed = prefixes[i] + name;
    if (prefixed in style) {
      return prefixCache[rawName] = prefixed;
    }
  }
  return rawName;
}
const xlinkNS = "http://www.w3.org/1999/xlink";
function patchAttr(el, key, value, isSVG, instance, isBoolean = isSpecialBooleanAttr(key)) {
  if (isSVG && key.startsWith("xlink:")) {
    if (value == null) {
      el.removeAttributeNS(xlinkNS, key.slice(6, key.length));
    } else {
      el.setAttributeNS(xlinkNS, key, value);
    }
  } else {
    if (value == null || isBoolean && !includeBooleanAttr(value)) {
      el.removeAttribute(key);
    } else {
      el.setAttribute(
        key,
        isBoolean ? "" : isSymbol(value) ? String(value) : value
      );
    }
  }
}
function patchDOMProp(el, key, value, parentComponent, attrName) {
  if (key === "innerHTML" || key === "textContent") {
    if (value != null) {
      el[key] = key === "innerHTML" ? unsafeToTrustedHTML(value) : value;
    }
    return;
  }
  const tag = el.tagName;
  if (key === "value" && tag !== "PROGRESS" && // custom elements may use _value internally
  !tag.includes("-")) {
    const oldValue = tag === "OPTION" ? el.getAttribute("value") || "" : el.value;
    const newValue = value == null ? (
      // #11647: value should be set as empty string for null and undefined,
      // but <input type="checkbox"> should be set as 'on'.
      el.type === "checkbox" ? "on" : ""
    ) : String(value);
    if (oldValue !== newValue || !("_value" in el)) {
      el.value = newValue;
    }
    if (value == null) {
      el.removeAttribute(key);
    }
    el._value = value;
    return;
  }
  let needRemove = false;
  if (value === "" || value == null) {
    const type = typeof el[key];
    if (type === "boolean") {
      value = includeBooleanAttr(value);
    } else if (value == null && type === "string") {
      value = "";
      needRemove = true;
    } else if (type === "number") {
      value = 0;
      needRemove = true;
    }
  }
  try {
    el[key] = value;
  } catch (e) {
  }
  needRemove && el.removeAttribute(attrName || key);
}
function addEventListener(el, event, handler, options) {
  el.addEventListener(event, handler, options);
}
function removeEventListener(el, event, handler, options) {
  el.removeEventListener(event, handler, options);
}
const veiKey = Symbol("_vei");
function patchEvent(el, rawName, prevValue, nextValue, instance = null) {
  const invokers = el[veiKey] || (el[veiKey] = {});
  const existingInvoker = invokers[rawName];
  if (nextValue && existingInvoker) {
    existingInvoker.value = nextValue;
  } else {
    const [name, options] = parseName(rawName);
    if (nextValue) {
      const invoker = invokers[rawName] = createInvoker(
        nextValue,
        instance
      );
      addEventListener(el, name, invoker, options);
    } else if (existingInvoker) {
      removeEventListener(el, name, existingInvoker, options);
      invokers[rawName] = void 0;
    }
  }
}
const optionsModifierRE = /(?:Once|Passive|Capture)$/;
function parseName(name) {
  let options;
  if (optionsModifierRE.test(name)) {
    options = {};
    let m;
    while (m = name.match(optionsModifierRE)) {
      name = name.slice(0, name.length - m[0].length);
      options[m[0].toLowerCase()] = true;
    }
  }
  const event = name[2] === ":" ? name.slice(3) : hyphenate(name.slice(2));
  return [event, options];
}
let cachedNow = 0;
const p = /* @__PURE__ */ Promise.resolve();
const getNow = () => cachedNow || (p.then(() => cachedNow = 0), cachedNow = Date.now());
function createInvoker(initialValue, instance) {
  const invoker = (e) => {
    if (!e._vts) {
      e._vts = Date.now();
    } else if (e._vts <= invoker.attached) {
      return;
    }
    callWithAsyncErrorHandling(
      patchStopImmediatePropagation(e, invoker.value),
      instance,
      5,
      [e]
    );
  };
  invoker.value = initialValue;
  invoker.attached = getNow();
  return invoker;
}
function patchStopImmediatePropagation(e, value) {
  if (isArray(value)) {
    const originalStop = e.stopImmediatePropagation;
    e.stopImmediatePropagation = () => {
      originalStop.call(e);
      e._stopped = true;
    };
    return value.map(
      (fn) => (e2) => !e2._stopped && fn && fn(e2)
    );
  } else {
    return value;
  }
}
const isNativeOn = (key) => key.charCodeAt(0) === 111 && key.charCodeAt(1) === 110 && // lowercase letter
key.charCodeAt(2) > 96 && key.charCodeAt(2) < 123;
const patchProp = (el, key, prevValue, nextValue, namespace, parentComponent) => {
  const isSVG = namespace === "svg";
  if (key === "class") {
    patchClass(el, nextValue, isSVG);
  } else if (key === "style") {
    patchStyle(el, prevValue, nextValue);
  } else if (isOn(key)) {
    if (!isModelListener(key)) {
      patchEvent(el, key, prevValue, nextValue, parentComponent);
    }
  } else if (key[0] === "." ? (key = key.slice(1), true) : key[0] === "^" ? (key = key.slice(1), false) : shouldSetAsProp(el, key, nextValue, isSVG)) {
    patchDOMProp(el, key, nextValue);
    if (!el.tagName.includes("-") && (key === "value" || key === "checked" || key === "selected")) {
      patchAttr(el, key, nextValue, isSVG, parentComponent, key !== "value");
    }
  } else if (
    // #11081 force set props for possible async custom element
    el._isVueCE && (/[A-Z]/.test(key) || !isString(nextValue))
  ) {
    patchDOMProp(el, camelize(key), nextValue, parentComponent, key);
  } else {
    if (key === "true-value") {
      el._trueValue = nextValue;
    } else if (key === "false-value") {
      el._falseValue = nextValue;
    }
    patchAttr(el, key, nextValue, isSVG);
  }
};
function shouldSetAsProp(el, key, value, isSVG) {
  if (isSVG) {
    if (key === "innerHTML" || key === "textContent") {
      return true;
    }
    if (key in el && isNativeOn(key) && isFunction(value)) {
      return true;
    }
    return false;
  }
  if (key === "spellcheck" || key === "draggable" || key === "translate") {
    return false;
  }
  if (key === "form") {
    return false;
  }
  if (key === "list" && el.tagName === "INPUT") {
    return false;
  }
  if (key === "type" && el.tagName === "TEXTAREA") {
    return false;
  }
  if (key === "width" || key === "height") {
    const tag = el.tagName;
    if (tag === "IMG" || tag === "VIDEO" || tag === "CANVAS" || tag === "SOURCE") {
      return false;
    }
  }
  if (isNativeOn(key) && isString(value)) {
    return false;
  }
  return key in el;
}
const systemModifiers = ["ctrl", "shift", "alt", "meta"];
const modifierGuards = {
  stop: (e) => e.stopPropagation(),
  prevent: (e) => e.preventDefault(),
  self: (e) => e.target !== e.currentTarget,
  ctrl: (e) => !e.ctrlKey,
  shift: (e) => !e.shiftKey,
  alt: (e) => !e.altKey,
  meta: (e) => !e.metaKey,
  left: (e) => "button" in e && e.button !== 0,
  middle: (e) => "button" in e && e.button !== 1,
  right: (e) => "button" in e && e.button !== 2,
  exact: (e, modifiers) => systemModifiers.some((m) => e[`${m}Key`] && !modifiers.includes(m))
};
const withModifiers = (fn, modifiers) => {
  const cache = fn._withMods || (fn._withMods = {});
  const cacheKey = modifiers.join(".");
  return cache[cacheKey] || (cache[cacheKey] = (event, ...args) => {
    for (let i = 0; i < modifiers.length; i++) {
      const guard = modifierGuards[modifiers[i]];
      if (guard && guard(event, modifiers)) return;
    }
    return fn(event, ...args);
  });
};
const rendererOptions = /* @__PURE__ */ extend({ patchProp }, nodeOps);
let renderer;
function ensureRenderer() {
  return renderer || (renderer = createRenderer(rendererOptions));
}
const createApp = (...args) => {
  const app = ensureRenderer().createApp(...args);
  const { mount } = app;
  app.mount = (containerOrSelector) => {
    const container = normalizeContainer(containerOrSelector);
    if (!container) return;
    const component = app._component;
    if (!isFunction(component) && !component.render && !component.template) {
      component.template = container.innerHTML;
    }
    if (container.nodeType === 1) {
      container.textContent = "";
    }
    const proxy = mount(container, false, resolveRootNamespace(container));
    if (container instanceof Element) {
      container.removeAttribute("v-cloak");
      container.setAttribute("data-v-app", "");
    }
    return proxy;
  };
  return app;
};
function resolveRootNamespace(container) {
  if (container instanceof SVGElement) {
    return "svg";
  }
  if (typeof MathMLElement === "function" && container instanceof MathMLElement) {
    return "mathml";
  }
}
function normalizeContainer(container) {
  if (isString(container)) {
    const res = document.querySelector(container);
    return res;
  }
  return container;
}
function getDevtoolsGlobalHook() {
  return getTarget().__VUE_DEVTOOLS_GLOBAL_HOOK__;
}
function getTarget() {
  return typeof navigator !== "undefined" && typeof window !== "undefined" ? window : typeof globalThis !== "undefined" ? globalThis : {};
}
const isProxyAvailable = typeof Proxy === "function";
const HOOK_SETUP = "devtools-plugin:setup";
const HOOK_PLUGIN_SETTINGS_SET = "plugin:settings:set";
let supported;
let perf;
function isPerformanceSupported() {
  var _a;
  if (supported !== void 0) {
    return supported;
  }
  if (typeof window !== "undefined" && window.performance) {
    supported = true;
    perf = window.performance;
  } else if (typeof globalThis !== "undefined" && ((_a = globalThis.perf_hooks) === null || _a === void 0 ? void 0 : _a.performance)) {
    supported = true;
    perf = globalThis.perf_hooks.performance;
  } else {
    supported = false;
  }
  return supported;
}
function now() {
  return isPerformanceSupported() ? perf.now() : Date.now();
}
class ApiProxy {
  constructor(plugin, hook) {
    this.target = null;
    this.targetQueue = [];
    this.onQueue = [];
    this.plugin = plugin;
    this.hook = hook;
    const defaultSettings = {};
    if (plugin.settings) {
      for (const id in plugin.settings) {
        const item = plugin.settings[id];
        defaultSettings[id] = item.defaultValue;
      }
    }
    const localSettingsSaveId = `__vue-devtools-plugin-settings__${plugin.id}`;
    let currentSettings = Object.assign({}, defaultSettings);
    try {
      const raw = localStorage.getItem(localSettingsSaveId);
      const data = JSON.parse(raw);
      Object.assign(currentSettings, data);
    } catch (e) {
    }
    this.fallbacks = {
      getSettings() {
        return currentSettings;
      },
      setSettings(value) {
        try {
          localStorage.setItem(localSettingsSaveId, JSON.stringify(value));
        } catch (e) {
        }
        currentSettings = value;
      },
      now() {
        return now();
      }
    };
    if (hook) {
      hook.on(HOOK_PLUGIN_SETTINGS_SET, (pluginId, value) => {
        if (pluginId === this.plugin.id) {
          this.fallbacks.setSettings(value);
        }
      });
    }
    this.proxiedOn = new Proxy({}, {
      get: (_target, prop) => {
        if (this.target) {
          return this.target.on[prop];
        } else {
          return (...args) => {
            this.onQueue.push({
              method: prop,
              args
            });
          };
        }
      }
    });
    this.proxiedTarget = new Proxy({}, {
      get: (_target, prop) => {
        if (this.target) {
          return this.target[prop];
        } else if (prop === "on") {
          return this.proxiedOn;
        } else if (Object.keys(this.fallbacks).includes(prop)) {
          return (...args) => {
            this.targetQueue.push({
              method: prop,
              args,
              resolve: () => {
              }
            });
            return this.fallbacks[prop](...args);
          };
        } else {
          return (...args) => {
            return new Promise((resolve2) => {
              this.targetQueue.push({
                method: prop,
                args,
                resolve: resolve2
              });
            });
          };
        }
      }
    });
  }
  async setRealTarget(target) {
    this.target = target;
    for (const item of this.onQueue) {
      this.target.on[item.method](...item.args);
    }
    for (const item of this.targetQueue) {
      item.resolve(await this.target[item.method](...item.args));
    }
  }
}
function setupDevtoolsPlugin(pluginDescriptor, setupFn) {
  const descriptor = pluginDescriptor;
  const target = getTarget();
  const hook = getDevtoolsGlobalHook();
  const enableProxy = isProxyAvailable && descriptor.enableEarlyProxy;
  if (hook && (target.__VUE_DEVTOOLS_PLUGIN_API_AVAILABLE__ || !enableProxy)) {
    hook.emit(HOOK_SETUP, pluginDescriptor, setupFn);
  } else {
    const proxy = enableProxy ? new ApiProxy(descriptor, hook) : null;
    const list = target.__VUE_DEVTOOLS_PLUGINS__ = target.__VUE_DEVTOOLS_PLUGINS__ || [];
    list.push({
      pluginDescriptor: descriptor,
      setupFn,
      proxy
    });
    if (proxy) {
      setupFn(proxy.proxiedTarget);
    }
  }
}
/*!
 * vuex v4.1.0
 * (c) 2022 Evan You
 * @license MIT
 */
var storeKey = "store";
function forEachValue(obj, fn) {
  Object.keys(obj).forEach(function(key) {
    return fn(obj[key], key);
  });
}
function isObject(obj) {
  return obj !== null && typeof obj === "object";
}
function isPromise(val) {
  return val && typeof val.then === "function";
}
function partial(fn, arg) {
  return function() {
    return fn(arg);
  };
}
function genericSubscribe(fn, subs, options) {
  if (subs.indexOf(fn) < 0) {
    options && options.prepend ? subs.unshift(fn) : subs.push(fn);
  }
  return function() {
    var i = subs.indexOf(fn);
    if (i > -1) {
      subs.splice(i, 1);
    }
  };
}
function resetStore(store2, hot) {
  store2._actions = /* @__PURE__ */ Object.create(null);
  store2._mutations = /* @__PURE__ */ Object.create(null);
  store2._wrappedGetters = /* @__PURE__ */ Object.create(null);
  store2._modulesNamespaceMap = /* @__PURE__ */ Object.create(null);
  var state = store2.state;
  installModule(store2, state, [], store2._modules.root, true);
  resetStoreState(store2, state, hot);
}
function resetStoreState(store2, state, hot) {
  var oldState = store2._state;
  var oldScope = store2._scope;
  store2.getters = {};
  store2._makeLocalGettersCache = /* @__PURE__ */ Object.create(null);
  var wrappedGetters = store2._wrappedGetters;
  var computedObj = {};
  var computedCache = {};
  var scope = effectScope(true);
  scope.run(function() {
    forEachValue(wrappedGetters, function(fn, key) {
      computedObj[key] = partial(fn, store2);
      computedCache[key] = computed(function() {
        return computedObj[key]();
      });
      Object.defineProperty(store2.getters, key, {
        get: function() {
          return computedCache[key].value;
        },
        enumerable: true
        // for local getters
      });
    });
  });
  store2._state = reactive({
    data: state
  });
  store2._scope = scope;
  if (store2.strict) {
    enableStrictMode(store2);
  }
  if (oldState) {
    if (hot) {
      store2._withCommit(function() {
        oldState.data = null;
      });
    }
  }
  if (oldScope) {
    oldScope.stop();
  }
}
function installModule(store2, rootState, path, module, hot) {
  var isRoot = !path.length;
  var namespace = store2._modules.getNamespace(path);
  if (module.namespaced) {
    if (store2._modulesNamespaceMap[namespace] && false) ;
    store2._modulesNamespaceMap[namespace] = module;
  }
  if (!isRoot && !hot) {
    var parentState = getNestedState(rootState, path.slice(0, -1));
    var moduleName = path[path.length - 1];
    store2._withCommit(function() {
      parentState[moduleName] = module.state;
    });
  }
  var local = module.context = makeLocalContext(store2, namespace, path);
  module.forEachMutation(function(mutation, key) {
    var namespacedType = namespace + key;
    registerMutation(store2, namespacedType, mutation, local);
  });
  module.forEachAction(function(action, key) {
    var type = action.root ? key : namespace + key;
    var handler = action.handler || action;
    registerAction(store2, type, handler, local);
  });
  module.forEachGetter(function(getter, key) {
    var namespacedType = namespace + key;
    registerGetter(store2, namespacedType, getter, local);
  });
  module.forEachChild(function(child, key) {
    installModule(store2, rootState, path.concat(key), child, hot);
  });
}
function makeLocalContext(store2, namespace, path) {
  var noNamespace = namespace === "";
  var local = {
    dispatch: noNamespace ? store2.dispatch : function(_type, _payload, _options) {
      var args = unifyObjectStyle(_type, _payload, _options);
      var payload = args.payload;
      var options = args.options;
      var type = args.type;
      if (!options || !options.root) {
        type = namespace + type;
      }
      return store2.dispatch(type, payload);
    },
    commit: noNamespace ? store2.commit : function(_type, _payload, _options) {
      var args = unifyObjectStyle(_type, _payload, _options);
      var payload = args.payload;
      var options = args.options;
      var type = args.type;
      if (!options || !options.root) {
        type = namespace + type;
      }
      store2.commit(type, payload, options);
    }
  };
  Object.defineProperties(local, {
    getters: {
      get: noNamespace ? function() {
        return store2.getters;
      } : function() {
        return makeLocalGetters(store2, namespace);
      }
    },
    state: {
      get: function() {
        return getNestedState(store2.state, path);
      }
    }
  });
  return local;
}
function makeLocalGetters(store2, namespace) {
  if (!store2._makeLocalGettersCache[namespace]) {
    var gettersProxy = {};
    var splitPos = namespace.length;
    Object.keys(store2.getters).forEach(function(type) {
      if (type.slice(0, splitPos) !== namespace) {
        return;
      }
      var localType = type.slice(splitPos);
      Object.defineProperty(gettersProxy, localType, {
        get: function() {
          return store2.getters[type];
        },
        enumerable: true
      });
    });
    store2._makeLocalGettersCache[namespace] = gettersProxy;
  }
  return store2._makeLocalGettersCache[namespace];
}
function registerMutation(store2, type, handler, local) {
  var entry = store2._mutations[type] || (store2._mutations[type] = []);
  entry.push(function wrappedMutationHandler(payload) {
    handler.call(store2, local.state, payload);
  });
}
function registerAction(store2, type, handler, local) {
  var entry = store2._actions[type] || (store2._actions[type] = []);
  entry.push(function wrappedActionHandler(payload) {
    var res = handler.call(store2, {
      dispatch: local.dispatch,
      commit: local.commit,
      getters: local.getters,
      state: local.state,
      rootGetters: store2.getters,
      rootState: store2.state
    }, payload);
    if (!isPromise(res)) {
      res = Promise.resolve(res);
    }
    if (store2._devtoolHook) {
      return res.catch(function(err) {
        store2._devtoolHook.emit("vuex:error", err);
        throw err;
      });
    } else {
      return res;
    }
  });
}
function registerGetter(store2, type, rawGetter, local) {
  if (store2._wrappedGetters[type]) {
    return;
  }
  store2._wrappedGetters[type] = function wrappedGetter(store22) {
    return rawGetter(
      local.state,
      // local state
      local.getters,
      // local getters
      store22.state,
      // root state
      store22.getters
      // root getters
    );
  };
}
function enableStrictMode(store2) {
  watch(function() {
    return store2._state.data;
  }, function() {
  }, { deep: true, flush: "sync" });
}
function getNestedState(state, path) {
  return path.reduce(function(state2, key) {
    return state2[key];
  }, state);
}
function unifyObjectStyle(type, payload, options) {
  if (isObject(type) && type.type) {
    options = payload;
    payload = type;
    type = type.type;
  }
  return { type, payload, options };
}
var LABEL_VUEX_BINDINGS = "vuex bindings";
var MUTATIONS_LAYER_ID = "vuex:mutations";
var ACTIONS_LAYER_ID = "vuex:actions";
var INSPECTOR_ID = "vuex";
var actionId = 0;
function addDevtools(app, store2) {
  setupDevtoolsPlugin(
    {
      id: "org.vuejs.vuex",
      app,
      label: "Vuex",
      homepage: "https://next.vuex.vuejs.org/",
      logo: "https://vuejs.org/images/icons/favicon-96x96.png",
      packageName: "vuex",
      componentStateTypes: [LABEL_VUEX_BINDINGS]
    },
    function(api) {
      api.addTimelineLayer({
        id: MUTATIONS_LAYER_ID,
        label: "Vuex Mutations",
        color: COLOR_LIME_500
      });
      api.addTimelineLayer({
        id: ACTIONS_LAYER_ID,
        label: "Vuex Actions",
        color: COLOR_LIME_500
      });
      api.addInspector({
        id: INSPECTOR_ID,
        label: "Vuex",
        icon: "storage",
        treeFilterPlaceholder: "Filter stores..."
      });
      api.on.getInspectorTree(function(payload) {
        if (payload.app === app && payload.inspectorId === INSPECTOR_ID) {
          if (payload.filter) {
            var nodes = [];
            flattenStoreForInspectorTree(nodes, store2._modules.root, payload.filter, "");
            payload.rootNodes = nodes;
          } else {
            payload.rootNodes = [
              formatStoreForInspectorTree(store2._modules.root, "")
            ];
          }
        }
      });
      api.on.getInspectorState(function(payload) {
        if (payload.app === app && payload.inspectorId === INSPECTOR_ID) {
          var modulePath = payload.nodeId;
          makeLocalGetters(store2, modulePath);
          payload.state = formatStoreForInspectorState(
            getStoreModule(store2._modules, modulePath),
            modulePath === "root" ? store2.getters : store2._makeLocalGettersCache,
            modulePath
          );
        }
      });
      api.on.editInspectorState(function(payload) {
        if (payload.app === app && payload.inspectorId === INSPECTOR_ID) {
          var modulePath = payload.nodeId;
          var path = payload.path;
          if (modulePath !== "root") {
            path = modulePath.split("/").filter(Boolean).concat(path);
          }
          store2._withCommit(function() {
            payload.set(store2._state.data, path, payload.state.value);
          });
        }
      });
      store2.subscribe(function(mutation, state) {
        var data = {};
        if (mutation.payload) {
          data.payload = mutation.payload;
        }
        data.state = state;
        api.notifyComponentUpdate();
        api.sendInspectorTree(INSPECTOR_ID);
        api.sendInspectorState(INSPECTOR_ID);
        api.addTimelineEvent({
          layerId: MUTATIONS_LAYER_ID,
          event: {
            time: Date.now(),
            title: mutation.type,
            data
          }
        });
      });
      store2.subscribeAction({
        before: function(action, state) {
          var data = {};
          if (action.payload) {
            data.payload = action.payload;
          }
          action._id = actionId++;
          action._time = Date.now();
          data.state = state;
          api.addTimelineEvent({
            layerId: ACTIONS_LAYER_ID,
            event: {
              time: action._time,
              title: action.type,
              groupId: action._id,
              subtitle: "start",
              data
            }
          });
        },
        after: function(action, state) {
          var data = {};
          var duration = Date.now() - action._time;
          data.duration = {
            _custom: {
              type: "duration",
              display: duration + "ms",
              tooltip: "Action duration",
              value: duration
            }
          };
          if (action.payload) {
            data.payload = action.payload;
          }
          data.state = state;
          api.addTimelineEvent({
            layerId: ACTIONS_LAYER_ID,
            event: {
              time: Date.now(),
              title: action.type,
              groupId: action._id,
              subtitle: "end",
              data
            }
          });
        }
      });
    }
  );
}
var COLOR_LIME_500 = 8702998;
var COLOR_DARK = 6710886;
var COLOR_WHITE = 16777215;
var TAG_NAMESPACED = {
  label: "namespaced",
  textColor: COLOR_WHITE,
  backgroundColor: COLOR_DARK
};
function extractNameFromPath(path) {
  return path && path !== "root" ? path.split("/").slice(-2, -1)[0] : "Root";
}
function formatStoreForInspectorTree(module, path) {
  return {
    id: path || "root",
    // all modules end with a `/`, we want the last segment only
    // cart/ -> cart
    // nested/cart/ -> cart
    label: extractNameFromPath(path),
    tags: module.namespaced ? [TAG_NAMESPACED] : [],
    children: Object.keys(module._children).map(
      function(moduleName) {
        return formatStoreForInspectorTree(
          module._children[moduleName],
          path + moduleName + "/"
        );
      }
    )
  };
}
function flattenStoreForInspectorTree(result, module, filter, path) {
  if (path.includes(filter)) {
    result.push({
      id: path || "root",
      label: path.endsWith("/") ? path.slice(0, path.length - 1) : path || "Root",
      tags: module.namespaced ? [TAG_NAMESPACED] : []
    });
  }
  Object.keys(module._children).forEach(function(moduleName) {
    flattenStoreForInspectorTree(result, module._children[moduleName], filter, path + moduleName + "/");
  });
}
function formatStoreForInspectorState(module, getters, path) {
  getters = path === "root" ? getters : getters[path];
  var gettersKeys = Object.keys(getters);
  var storeState = {
    state: Object.keys(module.state).map(function(key) {
      return {
        key,
        editable: true,
        value: module.state[key]
      };
    })
  };
  if (gettersKeys.length) {
    var tree = transformPathsToObjectTree(getters);
    storeState.getters = Object.keys(tree).map(function(key) {
      return {
        key: key.endsWith("/") ? extractNameFromPath(key) : key,
        editable: false,
        value: canThrow(function() {
          return tree[key];
        })
      };
    });
  }
  return storeState;
}
function transformPathsToObjectTree(getters) {
  var result = {};
  Object.keys(getters).forEach(function(key) {
    var path = key.split("/");
    if (path.length > 1) {
      var target = result;
      var leafKey = path.pop();
      path.forEach(function(p2) {
        if (!target[p2]) {
          target[p2] = {
            _custom: {
              value: {},
              display: p2,
              tooltip: "Module",
              abstract: true
            }
          };
        }
        target = target[p2]._custom.value;
      });
      target[leafKey] = canThrow(function() {
        return getters[key];
      });
    } else {
      result[key] = canThrow(function() {
        return getters[key];
      });
    }
  });
  return result;
}
function getStoreModule(moduleMap, path) {
  var names = path.split("/").filter(function(n) {
    return n;
  });
  return names.reduce(
    function(module, moduleName, i) {
      var child = module[moduleName];
      if (!child) {
        throw new Error('Missing module "' + moduleName + '" for path "' + path + '".');
      }
      return i === names.length - 1 ? child : child._children;
    },
    path === "root" ? moduleMap : moduleMap.root._children
  );
}
function canThrow(cb) {
  try {
    return cb();
  } catch (e) {
    return e;
  }
}
var Module = function Module2(rawModule, runtime) {
  this.runtime = runtime;
  this._children = /* @__PURE__ */ Object.create(null);
  this._rawModule = rawModule;
  var rawState = rawModule.state;
  this.state = (typeof rawState === "function" ? rawState() : rawState) || {};
};
var prototypeAccessors$1 = { namespaced: { configurable: true } };
prototypeAccessors$1.namespaced.get = function() {
  return !!this._rawModule.namespaced;
};
Module.prototype.addChild = function addChild(key, module) {
  this._children[key] = module;
};
Module.prototype.removeChild = function removeChild(key) {
  delete this._children[key];
};
Module.prototype.getChild = function getChild(key) {
  return this._children[key];
};
Module.prototype.hasChild = function hasChild(key) {
  return key in this._children;
};
Module.prototype.update = function update(rawModule) {
  this._rawModule.namespaced = rawModule.namespaced;
  if (rawModule.actions) {
    this._rawModule.actions = rawModule.actions;
  }
  if (rawModule.mutations) {
    this._rawModule.mutations = rawModule.mutations;
  }
  if (rawModule.getters) {
    this._rawModule.getters = rawModule.getters;
  }
};
Module.prototype.forEachChild = function forEachChild(fn) {
  forEachValue(this._children, fn);
};
Module.prototype.forEachGetter = function forEachGetter(fn) {
  if (this._rawModule.getters) {
    forEachValue(this._rawModule.getters, fn);
  }
};
Module.prototype.forEachAction = function forEachAction(fn) {
  if (this._rawModule.actions) {
    forEachValue(this._rawModule.actions, fn);
  }
};
Module.prototype.forEachMutation = function forEachMutation(fn) {
  if (this._rawModule.mutations) {
    forEachValue(this._rawModule.mutations, fn);
  }
};
Object.defineProperties(Module.prototype, prototypeAccessors$1);
var ModuleCollection = function ModuleCollection2(rawRootModule) {
  this.register([], rawRootModule, false);
};
ModuleCollection.prototype.get = function get(path) {
  return path.reduce(function(module, key) {
    return module.getChild(key);
  }, this.root);
};
ModuleCollection.prototype.getNamespace = function getNamespace(path) {
  var module = this.root;
  return path.reduce(function(namespace, key) {
    module = module.getChild(key);
    return namespace + (module.namespaced ? key + "/" : "");
  }, "");
};
ModuleCollection.prototype.update = function update$1(rawRootModule) {
  update2([], this.root, rawRootModule);
};
ModuleCollection.prototype.register = function register(path, rawModule, runtime) {
  var this$1$1 = this;
  if (runtime === void 0) runtime = true;
  var newModule = new Module(rawModule, runtime);
  if (path.length === 0) {
    this.root = newModule;
  } else {
    var parent = this.get(path.slice(0, -1));
    parent.addChild(path[path.length - 1], newModule);
  }
  if (rawModule.modules) {
    forEachValue(rawModule.modules, function(rawChildModule, key) {
      this$1$1.register(path.concat(key), rawChildModule, runtime);
    });
  }
};
ModuleCollection.prototype.unregister = function unregister(path) {
  var parent = this.get(path.slice(0, -1));
  var key = path[path.length - 1];
  var child = parent.getChild(key);
  if (!child) {
    return;
  }
  if (!child.runtime) {
    return;
  }
  parent.removeChild(key);
};
ModuleCollection.prototype.isRegistered = function isRegistered(path) {
  var parent = this.get(path.slice(0, -1));
  var key = path[path.length - 1];
  if (parent) {
    return parent.hasChild(key);
  }
  return false;
};
function update2(path, targetModule, newModule) {
  targetModule.update(newModule);
  if (newModule.modules) {
    for (var key in newModule.modules) {
      if (!targetModule.getChild(key)) {
        return;
      }
      update2(
        path.concat(key),
        targetModule.getChild(key),
        newModule.modules[key]
      );
    }
  }
}
function createStore(options) {
  return new Store(options);
}
var Store = function Store2(options) {
  var this$1$1 = this;
  if (options === void 0) options = {};
  var plugins = options.plugins;
  if (plugins === void 0) plugins = [];
  var strict = options.strict;
  if (strict === void 0) strict = false;
  var devtools = options.devtools;
  this._committing = false;
  this._actions = /* @__PURE__ */ Object.create(null);
  this._actionSubscribers = [];
  this._mutations = /* @__PURE__ */ Object.create(null);
  this._wrappedGetters = /* @__PURE__ */ Object.create(null);
  this._modules = new ModuleCollection(options);
  this._modulesNamespaceMap = /* @__PURE__ */ Object.create(null);
  this._subscribers = [];
  this._makeLocalGettersCache = /* @__PURE__ */ Object.create(null);
  this._scope = null;
  this._devtools = devtools;
  var store2 = this;
  var ref = this;
  var dispatch2 = ref.dispatch;
  var commit2 = ref.commit;
  this.dispatch = function boundDispatch(type, payload) {
    return dispatch2.call(store2, type, payload);
  };
  this.commit = function boundCommit(type, payload, options2) {
    return commit2.call(store2, type, payload, options2);
  };
  this.strict = strict;
  var state = this._modules.root.state;
  installModule(this, state, [], this._modules.root);
  resetStoreState(this, state);
  plugins.forEach(function(plugin) {
    return plugin(this$1$1);
  });
};
var prototypeAccessors = { state: { configurable: true } };
Store.prototype.install = function install(app, injectKey) {
  app.provide(injectKey || storeKey, this);
  app.config.globalProperties.$store = this;
  var useDevtools = this._devtools !== void 0 ? this._devtools : false;
  if (useDevtools) {
    addDevtools(app, this);
  }
};
prototypeAccessors.state.get = function() {
  return this._state.data;
};
prototypeAccessors.state.set = function(v) {
};
Store.prototype.commit = function commit(_type, _payload, _options) {
  var this$1$1 = this;
  var ref = unifyObjectStyle(_type, _payload, _options);
  var type = ref.type;
  var payload = ref.payload;
  var mutation = { type, payload };
  var entry = this._mutations[type];
  if (!entry) {
    return;
  }
  this._withCommit(function() {
    entry.forEach(function commitIterator(handler) {
      handler(payload);
    });
  });
  this._subscribers.slice().forEach(function(sub) {
    return sub(mutation, this$1$1.state);
  });
};
Store.prototype.dispatch = function dispatch(_type, _payload) {
  var this$1$1 = this;
  var ref = unifyObjectStyle(_type, _payload);
  var type = ref.type;
  var payload = ref.payload;
  var action = { type, payload };
  var entry = this._actions[type];
  if (!entry) {
    return;
  }
  try {
    this._actionSubscribers.slice().filter(function(sub) {
      return sub.before;
    }).forEach(function(sub) {
      return sub.before(action, this$1$1.state);
    });
  } catch (e) {
  }
  var result = entry.length > 1 ? Promise.all(entry.map(function(handler) {
    return handler(payload);
  })) : entry[0](payload);
  return new Promise(function(resolve2, reject) {
    result.then(function(res) {
      try {
        this$1$1._actionSubscribers.filter(function(sub) {
          return sub.after;
        }).forEach(function(sub) {
          return sub.after(action, this$1$1.state);
        });
      } catch (e) {
      }
      resolve2(res);
    }, function(error) {
      try {
        this$1$1._actionSubscribers.filter(function(sub) {
          return sub.error;
        }).forEach(function(sub) {
          return sub.error(action, this$1$1.state, error);
        });
      } catch (e) {
      }
      reject(error);
    });
  });
};
Store.prototype.subscribe = function subscribe(fn, options) {
  return genericSubscribe(fn, this._subscribers, options);
};
Store.prototype.subscribeAction = function subscribeAction(fn, options) {
  var subs = typeof fn === "function" ? { before: fn } : fn;
  return genericSubscribe(subs, this._actionSubscribers, options);
};
Store.prototype.watch = function watch$12(getter, cb, options) {
  var this$1$1 = this;
  return watch(function() {
    return getter(this$1$1.state, this$1$1.getters);
  }, cb, Object.assign({}, options));
};
Store.prototype.replaceState = function replaceState(state) {
  var this$1$1 = this;
  this._withCommit(function() {
    this$1$1._state.data = state;
  });
};
Store.prototype.registerModule = function registerModule(path, rawModule, options) {
  if (options === void 0) options = {};
  if (typeof path === "string") {
    path = [path];
  }
  this._modules.register(path, rawModule);
  installModule(this, this.state, path, this._modules.get(path), options.preserveState);
  resetStoreState(this, this.state);
};
Store.prototype.unregisterModule = function unregisterModule(path) {
  var this$1$1 = this;
  if (typeof path === "string") {
    path = [path];
  }
  this._modules.unregister(path);
  this._withCommit(function() {
    var parentState = getNestedState(this$1$1.state, path.slice(0, -1));
    delete parentState[path[path.length - 1]];
  });
  resetStore(this);
};
Store.prototype.hasModule = function hasModule(path) {
  if (typeof path === "string") {
    path = [path];
  }
  return this._modules.isRegistered(path);
};
Store.prototype.hotUpdate = function hotUpdate(newOptions) {
  this._modules.update(newOptions);
  resetStore(this, true);
};
Store.prototype._withCommit = function _withCommit(fn) {
  var committing = this._committing;
  this._committing = true;
  fn();
  this._committing = committing;
};
Object.defineProperties(Store.prototype, prototypeAccessors);
var mapGetters = normalizeNamespace(function(namespace, getters) {
  var res = {};
  normalizeMap(getters).forEach(function(ref) {
    var key = ref.key;
    var val = ref.val;
    val = namespace + val;
    res[key] = function mappedGetter() {
      if (namespace && !getModuleByNamespace(this.$store, "mapGetters", namespace)) {
        return;
      }
      return this.$store.getters[val];
    };
    res[key].vuex = true;
  });
  return res;
});
var mapActions = normalizeNamespace(function(namespace, actions) {
  var res = {};
  normalizeMap(actions).forEach(function(ref) {
    var key = ref.key;
    var val = ref.val;
    res[key] = function mappedAction() {
      var args = [], len = arguments.length;
      while (len--) args[len] = arguments[len];
      var dispatch2 = this.$store.dispatch;
      if (namespace) {
        var module = getModuleByNamespace(this.$store, "mapActions", namespace);
        if (!module) {
          return;
        }
        dispatch2 = module.context.dispatch;
      }
      return typeof val === "function" ? val.apply(this, [dispatch2].concat(args)) : dispatch2.apply(this.$store, [val].concat(args));
    };
  });
  return res;
});
function normalizeMap(map) {
  if (!isValidMap(map)) {
    return [];
  }
  return Array.isArray(map) ? map.map(function(key) {
    return { key, val: key };
  }) : Object.keys(map).map(function(key) {
    return { key, val: map[key] };
  });
}
function isValidMap(map) {
  return Array.isArray(map) || isObject(map);
}
function normalizeNamespace(fn) {
  return function(namespace, map) {
    if (typeof namespace !== "string") {
      map = namespace;
      namespace = "";
    } else if (namespace.charAt(namespace.length - 1) !== "/") {
      namespace += "/";
    }
    return fn(namespace, map);
  };
}
function getModuleByNamespace(store2, helper, namespace) {
  var module = store2._modulesNamespaceMap[namespace];
  return module;
}
function roundBackToNearest95(price) {
  price = price / 100;
  let adjustedPrice = price * 0.99;
  const result = Math.floor(adjustedPrice) + 0.95;
  return result * 100;
}
const store$1 = (config) => {
  return createStore({
    state() {
      var _a, _b;
      return {
        config,
        currency: config.currency,
        steps: config.steps,
        currentTab: "",
        currentGrid: 2,
        // default products qty per row on mobile
        addedProductsQuantity: 0,
        productsBox: [],
        productsBoxSeparatedVariants: [],
        discount: 0,
        /**
         * If the currency rate is not equal to 1, we need to round the specific discount amount to the nearest 0.95.
         */
        specificDiscountAmount: +window.Shopify.currency.rate < 1 || +window.Shopify.currency.rate > 1 ? roundBackToNearest95(+((_a = config.steps.find((step) => step.firstAddedProductDiscount)) == null ? void 0 : _a.firstAddedProductDiscount) * +window.Shopify.currency.rate) : +((_b = config.steps.find((step) => step.firstAddedProductDiscount)) == null ? void 0 : _b.firstAddedProductDiscount),
        stepWithSpecificDiscount: config.steps.find((step) => step.firstAddedProductDiscount),
        numberOfSpecificDiscountsUsed: 1,
        productTileWidth: null
      };
    },
    mutations: {
      setPassedStep(state, stepIdx) {
        state.steps.forEach((step, idx) => {
          step.isPassed = idx <= stepIdx ? true : false;
        });
      },
      setCurrentGrid(state, number) {
        state.currentGrid = number;
      },
      setCurrentTab(state, tabPosition) {
        state.steps.forEach((step) => {
          step.tabs.forEach((tab) => {
            tab.current = false;
          });
        });
        state.steps[tabPosition.stepIdx].tabs[tabPosition.tabIdx].current = true;
        state.currentTab = state.steps[tabPosition.stepIdx].tabs[tabPosition.tabIdx];
      },
      applySpecificDiscount(state) {
        state.numberOfSpecificDiscountsUsed--;
      },
      disapplySpecificDiscount(state) {
        state.numberOfSpecificDiscountsUsed++;
      },
      resetSpecificDiscountApplying(state) {
        state.numberOfSpecificDiscountsUsed = 1;
      },
      setProductsBoxSeparatedVariants(state, variants) {
        state.productsBoxSeparatedVariants = variants;
      },
      ADD_TO_BOX(state, variant) {
        const productIndex = state.productsBox.findIndex((p2) => p2.id === variant.id);
        if (productIndex !== -1) {
          state.productsBox[productIndex].addedQty += 1;
          state.addedProductsQuantity++;
        } else {
          state.productsBox.push({ ...variant, addedQty: 1 });
          state.addedProductsQuantity++;
        }
        state.productsBox = [...state.productsBox];
      },
      REMOVE_FROM_BOX(state, variant) {
        const productIndex = state.productsBox.findIndex((p2) => p2.id === variant.id);
        if (productIndex !== -1) {
          state.productsBox[productIndex].addedQty -= 1;
          if (state.productsBox[productIndex].addedQty === 0) {
            state.productsBox.splice(productIndex, 1);
          }
          state.addedProductsQuantity--;
        }
        state.productsBox = [...state.productsBox];
      },
      SET_QUANTITY(state, { variant, quantity }) {
        const productIndex = state.productsBox.findIndex((p2) => p2.id === variant.id);
        if (productIndex !== -1) {
          if (state.productsBox[productIndex].addedQty < quantity) {
            state.addedProductsQuantity -= quantity;
          }
          state.productsBox[productIndex].addedQty = quantity;
          if (state.productsBox[productIndex].addedQty === 0) {
            state.productsBox.splice(productIndex, 1);
            state.addedProductsQuantity -= quantity;
          }
        } else if (quantity > 0) {
          state.productsBox.push({ ...variant, addedQty: quantity });
          state.addedProductsQuantity += quantity;
        }
        state.productsBox = [...state.productsBox];
      },
      // Calculates the final discount as the SUM of per-step applied tiers (capped by maxTotalDiscount).
      calculateDiscount(state) {
        const products = Array.isArray(state.productsBox) ? state.productsBox : [];
        if (products.length === 0) {
          state.discount = 0;
          state.discountableProductsAddedQty = 0;
          return;
        }
        const normalizeKey2 = (s) => String(s ?? "").trim().toLowerCase();
        const resolveVariantStepKey = (v) => normalizeKey2(v.stepKey ?? v.stepName ?? v.tabName ?? v.step ?? v.title ?? v.id);
        const resolveStepKey = (s) => normalizeKey2(s.key ?? s.title ?? s.name ?? s.stepName ?? s.id);
        const steps = Array.isArray(state.steps) ? state.steps : [];
        let eligibleKeys;
        if (Array.isArray(state.globalDiscountStepKeys) && state.globalDiscountStepKeys.length) {
          eligibleKeys = new Set(state.globalDiscountStepKeys.map(normalizeKey2));
        } else {
          const eligibleSteps = steps.filter(
            (s) => (s == null ? void 0 : s.allowDiscount) === true || Array.isArray(s == null ? void 0 : s.discounts) && s.discounts.length > 0
          );
          eligibleKeys = new Set(eligibleSteps.map(resolveStepKey));
        }
        let totalQty = 0;
        for (const v of products) {
          const stepKey = resolveVariantStepKey(v);
          if (!eligibleKeys.has(stepKey)) continue;
          let addQty = Number(v.addedQty) || 0;
          totalQty += addQty;
        }
        state.discountableProductsAddedQty = totalQty;
        const firstStepWithDiscounts = steps.find((s) => Array.isArray(s == null ? void 0 : s.discounts) && s.discounts.length);
        let thresholds = Array.isArray(state.globalDiscounts) && state.globalDiscounts.length ? state.globalDiscounts : (firstStepWithDiscounts == null ? void 0 : firstStepWithDiscounts.discounts) || [];
        const uniq = /* @__PURE__ */ new Map();
        thresholds.forEach((t) => {
          const q = Number(t == null ? void 0 : t.products_quantity) || 0;
          const p2 = Number(t == null ? void 0 : t.discount_percent) || 0;
          const prev = uniq.get(q);
          if (prev == null || p2 > prev) uniq.set(q, p2);
        });
        thresholds = Array.from(uniq.entries()).map(([q, p2]) => ({ products_quantity: q, discount_percent: p2 })).sort((a, b) => a.products_quantity - b.products_quantity);
        let currentPercent = 0;
        for (const t of thresholds) {
          if (totalQty >= t.products_quantity) currentPercent = t.discount_percent;
          else break;
        }
        const cap = Number(state.maxTotalDiscount ?? 100) || 100;
        state.discount = Math.min(cap, currentPercent);
      },
      updateProductTileWidth(state, width) {
        state.productTileWidth = width;
      }
    },
    actions: {
      addToBox({ commit: commit2 }, variant) {
        commit2("ADD_TO_BOX", variant);
      },
      removeFromBox({ commit: commit2 }, variant) {
        commit2("REMOVE_FROM_BOX", variant);
      },
      setQuantity({ commit: commit2 }, payload) {
        commit2("SET_QUANTITY", payload);
      },
      updateProductTileWidth({ commit: commit2 }, width) {
        commit2("updateProductTileWidth", width);
      }
    },
    getters: {
      productTileWidth: (state) => state.productTileWidth,
      getAddedUniqueProductsQty: (state) => {
        var _a;
        return ((_a = state.productsBox) == null ? void 0 : _a.length) || 0;
      },
      getSteps: (state) => {
        return state.steps;
      },
      getCurrentGrid: (state) => {
        return state.currentGrid;
      },
      getCurrentStep: (state) => {
        const passedSteps = state.steps.filter((step) => step.isPassed);
        const currentStep = passedSteps[passedSteps.length - 1] || state.steps[0];
        return currentStep;
      },
      getCurrentTab(state) {
        return state.currentTab;
      },
      getProductsBox: (state) => {
        return state.productsBox;
      },
      getAddedProductsQuantity: (state) => {
        return state.addedProductsQuantity;
      },
      getDiscount: (state) => {
        return state.discount;
      },
      // Returns the "best next tier" hint across steps and guarantees non-negative `remaining_to_next`.
      // Adds `delta_percent` to express how much the final discount will actually increase.
      // Advises the "next best" tier to increase the TOTAL discount in SUM mode.
      // Returns the final total discount after the suggested upgrade in `resultingFinal`
      // (and also places it into `discount_percent` for UI templates that read "{discount}").
      // ---- Vuex getter style function ----
      // Returns the NEXT global threshold (if any) relative to the current total quantity across eligible steps.
      // If the last threshold is already reached, returns an object where products_quantity === current total,
      // so your nextDiscountMessage() yields "0" left instead of a negative number.
      getNextDiscount: (state) => {
        const normalizeKey2 = (s) => String(s ?? "").trim().toLowerCase();
        const resolveVariantStepKey = (v) => normalizeKey2(v.stepKey ?? v.stepName ?? v.tabName ?? v.step ?? v.title ?? v.id);
        const resolveStepKey = (s) => normalizeKey2(s.key ?? s.title ?? s.name ?? s.stepName ?? s.id);
        const steps = Array.isArray(state.steps) ? state.steps : [];
        const products = Array.isArray(state.productsBox) ? state.productsBox : [];
        let eligibleKeys;
        if (Array.isArray(state.globalDiscountStepKeys) && state.globalDiscountStepKeys.length) {
          eligibleKeys = new Set(state.globalDiscountStepKeys.map(normalizeKey2));
        } else {
          const eligibleSteps = steps.filter(
            (s) => (s == null ? void 0 : s.allowDiscount) === true || Array.isArray(s == null ? void 0 : s.discounts) && s.discounts.length > 0
          );
          eligibleKeys = new Set(eligibleSteps.map(resolveStepKey));
        }
        let totalQty = 0;
        for (const v of products) {
          const stepKey = resolveVariantStepKey(v);
          if (!eligibleKeys.has(stepKey)) continue;
          let addQty = Number(v.addedQty) || 0;
          totalQty += addQty;
        }
        const firstStepWithDiscounts = steps.find((s) => Array.isArray(s == null ? void 0 : s.discounts) && s.discounts.length);
        let thresholds = Array.isArray(state.globalDiscounts) && state.globalDiscounts.length ? state.globalDiscounts : (firstStepWithDiscounts == null ? void 0 : firstStepWithDiscounts.discounts) || [];
        const uniq = /* @__PURE__ */ new Map();
        thresholds.forEach((t) => {
          const q = Number(t == null ? void 0 : t.products_quantity) || 0;
          const p2 = Number(t == null ? void 0 : t.discount_percent) || 0;
          const prev = uniq.get(q);
          if (prev == null || p2 > prev) uniq.set(q, p2);
        });
        const sorted = Array.from(uniq.entries()).map(([q, p2]) => ({ products_quantity: q, discount_percent: p2 })).sort((a, b) => a.products_quantity - b.products_quantity);
        if (sorted.length === 0) {
          return {
            discount_percent: 0,
            products_quantity: totalQty,
            // so productLeft = 0 in your message
            theLastOneReached: true
          };
        }
        const cap = Number(state.maxTotalDiscount ?? 100) || 100;
        for (const t of sorted) {
          if (totalQty < t.products_quantity) {
            return {
              discount_percent: Math.min(cap, Number(t.discount_percent) || 0),
              products_quantity: Number(t.products_quantity) || 0,
              theLastOneReached: false
            };
          }
        }
        const last = sorted[sorted.length - 1];
        const lastPct = Math.min(cap, Number(last.discount_percent) || 0);
        return {
          discount_percent: lastPct,
          // IMPORTANT: set products_quantity to current total so productLeft becomes 0
          products_quantity: Number(totalQty) || 0,
          theLastOneReached: true
        };
      },
      getSpecificDiscountAmount: (state) => {
        return state.specificDiscountAmount;
      },
      getNumberOfSpecificDiscountsUsed: (state) => {
        return state.numberOfSpecificDiscountsUsed;
      },
      getVariantsAddedQuantuty: (state) => (variantId) => {
        var _a;
        return state.addedProductsQuantity > 0 ? (_a = state.productsBox.find((v) => v.id === variantId)) == null ? void 0 : _a.addedQty : 0;
      },
      // Cached map for O(1) qty lookup across many QuantitySelector instances.
      // Recomputed only when `state.productsBox` changes (Vuex getter caching).
      getProductsBoxQtyMap: (state) => {
        const map = /* @__PURE__ */ Object.create(null);
        const products = Array.isArray(state.productsBox) ? state.productsBox : [];
        for (const v of products) {
          if (!v || v.id == null) continue;
          map[v.id] = Number(v.addedQty) || 0;
        }
        return map;
      },
      getVariantQtyById: (state, getters) => (variantId) => {
        const map = getters.getProductsBoxQtyMap;
        return Number(map == null ? void 0 : map[variantId]) || 0;
      },
      getAddedDiscountableProductsQty: (state) => {
        if (state.addedProductsQuantity < 1) return 0;
        const normalizeKey2 = (s) => String(s ?? "").trim().toLowerCase();
        const resolveVariantStepKey = (v) => normalizeKey2(v.stepKey ?? v.stepName ?? v.tabName ?? v.step ?? v.title ?? v.id);
        const resolveStepKey = (s) => normalizeKey2(s.key ?? s.title ?? s.name ?? s.stepName ?? s.id);
        const steps = Array.isArray(state.steps) ? state.steps : [];
        const products = Array.isArray(state.productsBox) ? state.productsBox : [];
        if (products.length === 0) return 0;
        let eligibleKeys;
        if (Array.isArray(state.globalDiscountStepKeys) && state.globalDiscountStepKeys.length) {
          eligibleKeys = new Set(state.globalDiscountStepKeys.map(normalizeKey2));
        } else {
          const eligibleSteps = steps.filter(
            (s) => (s == null ? void 0 : s.allowDiscount) === true || Array.isArray(s == null ? void 0 : s.discounts) && s.discounts.length > 0
          );
          eligibleKeys = new Set(eligibleSteps.map(resolveStepKey));
        }
        let total = 0;
        for (const v of products) {
          const stepKey = resolveVariantStepKey(v);
          if (!eligibleKeys.has(stepKey)) continue;
          let qty = Number(v.addedQty) || 0;
          if (qty <= 0) continue;
          total += qty;
        }
        return total;
      },
      getProductsBoxSeparatedVariants: (state) => {
        return state.productsBoxSeparatedVariants;
      },
      // price total / portions qty - discount
      // portions qty = metafield * added qty
      getReductedPricePerPortion: (state) => {
        let priceTotal = 0;
        let portionsTotal = 0;
        state.productsBox.forEach((variant) => {
          const product = variant.product;
          const productPortions = product.portions;
          const variantPortions = variant.portions;
          const portions = +productPortions || +variantPortions;
          if (portions) {
            portionsTotal += portions * +variant.addedQty;
            priceTotal += +variant.price / 100 * +variant.addedQty;
          }
        });
        const pricePerPortion = priceTotal / portionsTotal;
        const pricePerPortionDiscounted = pricePerPortion * (1 - state.discount / 100);
        return pricePerPortionDiscounted;
      }
    }
  });
};
const _export_sfc = (sfc, props) => {
  const target = sfc.__vccOpts || sfc;
  for (const [key, val] of props) {
    target[key] = val;
  }
  return target;
};
const _sfc_main$j = {
  props: [
    "tab",
    "tabIdx",
    "isCurrent",
    "tabsContainer"
  ],
  data() {
    return {};
  },
  methods: {
    setCurrentTab(event) {
      if (!!this.tabsContainer && !!this.tabsContainer.offsetLeft && (!!event.target && !!event.target.offsetLeft)) {
        const x = event.target.offsetLeft - this.tabsContainer.offsetLeft;
        this.tabsContainer.scroll({
          left: x,
          behavior: "smooth"
        });
      }
      this.$emit("setCurrentTab", this.tabIdx);
    },
    title(str) {
      return str.includes("®") ? str.replace("®", `<sup>®</sup>`) : str;
    }
  },
  mounted() {
  }
};
const _hoisted_1$j = ["innerHTML"];
function _sfc_render$j(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("button", {
    onClick: _cache[0] || (_cache[0] = (...args) => $options.setCurrentTab && $options.setCurrentTab(...args)),
    class: normalizeClass(["bb-product-grid__tab", { "bb-product-grid__tab--active": $props.isCurrent }])
  }, [
    createBaseVNode("span", {
      innerHTML: $options.title($props.tab.title)
    }, null, 8, _hoisted_1$j)
  ], 2);
}
const Tab = /* @__PURE__ */ _export_sfc(_sfc_main$j, [["render", _sfc_render$j]]);
const _sfc_main$i = {
  props: {
    src: String,
    additionalClasses: String,
    loading: String,
    alt: String,
    width: Number,
    height: Number,
    useSrcset: Boolean,
    usePlaceholder: Boolean,
    sizes: {
      type: Array,
      default: () => [200, 300, 400, 500, 600, 700, 800, 900, 1e3, 1100, 1200]
    }
  },
  data() {
    return {
      showPlaceholder: true
    };
  },
  methods: {
    addPrefixBeforeExtension(str, prefix) {
      if (!str) return "";
      const [url, query] = str.split("?", 2);
      const dotIndex = url.lastIndexOf(".");
      if (dotIndex === -1) {
        return str;
      }
      const newUrl = url.slice(0, dotIndex) + prefix + url.slice(dotIndex);
      return query ? `${newUrl}?${query}` : newUrl;
    }
  },
  computed: {
    srcset() {
      let result = "";
      this.sizes.forEach((size, idx) => {
        const newSrc = this.addPrefixBeforeExtension(this.src, `_${size}x`);
        const comma = idx === this.sizes.length - 1 ? "" : ",";
        result += `${newSrc} ${size}w${comma}`;
      });
      return result;
    },
    imageStyle() {
      return this.showPlaceholder ? { opacity: 0, height: "1px", width: "1px" } : {};
    }
  }
};
const _hoisted_1$i = ["src", "width", "height", "alt"];
const _hoisted_2$f = ["loading", "src", "srcset", "width", "height", "alt"];
const _hoisted_3$b = ["loading", "src", "width", "height", "alt"];
function _sfc_render$i(_ctx, _cache, $props, $setup, $data, $options) {
  return $props.useSrcset ? (openBlock(), createElementBlock("div", {
    key: 0,
    class: normalizeClass(["bb-image-wrapper", $props.additionalClasses])
  }, [
    withDirectives(createBaseVNode("img", {
      src: $options.addPrefixBeforeExtension($props.src, "_10x"),
      width: $props.width,
      height: $props.height,
      alt: $props.alt,
      class: "bb-image-wrapper__placeholder"
    }, null, 8, _hoisted_1$i), [
      [vShow, $data.showPlaceholder && $props.usePlaceholder]
    ]),
    createBaseVNode("img", {
      loading: $props.loading,
      src: $props.src,
      sizes: "(max-width: 740px) calc(50vw - 24px), calc((min(100vw - 80px, 1520px) - 0px) / 4 - 18px)",
      srcset: $options.srcset,
      width: $props.width,
      height: $props.height,
      alt: $props.alt,
      style: normalizeStyle($options.imageStyle),
      onLoad: _cache[0] || (_cache[0] = () => $data.showPlaceholder = false)
    }, null, 44, _hoisted_2$f)
  ], 2)) : (openBlock(), createElementBlock("div", {
    key: 1,
    class: normalizeClass(["bb-image-wrapper", $props.additionalClasses])
  }, [
    createBaseVNode("img", {
      loading: $props.loading,
      src: $props.src,
      width: $props.width,
      height: $props.height,
      alt: $props.alt
    }, null, 8, _hoisted_3$b)
  ], 2));
}
const Image = /* @__PURE__ */ _export_sfc(_sfc_main$i, [["render", _sfc_render$i]]);
const _sfc_main$h = {
  props: {
    variantIncoming: Object,
    product: Object
  },
  data() {
    return {
      disabled: false
    };
  },
  methods: {
    validateInput() {
    },
    addToBox() {
      if (this.disabled || !this.variant.available) {
        return;
      }
      this.validateInput();
      this.$store.dispatch("addToBox", this.variant);
    },
    increaseQty() {
      this.validateInput();
      this.$store.dispatch("addToBox", this.variant);
    },
    decreaseQty() {
      if (this.addedQty > 0) {
        this.$store.dispatch("removeFromBox", this.variant);
      }
    },
    updateQty() {
      this.validateInput();
      this.$store.dispatch("setQuantity", {
        variant: this.variant,
        quantity: this.addedQty
      });
    },
    getVariantsAddedQuantuty() {
      return this.$store.getters.getVariantQtyById(this.variantIncoming.id);
    }
  },
  computed: {
    variant() {
      return this.variantIncoming;
    },
    maximum() {
      var _a;
      return ((_a = this.variantIncoming) == null ? void 0 : _a.inventory_quantity) || 99;
    },
    addedQty() {
      var _a;
      return this.$store.getters.getVariantQtyById((_a = this.variantIncoming) == null ? void 0 : _a.id);
    },
    minusButtonClass() {
      return {
        "bb-quantity-selector__button--disabled": this.addedQty < 1
      };
    },
    plusButtonClass() {
      return {
        "bb-quantity-selector__button--disabled": this.maximum === this.addedQty
      };
    },
    ...mapGetters({
      addedUniqueProductsQty: "getAddedUniqueProductsQty",
      productsBoxQtyMap: "getProductsBoxQtyMap"
    }),
    disabledATB() {
      const condition = !this.variant.available || this.addedUniqueProductsQty === +this.$store.state.config.maximum_unique_products;
      this.disabled = condition;
      return {
        "disabled": condition
      };
    },
    innerTextATB() {
      return this.variant.available ? this.$store.state.config.translations.addToBox : this.$store.state.config.translations.soldOut;
    }
  }
};
const _hoisted_1$h = {
  key: 0,
  class: "bb-quantity-selector"
};
const _hoisted_2$e = ["value"];
function _sfc_render$h(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock(Fragment, null, [
    withDirectives(createBaseVNode("button", {
      class: normalizeClass(["bb-product__add-to-box", $options.disabledATB]),
      onClick: _cache[0] || (_cache[0] = (...args) => $options.addToBox && $options.addToBox(...args)),
      type: "button"
    }, [
      _cache[3] || (_cache[3] = createBaseVNode("svg", {
        class: "bb-product__add-to-box_svg",
        xmlns: "http://www.w3.org/2000/svg",
        width: "14",
        height: "14",
        viewBox: "0 0 14 14",
        fill: "none"
      }, [
        createBaseVNode("rect", {
          x: "0.75",
          y: "5.75",
          width: "12.5",
          height: "2.5",
          fill: "#FFEE1D"
        }),
        createBaseVNode("rect", {
          x: "8.25",
          y: "0.75",
          width: "12.5",
          height: "2.5",
          transform: "rotate(90 8.25 0.75)",
          fill: "#FFEE1D"
        })
      ], -1)),
      createBaseVNode("span", null, toDisplayString($options.innerTextATB), 1)
    ], 2), [
      [vShow, $options.addedQty === 0]
    ]),
    withDirectives(createBaseVNode("div", {
      class: normalizeClass(["bb-quantity-selector", {
        "bb-quantity-selector--opened": $options.addedQty > 0
      }])
    }, [
      $options.addedQty > 0 ? (openBlock(), createElementBlock("div", _hoisted_1$h, [
        createBaseVNode("button", {
          class: normalizeClass(["bb-quantity-selector__button", $options.minusButtonClass]),
          onClick: _cache[1] || (_cache[1] = (...args) => $options.decreaseQty && $options.decreaseQty(...args))
        }, _cache[4] || (_cache[4] = [
          createBaseVNode("svg", {
            xmlns: "http://www.w3.org/2000/svg",
            width: "10",
            height: "3",
            viewBox: "0 0 10 3",
            fill: "none"
          }, [
            createBaseVNode("rect", {
              y: "0.0566406",
              width: "10",
              height: "2.12539",
              fill: "#FFEE1D"
            })
          ], -1)
        ]), 2),
        createBaseVNode("input", {
          class: "bb-quantity-selector__input",
          type: "number",
          value: $options.addedQty,
          readonly: ""
        }, null, 8, _hoisted_2$e),
        createBaseVNode("button", {
          class: normalizeClass(["bb-quantity-selector__button bb-quantity-selector__button--plus", $options.plusButtonClass]),
          onClick: _cache[2] || (_cache[2] = (...args) => $options.increaseQty && $options.increaseQty(...args))
        }, _cache[5] || (_cache[5] = [
          createBaseVNode("svg", {
            xmlns: "http://www.w3.org/2000/svg",
            width: "10",
            height: "12",
            viewBox: "0 0 10 12",
            fill: "none"
          }, [
            createBaseVNode("rect", {
              y: "5.05688",
              width: "10",
              height: "2.12539",
              fill: "#FFEE1D"
            }),
            createBaseVNode("rect", {
              x: "6",
              y: "0.80542",
              width: "10.627",
              height: "2",
              transform: "rotate(90 6 0.80542)",
              fill: "#FFEE1D"
            })
          ], -1)
        ]), 2)
      ])) : createCommentVNode("", true)
    ], 2), [
      [vShow, $options.addedQty > 0]
    ])
  ], 64);
}
const QuantitySelector = /* @__PURE__ */ _export_sfc(_sfc_main$h, [["render", _sfc_render$h]]);
const _sfc_main$g = {
  props: {
    product: Object
  },
  data() {
    return {
      isActive: false
    };
  },
  methods: {
    showContext() {
      if (window.innerWidth > 768) {
        this.isActive = true;
      }
    },
    hideContext() {
      if (window.innerWidth > 768) {
        this.isActive = false;
      }
    },
    toggleContext() {
      if (window.innerWidth <= 768) {
        this.isActive = !this.isActive;
      }
    },
    closeOnClickOutside(event) {
      if (!this.$el.contains(event.target)) {
        this.isActive = false;
      }
    }
  },
  computed: {
    ...mapGetters(["productTileWidth"])
  },
  mounted() {
    document.addEventListener("click", this.closeOnClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeOnClickOutside);
  }
};
const _hoisted_1$g = { class: "bb-product__mhd-title" };
const _hoisted_2$d = { class: "bb-product__mhd-context_content" };
function _sfc_render$g(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", {
    class: "bb-product__mhd-wrapper",
    onClick: [
      _cache[2] || (_cache[2] = (...args) => $options.toggleContext && $options.toggleContext(...args)),
      _cache[3] || (_cache[3] = withModifiers(() => {
      }, ["stop"]))
    ]
  }, [
    createBaseVNode("div", {
      class: "bb-product__mhd",
      onMouseenter: _cache[0] || (_cache[0] = (...args) => $options.showContext && $options.showContext(...args)),
      onMouseleave: _cache[1] || (_cache[1] = (...args) => $options.hideContext && $options.hideContext(...args))
    }, [
      createBaseVNode("span", _hoisted_1$g, toDisplayString($props.product.mhd.title), 1),
      _cache[4] || (_cache[4] = createBaseVNode("div", { class: "bb-product__mhd-icon-wrapper" }, [
        createBaseVNode("svg", {
          xmlns: "http://www.w3.org/2000/svg",
          width: "23",
          height: "22",
          viewBox: "0 0 23 22",
          fill: "none"
        }, [
          createBaseVNode("path", {
            "fill-rule": "evenodd",
            "clip-rule": "evenodd",
            d: "M11.0588 22C17.134 22 22.0588 17.0751 22.0588 11C22.0588 4.92487 17.134 0 11.0588 0C4.98371 0 0.0588379 4.92487 0.0588379 11C0.0588379 17.0751 4.98371 22 11.0588 22ZM9.47999 5.74861C9.47999 6.68861 10.1081 7.3213 11.0502 7.3213C11.9923 7.3213 12.6379 6.68861 12.6379 5.74861C12.6379 4.79054 11.9923 4.15785 11.0502 4.15785C10.1081 4.15785 9.47999 4.79054 9.47999 5.74861ZM9.72425 8.58669V17.8421H12.3762V8.58669H9.72425Z"
          })
        ])
      ], -1))
    ], 32),
    $data.isActive ? (openBlock(), createElementBlock("p", {
      key: 0,
      class: "bb-product__mhd-context",
      style: normalizeStyle(`width: calc(${_ctx.productTileWidth}px - 20px);`)
    }, [
      _cache[5] || (_cache[5] = createBaseVNode("span", { class: "bb-product__mhd-triangle" }, null, -1)),
      createBaseVNode("span", _hoisted_2$d, toDisplayString($props.product.mhd.description), 1)
    ], 4)) : createCommentVNode("", true)
  ]);
}
const MHD = /* @__PURE__ */ _export_sfc(_sfc_main$g, [["render", _sfc_render$g]]);
const _sfc_main$f = {
  props: {
    variant: Object,
    titleFromVariant: Boolean
  },
  data() {
    var _a;
    return {
      product: (_a = this.variant) == null ? void 0 : _a.product
    };
  },
  methods: {
    formatPrice(price) {
      if (!price || price <= 0) {
        return "0.00";
      }
      const isUKStore = Shopify.currency.active === "GBP" || Shopify.locale === "en-GB";
      const formattedPrice = (price / 100).toFixed(2);
      if (isUKStore) {
        return `£ ${formattedPrice}`;
      }
      return `${formattedPrice.split(".").join(",")} ${this.$store.state.currency}`;
    }
  },
  computed: {
    price() {
      var _a, _b, _c, _d, _e;
      const discount = this.discount;
      let discountedPrice = ((_a = this.variant.bundleInfo) == null ? void 0 : _a.length) ? 0 : (_b = this.variant) == null ? void 0 : _b.price;
      let variantPrice = ((_c = this.variant.bundleInfo) == null ? void 0 : _c.length) ? 0 : (_d = this.variant) == null ? void 0 : _d.price;
      if ((_e = this.variant.bundleInfo) == null ? void 0 : _e.length) {
        const bundlePrice = this.variant.bundleInfo.reduce((total, item) => {
          return total + item.price * 100 * item.quantity_in_bundle;
        }, 0);
        variantPrice += bundlePrice;
        discountedPrice += bundlePrice;
      }
      if (this.numberOfSpecificDiscountsUsed > 0 && this.variant.specificDiscountable && this.atLeastOneProductFromTheFirstStep) {
        discountedPrice -= this.specificDiscountAmount;
      } else {
        discountedPrice = discount ? discountedPrice - variantPrice * (discount / 100) : discountedPrice;
      }
      return {
        formatted: this.formatPrice(discountedPrice),
        unformatted: discountedPrice,
        specificDiscount: this.specificDiscountAmount
      };
    },
    priceCompare() {
      var _a, _b, _c;
      let result = ((_a = this.variant.bundleInfo) == null ? void 0 : _a.length) ? 0 : (_b = this.variant) == null ? void 0 : _b.price;
      if ((_c = this.variant.bundleInfo) == null ? void 0 : _c.length) {
        const compareAtPrice = this.variant.bundleInfo.reduce((total, item) => {
          return total + item.price * 100 * item.quantity_in_bundle;
        }, 0);
        result += compareAtPrice;
      }
      return {
        formatted: this.formatPrice(result),
        unformatted: result
      };
    },
    atLeastOneProductFromTheFirstStep() {
      return this.productsBoxWithVariants.find((v) => v.stepName === this.steps[0].title);
    },
    featuredImageSrc() {
      var _a, _b, _c, _d, _e, _f, _g;
      let image = (_b = (_a = this.variant) == null ? void 0 : _a.featured_image) == null ? void 0 : _b.src;
      if (window.Shopify.locale === "nl" && ((_d = (_c = this.product) == null ? void 0 : _c.images) == null ? void 0 : _d[0])) {
        image = (_g = (_f = (_e = this.variant) == null ? void 0 : _e.product) == null ? void 0 : _f.images) == null ? void 0 : _g[0];
      }
      if (!image) {
        console.log("No featured image found for variant", this.variant);
      }
      return image;
    },
    ...mapGetters({
      discount: "getDiscount",
      specificDiscountAmount: "getSpecificDiscountAmount",
      numberOfSpecificDiscountsUsed: "getNumberOfSpecificDiscountsUsed",
      productsBoxWithVariants: "getProductsBox",
      steps: "getSteps"
    })
  },
  components: {
    Image,
    QuantitySelector,
    MHD
  }
};
const _hoisted_1$f = {
  key: 0,
  class: "bb-product__variant"
};
const _hoisted_2$c = { class: "bb-product__variant_header" };
const _hoisted_3$a = { class: "bb-product__title_additional" };
const _hoisted_4$8 = ["href"];
const _hoisted_5$6 = { class: "bb-product__title_additional" };
const _hoisted_6$6 = {
  key: 1,
  class: "bb-product__title-flavour"
};
const _hoisted_7$5 = {
  key: 2,
  class: "bb-product__title-flavour"
};
const _hoisted_8$5 = { class: "bb-product__price" };
const _hoisted_9$4 = {
  key: 0,
  class: "bb-product__price-item bb-product__price--normal"
};
const _hoisted_10$4 = {
  key: 1,
  class: "bb-product__price-item bb-product__price--free"
};
const _hoisted_11$4 = {
  key: 2,
  class: "bb-product__price-item bb-product__price--compare"
};
const _hoisted_12$4 = { class: "bb-product__controls" };
function _sfc_render$f(_ctx, _cache, $props, $setup, $data, $options) {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k, _l, _m, _n, _o, _p, _q, _r;
  const _component_MHD = resolveComponent("MHD");
  const _component_Image = resolveComponent("Image");
  const _component_QuantitySelector = resolveComponent("QuantitySelector");
  return $props.variant ? (openBlock(), createElementBlock("div", _hoisted_1$f, [
    createBaseVNode("div", _hoisted_2$c, [
      ((_c = (_b = (_a = $props.variant) == null ? void 0 : _a.product) == null ? void 0 : _b.mhd) == null ? void 0 : _c.description) && ((_f = (_e = (_d = $props.variant) == null ? void 0 : _d.product) == null ? void 0 : _e.mhd) == null ? void 0 : _f.title) ? (openBlock(), createBlock(_component_MHD, {
        key: 0,
        product: $props.variant.product
      }, null, 8, ["product"])) : createCommentVNode("", true),
      createVNode(_component_Image, {
        src: $options.featuredImageSrc,
        additionalClasses: "bb-product__image-wrapper",
        alt: `${((_h = (_g = $props.variant) == null ? void 0 : _g.featured_image) == null ? void 0 : _h.alt) || ((_i = $props.variant) == null ? void 0 : _i.productTitle)} : ${$props.variant.productTitle} : ${$props.variant.id}`,
        width: 200,
        height: 200,
        useSrcset: true,
        usePlaceholder: true,
        loading: "lazy"
      }, null, 8, ["src", "alt"]),
      createBaseVNode("span", _hoisted_3$a, toDisplayString((_j = $props.variant.product) == null ? void 0 : _j.type), 1),
      createBaseVNode("a", {
        class: "bb-product__title",
        href: "/products/" + ((_l = (_k = $props.variant) == null ? void 0 : _k.product) == null ? void 0 : _l.handle)
      }, [
        createBaseVNode("span", null, toDisplayString($props.variant.productTitle), 1)
      ], 8, _hoisted_4$8),
      createBaseVNode("span", _hoisted_5$6, toDisplayString($props.variant.title), 1),
      ((_m = $props.variant.product) == null ? void 0 : _m.flavour) ? (openBlock(), createElementBlock("span", _hoisted_6$6, toDisplayString((_n = $props.variant.product) == null ? void 0 : _n.flavour), 1)) : createCommentVNode("", true),
      ((_o = $props.variant) == null ? void 0 : _o.shakerCapacity) || ((_q = (_p = $props.variant) == null ? void 0 : _p.product) == null ? void 0 : _q.shakerCapacity) ? (openBlock(), createElementBlock("span", _hoisted_7$5, toDisplayString($props.titleFromVariant ? $props.variant.title === "Default Title" ? $props.variant.productTitle : $props.variant.title : (_r = $props.variant) == null ? void 0 : _r.productTitle), 1)) : createCommentVNode("", true)
    ]),
    createBaseVNode("div", _hoisted_8$5, [
      $options.price.unformatted > 0 ? (openBlock(), createElementBlock("span", _hoisted_9$4, toDisplayString($options.price.formatted), 1)) : (openBlock(), createElementBlock("span", _hoisted_10$4, toDisplayString(_ctx.$store.state.config.translations.free), 1)),
      $options.priceCompare.unformatted > $options.price.unformatted ? (openBlock(), createElementBlock("del", _hoisted_11$4, toDisplayString($options.priceCompare.formatted), 1)) : createCommentVNode("", true)
    ]),
    createBaseVNode("div", _hoisted_12$4, [
      createVNode(_component_QuantitySelector, {
        variantIncoming: $props.variant,
        product: $data.product
      }, null, 8, ["variantIncoming", "product"])
    ])
  ])) : createCommentVNode("", true);
}
const Variant = /* @__PURE__ */ _export_sfc(_sfc_main$f, [["render", _sfc_render$f]]);
const _sfc_main$e = {
  props: {
    isLastRendered: Boolean,
    variant: Object,
    step: Object,
    product: Object,
    useFirstAvailabeVariant: Boolean,
    separateVariants: Boolean
  },
  data() {
    var _a;
    return {
      selectedVariant: this.getFirstAvailableVariant() || ((_a = this.product) == null ? void 0 : _a.variants[0])
    };
  },
  methods: {
    ...mapActions(["updateProductTileWidth"]),
    getFirstAvailableVariant() {
      var _a, _b, _c;
      if (!this.product) {
        return;
      }
      return ((_a = this.product) == null ? void 0 : _a.variants.length) === 1 ? (_b = this.product) == null ? void 0 : _b.variants[0] : (_c = this.product) == null ? void 0 : _c.variants.find((v) => {
        return v.available;
      });
    },
    updateTileWidth() {
      const newWidth = this.$el.offsetWidth;
      this.updateProductTileWidth(newWidth);
    }
  },
  mounted() {
    if (this.isLastRendered) {
      this.$emit("theLastOneMounted");
    }
    window.addEventListener("resize", () => {
      this.updateTileWidth();
    });
    this.updateTileWidth();
  },
  components: {
    Image,
    Variant
  }
};
const _hoisted_1$e = {
  key: 0,
  class: "bb-product"
};
const _hoisted_2$b = {
  key: 1,
  class: "bb-product"
};
function _sfc_render$e(_ctx, _cache, $props, $setup, $data, $options) {
  var _a;
  const _component_Variant = resolveComponent("Variant");
  return $options.getFirstAvailableVariant() && ((_a = $props.product) == null ? void 0 : _a.available) && $props.useFirstAvailabeVariant ? (openBlock(), createElementBlock("div", _hoisted_1$e, [
    createVNode(_component_Variant, {
      product: $props.product,
      variant: $data.selectedVariant
    }, null, 8, ["product", "variant"])
  ])) : $props.separateVariants && !$props.useFirstAvailabeVariant ? (openBlock(), createElementBlock("div", _hoisted_2$b, [
    createVNode(_component_Variant, {
      variant: $props.variant,
      titleFromVariant: $props.step.number !== 1
    }, null, 8, ["variant", "titleFromVariant"])
  ])) : createCommentVNode("", true);
}
const Product = /* @__PURE__ */ _export_sfc(_sfc_main$e, [["render", _sfc_render$e]]);
const _sfc_main$d = {
  props: {
    isLastRendered: Boolean
  },
  mounted() {
    if (this.isLastRendered) {
      this.$emit("theLastOneMounted");
    }
  }
};
const _hoisted_1$d = { class: "bb-product--placeholder bb-product" };
function _sfc_render$d(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", _hoisted_1$d, _cache[0] || (_cache[0] = [
    createBaseVNode("div", { class: "bb-product__variant" }, [
      createBaseVNode("div", { class: "bb-product__image-placeholder" }),
      createBaseVNode("div", { class: "bb-product__controls" })
    ], -1)
  ]));
}
const ProductPlaceholder = /* @__PURE__ */ _export_sfc(_sfc_main$d, [["render", _sfc_render$d]]);
const _sfc_main$c = {
  props: {
    tab: Object,
    step: Object
  },
  data() {
    return {
      variantsPerRow: 0,
      variantsToShow: []
    };
  },
  mounted() {
  },
  updated() {
  },
  beforeDestroy() {
  },
  methods: {
    getHeadingId() {
      return `bb-product-grid__heading--${this.tab.title.replace(/\s+/g, "-").toLowerCase()}`;
    }
    // calculateVariantsPerRow() {
    //   if (!this.isShown) {
    //     this.variantsPerRow = 0;
    //     return;
    //   }
    //   if (window.innerWidth <= 768) {
    //     if (this.currentGrid > 0) {
    //       this.variantsPerRow = this.currentGrid;
    //     } else if (window.innerWidth < 375) {
    //       this.variantsPerRow = 1;
    //     }
    //     return;
    //   }
    //   const container = this.$el.offsetWidth ? this.$el : this.$el.parentNode;
    //   const containerWidth = container ? container.getBoundingClientRect()?.width : 0;
    //   const firstVariantEl = this.$refs.variants && this.$refs.variants.length > 0 ? this.$refs.variants[0].$el : null;
    //   const itemWidth = firstVariantEl ? firstVariantEl.getBoundingClientRect()?.width : this.$refs.productPlaceholder?.$el?.getBoundingClientRect().width;
    //   const gridGap = 30;
    //   const containerStyles = window.getComputedStyle(container);
    //   const paddingLeft = parseFloat(containerStyles.paddingLeft);
    //   const paddingRight = parseFloat(containerStyles.paddingRight);
    //   const res = Math.floor((containerWidth - (paddingLeft + paddingRight) + gridGap) / (itemWidth + gridGap));
    //   this.variantsPerRow = res < 0 ? this.variantsPerRow : res;
    // },
    // isInViewport(element) {
    //   const rect = element.getBoundingClientRect();
    //   return element ? (
    //     rect.top >= 0 &&
    //     rect.left >= 0 &&
    //     rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    //     rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    //   ) : false;
    // },
    // calculateVariantsToShow(initialValue = 0) {
    //   if (!this.isShown || !this.tab.variants || !this.tab.variants.length || this.tab.variants.length < 1) {
    //     return;
    //   }
    //   if (initialValue > 0) {
    //     this.variantsToShow = this.tab.variants.slice(0, this.variantsPerRow * 2);
    //   }
    //   if (!this.$refs.variants) {
    //     return;
    //   }
    //   const variantsRefsLength = this.$refs.variants.length;
    //   const variantElementIdx = variantsRefsLength - this.variantsPerRow;
    //   const variantElement = variantElementIdx < variantsRefsLength ? this.$refs.variants[variantElementIdx]?.$el : null;
    //   if (!variantElement) {
    //     return;
    //   }
    //   if (this.isInViewport(variantElement)) {
    //     const fromIdx = variantsRefsLength - 1;
    //     const toIdx = variantsRefsLength - 1 + this.variantsPerRow;
    //     const additionalVariants = this.tab.variants.slice(fromIdx, toIdx);
    //     additionalVariants.forEach(v => {
    //       const alreadyAddedVariant = this.variantsToShow.find(variant => variant?.id === v?.id);
    //       if (!alreadyAddedVariant) {
    //         this.variantsToShow.push(v);
    //       }
    //     });
    //   }
    // },
    // theLastOneMounted() {
    //   this.calculateVariantsToShow();
    // }
  },
  computed: {
    ...mapGetters({
      currentGrid: "getCurrentGrid",
      currentTab: "getCurrentTab"
    }),
    isShown() {
      return this.currentTab.title === this.tab.title;
    },
    gridClass() {
      return {
        "bb-product-grid__container--2_per_row": this.currentGrid === 2,
        "bb-product-grid__container--3_per_row": this.currentGrid === 3
      };
    }
  },
  watch: {
    step(newStep, old) {
      if (this.step.title === newStep.title) ;
    }
  },
  components: {
    Product,
    ProductPlaceholder
  }
};
const _hoisted_1$c = { class: "bb-product-grid__container_wrapper" };
function _sfc_render$c(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_Product = resolveComponent("Product");
  return openBlock(), createElementBlock("div", _hoisted_1$c, [
    withDirectives(createBaseVNode("div", {
      class: normalizeClass(["bb-product-grid__container", $options.gridClass])
    }, [
      (openBlock(true), createElementBlock(Fragment, null, renderList($props.tab.variants, (variant, idx) => {
        var _a;
        return withDirectives((openBlock(), createBlock(_component_Product, {
          ref_for: true,
          ref: "variants",
          key: idx,
          variant,
          separateVariants: true,
          step: $props.step,
          onTheLastOneMounted: _ctx.theLastOneMounted
        }, null, 8, ["variant", "step", "onTheLastOneMounted"])), [
          [vShow, !((_a = variant == null ? void 0 : variant.title) == null ? void 0 : _a.includes("Reward"))]
        ]);
      }), 128))
    ], 2), [
      [vShow, $options.isShown]
    ])
  ]);
}
const ProductsContainer = /* @__PURE__ */ _export_sfc(_sfc_main$c, [["render", _sfc_render$c]]);
const _sfc_main$b = {
  props: ["tabs", "currentStep"],
  data() {
    return {};
  },
  methods: {
    initCurrentTab() {
      this.$store.commit("setCurrentTab", {
        stepIdx: this.currentStep.number - 1,
        tabIdx: this.getInitalTabFromUrl()
      });
    },
    setCurrentTab(clickedTabIdx) {
      this.$store.commit("setCurrentTab", {
        stepIdx: this.currentStep.number - 1,
        tabIdx: clickedTabIdx
      });
    },
    getInitalTabFromUrl() {
      var _a;
      const url = window.location.search;
      const urlParams = new URLSearchParams(url);
      const tabFromUrlParams = Number(urlParams.get("tab")) || 0;
      const tabsLength = ((_a = this.tabs) == null ? void 0 : _a.length) ?? 0;
      const isTabFromUrlParamsValid = tabFromUrlParams > 0 && tabFromUrlParams <= tabsLength;
      return this.currentStep.number === 1 && isTabFromUrlParamsValid ? tabFromUrlParams - 1 : 0;
    }
  },
  mounted() {
    this.initCurrentTab();
  },
  computed: {
    ...mapGetters({
      currentTab: "getCurrentTab"
    }),
    currentTabIndex() {
      const index = this.tabs.findIndex((tab) => tab.current);
      return index === -1 ? 0 : index;
    }
  },
  watch: {
    currentStep(newV, old) {
      this.setCurrentTab(0);
    }
  },
  components: {
    Tab,
    ProductsContainer
  }
};
const _hoisted_1$b = { class: "bb-product-grid" };
const _hoisted_2$a = ["innerHTML"];
const _hoisted_3$9 = { class: "bb-product-grid__header" };
const _hoisted_4$7 = { class: "bb-product-grid__body" };
function _sfc_render$b(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_Tab = resolveComponent("Tab");
  const _component_ProductsContainer = resolveComponent("ProductsContainer");
  return openBlock(), createElementBlock("div", _hoisted_1$b, [
    createBaseVNode("div", {
      class: "bb-product-grid__heading hidden-on-desktop",
      innerHTML: $props.currentStep.heading
    }, null, 8, _hoisted_2$a),
    createBaseVNode("div", _hoisted_3$9, [
      createBaseVNode("div", {
        class: normalizeClass(["bb-product-grid__tabs", $props.tabs.length > 4 ? `bb-product-grid-big` : "bb-product-grid-small"]),
        ref: "tabsContainer"
      }, [
        (openBlock(true), createElementBlock(Fragment, null, renderList($props.tabs, (tab, idx) => {
          return openBlock(), createBlock(_component_Tab, {
            onSetCurrentTab: $options.setCurrentTab,
            key: idx,
            tab,
            tabIdx: idx,
            isCurrent: idx === $options.currentTabIndex,
            tabsContainer: _ctx.$refs.tabsContainer
          }, null, 8, ["onSetCurrentTab", "tab", "tabIdx", "isCurrent", "tabsContainer"]);
        }), 128))
      ], 2)
    ]),
    createBaseVNode("div", _hoisted_4$7, [
      (openBlock(true), createElementBlock(Fragment, null, renderList($props.tabs, (tab, idx) => {
        return openBlock(), createBlock(_component_ProductsContainer, {
          key: idx,
          tab,
          step: $props.currentStep
        }, null, 8, ["tab", "step"]);
      }), 128))
    ])
  ]);
}
const ProductGrid = /* @__PURE__ */ _export_sfc(_sfc_main$b, [["render", _sfc_render$b]]);
const _sfc_main$a = {
  props: [
    "number",
    "title",
    "isPassed",
    "addDivider",
    "filledDivider"
  ],
  data() {
    return {};
  },
  methods: {
    passTheStep() {
      this.$store.commit("setPassedStep", this.number - 1);
    }
  },
  mounted() {
  },
  computed: {},
  watch: {},
  components: {}
};
const _hoisted_1$a = { class: "bb-step__header" };
const _hoisted_2$9 = { class: "bb-step__header_number--wrapper" };
const _hoisted_3$8 = { class: "bb-step__header_number" };
const _hoisted_4$6 = { class: "bb-step__headr_title" };
function _sfc_render$a(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", {
    class: normalizeClass(["bb-step__wrapper", { "bb-step__wrapper--active": $props.isPassed }])
  }, [
    createBaseVNode("button", {
      onClick: _cache[0] || (_cache[0] = (...args) => $options.passTheStep && $options.passTheStep(...args)),
      class: normalizeClass(["bb-step", { "bb-step--active": $props.isPassed }])
    }, [
      createBaseVNode("div", _hoisted_1$a, [
        createBaseVNode("div", _hoisted_2$9, [
          createBaseVNode("span", _hoisted_3$8, toDisplayString($props.number) + ". ", 1)
        ]),
        createBaseVNode("span", _hoisted_4$6, toDisplayString($props.title), 1)
      ])
    ], 2),
    $props.addDivider ? (openBlock(), createElementBlock("div", {
      key: 0,
      class: normalizeClass(["divider", {
        "active": $props.isPassed,
        "filled": $props.filledDivider
      }])
    }, null, 2)) : createCommentVNode("", true)
  ], 2);
}
const Step = /* @__PURE__ */ _export_sfc(_sfc_main$a, [["render", _sfc_render$a]]);
const BREAKPOINT = 1240;
const _sfc_main$9 = {
  props: [
    "bbHeaderCoords"
  ],
  data() {
    return {
      expandedView: false,
      productsPerRow: window.innerWidth <= BREAKPOINT ? 5 : 5,
      specificDiscountedVariantIndex: "",
      initedFromSessionStorage: false,
      proccessingAddToCart: false,
      isVisible: window.innerWidth >= 425
    };
  },
  methods: {
    onScroll() {
      this.isVisible = true;
      window.removeEventListener("scroll", this.onScroll);
    },
    goToNextStep() {
      if (this.isDisabled) {
        return;
      }
      this.$store.commit("setPassedStep", this.currentStep.number);
      window.scrollTo({ top: this.bbHeaderCoords.top - this.bbHeaderCoords.height * 2, behavior: "smooth" });
    },
    async updateCart() {
      const cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer && typeof cartDrawer.setLoadingStatus === "function") {
        cartDrawer.setLoadingStatus(true);
        cartDrawer.emit("start-update", {});
      }
      if (cartDrawer && typeof cartDrawer.update === "function") {
        setTimeout(async () => {
          await cartDrawer.update();
          cartDrawer.setLoadingStatus(false);
        }, 1800);
      }
    },
    getTooManyProductsToastMessage() {
      var _a, _b;
      const messageFromSchema = (_b = (_a = this.$store.state.config) == null ? void 0 : _a.section) == null ? void 0 : _b.too_many_products_toast_message;
      if (typeof messageFromSchema === "string" && messageFromSchema.trim()) {
        return messageFromSchema;
      }
      return "<p>You added too many products to this bundle. Please remove some items and try again.</p>";
    },
    async showGlobalWarningToast(htmlMessage, durationMs = 6e3) {
      try {
        if (typeof customElements === "undefined" || typeof customElements.whenDefined !== "function") {
          return;
        }
        await customElements.whenDefined("notification-component");
        const notification = document.querySelector("#GlobalNotification");
        if (!notification || typeof notification.show !== "function") {
          return;
        }
        notification.show(htmlMessage, "warning", durationMs);
      } catch (_) {
      }
    },
    async fetchCartJson() {
      const response = await fetch("/cart.js", {
        method: "GET",
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch cart state (HTTP ${response.status})`);
      }
      return response.json();
    },
    findBundleLineItem(cart, { parentVariantId, orderTimestamp }) {
      const items = Array.isArray(cart == null ? void 0 : cart.items) ? cart.items : [];
      const ts = Number(orderTimestamp);
      return items.find((it) => {
        if (!it) return false;
        if (Number(it.variant_id) !== Number(parentVariantId)) return false;
        const props = it.properties && typeof it.properties === "object" ? it.properties : {};
        return Number(props._order_timestamp) === ts;
      }) || null;
    },
    isZeroPriceLineItem(lineItem) {
      if (!lineItem) return false;
      const finalLinePrice = Number(lineItem.final_line_price);
      const linePrice = Number(lineItem.line_price);
      return Number.isFinite(finalLinePrice) && finalLinePrice === 0 || Number.isFinite(linePrice) && linePrice === 0;
    },
    /**
     * Attempts to read the just-added line item from Shopify /cart/add.js response.
     * Shopify can return either `{ items: [...] }` or `{ item: {...} }` depending on request payload/version.
     *
     * We prefer matching by `_order_timestamp` because the same variant can already exist in the cart.
     */
    getAddedLineItemFromAddResponse(addResponseJson, { parentVariantId, orderTimestamp }) {
      const ts = Number(orderTimestamp);
      const candidates = [];
      if (addResponseJson && typeof addResponseJson === "object") {
        if (Array.isArray(addResponseJson.items)) {
          candidates.push(...addResponseJson.items);
        } else if (addResponseJson.item && typeof addResponseJson.item === "object") {
          candidates.push(addResponseJson.item);
        }
      }
      const match = candidates.find((it) => {
        if (!it) return false;
        const variantId = Number(it.variant_id ?? it.id);
        if (Number(variantId) !== Number(parentVariantId)) return false;
        const props = it.properties && typeof it.properties === "object" ? it.properties : {};
        return Number(props._order_timestamp) === ts;
      });
      return match || null;
    },
    async removeCartLineItemByKey(lineItemKey) {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ id: lineItemKey, quantity: 0 })
      });
      if (!response.ok) {
        const text = await response.text().catch(() => "");
        throw new Error(text || `Failed to remove invalid bundle (HTTP ${response.status})`);
      }
      return response.json();
    },
    /**
     * If Shopify accepted the add request but the bundle ended up with a 0 line price,
     * we treat it as invalid (typically "too many products") and remove it immediately.
     *
     * @returns {Promise<boolean>} true if the newly added bundle was removed
     */
    async removeNewlyAddedZeroPriceBundleIfNeeded({ parentVariantId, orderTimestamp }) {
      try {
        const cart = await this.fetchCartJson();
        const addedBundle = this.findBundleLineItem(cart, { parentVariantId, orderTimestamp });
        if (!addedBundle) return false;
        if (!this.isZeroPriceLineItem(addedBundle)) return false;
        await this.removeCartLineItemByKey(addedBundle.key);
        await this.showGlobalWarningToast(this.getTooManyProductsToastMessage());
        this.proccessingAddToCart = false;
        await this.updateCart();
        this.setCookie("box-builder-products", "", -1);
        return true;
      } catch (e) {
        return false;
      }
    },
    buildMixMatchSentryContext(itemsPayload, requestMeta = {}) {
      var _a, _b, _c, _d, _e;
      const boxProducts = this.productsBoxWithVariants || [];
      return {
        bundle_type: "mix-match",
        parent_variant_id: ((_b = (_a = this.$store.state.config) == null ? void 0 : _a.parentVariant) == null ? void 0 : _b.id) ?? null,
        parent_variant_title: ((_d = (_c = this.$store.state.config) == null ? void 0 : _c.parentVariant) == null ? void 0 : _d.title) ?? null,
        added_qty: this.addedQty,
        discount_percent: this.discount,
        current_step_number: ((_e = this.currentStep) == null ? void 0 : _e.number) ?? null,
        inited_from_session_storage: this.initedFromSessionStorage,
        cart_drawer_present: Boolean(document.querySelector("#CartDrawer") || document.querySelector("cart-drawer")),
        box_product_rows: boxProducts.map((v) => {
          var _a2;
          return {
            variant_id: v.id,
            title: v.title,
            added_qty: v.addedQty,
            step_name: v.stepName,
            tab_name: v.tabName,
            has_bundle_info: Boolean((_a2 = v.bundleInfo) == null ? void 0 : _a2.length)
          };
        }),
        items_payload: itemsPayload ?? null,
        ...requestMeta
      };
    },
    /**
     * Multi-line English summary of the mix-match box for Sentry "Additional Data".
     * @param {Array|null} itemsPayload — line item(s) sent to /cart/add.js
     * @returns {string}
     */
    buildMixMatchHumanReadable(itemsPayload) {
      var _a, _b, _c, _d;
      const lines = [];
      const pv = (_a = this.$store.state.config) == null ? void 0 : _a.parentVariant;
      lines.push("—— Mix & match (box builder) configuration ——");
      lines.push(
        `Parent bundle variant: ${(pv == null ? void 0 : pv.title) || (pv == null ? void 0 : pv.name) || "(unknown)"} [variant_id ${(pv == null ? void 0 : pv.id) ?? "n/a"}]`
      );
      lines.push(`Total slots filled (expanded rows): ${this.addedQty}`);
      lines.push(`Volume discount on box: ${this.discount ?? 0}%`);
      lines.push(`Current builder step: ${((_b = this.currentStep) == null ? void 0 : _b.number) ?? "n/a"} of ${((_c = this.$store.state.steps) == null ? void 0 : _c.length) ?? "n/a"}`);
      const boxProducts = this.productsBoxWithVariants || [];
      lines.push("—— Unique products in the box (one row per product variant) ——");
      if (!boxProducts.length) {
        lines.push("(no products in box)");
      } else {
        boxProducts.forEach((v, i) => {
          var _a2;
          let row = `${i + 1}. ${v.title || "(no title)"} × ${v.addedQty} [variant_id ${v.id}]`;
          if (v.sku) {
            row += ` | sku ${v.sku}`;
          }
          if (v.stepName) {
            row += ` | step: ${v.stepName}`;
          }
          if (v.tabName) {
            row += ` | tab: ${v.tabName}`;
          }
          lines.push(row);
          if ((_a2 = v.bundleInfo) == null ? void 0 : _a2.length) {
            lines.push("   Fixed-price sub-bundle component variant IDs (from builder):");
            v.bundleInfo.forEach((b, j) => {
              const label = b.title || b.product_title || b.name || "";
              lines.push(
                `     ${j + 1}. variant_id ${b.variant_id} | qty_in_bundle ${b.quantity_in_bundle ?? "?"} | unit price (theme field) ${b.price ?? "n/a"}${label ? ` | ${label}` : ""}`
              );
            });
          }
        });
      }
      if ((_d = itemsPayload == null ? void 0 : itemsPayload[0]) == null ? void 0 : _d.properties) {
        const p2 = itemsPayload[0].properties;
        lines.push("—— Payload properties sent on the parent line item ——");
        lines.push(`_order_timestamp: ${p2._order_timestamp ?? "n/a"}`);
        lines.push(
          `_nestedVariantsIds (variant_id → count): ${JSON.stringify(p2._nestedVariantsIds || {})}`
        );
        lines.push(
          `_fixed_bundles: ${JSON.stringify(p2._fixed_bundles || [])}`
        );
        lines.push(`_price_compare_in_cents: ${p2._price_compare_in_cents ?? "n/a"}`);
      }
      return lines.join("\n");
    },
    async addToCart() {
      var _a, _b;
      console.log("✌️addToCart --->");
      const cartDrawer = document.querySelector("#CartDrawer") || document.querySelector("cart-drawer");
      const parser = new DOMParser();
      if (this.proccessingAddToCart || this.addedQty < 1 || this.disableATC) {
        return;
      }
      this.proccessingAddToCart = true;
      let cartAddHttpFailure = null;
      let items = null;
      try {
        await this.removeBundleFromCart();
        const orderTimestamp = Date.now();
        const parentVariantId = (_b = (_a = this.$store.state.config) == null ? void 0 : _a.parentVariant) == null ? void 0 : _b.id;
        if (!parentVariantId) return;
        const nestedProductVariantsIds = {};
        const fixedBunldes = [];
        this.productsVariants.forEach((v) => {
          var _a2;
          if ((_a2 = v.bundleInfo) == null ? void 0 : _a2.length) {
            const isAlreadyAdded = fixedBunldes.find((b) => b.parentVariantId === v.id);
            if (isAlreadyAdded) return;
            fixedBunldes.push(
              {
                parentVariantId: v.id,
                componentVariantsIds: v.bundleInfo.map((b) => b.variant_id),
                quantity: v.addedQty,
                title: v.title
              }
            );
          } else {
            nestedProductVariantsIds[v.id] = (nestedProductVariantsIds[v.id] || 0) + 1;
          }
        });
        items = [{
          id: parentVariantId,
          quantity: 1,
          properties: {
            _order_timestamp: orderTimestamp,
            _nestedVariantsIds: nestedProductVariantsIds,
            _price_compare_in_cents: this.priceCompare.unformatted * 100,
            _fixed_bundles: fixedBunldes
          }
        }];
        const sections = (cartDrawer == null ? void 0 : cartDrawer.getAttribute("data-section-id")) ?? null;
        const sections_url = window.location.pathname;
        const requestPayload = { items, sections, sections_url };
        console.log("[MixMatch] cart cookie before /cart/add.js:", this.getCookieValueByName("cart"));
        const addResponse = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestPayload)
        });
        if (addResponse.status === 422) {
          await this.handleMixMatch422Recovery({ bundleItemsPayload: items, parentVariantId, orderTimestamp });
          return;
        }
        if (!addResponse.ok) {
          let errText = "";
          try {
            errText = await addResponse.text();
          } catch (_) {
          }
          cartAddHttpFailure = {
            httpStatus: addResponse.status,
            responseBody: errText,
            requestPayload
          };
          throw new Error(errText || "Failed to add to cart");
        }
        if (cartDrawer && typeof cartDrawer.update === "function" && typeof cartDrawer.open === "function") {
          const parsedState = await addResponse.json();
          const addedLineItem = this.getAddedLineItemFromAddResponse(parsedState, { parentVariantId, orderTimestamp });
          const shouldRemoveBecauseZero = addedLineItem && this.isZeroPriceLineItem(addedLineItem);
          const removed = shouldRemoveBecauseZero ? await (async () => {
            const lineKey = addedLineItem == null ? void 0 : addedLineItem.key;
            if (lineKey) {
              await this.removeCartLineItemByKey(lineKey);
              await this.showGlobalWarningToast(this.getTooManyProductsToastMessage());
              this.proccessingAddToCart = false;
              await this.updateCart();
              this.setCookie("box-builder-products", "", -1);
              return true;
            }
            return this.removeNewlyAddedZeroPriceBundleIfNeeded({ parentVariantId, orderTimestamp });
          })() : await this.removeNewlyAddedZeroPriceBundleIfNeeded({ parentVariantId, orderTimestamp });
          if (removed) {
            return;
          }
          this.setCookie("box-builder-products", JSON.stringify(items), 14);
          const htmlSection = parsedState.sections[sections];
          const doc2 = parser.parseFromString(htmlSection, "text/html");
          await cartDrawer.purchaseHandler(doc2, parsedState);
          this.proccessingAddToCart = false;
        } else {
          const parsedState = await addResponse.json().catch(() => null);
          const addedLineItem = this.getAddedLineItemFromAddResponse(parsedState, { parentVariantId, orderTimestamp });
          const shouldRemoveBecauseZero = addedLineItem != null && this.isZeroPriceLineItem(addedLineItem);
          const removed = shouldRemoveBecauseZero ? await (async () => {
            const lineKey = addedLineItem == null ? void 0 : addedLineItem.key;
            if (lineKey) {
              await this.removeCartLineItemByKey(lineKey);
              await this.showGlobalWarningToast(this.getTooManyProductsToastMessage());
              this.proccessingAddToCart = false;
              await this.updateCart();
              this.setCookie("box-builder-products", "", -1);
              return true;
            }
            return this.removeNewlyAddedZeroPriceBundleIfNeeded({ parentVariantId, orderTimestamp });
          })() : await this.removeNewlyAddedZeroPriceBundleIfNeeded({ parentVariantId, orderTimestamp });
          if (removed) {
            return;
          }
          this.setCookie("box-builder-products", JSON.stringify(items), 14);
          this.proccessingAddToCart = false;
          location.reload();
        }
      } catch (e) {
        if (items != null && cartAddHttpFailure) {
          void reportBundleCartAddFailure({
            feature: "mix-match",
            httpStatus: cartAddHttpFailure.httpStatus,
            responseBody: cartAddHttpFailure.responseBody,
            requestPayload: cartAddHttpFailure.requestPayload,
            error: e instanceof Error ? e : new Error(String(e)),
            humanReadableBundle: this.buildMixMatchHumanReadable(items),
            additionalContext: this.buildMixMatchSentryContext(items, {
              failure_stage: "cart_add_http_error"
            })
          }).catch(() => {
          });
        }
      } finally {
        this.proccessingAddToCart = false;
      }
    },
    setCookie(key, value, daysToExpire = 14) {
      const expirationDate = /* @__PURE__ */ new Date();
      expirationDate.setTime(expirationDate.getTime() + daysToExpire * 24 * 60 * 60 * 1e3);
      const expires = "expires=" + expirationDate.toUTCString();
      document.cookie = key + "=" + encodeURIComponent(value) + ";" + expires + ";path=/";
    },
    getCookie(key) {
      const name = key + "=";
      const decodedCookie = decodeURIComponent(document.cookie);
      const cookieArray = decodedCookie.split(";");
      for (let i = 0; i < cookieArray.length; i++) {
        const cookie = cookieArray[i].trim();
        if (cookie.indexOf(name) === 0) {
          return cookie.substring(name.length, cookie.length);
        }
      }
      return "";
    },
    async initItemsFromSessionStorage() {
      let products = this.getCookie("box-builder-products");
      if (!products) {
        return;
      }
      try {
        products = JSON.parse(products);
      } catch (error) {
      }
      let cartItems;
      await fetch("/cart.js", {
        method: "GET",
        headers: {
          "Content-Type": "application/json"
        }
      }).then((response) => {
        return response.json();
      }).then((data) => {
        cartItems = data.items;
        return data;
      }).catch((error) => {
        console.error("Error:", error);
      });
      const cartQuantityById = new Map(cartItems.map((item) => [item.id, item.quantity]));
      const updatedProducts = products.filter((product) => cartQuantityById.has(product.id)).map((product) => ({
        ...product,
        // Keep all existing fields
        quantity: cartQuantityById.get(product.id)
        // Update quantity
      }));
      updatedProducts.forEach((p2) => {
        const step = this.steps.find((s) => s.title === p2.properties._stepName);
        const tab = step.tabs.find((t) => t.title === p2.properties._tabName);
        const foundVariant = tab.variants.find((v) => v.id === p2.id);
        for (let i = 0; i < p2.quantity; i++) {
          this.$store.dispatch("addToBox", foundVariant);
        }
      });
      this.initedFromSessionStorage = true;
    },
    remove(variant) {
      const foundVariantIdx = this.productsVariants.findIndex(
        (v) => v.id === variant.id
      );
      if (foundVariantIdx < 0) {
        return;
      }
      const foundVariant = this.productsVariants[foundVariantIdx];
      if (foundVariant.specificDiscount) {
        this.$store.commit("disapplySpecificDiscount");
      }
      this.$store.dispatch("removeFromBox", foundVariant);
    },
    updateProductsPerRow() {
      this.productsPerRow = window.innerWidth <= BREAKPOINT ? 5 : 5;
    },
    expand() {
      if (this.addedQty >= this.productsPerRow) {
        this.expandedView = true;
      } else {
        this.expandedView = false;
      }
    },
    formatPrice(price, dontDevideBy100 = false) {
      var _a, _b, _c, _d, _e;
      const isUKStore = Shopify.currency.active === "GBP" || Shopify.locale === "en-GB";
      if (isUKStore) {
        if (dontDevideBy100) {
          const priceDecimalsLength = (_b = (_a = price.toString().split(".")) == null ? void 0 : _a[1]) == null ? void 0 : _b.length;
          if (priceDecimalsLength > 2) {
            price = +price.toString().slice(0, -1 * (priceDecimalsLength - 2));
          }
          if (priceDecimalsLength < 2) {
            price = +price.toFixed(2);
          }
          const result = price && price > 0 ? `${price}` : "0.00";
          return `£ ${result}`;
        } else {
          const result = price && price > 0 ? (price / 100).toFixed(2) : "0.00";
          return `£ ${result}`;
        }
      } else {
        if (dontDevideBy100) {
          const priceDecimalsLength = (_d = (_c = price.toString().split(".")) == null ? void 0 : _c[1]) == null ? void 0 : _d.length;
          if (priceDecimalsLength > 2) {
            price = +price.toString().slice(0, -1 * (priceDecimalsLength - 2));
          }
          if (priceDecimalsLength < 2) {
            price = +price.toFixed(2);
          }
          const result = price && price > 0 ? (_e = `${price}`) == null ? void 0 : _e.split(".").join(",") : "0,00";
          return `${result} ${this.$store.state.currency}`;
        } else {
          const result = price && price > 0 ? (price / 100).toFixed(2).split(".").join(",") : "0,00";
          return `${result} ${this.$store.state.currency}`;
        }
      }
    },
    async removeBundleFromCart() {
      let products = this.getCookie("box-builder-products");
      if (!products) {
        return;
      }
      try {
        products = JSON.parse(products);
      } catch (error) {
      }
      const updates = products.reduce((obj, product) => {
        obj[product.id] = 0;
        return obj;
      }, {});
      return fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          updates
        })
      }).then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      }).catch((error) => {
        console.error("Error:", error);
      });
    },
    getCustomerDebugInfo() {
      var _a, _b, _c, _d, _e, _f, _g, _h;
      const customerId = ((_c = (_b = (_a = window == null ? void 0 : window.ShopifyAnalytics) == null ? void 0 : _a.meta) == null ? void 0 : _b.page) == null ? void 0 : _c.customerId) ?? null;
      return {
        customer: customerId ? { id: customerId } : null,
        shop: ((_d = window == null ? void 0 : window.Shopify) == null ? void 0 : _d.shop) ?? null,
        locale: ((_e = window == null ? void 0 : window.Shopify) == null ? void 0 : _e.locale) ?? null,
        currency: ((_g = (_f = window == null ? void 0 : window.Shopify) == null ? void 0 : _f.currency) == null ? void 0 : _g.active) ?? null,
        page_url: ((_h = window == null ? void 0 : window.location) == null ? void 0 : _h.href) ?? null
      };
    },
    getCookieValueByName(name) {
      try {
        const raw = typeof document !== "undefined" ? document.cookie : "";
        const parts = raw.split(";").map((p2) => p2.trim());
        const prefix = `${name}=`;
        const hit = parts.find((p2) => p2.startsWith(prefix));
        return hit ? decodeURIComponent(hit.slice(prefix.length)) : "";
      } catch (_) {
        return "";
      }
    },
    clearCookieByName(name) {
      try {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      } catch (_) {
      }
    },
    async showThemeLoadingOverlay() {
      try {
        if (typeof customElements === "undefined" || typeof customElements.whenDefined !== "function") return;
        await customElements.whenDefined("body-element");
        const bodyElement = document.querySelector("body-element");
        if (bodyElement && typeof bodyElement.showLoadingOverlay === "function") {
          bodyElement.showLoadingOverlay("#GlobalLoadingOverlayDots");
        }
      } catch (_) {
      }
    },
    async hideThemeLoadingOverlay() {
      try {
        if (typeof customElements === "undefined" || typeof customElements.whenDefined !== "function") return;
        await customElements.whenDefined("body-element");
        const bodyElement = document.querySelector("body-element");
        if (bodyElement && typeof bodyElement.removeLoadingOverlay === "function") {
          await bodyElement.removeLoadingOverlay("#GlobalLoadingOverlayDots");
        }
      } catch (_) {
      }
    },
    getGenericBundleErrorToastMessage() {
      return "<p>Something went wrong with your bundle. Please clear your cookies or contact us.</p>";
    },
    isMixMatchBundleLineItem(lineItem, parentVariantId) {
      if (!lineItem) return false;
      const lineVariantId = Number(lineItem.variant_id);
      const expectedParentId = Number(parentVariantId);
      const props = lineItem.properties && typeof lineItem.properties === "object" ? lineItem.properties : {};
      const hasBundleMarkers = Number(props._order_timestamp) > 0 || typeof props._nestedVariantsIds === "object" || Array.isArray(props._fixed_bundles);
      return Number.isFinite(lineVariantId) && Number.isFinite(expectedParentId) && lineVariantId === expectedParentId && hasBundleMarkers;
    },
    async clearCartForMixMatchRecovery(cartCookieBefore) {
      const clearRes = await fetch("/cart/clear.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin"
      });
      if (!clearRes.ok) {
        const body = await clearRes.text().catch(() => "");
        await reportBundleCartEvent({
          feature: "mix-match",
          eventName: "mix_match_422_clear_cart_failed",
          requestPayload: null,
          additionalContext: {
            ...this.getCustomerDebugInfo(),
            cart_cookie_before: cartCookieBefore || null,
            cart_cookie_after: this.getCookieValueByName("cart") || null,
            stage: "clear_cart",
            httpStatus: clearRes.status,
            responseBody: body
          }
        });
        return { ok: false, httpStatus: clearRes.status };
      }
      return { ok: true };
    },
    async recreateCartAfter422({ cartSnapshot, bundleItemsPayload, orderTimestamp, parentVariantId, cartCookieBefore }) {
      const cartItems = Array.isArray(cartSnapshot == null ? void 0 : cartSnapshot.items) ? cartSnapshot.items : [];
      const previousBundleItemsCount = cartItems.filter((it) => this.isMixMatchBundleLineItem(it, parentVariantId)).length;
      const itemsToRestore = cartItems.filter((it) => !this.isMixMatchBundleLineItem(it, parentVariantId)).filter((it) => Number(it == null ? void 0 : it.quantity) > 0 && Number.isFinite(Number(it == null ? void 0 : it.variant_id))).map((it) => ({
        id: it.variant_id,
        quantity: it.quantity,
        properties: it.properties || {}
      }));
      if (itemsToRestore.length > 0) {
        const restorePayload = { items: itemsToRestore };
        const restoreRes = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify(restorePayload)
        });
        if (!restoreRes.ok) {
          const body = await restoreRes.text().catch(() => "");
          await reportBundleCartEvent({
            feature: "mix-match",
            eventName: "mix_match_422_restore_items_failed",
            requestPayload: restorePayload,
            humanReadableBundle: this.buildMixMatchHumanReadable(bundleItemsPayload),
            additionalContext: {
              ...this.getCustomerDebugInfo(),
              cart_cookie_before: cartCookieBefore || null,
              cart_cookie_after: this.getCookieValueByName("cart") || null,
              stage: "restore_items",
              httpStatus: restoreRes.status,
              responseBody: body,
              attempted_restore_items_count: itemsToRestore.length,
              skipped_previous_bundle_items_count: previousBundleItemsCount
            }
          });
          return { ok: false, stage: "restore_items", httpStatus: restoreRes.status };
        }
      }
      const bundleRes = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ items: bundleItemsPayload })
      });
      if (bundleRes.status === 422) {
        const body = await bundleRes.text().catch(() => "");
        await reportBundleCartEvent({
          feature: "mix-match",
          eventName: "mix_match_422_retry_failed_after_cookie_clear",
          requestPayload: { items: bundleItemsPayload },
          humanReadableBundle: this.buildMixMatchHumanReadable(bundleItemsPayload),
          additionalContext: {
            ...this.getCustomerDebugInfo(),
            cart_cookie_before: cartCookieBefore || null,
            cart_cookie_after: this.getCookieValueByName("cart") || null,
            stage: "retry_bundle_after_restore",
            httpStatus: 422,
            responseBody: body,
            orderTimestamp,
            parentVariantId
          }
        });
        return { ok: false, stage: "retry_bundle", httpStatus: 422 };
      }
      if (!bundleRes.ok) {
        const body = await bundleRes.text().catch(() => "");
        await reportBundleCartEvent({
          feature: "mix-match",
          eventName: "mix_match_422_retry_bundle_unexpected_http",
          requestPayload: { items: bundleItemsPayload },
          humanReadableBundle: this.buildMixMatchHumanReadable(bundleItemsPayload),
          additionalContext: {
            ...this.getCustomerDebugInfo(),
            cart_cookie_before: cartCookieBefore || null,
            cart_cookie_after: this.getCookieValueByName("cart") || null,
            stage: "retry_bundle_after_restore",
            httpStatus: bundleRes.status,
            responseBody: body,
            orderTimestamp,
            parentVariantId
          }
        });
        return { ok: false, stage: "retry_bundle", httpStatus: bundleRes.status };
      }
      const parsedState = await bundleRes.json().catch(() => null);
      const addedLineItem = this.getAddedLineItemFromAddResponse(parsedState, { parentVariantId, orderTimestamp });
      if (addedLineItem && this.isZeroPriceLineItem(addedLineItem)) {
        const key = addedLineItem.key;
        if (key) {
          await this.removeCartLineItemByKey(key).catch(() => {
          });
        }
        return { ok: false, stage: "retry_bundle_zero_price" };
      }
      await reportBundleCartEvent({
        feature: "mix-match",
        eventName: "mix_match_422_recovered_after_cookie_clear",
        requestPayload: { items: bundleItemsPayload },
        humanReadableBundle: this.buildMixMatchHumanReadable(bundleItemsPayload),
        additionalContext: {
          ...this.getCustomerDebugInfo(),
          cart_cookie_before: cartCookieBefore || null,
          cart_cookie_after: this.getCookieValueByName("cart") || null,
          stage: "recovered",
          orderTimestamp,
          parentVariantId,
          restored_items_count: itemsToRestore.length,
          skipped_previous_bundle_items_count: previousBundleItemsCount
        }
      });
      this.setCookie("box-builder-products", JSON.stringify(bundleItemsPayload), 14);
      return { ok: true };
    },
    async handleMixMatch422Recovery({ bundleItemsPayload, parentVariantId, orderTimestamp }) {
      const cartSnapshot = await this.fetchCartJson().catch(() => null);
      const cartCookieBefore = this.getCookieValueByName("cart");
      await reportBundleCartEvent({
        feature: "mix-match",
        eventName: "mix_match_422_received_initial_add",
        requestPayload: { items: bundleItemsPayload },
        humanReadableBundle: this.buildMixMatchHumanReadable(bundleItemsPayload),
        additionalContext: {
          ...this.getCustomerDebugInfo(),
          cart_cookie_before: cartCookieBefore || null,
          httpStatus: 422
        }
      });
      await this.showThemeLoadingOverlay();
      try {
        const clearResult = await this.clearCartForMixMatchRecovery(cartCookieBefore);
        if (!clearResult.ok) {
          await this.showGlobalWarningToast(this.getGenericBundleErrorToastMessage());
          return;
        }
        const result = await this.recreateCartAfter422({
          cartSnapshot,
          bundleItemsPayload,
          parentVariantId,
          orderTimestamp,
          cartCookieBefore
        });
        if (!result.ok) {
          await this.showGlobalWarningToast(this.getGenericBundleErrorToastMessage());
          return;
        }
        await this.updateCart();
      } finally {
        await this.hideThemeLoadingOverlay();
      }
    }
  },
  mounted() {
    window.addEventListener("resize", this.updateProductsPerRow);
    window.addEventListener("scroll", this.onScroll);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateProductsPerRow);
    window.removeEventListener("scroll", this.onScroll);
  },
  computed: {
    ...mapGetters({
      productsBoxWithVariants: "getProductsBox",
      addedQty: "getAddedProductsQuantity",
      discount: "getDiscount",
      currentStep: "getCurrentStep",
      nextDiscount: "getNextDiscount",
      discountableProductsAddedQty: "getAddedDiscountableProductsQty",
      numberOfSpecificDiscountsUsed: "getNumberOfSpecificDiscountsUsed",
      specificDiscountAmount: "getSpecificDiscountAmount",
      steps: "getSteps",
      pricePerPortion: "getReductedPricePerPortion"
    }),
    isTheLastStep() {
      return this.currentStep.number === this.$store.state.steps.length;
    },
    shakerDiscountFormatted() {
      return this.formatPrice(this.$store.state.specificDiscountAmount);
    },
    pricePerPortionFormatted() {
      return this.formatPrice(this.pricePerPortion, true);
    },
    savingsPrice() {
      return this.formatPrice(this.priceCompare.unformatted - this.price.unformatted, true);
    },
    atLeastOneProductFromTheFirstStep() {
      return this.productsBoxWithVariants.find((v) => v.stepName === this.steps[0].title);
    },
    productsVariants() {
      if (!this.productsBoxWithVariants || !this.productsBoxWithVariants.length || this.productsBoxWithVariants.length < 1) {
        return;
      }
      const result = [];
      let specificDiscountApplied = false;
      this.productsBoxWithVariants.forEach((variant) => {
        for (let i = 0; i < (variant.addedQty || 1); i++) {
          const specificDiscount = !specificDiscountApplied && variant.specificDiscountable && this.atLeastOneProductFromTheFirstStep ? this.specificDiscountAmount : 0;
          if (specificDiscount > 0) specificDiscountApplied = true;
          result.push({ ...variant, specificDiscount });
        }
      });
      return result;
    },
    rows() {
      var _a;
      let products, rowsQty;
      if ((_a = this.productsVariants) == null ? void 0 : _a.length) {
        products = this.productsVariants.map((product, index) => ({
          ...product,
          index
        }));
        rowsQty = ~~(this.addedQty / this.productsPerRow) + 1;
      }
      rowsQty = !products ? 1 : rowsQty;
      const rows = [];
      for (let i = 0; i < rowsQty; i++) {
        const row = [];
        for (let j = i === 0 ? 0 : i * this.productsPerRow; j < i * this.productsPerRow + this.productsPerRow; j++) {
          if (products == null ? void 0 : products.length) {
            const productVariant = products[j];
            if (productVariant) {
              row.push({
                variant: productVariant,
                placeholder: false,
                expandButton: i === 0 && j === this.productsPerRow - 1
              });
              continue;
            }
          }
          row.push({
            placeholder: true,
            expandButton: i === 0 && j === this.productsPerRow - 1
          });
        }
        rows.push(row);
      }
      return rows;
    },
    price() {
      var _a;
      let result = 0;
      let specificDiscountedPrice = 0;
      if (!((_a = this.productsVariants) == null ? void 0 : _a.length)) {
        return {
          formatted: this.formatPrice(result),
          unformatted: result
        };
      }
      const handledBundledVariantsIds = [];
      this.productsVariants.forEach((variant, idx) => {
        var _a2, _b, _c;
        const variantPrice = +variant.price / 100;
        if ((_a2 = variant.bundleInfo) == null ? void 0 : _a2.length) {
          variant.bundleInfo.forEach((bundleVariant) => {
            if (handledBundledVariantsIds.includes(bundleVariant.variant_id)) {
              return;
            }
            const bundleVariantPricePerUnit = +bundleVariant.price * +bundleVariant.quantity_in_bundle;
            if (this.discount > 0) {
              const discountedPricePerUnitRaw = bundleVariantPricePerUnit - bundleVariantPricePerUnit * (this.discount / 100);
              const discountedPricePerUnit = Math.trunc(discountedPricePerUnitRaw * 100) / 100;
              const discountedPrice = +discountedPricePerUnit * +variant.addedQty;
              result += discountedPrice;
            } else {
              result += bundleVariantPricePerUnit * +variant.addedQty;
            }
            handledBundledVariantsIds.push(bundleVariant.variant_id);
          });
        } else if (variant.specificDiscount && this.atLeastOneProductFromTheFirstStep) {
          const specificDiscount = +((_b = variant.specificDiscount / 100) == null ? void 0 : _b.toFixed(2));
          this.specificDiscountedVariantIndex = idx;
          const discountedPrice = +((_c = variantPrice - specificDiscount) == null ? void 0 : _c.toFixed(2));
          specificDiscountedPrice += discountedPrice;
        } else {
          if (this.discount > 0) {
            const discountedPriceRaw = variantPrice - variantPrice * (this.discount / 100);
            const discountedPrice = Math.trunc(discountedPriceRaw * 100) / 100;
            result += discountedPrice;
          } else {
            result += variantPrice;
          }
        }
      });
      result += specificDiscountedPrice;
      result = result.toFixed(2);
      return {
        formatted: this.formatPrice(result, true),
        unformatted: result
      };
    },
    priceCompare() {
      var _a;
      let result = 0;
      if (!((_a = this.productsVariants) == null ? void 0 : _a.length)) {
        return {
          formatted: this.formatPrice(result),
          unformatted: result
        };
      }
      const handledBundledVariantsIds = [];
      this.productsVariants.forEach((variant) => {
        var _a2;
        if ((_a2 = variant.bundleInfo) == null ? void 0 : _a2.length) {
          variant.bundleInfo.forEach((bundleVariant) => {
            if (handledBundledVariantsIds.includes(bundleVariant.variant_id)) {
              return;
            }
            const bundleVariantPrice = +bundleVariant.price * 100 * +bundleVariant.quantity_in_bundle * +variant.addedQty;
            result += bundleVariantPrice;
            handledBundledVariantsIds.push(bundleVariant.variant_id);
          });
        } else {
          result += variant.price;
        }
      });
      return {
        formatted: this.formatPrice(result),
        unformatted: result / 100
      };
    },
    nextDiscountMessage() {
      var _a, _b;
      const productLeft = this.nextDiscount.products_quantity - this.discountableProductsAddedQty;
      const discountTemplate = productLeft == 1 ? (_a = this.$store.state.config.htmlSnippets) == null ? void 0 : _a.discountTemplateSinglular : (_b = this.$store.state.config.htmlSnippets) == null ? void 0 : _b.discountTemplate;
      const canWordTranslated = productLeft > 1 ? this.$store.state.config.translations.cans : this.$store.state.config.translations.can;
      const leftCub = discountTemplate.includes("&lcub;") ? "&lcub;" : "{";
      const rightCub = discountTemplate.includes("&rcub;") ? "&rcub;" : "}";
      const qtyStr = `${leftCub}qty${rightCub}`;
      const discountStr = `${leftCub}discount${rightCub}`;
      const canStr = `${leftCub}can${rightCub}`;
      return this.nextDiscount && discountTemplate ? discountTemplate.replace(qtyStr, `${productLeft}`).replace(discountStr, this.nextDiscount.discount_percent).replace(canStr, canWordTranslated) : "";
    },
    disableATC() {
      return this.addedQty < 1;
    }
  },
  watch: {
    addedQty(newV, old) {
      if (old >= this.productsPerRow && newV < this.productsPerRow) {
        this.expandedView = false;
      }
    }
  },
  components: {
    Image
  }
};
const _hoisted_1$9 = { class: "bb-box" };
const _hoisted_2$8 = { class: "bb-box__header-wrapper" };
const _hoisted_3$7 = { class: "bb-box__header" };
const _hoisted_4$5 = { class: "bb-box__heading" };
const _hoisted_5$5 = ["innerHTML"];
const _hoisted_6$5 = ["innerHTML"];
const _hoisted_7$4 = { class: "bb-box__inner" };
const _hoisted_8$4 = { class: "bb-box__body" };
const _hoisted_9$3 = { class: "bb-box__summary_controls hidden-on-desktop" };
const _hoisted_10$3 = ["disabled", "aria-busy"];
const _hoisted_11$3 = { class: "bb-box__summary_checkout-button-label" };
const _hoisted_12$3 = { key: 0 };
const _hoisted_13$3 = { key: 1 };
const _hoisted_14$3 = {
  class: "bb-box__summary_checkout-button-spinner-wrap",
  "aria-hidden": "true"
};
const _hoisted_15$2 = { class: "bb-box__scroll-container_wrapper" };
const _hoisted_16$2 = { class: "bb-box__scroll-container" };
const _hoisted_17$1 = ["onClick", "innerHTML"];
const _hoisted_18$1 = ["innerHTML"];
const _hoisted_19$1 = {
  key: 3,
  class: "bb-product__variant_specific-discount"
};
const _hoisted_20$1 = { class: "bb-box__body_summary" };
const _hoisted_21$1 = { class: "bb-box__summary_price-left" };
const _hoisted_22$1 = { class: "bb-box__savings" };
const _hoisted_23$1 = { class: "bb-box__savings_title" };
const _hoisted_24$1 = { class: "bb-box__stavings_price" };
const _hoisted_25$1 = { class: "bb-box__summary_price-right" };
const _hoisted_26$1 = {
  key: 0,
  class: "bb-box__summary_price-compare"
};
const _hoisted_27$1 = { class: "bb-box__summary_price-normal" };
const _hoisted_28$1 = { class: "bb-box__summary_controls hidden-on-mobile" };
const _hoisted_29$1 = ["disabled", "aria-busy"];
const _hoisted_30$1 = { class: "bb-box__summary_checkout-button-label" };
const _hoisted_31$1 = { key: 0 };
const _hoisted_32$1 = { key: 1 };
const _hoisted_33$1 = {
  class: "bb-box__summary_checkout-button-spinner-wrap",
  "aria-hidden": "true"
};
function _sfc_render$9(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_Image = resolveComponent("Image");
  return withDirectives((openBlock(), createElementBlock("div", _hoisted_1$9, [
    createBaseVNode("div", _hoisted_2$8, [
      createBaseVNode("div", _hoisted_3$7, [
        createBaseVNode("h3", _hoisted_4$5, toDisplayString(_ctx.$store.state.config.translations.yourChoice), 1),
        !_ctx.nextDiscount.theLastOneReached ? (openBlock(), createElementBlock("div", {
          key: 0,
          class: "bb-box__discount-message hidden-on-mobile",
          innerHTML: $options.nextDiscountMessage
        }, null, 8, _hoisted_5$5)) : (openBlock(), createElementBlock("div", {
          key: 1,
          class: "bb-box__discount-message hidden-on-mobile",
          innerHTML: _ctx.$store.state.config.section.discount_reached_message
        }, null, 8, _hoisted_6$5)),
        $data.expandedView ? (openBlock(), createElementBlock("button", {
          key: 2,
          class: "bb-box__collapse-button",
          type: "button",
          onClick: _cache[0] || (_cache[0] = () => {
            this.expandedView = false;
          })
        }, toDisplayString(this.$store.state.config.translations.collapse), 1)) : createCommentVNode("", true)
      ])
    ]),
    createBaseVNode("div", _hoisted_7$4, [
      createBaseVNode("div", _hoisted_8$4, [
        createBaseVNode("div", _hoisted_9$3, [
          createBaseVNode("button", {
            class: normalizeClass(["bb-box__summary_checkout-button", {
              "bb-box__summary_checkout-button--wider": _ctx.currentStep.number === _ctx.$store.state.steps.length,
              "bb-box__checkout-button_svg-wrapper--disabled": $options.disableATC || $data.proccessingAddToCart,
              "loading": $data.proccessingAddToCart
            }]),
            onClick: _cache[1] || (_cache[1] = (...args) => $options.addToCart && $options.addToCart(...args)),
            disabled: $data.proccessingAddToCart,
            "aria-busy": $data.proccessingAddToCart ? "true" : "false",
            type: "button"
          }, [
            createBaseVNode("span", _hoisted_11$3, [
              !$data.initedFromSessionStorage ? (openBlock(), createElementBlock("span", _hoisted_12$3, toDisplayString(_ctx.$store.state.config.translations.checkout), 1)) : (openBlock(), createElementBlock("span", _hoisted_13$3, toDisplayString(_ctx.$store.state.config.translations.updateCart), 1))
            ]),
            withDirectives(createBaseVNode("div", _hoisted_14$3, _cache[6] || (_cache[6] = [
              createBaseVNode("div", { class: "loading-spinner bb-box__summary_checkout-button-spinner" }, null, -1)
            ]), 512), [
              [vShow, $data.proccessingAddToCart]
            ])
          ], 10, _hoisted_10$3),
          withDirectives(createBaseVNode("button", {
            class: "bb-box__summary_next-step-button",
            type: "button",
            onClick: _cache[2] || (_cache[2] = (...args) => $options.goToNextStep && $options.goToNextStep(...args))
          }, toDisplayString(_ctx.$store.state.config.translations.next), 513), [
            [vShow, !$options.isTheLastStep]
          ])
        ]),
        createBaseVNode("div", _hoisted_15$2, [
          createBaseVNode("div", _hoisted_16$2, [
            createBaseVNode("div", {
              class: normalizeClass(["bb-box__container", {
                "bb-box__container--expanded": $data.expandedView
              }])
            }, [
              (openBlock(true), createElementBlock(Fragment, null, renderList($options.rows, (row, topIdx) => {
                return withDirectives((openBlock(), createElementBlock("div", {
                  class: "bb-box__container_row",
                  key: topIdx
                }, [
                  (openBlock(true), createElementBlock(Fragment, null, renderList(row, (item, idx) => {
                    var _a, _b, _c, _d;
                    return openBlock(), createElementBlock("div", {
                      class: normalizeClass(["bb-box__product", {
                        "bb-box__product_expand-btn--wrapper": item.expandButton ? !$data.expandedView : false,
                        "bb-box__box_expand-btn--disabled": item.expandButton && _ctx.addedQty < $data.productsPerRow
                      }]),
                      key: idx
                    }, [
                      (item.variant && item.expandButton ? $data.expandedView : !item.placeholder) ? (openBlock(), createElementBlock("button", {
                        key: 0,
                        class: "bb-box__product-remove",
                        type: "button",
                        onClick: ($event) => $options.remove(item.variant),
                        innerHTML: _ctx.$store.state.config.htmlSnippets.icons.close
                      }, null, 8, _hoisted_17$1)) : createCommentVNode("", true),
                      item.expandButton ? (openBlock(), createElementBlock("button", {
                        key: 1,
                        class: "bb-box__product_placeholder bb-box__box_expand-btn",
                        type: "button",
                        onClick: _cache[3] || (_cache[3] = (...args) => $options.expand && $options.expand(...args))
                      }, toDisplayString(_ctx.$store.state.config.translations.expand), 1)) : createCommentVNode("", true),
                      item.placeholder && !item.expandButton ? (openBlock(), createElementBlock("div", {
                        key: 2,
                        class: "bb-box__product_placeholder",
                        innerHTML: _ctx.$store.state.config.htmlSnippets.icons.placeholder
                      }, null, 8, _hoisted_18$1)) : createCommentVNode("", true),
                      ((_a = item.variant) == null ? void 0 : _a.specificDiscountable) && ((_b = item.variant) == null ? void 0 : _b.index) === $data.specificDiscountedVariantIndex && $options.atLeastOneProductFromTheFirstStep ? (openBlock(), createElementBlock("span", _hoisted_19$1, " -" + toDisplayString($options.shakerDiscountFormatted), 1)) : createCommentVNode("", true),
                      (item.expandButton ? $data.expandedView : !item.placeholder) && item.variant ? (openBlock(), createBlock(_component_Image, {
                        key: 4,
                        src: ((_c = item.variant.featured_image) == null ? void 0 : _c.src) || item.variant.featured_image,
                        additionalClasses: "bb-box__product_image-wrapper",
                        alt: ((_d = item.variant.featured_image) == null ? void 0 : _d.alt) || item.variant.title,
                        width: 60,
                        height: 60,
                        useSrcset: true,
                        usePlaceholder: false,
                        loading: "eager"
                      }, null, 8, ["src", "alt"])) : createCommentVNode("", true)
                    ], 2);
                  }), 128))
                ])), [
                  [vShow, topIdx === 0 || $data.expandedView]
                ]);
              }), 128))
            ], 2)
          ])
        ]),
        createBaseVNode("div", _hoisted_20$1, [
          createBaseVNode("div", {
            class: normalizeClass(["bb-box__summary_price-wrapper", {
              "bb-box__summary_price-wrapper--expanded": $data.expandedView
            }])
          }, [
            createBaseVNode("div", _hoisted_21$1, [
              createBaseVNode("span", _hoisted_22$1, [
                createBaseVNode("span", _hoisted_23$1, toDisplayString(_ctx.$store.state.config.translations.savings), 1),
                createBaseVNode("span", _hoisted_24$1, toDisplayString($options.savingsPrice), 1)
              ])
            ]),
            createBaseVNode("div", _hoisted_25$1, [
              $options.priceCompare.unformatted > $options.price.unformatted ? (openBlock(), createElementBlock("del", _hoisted_26$1, toDisplayString($options.priceCompare.formatted), 1)) : createCommentVNode("", true),
              createBaseVNode("span", _hoisted_27$1, toDisplayString($options.price.formatted), 1)
            ])
          ], 2),
          createBaseVNode("div", _hoisted_28$1, [
            createBaseVNode("button", {
              class: normalizeClass(["bb-box__summary_checkout-button", {
                "bb-box__summary_checkout-button--wider": _ctx.currentStep.number === _ctx.$store.state.steps.length,
                "bb-box__checkout-button_svg-wrapper--disabled": $options.disableATC || $data.proccessingAddToCart,
                "loading": $data.proccessingAddToCart
              }]),
              onClick: _cache[4] || (_cache[4] = (...args) => $options.addToCart && $options.addToCart(...args)),
              disabled: $data.proccessingAddToCart,
              "aria-busy": $data.proccessingAddToCart ? "true" : "false",
              type: "button"
            }, [
              createBaseVNode("span", _hoisted_30$1, [
                !$data.initedFromSessionStorage ? (openBlock(), createElementBlock("span", _hoisted_31$1, toDisplayString(_ctx.$store.state.config.translations.checkout), 1)) : (openBlock(), createElementBlock("span", _hoisted_32$1, toDisplayString(_ctx.$store.state.config.translations.updateCart), 1))
              ]),
              withDirectives(createBaseVNode("div", _hoisted_33$1, _cache[7] || (_cache[7] = [
                createBaseVNode("div", { class: "loading-spinner bb-box__summary_checkout-button-spinner" }, null, -1)
              ]), 512), [
                [vShow, $data.proccessingAddToCart]
              ])
            ], 10, _hoisted_29$1),
            withDirectives(createBaseVNode("button", {
              class: "bb-box__summary_next-step-button",
              type: "button",
              onClick: _cache[5] || (_cache[5] = (...args) => $options.goToNextStep && $options.goToNextStep(...args))
            }, toDisplayString(_ctx.$store.state.config.translations.next), 513), [
              [vShow, !$options.isTheLastStep]
            ])
          ])
        ])
      ])
    ])
  ], 512)), [
    [vShow, $data.isVisible]
  ]);
}
const Box = /* @__PURE__ */ _export_sfc(_sfc_main$9, [["render", _sfc_render$9]]);
const _sfc_main$8 = {
  data() {
    return {
      config: null,
      bbHeaderCoords: { top: 0, left: 0, width: 0, height: 0 }
    };
  },
  methods: {
    calculateDiscount() {
      this.$store.commit("calculateDiscount");
    },
    getBBHeaderCoords() {
      this.$nextTick(() => {
        const bbHeader = this.$refs.bbHeader;
        if (bbHeader) {
          let element = bbHeader;
          let top = 0;
          let left = 0;
          while (element) {
            top += element.offsetTop;
            left += element.offsetLeft;
            element = element.offsetParent;
          }
          this.bbHeaderCoords = {
            top,
            left,
            width: bbHeader.offsetWidth,
            height: bbHeader.offsetHeight
          };
        }
      });
    },
    getListOfIndexesPassedSteps() {
      const result = this.steps.map((step, index) => step.isPassed ? index : -1).filter((index) => index !== -1);
      return result.slice(0, result.length - 1);
    }
  },
  created() {
    this.config = this.$store.state.config;
  },
  computed: {
    ...mapGetters({
      steps: "getSteps",
      currentStep: "getCurrentStep",
      addedProductsQty: "getAddedProductsQuantity",
      addedDiscountableProductsQty: "getAddedDiscountableProductsQty"
    })
  },
  mounted() {
    this.getBBHeaderCoords();
    window.addEventListener("resize", this.getBBHeaderCoords);
  },
  watch: {
    addedProductsQty(newV, old) {
      this.calculateDiscount();
    }
  },
  components: {
    Step,
    // GridSwitcher,
    ProductGrid,
    Box
  }
};
const _hoisted_1$8 = { class: "bb-steps__wrapper" };
const _hoisted_2$7 = { class: "bb-steps" };
function _sfc_render$8(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_Step = resolveComponent("Step");
  const _component_ProductGrid = resolveComponent("ProductGrid");
  const _component_Box = resolveComponent("Box");
  return openBlock(), createElementBlock(Fragment, null, [
    createBaseVNode("div", {
      class: normalizeClass(["bb-header", _ctx.steps.length >= 4 ? `bb-steps-big` : "bb-steps-small"]),
      id: "mix-n-match-app",
      ref: "bbHeader"
    }, [
      createBaseVNode("div", _hoisted_1$8, [
        createBaseVNode("div", _hoisted_2$7, [
          (openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.steps, (step, idx) => {
            return openBlock(), createBlock(_component_Step, {
              key: idx,
              title: step.title,
              number: step.number,
              isPassed: idx === 0 || step.isPassed,
              addDivider: idx !== _ctx.steps.length - 1,
              filledDivider: $options.getListOfIndexesPassedSteps().includes(idx)
            }, null, 8, ["title", "number", "isPassed", "addDivider", "filledDivider"]);
          }), 128))
        ])
      ])
    ], 2),
    createVNode(_component_ProductGrid, {
      tabs: _ctx.currentStep.tabs,
      currentStep: _ctx.currentStep
    }, null, 8, ["tabs", "currentStep"]),
    createVNode(_component_Box, { bbHeaderCoords: $data.bbHeaderCoords }, null, 8, ["bbHeaderCoords"])
  ], 64);
}
const App$1 = /* @__PURE__ */ _export_sfc(_sfc_main$8, [["render", _sfc_render$8]]);
const HIDE_PRODUCT_TAG = "hidden";
const SHOW_IN_BUNDLE_BUILDER_TAG = "show_in_bundle_builder";
function isProductHiddenInBundleBuilder(product) {
  const tags = (product == null ? void 0 : product.tags) ?? [];
  return tags.includes(HIDE_PRODUCT_TAG) && !tags.includes(SHOW_IN_BUNDLE_BUILDER_TAG);
}
function sortObjectsByAvailability(arr) {
  arr.sort((a, b) => {
    if (!a.available && b.available) {
      return 1;
    } else if (a.available && !b.available) {
      return -1;
    }
    return 0;
  });
}
function matchesRule$1(rule, value) {
  if (!rule || typeof rule !== "string") return false;
  const normalized = rule.trim().toLowerCase();
  const tokens = normalized.match(/(<=|>=|<|>|==|\d+(?:\.\d+)?|and|or)/g);
  if (!tokens) return false;
  const expr = tokens.map((token) => {
    if (["<", "<=", ">", ">=", "=="].includes(token)) {
      return `(${value} ${token}`;
    }
    if (!isNaN(token)) {
      return `${token})`;
    }
    if (token === "and") return "&&";
    if (token === "or") return "||";
    return "";
  }).join(" ");
  try {
    return new Function(`return ${expr};`)();
  } catch (e) {
    console.error("Rule evaluation error:", e);
    return false;
  }
}
const mixMatchInit = (selectors) => {
  var _a;
  const jsonData = (_a = document.querySelector(selectors.json)) == null ? void 0 : _a.textContent;
  let data;
  try {
    data = jsonData ? JSON.parse(jsonData) : null;
  } catch (error) {
    console.error(error);
    console.log("✌️jsonData --->", jsonData);
    return;
  }
  if (!data) return;
  for (let i = 0; i < data.steps.length; i++) {
    const step = data.steps[i];
    step.tabs.forEach((tab, j) => {
      let variants = [];
      tab.products.forEach((product) => {
        var _a2;
        if (isProductHiddenInBundleBuilder(product)) return;
        if (((_a2 = window.Shopify) == null ? void 0 : _a2.locale) === "nl" && product.media && Array.isArray(product.media)) {
          const nlOnlyMedia = product.media.filter((mediaItem) => mediaItem.alt === "nl_only");
          if (product.images && Array.isArray(product.images)) {
            product.images = nlOnlyMedia.map((mediaItem) => {
              var _a3;
              return mediaItem.src || ((_a3 = mediaItem.preview_image) == null ? void 0 : _a3.src);
            }).filter(Boolean);
          }
        }
        const newVariantsList = [];
        product.variants.forEach((variant) => {
          const variantPrice = +variant.price / 100;
          const isVariantHiddenByPriceRule = step.hidingPriceRule ? matchesRule$1(step.hidingPriceRule, variantPrice) : false;
          if (product.tags.includes(`_hidden-variant:${variant.id}`) || variant.isHidden == "true" || isVariantHiddenByPriceRule) return;
          const newV = {
            ...variant,
            collectionName: tab.collection.title,
            stepName: step.title,
            tabName: tab.title,
            productTitle: product.title,
            product,
            specificDiscountable: step.firstAddedProductDiscount ? true : false,
            featured_image: !variant.featured_image ? { src: product.featured_image } : variant.featured_image
          };
          newVariantsList.push(newV);
        });
        product.variants = newVariantsList;
        variants.push(...product.variants);
      });
      sortObjectsByAvailability(variants);
      step.tabs[j].variants = variants;
    });
  }
  data.steps.forEach((step, i) => {
    const newDiscounts = [];
    step.discounts.forEach((discount, j) => {
      if (discount.steps.includes(step.id)) {
        newDiscounts.push(discount);
      }
    });
    step.discounts = newDiscounts;
  });
  const vuexStore = store$1(data);
  const app = createApp(App$1);
  app.use(vuexStore);
  app.config.devtools = true;
  app.config.errorHandler = (err, vm, info) => {
    console.error("Error in component!");
    console.log("✌️info --->", info);
    console.log("✌️vm --->", vm);
    console.log("✌️err --->", err);
    console.log("\n\n\n");
  };
  app.mount(selectors.app);
};
const variantPicker = () => {
  const variantPickerBlocks = document.querySelectorAll("[data-variant-picker-option-labels]");
  variantPickerBlocks.forEach((block) => {
    const pricePerPortionValue = block.querySelector("[data-price-per-portion-value]");
    if (!pricePerPortionValue) return;
    const labels = block.querySelectorAll(".product-variant-picker__pill-list label");
    if (!labels.length) return;
    const variantPicker2 = block.closest("variant-picker");
    if (!variantPicker2) return;
    const updatePricePerPortion = () => {
      let selectedLabel = Array.from(labels).find(
        (label) => label.classList.contains("selected")
      );
      if (!selectedLabel) {
        const allInputs = variantPicker2.querySelectorAll('[data-variant-picker-option-inputs] input[type="radio"]:checked');
        if (allInputs.length > 0) {
          const optionName = block.getAttribute("option-picker-name");
          const matchingInput = Array.from(allInputs).find((input) => {
            const inputName = input.name.replace(/-\d+$/, "");
            return inputName === optionName;
          });
          if (matchingInput) {
            const labelId = matchingInput.id;
            selectedLabel = Array.from(labels).find(
              (label) => label.getAttribute("for") === labelId
            );
          }
        }
      }
      if (selectedLabel) {
        const pricePerPortion = selectedLabel.getAttribute("data-price-per-portion");
        if (pricePerPortion && pricePerPortion.trim() !== "") {
          pricePerPortionValue.textContent = pricePerPortion;
        } else {
          pricePerPortionValue.textContent = "";
        }
      } else {
        pricePerPortionValue.textContent = "";
      }
    };
    updatePricePerPortion();
    variantPicker2.addEventListener("change", (event) => {
      if (event.target.type === "radio") {
        const inputId = event.target.id;
        const relatedLabel = Array.from(labels).find(
          (label) => label.getAttribute("for") === inputId
        );
        if (relatedLabel) {
          setTimeout(updatePricePerPortion, 0);
        }
      }
    });
    labels.forEach((label) => {
      label.addEventListener("click", () => {
        setTimeout(updatePricePerPortion, 0);
      });
    });
  });
};
const overlayModification = () => {
  function initFlyInDrawer() {
    const buttons = document.querySelectorAll("[fly-in-drawer]");
    buttons.forEach(function(button) {
      const sidebarId = button.getAttribute("data-sidebar-id");
      if (!sidebarId) return;
      button.addEventListener("click", function() {
        document.body.classList.add("fly-in-open");
      });
    });
    function removeBodyClasses(sidebarId) {
      const openButton = document.querySelector(`[fly-in-drawer][data-sidebar-id="${sidebarId}"]`);
      if (openButton) {
        setTimeout(function() {
          document.body.classList.remove("fly-in-open");
        }, 200);
      }
    }
    document.addEventListener("click", function(event) {
      const clickedButton = event.target.closest("[data-sidebar-button]");
      if (clickedButton) {
        if (!clickedButton.hasAttribute("fly-in-drawer")) {
          const sidebarId = clickedButton.getAttribute("data-sidebar-id");
          if (sidebarId) {
            const drawer = document.getElementById(sidebarId);
            if (drawer && drawer.classList.contains("sf-fly-content-drawer")) {
              document.body.classList.remove("fly-in-open");
              setTimeout(function() {
                removeBodyClasses(sidebarId);
              }, 350);
            }
          }
        }
      }
      const closeCursor = event.target.closest("close-cursor");
      if (closeCursor) {
        document.body.classList.remove("fly-in-open");
        const openDrawers = document.querySelectorAll(".sf-fly-content-drawer.is-opened");
        openDrawers.forEach(function(drawer) {
          const drawerId = drawer.id;
          setTimeout(function() {
            removeBodyClasses(drawerId);
          }, 200);
        });
      }
      const bodyElement = event.target.closest("body-element");
      if (bodyElement) {
        document.body.classList.remove("fly-in-open");
        const openDrawers = document.querySelectorAll(".sf-fly-content-drawer.is-opened");
        openDrawers.forEach(function(drawer) {
          const drawerId = drawer.id;
          setTimeout(function() {
            removeBodyClasses(drawerId);
          }, 200);
        });
      }
    }, true);
    document.addEventListener("keydown", function(event) {
      if (event.key === "Escape" || event.keyCode === 27) {
        const openDrawer = document.querySelector(".sf-fly-content-drawer.is-opened");
        if (openDrawer) {
          document.body.classList.remove("fly-in-open");
          const drawerId = openDrawer.id;
          setTimeout(function() {
            removeBodyClasses(drawerId);
          }, 200);
        }
      }
    });
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initFlyInDrawer);
  } else {
    initFlyInDrawer();
  }
};
const stockBarometer = () => {
  const initStockBarometers = () => {
    const stockBarometersV1 = document.querySelectorAll(
      '.sf-stock-barometer:not([data-stock-barometer-version="2"])'
    );
    const stockBarometersV2 = document.querySelectorAll(
      '[data-stock-barometer-version="2"]'
    );
    if (!stockBarometersV1.length && !stockBarometersV2.length) return;
    stockBarometersV1.forEach((el) => {
      const {
        start: startValue,
        end: endValue,
        steps: stepsValue
      } = el.dataset;
      const fill2 = el.querySelector(".barometer-fill");
      const valueLabel = el.querySelector(".barometer-value");
      if (!startValue || !endValue || !stepsValue || !fill2 || !valueLabel)
        return;
      const start = new Date(startValue.trim());
      const end = new Date(endValue.trim());
      const now2 = /* @__PURE__ */ new Date();
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()))
        return;
      let steps;
      try {
        steps = JSON.parse(stepsValue);
      } catch (error) {
        console.warn("Stock Barometer: invalid steps JSON.", error);
        return;
      }
      if (!Array.isArray(steps)) return;
      function getCurrentValue() {
        if (now2 <= start) return 100;
        if (now2 >= end) return 0;
        let prev = { time: start, value: 100 };
        let next = { time: end, value: 0 };
        steps.forEach((step) => {
          if (!step || typeof step.time !== "string" || !Number.isFinite(Number(step.value)))
            return;
          const stepTime = new Date(step.time.trim());
          if (Number.isNaN(stepTime.getTime())) return;
          if (stepTime <= now2 && stepTime >= prev.time) {
            prev = { time: stepTime, value: Number(step.value) };
          }
          if (stepTime >= now2 && stepTime <= next.time) {
            next = { time: stepTime, value: Number(step.value) };
          }
        });
        const duration = next.time - prev.time;
        if (duration <= 0) return prev.value;
        const progress = (now2 - prev.time) / duration;
        return prev.value + (next.value - prev.value) * progress;
      }
      const currentValue = getCurrentValue();
      if (!Number.isFinite(currentValue)) return;
      const value = Math.max(0, Math.min(100, currentValue));
      fill2.style.width = `${value}%`;
      valueLabel.textContent = `${Math.round(value)}%`;
    });
    const updateByVariantId = (barometer, variantId) => {
      if (!barometer || !variantId) return;
      const template2 = barometer.querySelector(
        `[data-product-block-template="${variantId}"]`
      );
      const container = barometer.querySelector(
        "[data-product-block-container]"
      );
      if (!template2 || !container) return;
      container.replaceChildren(template2.content.cloneNode(true));
    };
    stockBarometersV2.forEach((barometer) => {
      var _a;
      const productInfo = barometer.closest("product-info");
      const variantPicker2 = productInfo == null ? void 0 : productInfo.querySelector("variant-picker");
      if (!variantPicker2) return;
      updateByVariantId(barometer, (_a = variantPicker2.variant) == null ? void 0 : _a.id);
      variantPicker2.addEventListener("change", () => {
        setTimeout(
          () => {
            var _a2;
            return updateByVariantId(barometer, (_a2 = variantPicker2.variant) == null ? void 0 : _a2.id);
          },
          0
        );
      });
    });
    if (stockBarometersV2.length) {
      document.addEventListener(
        "mini-bundle:variant-changed",
        (event) => {
          var _a;
          const variantId = (_a = event.detail) == null ? void 0 : _a.variantId;
          if (!variantId) return;
          stockBarometersV2.forEach((barometer) => {
            updateByVariantId(barometer, variantId);
          });
        }
      );
    }
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initStockBarometers, {
      once: true
    });
  } else {
    initStockBarometers();
  }
};
const SECTION_SELECTOR = "[data-parallax-section]";
const LAYER_SELECTOR = "[data-parallax-layer]";
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const sectionStates = /* @__PURE__ */ new WeakMap();
const computeBounds = (section, state) => {
  const rect = section.getBoundingClientRect();
  const scrollY = window.scrollY || window.pageYOffset;
  state.bounds.start = scrollY + rect.top - window.innerHeight;
  state.bounds.end = scrollY + rect.bottom;
};
const updateLayers = (section, state) => {
  state.rafId = null;
  if (!state.isActive) {
    return;
  }
  const range = state.bounds.end - state.bounds.start || 1;
  const progress = clamp((window.scrollY - state.bounds.start) / range, 0, 1);
  const centered = progress * 2 - 1;
  state.layers.forEach((layer) => {
    const axis = layer.dataset.parallaxAxis || "y";
    const speed = parseFloat(layer.dataset.parallaxSpeed || "0");
    const translateValue = centered * speed;
    if (axis === "x") {
      layer.style.transform = `translate3d(${translateValue}px, 0, 0)`;
    } else {
      layer.style.transform = `translate3d(0, ${translateValue}px, 0)`;
    }
  });
};
const requestUpdate = (section, state) => {
  if (state.rafId) {
    return;
  }
  state.rafId = requestAnimationFrame(() => updateLayers(section, state));
};
const teardownSection = (section) => {
  var _a;
  const state = sectionStates.get(section);
  if (!state) {
    return;
  }
  (_a = state.observer) == null ? void 0 : _a.disconnect();
  window.removeEventListener("scroll", state.handleScroll);
  window.removeEventListener("resize", state.handleResize);
  if (state.rafId) {
    cancelAnimationFrame(state.rafId);
  }
  section.removeEventListener("shopify:section:unload", state.handleUnload);
  sectionStates.delete(section);
};
const initSection = (section) => {
  if (!section || sectionStates.has(section)) {
    return;
  }
  const layers = section.querySelectorAll(LAYER_SELECTOR);
  if (!layers.length) {
    return;
  }
  const state = {
    layers,
    bounds: { start: 0, end: 0 },
    rafId: null,
    isActive: false,
    observer: null
  };
  state.handleScroll = () => requestUpdate(section, state);
  state.handleResize = () => {
    computeBounds(section, state);
    requestUpdate(section, state);
  };
  state.handleUnload = () => teardownSection(section);
  state.observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.target !== section) {
          return;
        }
        if (entry.isIntersecting) {
          state.isActive = true;
          computeBounds(section, state);
          window.addEventListener("scroll", state.handleScroll, { passive: true });
          window.addEventListener("resize", state.handleResize);
          requestUpdate(section, state);
        } else if (state.isActive) {
          state.isActive = false;
          window.removeEventListener("scroll", state.handleScroll);
          window.removeEventListener("resize", state.handleResize);
        }
      });
    },
    { threshold: 0 }
  );
  state.observer.observe(section);
  section.addEventListener("shopify:section:unload", state.handleUnload);
  sectionStates.set(section, state);
};
const initExistingSections = () => {
  document.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
};
const handleSectionLoad = (event) => {
  var _a;
  const sectionId = (_a = event.detail) == null ? void 0 : _a.sectionId;
  if (!sectionId) {
    return;
  }
  const container = document.getElementById(`shopify-section-${sectionId}`) || document.querySelector(`[data-parallax-section-id="${sectionId}"]`);
  if (!container) {
    return;
  }
  if (container.matches(SECTION_SELECTOR)) {
    initSection(container);
    return;
  }
  const section = container.querySelector(SECTION_SELECTOR);
  if (section) {
    initSection(section);
  }
};
const parallax = () => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initExistingSections, { once: true });
  } else {
    initExistingSections();
  }
  document.addEventListener("shopify:section:load", handleSectionLoad);
};
const rukRating = () => {
  (function() {
    let observer = null;
    let processed = false;
    let ratingClickHandlerAdded = false;
    function checkReviewsAvailability() {
      const ratingSnippet2 = document.querySelector(".product--page .ruk_rating_snippet, .ruk_rating_snippet");
      if (!ratingSnippet2) return;
      ratingSnippet2.hasAttribute("data-done") && ratingSnippet2.getAttribute("data-done") === "true";
      ratingSnippet2.hasAttribute("data-retrieved") && ratingSnippet2.getAttribute("data-retrieved") === "true";
      ratingSnippet2.hasAttribute("data-pending") && ratingSnippet2.getAttribute("data-pending") === "true";
      ratingSnippet2.innerHTML.trim().length > 0;
    }
    function addRatingValueToSocialProofWidgets() {
      const ratingSnippets = document.querySelectorAll(".sf-reviews-social-proof__rating");
      ratingSnippets.forEach((ratingSnippet2) => {
        const ratingCounts = ratingSnippet2.querySelectorAll(".ruk-rating-snippet-count");
        if (ratingCounts.length !== 1) return;
        const ratingLabel = ratingSnippet2.getAttribute("title") || ratingSnippet2.getAttribute("aria-label") || "";
        const ratingMatch = ratingLabel.match(/(\d+(?:[.,]\d+)?)\s*Stars/i);
        if (!ratingMatch) return;
        const rating = Number.parseFloat(ratingMatch[1].replace(",", "."));
        if (Number.isNaN(rating)) return;
        const ratingValue = document.createElement("span");
        ratingValue.className = "ruk-rating-snippet-count";
        ratingValue.setAttribute("aria-hidden", "true");
        ratingValue.textContent = rating.toFixed(1);
        ratingSnippet2.insertBefore(ratingValue, ratingCounts[0]);
      });
    }
    function processReviewsWidget() {
      addRatingValueToSocialProofWidgets();
      const ratingCountElements = document.querySelectorAll(".product--page .ruk-rating-snippet-count");
      if (ratingCountElements.length === 0) return false;
      const ratingCountElement = ratingCountElements[ratingCountElements.length - 1];
      const ratingValueElements = ratingCountElements[0];
      const text = ratingCountElement.textContent.trim();
      if (!text) return false;
      ratingCountElement.style.display = "block";
      if (!(text.startsWith("(") && text.endsWith(")"))) {
        ratingCountElement.textContent = "(" + text + ")";
      }
      if (ratingValueElements) {
        const ratingText = ratingValueElements.textContent.trim();
        if (ratingText && !ratingText.startsWith("(") && !ratingText.endsWith(")")) {
          const ratingValue = parseFloat(ratingText);
          if (!isNaN(ratingValue)) {
            const roundedValue = Math.round(ratingValue * 10) / 10;
            ratingValueElements.textContent = roundedValue.toFixed(1);
          }
        }
      }
      checkReviewsAvailability();
      return true;
    }
    function addRatingClickHandler() {
      if (ratingClickHandlerAdded) return;
      const ratingElement = document.querySelector(".product--page .ruk_rating_snippet");
      if (!ratingElement) return;
      if (ratingElement.closest("sidebar-button") || ratingElement.hasAttribute("data-rating-click-handler")) {
        ratingClickHandlerAdded = true;
        return;
      }
      let bewertungenButton = document.querySelector("[fly-in-testimonials-drawer][data-sidebar-id]");
      if (!bewertungenButton) {
        setTimeout(addRatingClickHandler, 500);
        return;
      }
      const sidebarId = bewertungenButton.getAttribute("data-sidebar-id");
      if (!sidebarId) {
        setTimeout(addRatingClickHandler, 500);
        return;
      }
      ratingElement.setAttribute("data-rating-click-handler", "true");
      ratingElement.addEventListener("click", function(e) {
        e.preventDefault();
        e.stopPropagation();
        const triggerButton = document.querySelector(`[fly-in-drawer][data-sidebar-id="${sidebarId}"], [data-sidebar-button][data-sidebar-id="${sidebarId}"]`);
        if (triggerButton) {
          triggerButton.click();
        }
      });
      ratingClickHandlerAdded = true;
    }
    function checkWidgetLoaded() {
      const widgetElements = document.querySelectorAll(".product--page .ruk_rating_snippet, .ruk-rating-snippet-count");
      if (widgetElements.length > 0) {
        const success = processReviewsWidget();
        addRatingClickHandler();
        checkReviewsAvailability();
        if (success && observer) {
          observer.disconnect();
          observer = null;
          processed = true;
        }
        return success;
      }
      checkReviewsAvailability();
      return false;
    }
    function init2() {
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", startChecking);
      } else {
        startChecking();
      }
    }
    function fetchRatingFromAPI(sku, ratingSnippet2, reviewsioElement) {
      const store2 = "aiheadnutrition.com";
      const lang = document.documentElement.lang || "de";
      const apiUrl = `https://api.reviews.io/product/rating-batch?sku=${encodeURIComponent(sku)}&store=${store2}&lang=${lang}`;
      console.log("[Reviews.io] Fetching rating from API:", apiUrl);
      fetch(apiUrl).then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      }).then((data) => {
        console.log("[Reviews.io] Rating data received:", data);
        let productData = null;
        if (Array.isArray(data) && data.length > 0) {
          productData = data[0];
        } else if (data && data.products && Array.isArray(data.products) && data.products.length > 0) {
          productData = data.products[0];
        } else if (data && typeof data === "object" && !Array.isArray(data)) {
          productData = data;
        }
        if (productData) {
          const rating = productData.average_rating || productData.rating || productData.averageRating || productData.avg_rating;
          const reviewCount = productData.num_ratings || productData.review_count || productData.reviewCount || productData.count || productData.total_reviews || productData.totalReviews;
          console.log("[Reviews.io] Product data:", JSON.stringify(productData).substring(0, 200));
          console.log("[Reviews.io] Extracted rating:", rating, "reviewCount:", reviewCount);
          if (rating !== void 0 && rating !== null && reviewCount !== void 0 && reviewCount !== null) {
            console.log("[Reviews.io] Rating data found:", rating, "with", reviewCount, "reviews");
            displayRatingInSnippet(ratingSnippet2, reviewsioElement, parseFloat(rating), parseInt(reviewCount));
            ratingSnippet2.setAttribute("data-done", "true");
            ratingSnippet2.setAttribute("data-retrieved", "true");
            setTimeout(() => {
              processReviewsWidget();
            }, 100);
          } else {
            console.warn("[Reviews.io] No rating data found in product. Available keys:", Object.keys(productData));
          }
        } else {
          console.warn("[Reviews.io] No products found in API response. Data structure:", typeof data, Array.isArray(data) ? "array" : "object");
        }
      }).catch((error) => {
        console.error("[Reviews.io] Failed to fetch rating from API:", error);
      });
    }
    function displayRatingInSnippet(ratingSnippet2, reviewsioElement, rating, reviewCount) {
      const starsHTML = generateStarsHTML(rating);
      const isReviewsSocialProofWidget = ratingSnippet2.classList.contains("sf-reviews-social-proof__rating");
      const ratingDetailsHTML = isReviewsSocialProofWidget ? `<span aria-hidden="true" class="ruk-rating-snippet-count">${rating.toFixed(1)}</span><span aria-hidden="true" class="ruk-rating-snippet-count">(${reviewCount})</span>` : `<span aria-hidden="true" class="ruk-rating-snippet-count" style="color: rgb(107, 42, 138); display: block;">(${reviewCount})</span>`;
      const ratingHtml = starsHTML + ratingDetailsHTML;
      console.log("[Reviews.io] Setting rating HTML:", ratingHtml);
      ratingSnippet2.setAttribute("data-rating-click-handler", "true");
      ratingSnippet2.setAttribute("rating-snippet-batch", "0");
      ratingSnippet2.setAttribute("style", "color: rgb(255, 191, 26);");
      ratingSnippet2.setAttribute("title", `${rating.toFixed(2)} Stars - ${reviewCount}`);
      ratingSnippet2.setAttribute("aria-label", `${rating.toFixed(2)} Stars - ${reviewCount}`);
      ratingSnippet2.setAttribute("tabindex", "0");
      ratingSnippet2.innerHTML = ratingHtml;
      console.log("[Reviews.io] Rating snippet innerHTML after update:", ratingSnippet2.innerHTML);
    }
    function generateStarsHTML(rating) {
      const fullStars = Math.floor(rating);
      const decimal = rating % 1;
      const hasPartialStar = decimal >= 0.5;
      let starsHTML = "";
      for (let i = 0; i < fullStars; i++) {
        starsHTML += '<i aria-hidden="true" class="ruk-icon-percentage-star--100"></i>';
      }
      if (hasPartialStar) {
        starsHTML += '<i aria-hidden="true" class="ruk-icon-percentage-star--75"></i>';
      }
      console.log("[Reviews.io] Generated stars HTML for rating", rating, ":", starsHTML);
      return starsHTML;
    }
    function updateReviewsIOSku() {
      const reviewsioElement = document.querySelector("reviewsio-product-ratings");
      if (!reviewsioElement) return false;
      const ratingSnippet2 = reviewsioElement.querySelector(".ruk_rating_snippet");
      if (!ratingSnippet2) return false;
      const currentSku = ratingSnippet2.getAttribute("data-sku") || "";
      let reviewsSku = window.productReviewsSku || "";
      let variantsSku = window.productVariantsSku || "";
      let variantsId = window.productVariantsId || "";
      if (!reviewsSku || !variantsSku || !variantsId) {
        const appBlock = reviewsioElement.closest("[data-custom-app]");
        if (appBlock) {
          reviewsSku = reviewsSku || appBlock.getAttribute("data-product-reviews-sku") || "";
          variantsSku = variantsSku || appBlock.getAttribute("data-product-variants-sku") || "";
          variantsId = variantsId || appBlock.getAttribute("data-product-variants-id") || "";
        }
      }
      const currentParts = currentSku.split(";").filter((p2) => p2 && p2.trim() && !p2.match(/^mix-save-mini$/));
      const variantIdsFromCurrent = currentParts.filter((p2) => p2.match(/^\d+$/)).join(";");
      let newSku = "";
      if (variantIdsFromCurrent) {
        newSku = variantIdsFromCurrent;
      } else if (variantsId && variantsId.trim()) {
        newSku = variantsId;
      } else if (variantsSku && variantsSku.trim()) {
        newSku = variantsSku;
      }
      if (reviewsSku && reviewsSku.trim()) {
        if (newSku) {
          newSku += ";" + reviewsSku;
        } else {
          newSku = reviewsSku;
        }
      }
      const needsUpdate = !currentSku || currentSku.startsWith(";;;") || currentSku.match(/^;+$/) || reviewsSku && !currentSku.includes(reviewsSku);
      if (newSku && needsUpdate && newSku !== currentSku) {
        console.log("[Reviews.io] Updating data-sku from:", currentSku, "to:", newSku);
        ratingSnippet2.setAttribute("data-sku", newSku);
        ratingSnippet2.removeAttribute("data-done");
        ratingSnippet2.removeAttribute("data-retrieved");
        ratingSnippet2.innerHTML = "";
        const event = new CustomEvent("reviewsio:sku-updated", {
          detail: { sku: newSku, element: ratingSnippet2 }
        });
        reviewsioElement.dispatchEvent(event);
        ratingSnippet2.dispatchEvent(event);
        if (window.ratingSnippet && typeof window.ratingSnippet === "function") {
          try {
            window.ratingSnippet();
          } catch (e) {
            console.warn("Failed to manually trigger Reviews.io rating:", e);
          }
        }
        fetchRatingFromAPI(newSku, ratingSnippet2, reviewsioElement);
        return true;
      }
      return false;
    }
    function initReviewsIORating() {
      updateReviewsIOSku();
      const ratingSnippet2 = document.querySelector(".product--page .ruk_rating_snippet, .ruk_rating_snippet");
      if (!ratingSnippet2) return false;
      const hasDone = ratingSnippet2.hasAttribute("data-done") && ratingSnippet2.getAttribute("data-done") === "true";
      const hasRetrieved = ratingSnippet2.hasAttribute("data-retrieved") && ratingSnippet2.getAttribute("data-retrieved") === "true";
      const hasContent = ratingSnippet2.innerHTML.trim().length > 0;
      if (hasDone && hasRetrieved && hasContent) {
        return true;
      }
      return false;
    }
    function startChecking() {
      addRatingClickHandler();
      initReviewsIORating();
      if (checkWidgetLoaded()) {
        return;
      }
      observer = new MutationObserver(function(mutations) {
        if (!processed) {
          updateReviewsIOSku();
        }
        if (!processed) {
          initReviewsIORating();
        }
        if (!processed && checkWidgetLoaded()) {
          return;
        }
        if (!ratingClickHandlerAdded) {
          addRatingClickHandler();
        }
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        characterData: true
      });
      const timeouts = [500, 1e3, 2e3, 3e3, 5e3];
      timeouts.forEach((delay) => {
        setTimeout(function() {
          if (!processed) {
            updateReviewsIOSku();
          }
          if (!processed) {
            initReviewsIORating();
          }
          if (!processed && checkWidgetLoaded()) {
            return;
          }
          if (!ratingClickHandlerAdded) {
            addRatingClickHandler();
          }
        }, delay);
      });
      setTimeout(function() {
        if (!ratingClickHandlerAdded) {
          addRatingClickHandler();
        }
        if (observer) {
          observer.disconnect();
          observer = null;
        }
      }, 1e4);
    }
    init2();
  })();
};
function matchesRule(rule, value) {
  if (!rule || typeof rule !== "string") return false;
  const normalized = rule.trim().toLowerCase();
  const tokens = normalized.match(/(<=|>=|<|>|==|\d+(?:\.\d+)?|and|or)/g);
  if (!tokens) return false;
  const expr = tokens.map((token) => {
    if (["<", "<=", ">", ">=", "=="].includes(token)) {
      return `(${value} ${token}`;
    }
    if (!Number.isNaN(Number(token))) {
      return `${token})`;
    }
    if (token === "and") return "&&";
    if (token === "or") return "||";
    return "";
  }).join(" ");
  try {
    return new Function(`return ${expr};`)();
  } catch (error) {
    console.error("Mini bundle price rule evaluation error:", error);
    return false;
  }
}
function getProductVariants(product) {
  const variants = (product == null ? void 0 : product.variantsWithMetafields) || (product == null ? void 0 : product.variants) || [];
  return Array.isArray(variants) ? variants : [];
}
function getVariantContentLabel(product, variant) {
  if (!product || !variant) return (product == null ? void 0 : product.variantContentLabel) || "";
  const labelsByVariantId = product.variantContentLabels;
  if (labelsByVariantId && variant.id != null) {
    const localizedLabel = labelsByVariantId[String(variant.id)] ?? labelsByVariantId[variant.id];
    if (localizedLabel) return localizedLabel;
  }
  const selected = product.selected_or_first_available_variant;
  if (product.variantContentLabel && selected && String(selected.id) === String(variant.id)) {
    return product.variantContentLabel;
  }
  const optionNames = Array.isArray(product.options) ? product.options.map((option) => String(option).toLowerCase()) : [];
  const contentOptionIndex = optionNames.findIndex((optionName) => optionName === "inhalt" || optionName === "inhoud" || optionName === "size");
  if (contentOptionIndex === 0) return variant.option1 || "";
  if (contentOptionIndex === 1) return variant.option2 || "";
  if (contentOptionIndex === 2) return variant.option3 || "";
  return (product == null ? void 0 : product.variantContentLabel) || "";
}
function getFreeSamplesPriceRule(config, variant) {
  var _a, _b, _c, _d, _e, _f;
  return (config == null ? void 0 : config.freeSamplesPriceRule) || ((_b = (_a = variant == null ? void 0 : variant.metafields) == null ? void 0 : _a.miniBundle) == null ? void 0 : _b.freeSamplesPriceRule) || ((_d = (_c = variant == null ? void 0 : variant.metafields) == null ? void 0 : _c.miniBundle) == null ? void 0 : _d.giftsPriceRule) || ((_f = (_e = variant == null ? void 0 : variant.metafields) == null ? void 0 : _e.miniBundle) == null ? void 0 : _f.hidingPriceRule) || "";
}
function filterFreeSampleProductByPriceRule(product, priceRule) {
  if (!product || !priceRule) {
    return product;
  }
  const variants = getProductVariants(product);
  const filteredVariants = variants.filter((variant) => {
    const variantPrice = Number((variant == null ? void 0 : variant.price) || 0) / 100;
    return !matchesRule(priceRule, variantPrice);
  });
  if (filteredVariants.length === 0) {
    return null;
  }
  const nextDisplayVariant = filteredVariants.find((variant) => variant == null ? void 0 : variant.available) || filteredVariants[0] || null;
  return {
    ...product,
    variantsWithMetafields: filteredVariants,
    selected_or_first_available_variant: nextDisplayVariant,
    price: (nextDisplayVariant == null ? void 0 : nextDisplayVariant.price) ?? product.price,
    compare_at_price: (nextDisplayVariant == null ? void 0 : nextDisplayVariant.compare_at_price) ?? product.compare_at_price,
    variantContentLabel: getVariantContentLabel(product, nextDisplayVariant)
  };
}
function filterFreeSampleCollectionsByPriceRule(products, priceRule) {
  var _a;
  if (!priceRule) {
    return products;
  }
  if (products.length > 0 && ((_a = products[0]) == null ? void 0 : _a.collectionId)) {
    return products.map((collection) => ({
      ...collection,
      products: (collection.products || []).map((product) => filterFreeSampleProductByPriceRule(product, priceRule)).filter(Boolean)
    })).filter((collection) => (collection.products || []).length > 0);
  }
  return products.map((product) => filterFreeSampleProductByPriceRule(product, priceRule)).filter(Boolean);
}
function isProductInStock(product) {
  if (!product) return false;
  if (product.available === false) return false;
  const variants = getProductVariants(product);
  const displayVariant = product.selected_or_first_available_variant || variants.find((variant) => variant == null ? void 0 : variant.available) || variants[0] || null;
  return (displayVariant == null ? void 0 : displayVariant.available) !== false;
}
function filterOutOfStockProducts(products) {
  var _a;
  if (!Array.isArray(products) || products.length === 0) {
    return products || [];
  }
  if ((_a = products[0]) == null ? void 0 : _a.collectionId) {
    return products.map((collection) => ({
      ...collection,
      products: (collection.products || []).filter(isProductInStock)
    })).filter((collection) => (collection.products || []).length > 0);
  }
  return products.filter(isProductInStock);
}
const store = (config) => {
  return createStore({
    state() {
      return {
        // Configuration data
        config,
        parentProduct: config.parentProduct || null,
        currentParentVariantId: config.currentParentVariantId || null,
        samplePackagesProducts: config.samplePackagesProducts || [],
        freeSamplesProducts: config.freeSamplesProducts || [],
        textContent: config.textContent || {},
        icons: config.icons || {},
        // State for selected products
        // Array of selected sample packages (each item is a product object or null)
        selectedSamplePackages: [],
        // Array of selected free samples (each item is a product object or null)
        selectedFreeSamples: []
      };
    },
    mutations: {
      /**
       * Sets the current parent variant ID
       * Preserves existing selections up to the new required quantity
       * @param {Object} state - Vuex state
       * @param {Object} payload - Payload object
       * @param {number} payload.variantId - Variant ID to set
       * @param {number} payload.requiredPackages - Required packages for new variant
       * @param {number} payload.requiredSamples - Required samples for new variant
       */
      SET_CURRENT_PARENT_VARIANT(state, { variantId, requiredPackages = 0, requiredSamples = 0 }) {
        state.currentParentVariantId = variantId;
        if (Array.isArray(state.selectedSamplePackages)) {
          state.selectedSamplePackages = state.selectedSamplePackages.slice(0, requiredPackages);
          while (state.selectedSamplePackages.length < requiredPackages) {
            state.selectedSamplePackages.push(null);
          }
        } else {
          state.selectedSamplePackages = Array(requiredPackages).fill(null);
        }
        if (Array.isArray(state.selectedFreeSamples)) {
          state.selectedFreeSamples = state.selectedFreeSamples.slice(0, requiredSamples);
          while (state.selectedFreeSamples.length < requiredSamples) {
            state.selectedFreeSamples.push(null);
          }
        } else {
          state.selectedFreeSamples = Array(requiredSamples).fill(null);
        }
      },
      /**
       * Sets a sample package at a specific index
       * @param {Object} state - Vuex state
       * @param {Object} payload - Payload object
       * @param {number} payload.index - Index in the array
       * @param {Object|null} payload.product - Product object or null to clear
       */
      SET_SAMPLE_PACKAGE(state, { index, product }) {
        if (!Array.isArray(state.selectedSamplePackages)) {
          state.selectedSamplePackages = [];
        }
        state.selectedSamplePackages[index] = product;
        state.selectedSamplePackages = [
          ...state.selectedSamplePackages
        ];
      },
      /**
       * Sets a free sample at a specific index
       * @param {Object} state - Vuex state
       * @param {Object} payload - Payload object
       * @param {number} payload.index - Index in the array
       * @param {Object|null} payload.product - Product object or null to clear
       */
      SET_FREE_SAMPLE(state, { index, product }) {
        if (!Array.isArray(state.selectedFreeSamples)) {
          state.selectedFreeSamples = [];
        }
        state.selectedFreeSamples[index] = product;
        state.selectedFreeSamples = [...state.selectedFreeSamples];
      },
      /**
       * Clears all sample packages
       * @param {Object} state - Vuex state
       */
      CLEAR_SAMPLE_PACKAGES(state) {
        state.selectedSamplePackages = [];
      },
      /**
       * Clears all free samples
       * @param {Object} state - Vuex state
       */
      CLEAR_FREE_SAMPLES(state) {
        state.selectedFreeSamples = [];
      }
    },
    actions: {
      /**
       * Action to set current parent variant
       * Preserves existing selections when switching variants
       * @param {Object} context - Vuex context
       * @param {number} variantId - Variant ID to set
       */
      setCurrentParentVariant({ commit: commit2, state }, variantId) {
        var _a, _b, _c, _d, _e, _f;
        const variant = (_b = (_a = state.parentProduct) == null ? void 0 : _a.variantsWithMetafields) == null ? void 0 : _b.find((v) => v.id === variantId);
        const requiredPackages = ((_d = (_c = variant == null ? void 0 : variant.metafields) == null ? void 0 : _c.miniBundle) == null ? void 0 : _d.packages) || 0;
        const requiredSamples = ((_f = (_e = variant == null ? void 0 : variant.metafields) == null ? void 0 : _e.miniBundle) == null ? void 0 : _f.freeSamplesQuantity) || 0;
        commit2("SET_CURRENT_PARENT_VARIANT", {
          variantId,
          requiredPackages: Number(requiredPackages) || 0,
          requiredSamples: Number(requiredSamples) || 0
        });
      },
      /**
       * Action to set a sample package
       * @param {Object} context - Vuex context
       * @param {Object} payload - Payload with index and product
       */
      setSamplePackage({ commit: commit2 }, payload) {
        commit2("SET_SAMPLE_PACKAGE", payload);
      },
      /**
       * Action to set a free sample
       * @param {Object} context - Vuex context
       * @param {Object} payload - Payload with index and product
       */
      setFreeSample({ commit: commit2 }, payload) {
        commit2("SET_FREE_SAMPLE", payload);
      },
      /**
       * Action to clear all sample packages
       * @param {Object} context - Vuex context
       */
      clearSamplePackages({ commit: commit2 }) {
        commit2("CLEAR_SAMPLE_PACKAGES");
      },
      /**
       * Action to clear all free samples
       * @param {Object} context - Vuex context
       */
      clearFreeSamples({ commit: commit2 }) {
        commit2("CLEAR_FREE_SAMPLES");
      }
    },
    getters: {
      /**
       * Gets the current parent variant object
       * @param {Object} state - Vuex state
       * @returns {Object|null} Current variant or null
       */
      getCurrentParentVariant: (state) => {
        if (!state.parentProduct || !state.currentParentVariantId) {
          return null;
        }
        const variants = state.parentProduct.variantsWithMetafields || [];
        return variants.find(
          (v) => v.id === state.currentParentVariantId
        ) || null;
      },
      /**
       * Gets the number of required sample packages from current variant
       * @param {Object} state - Vuex state
       * @param {Object} getters - Vuex getters
       * @returns {number} Required packages count
       */
      getRequiredSamplePackages: (state, getters) => {
        const variant = getters.getCurrentParentVariant;
        if (!variant || !variant.metafields || !variant.metafields.miniBundle) {
          return 0;
        }
        return Number(variant.metafields.miniBundle.packages) || 0;
      },
      /**
       * Gets the number of required free samples from current variant
       * @param {Object} state - Vuex state
       * @param {Object} getters - Vuex getters
       * @returns {number} Required free samples count
       */
      getRequiredFreeSamples: (state, getters) => {
        const variant = getters.getCurrentParentVariant;
        if (!variant || !variant.metafields || !variant.metafields.miniBundle) {
          return 0;
        }
        return Number(variant.metafields.miniBundle.freeSamplesQuantity) || 0;
      },
      /**
       * Gets the discount percent from current variant
       * @param {Object} state - Vuex state
       * @param {Object} getters - Vuex getters
       * @returns {number} Discount percent
       */
      getDiscountPercent: (state, getters) => {
        const variant = getters.getCurrentParentVariant;
        if (!variant || !variant.metafields || !variant.metafields.miniBundle) {
          return 0;
        }
        return Number(variant.metafields.miniBundle.discountPercent) || 0;
      },
      /**
       * Gets selected sample packages array
       * @param {Object} state - Vuex state
       * @returns {Array} Array of selected products (or null for empty slots)
       */
      getSelectedSamplePackages: (state) => {
        return state.selectedSamplePackages || [];
      },
      /**
       * Gets selected free samples array
       * @param {Object} state - Vuex state
       * @returns {Array} Array of selected products (or null for empty slots)
       */
      getSelectedFreeSamples: (state) => {
        return state.selectedFreeSamples || [];
      },
      /**
       * Checks if all required sample packages are selected
       * @param {Object} state - Vuex state
       * @param {Object} getters - Vuex getters
       * @returns {boolean} True if all required packages are selected
       */
      isSamplePackagesComplete: (state, getters) => {
        const required = getters.getRequiredSamplePackages;
        const selected = state.selectedSamplePackages || [];
        const filledCount = selected.filter(
          (p2) => p2 !== null && p2 !== void 0
        ).length;
        return filledCount >= required;
      },
      /**
       * Checks if all required free samples are selected
       * @param {Object} state - Vuex state
       * @param {Object} getters - Vuex getters
       * @returns {boolean} True if all required samples are selected
       */
      isFreeSamplesComplete: (state, getters) => {
        const required = getters.getRequiredFreeSamples;
        const selected = state.selectedFreeSamples || [];
        const filledCount = selected.filter(
          (p2) => p2 !== null && p2 !== void 0
        ).length;
        return filledCount >= required;
      },
      /**
       * Gets text content for customizer
       * @param {Object} state - Vuex state
       * @returns {Object} Text content object
       */
      getTextContent: (state) => {
        var _a;
        return ((_a = state.textContent) == null ? void 0 : _a.customizer) || {};
      },
      /**
       * Gets locales for text content
       * @param {Object} state - Vuex state
       * @return {Object} Locales object
       */
      getLocales: (state) => {
        var _a;
        return ((_a = state.textContent) == null ? void 0 : _a.locales) || {};
      },
      /**
       * Gets icons object
       * @param {Object} state - Vuex state
       * @returns {Object} Icons object
       */
      getIcons: (state) => {
        return state.icons || {};
      },
      /**
       * Checks if at least one sample package is selected
       * @param {Object} state - Vuex state
       * @returns {boolean} True if at least one package is selected
       */
      hasSelectedSamplePackages: (state) => {
        const selected = state.selectedSamplePackages || [];
        return selected.some((p2) => p2 !== null && p2 !== void 0);
      },
      /**
       * Gets available sample packages products
       * Supports both old format (flat array) and new format (array of collections)
       * @param {Object} state - Vuex state
       * @returns {Array} Array of sample package products or collections
       */
      getSamplePackagesProducts: (state) => {
        const products = state.samplePackagesProducts || [];
        return filterOutOfStockProducts(products);
      },
      /**
       * Gets available free samples products
       * Supports both old format (flat array) and new format (array of collections)
       * @param {Object} state - Vuex state
       * @returns {Array} Array of free sample products or collections
       */
      getFreeSamplesProducts: (state) => {
        var _a;
        const products = state.freeSamplesProducts || [];
        const currentParentVariant = (((_a = state.parentProduct) == null ? void 0 : _a.variantsWithMetafields) || []).find((variant) => variant.id === state.currentParentVariantId);
        const freeSamplesPriceRule = getFreeSamplesPriceRule(state.config, currentParentVariant);
        const filteredProducts = filterFreeSampleCollectionsByPriceRule(
          products,
          freeSamplesPriceRule
        );
        return filterOutOfStockProducts(filteredProducts);
      }
    }
  });
};
const _sfc_main$7 = {
  name: "ProductRow",
  props: {
    /**
     * Selected product object or null for empty state
     * @type {Object|null}
     */
    selectedProduct: {
      type: Object,
      default: null
    },
    /**
     * Message to display in empty state
     * @type {String}
     */
    emptyMessage: {
      type: String,
      required: true
    },
    /**
     * Plus icon SVG markup
     * @type {String}
     */
    plusIcon: {
      type: String,
      required: true
    },
    /**
     * Swap icon SVG markup
     * @type {String}
     */
    swapIcon: {
      type: String,
      required: true
    },
    /**
     * Discount percent to apply
     * @type {Number}
     */
    discountPercent: {
      type: Number,
      default: 0
    },
    /**
     * Whether this is a gift/free sample (100% discount)
     * @type {Boolean}
     */
    isGift: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters({
      locales: "getLocales"
    }),
    /**
     * Gets product variant for price calculation
     * @returns {Object|null} Variant object or null
     */
    productVariant() {
      if (!this.selectedProduct) {
        return null;
      }
      if (this.selectedProduct.selected_or_first_available_variant) {
        return this.selectedProduct.selected_or_first_available_variant;
      }
      if (this.selectedProduct.variants && this.selectedProduct.variants.length > 0) {
        return this.selectedProduct.variants[0];
      }
      return null;
    },
    /**
     * Gets product price from variant or product object
     * @returns {Number} Price in cents
     */
    productPrice() {
      var _a, _b;
      if ((_a = this.productVariant) == null ? void 0 : _a.price) {
        return this.productVariant.price;
      }
      return ((_b = this.selectedProduct) == null ? void 0 : _b.price) || 0;
    },
    /**
     * Gets compare at price from variant
     * @returns {Number} Compare at price in cents
     */
    compareAtPrice() {
      var _a;
      if ((_a = this.productVariant) == null ? void 0 : _a.compare_at_price) {
        return this.productVariant.compare_at_price;
      }
      return 0;
    },
    /**
     * Calculated discounted price
     * @returns {Number} Discounted price in cents
     */
    discountedPrice() {
      if (this.isGift) {
        return 0;
      }
      return this.calculateDiscountedPrice(this.productPrice, this.discountPercent);
    },
    /**
     * Price to show as compare price (original price before discount)
     * @returns {Number} Compare price in cents
     */
    displayComparePrice() {
      if (this.isGift) {
        return this.productPrice;
      }
      if (this.discountPercent > 0) {
        return this.productPrice;
      }
      if (this.compareAtPrice > this.productPrice) {
        return this.compareAtPrice;
      }
      return 0;
    },
    /**
     * Price to show as current price
     * @returns {Number} Current price in cents
     */
    displayCurrentPrice() {
      return this.discountedPrice;
    },
    /**
     * Whether to show compare price
     * @returns {Boolean}
     */
    showComparePrice() {
      return this.displayComparePrice > 0 && this.displayComparePrice > this.displayCurrentPrice;
    },
    /**
     * Product image URL.
     * Prioritizes collectionImage from JSON (which comes from collection_image metafield) if available,
     * otherwise falls back to featured_image
     * @returns {String}
     */
    productImage() {
      var _a, _b, _c;
      if (!this.selectedProduct) {
        return "";
      }
      if ((_a = this.selectedProduct) == null ? void 0 : _a.collectionImage) {
        return this.selectedProduct.collectionImage;
      }
      if (this.selectedProduct.featured_image) {
        return this.selectedProduct.featured_image;
      }
      if ((_c = (_b = this.selectedProduct.featured_media) == null ? void 0 : _b.preview_image) == null ? void 0 : _c.src) {
        return this.selectedProduct.featured_media.preview_image.src;
      }
      return "";
    }
  },
  methods: {
    /**
     * Handles click on add button
     * Emits 'add' event for parent component to handle
     */
    handleAddClick() {
      this.$emit("add");
    },
    /**
     * Handles click on swap button
     * Emits 'swap' event for parent component to handle
     */
    handleSwapClick() {
      this.$emit("swap");
    },
    /**
     * Formats price from cents to currency string
     * Handles UK store format vs other locales
     * @param {Number} priceInCents - Price in cents
     * @returns {String} Formatted price string
     */
    formatPrice(priceInCents) {
      var _a, _b, _c, _d, _e;
      if (typeof priceInCents !== "number") {
        return "0,00 €";
      }
      const isUKStore = typeof window !== "undefined" && (((_b = (_a = window.Shopify) == null ? void 0 : _a.currency) == null ? void 0 : _b.active) === "GBP" || ((_c = window.Shopify) == null ? void 0 : _c.locale) === "en-GB");
      const priceValue = priceInCents <= 0 ? 0 : priceInCents;
      const formattedPrice = (priceValue / 100).toFixed(2);
      if (isUKStore) {
        return `£ ${formattedPrice}`;
      }
      const currency = ((_e = (_d = this.$store) == null ? void 0 : _d.state) == null ? void 0 : _e.currency) || "€";
      return `${formattedPrice.split(".").join(",")} ${currency}`;
    },
    /**
     * Calculates discounted price
     * @param {Number} originalPrice - Original price in cents
     * @param {Number} discountPercent - Discount percentage
     * @returns {Number} Discounted price in cents
     */
    calculateDiscountedPrice(originalPrice, discountPercent) {
      if (typeof originalPrice !== "number" || typeof discountPercent !== "number") {
        return originalPrice || 0;
      }
      const discountMultiplier = 1 - discountPercent / 100;
      return Math.round(originalPrice * discountMultiplier);
    }
  }
};
const _hoisted_1$7 = {
  key: 0,
  class: "mini-bundle-product-row--empty"
};
const _hoisted_2$6 = { class: "mini-bundle-product-row--empty-message" };
const _hoisted_3$6 = ["innerHTML"];
const _hoisted_4$4 = {
  key: 1,
  class: "mini-bundle-product-row--selected"
};
const _hoisted_5$4 = { class: "mini-bundle-product-row-image-wrapper" };
const _hoisted_6$4 = ["src", "alt"];
const _hoisted_7$3 = { class: "mini-bundle-product-row--info" };
const _hoisted_8$3 = { class: "mini-bundle-product-row--type" };
const _hoisted_9$2 = { class: "mini-bundle-product-row--title" };
const _hoisted_10$2 = { class: "mini-bundle-product-row--price-wrapper" };
const _hoisted_11$2 = {
  key: 0,
  class: "mini-bundle-product-row--price-compare"
};
const _hoisted_12$2 = {
  key: 1,
  class: "mini-bundle-product-row--price-normal"
};
const _hoisted_13$2 = {
  key: 2,
  class: "mini-bundle-product-row--discount"
};
const _hoisted_14$2 = ["innerHTML"];
function _sfc_render$7(_ctx, _cache, $props, $setup, $data, $options) {
  return openBlock(), createElementBlock("div", {
    class: normalizeClass(["mini-bundle-product-row", { selected: !!$props.selectedProduct }]),
    onClick: _cache[2] || (_cache[2] = (...args) => $options.handleAddClick && $options.handleAddClick(...args))
  }, [
    !$props.selectedProduct ? (openBlock(), createElementBlock("div", _hoisted_1$7, [
      createBaseVNode("span", _hoisted_2$6, toDisplayString($props.emptyMessage), 1),
      createBaseVNode("button", {
        class: "mini-bundle-product-row--add-button",
        type: "button",
        onClick: _cache[0] || (_cache[0] = (...args) => $options.handleAddClick && $options.handleAddClick(...args))
      }, [
        createBaseVNode("span", { innerHTML: $props.plusIcon }, null, 8, _hoisted_3$6)
      ])
    ])) : (openBlock(), createElementBlock("div", _hoisted_4$4, [
      createBaseVNode("div", _hoisted_5$4, [
        createBaseVNode("img", {
          class: "mini-bundle-product-row-image",
          src: $options.productImage,
          alt: $props.selectedProduct.title
        }, null, 8, _hoisted_6$4)
      ]),
      createBaseVNode("div", _hoisted_7$3, [
        createBaseVNode("span", _hoisted_8$3, toDisplayString($props.selectedProduct.type), 1),
        createBaseVNode("span", _hoisted_9$2, toDisplayString($props.selectedProduct.title), 1),
        createBaseVNode("div", _hoisted_10$2, [
          $options.showComparePrice ? (openBlock(), createElementBlock("span", _hoisted_11$2, toDisplayString($options.formatPrice($options.displayComparePrice)), 1)) : createCommentVNode("", true),
          $options.displayCurrentPrice > 0 ? (openBlock(), createElementBlock("span", _hoisted_12$2, toDisplayString($options.formatPrice($options.displayCurrentPrice)), 1)) : createCommentVNode("", true),
          $props.isGift || $props.discountPercent > 0 ? (openBlock(), createElementBlock("span", _hoisted_13$2, toDisplayString($props.isGift ? _ctx.locales.free || "free" : `-${$props.discountPercent}%`), 1)) : createCommentVNode("", true)
        ])
      ]),
      createBaseVNode("button", {
        class: "mini-bundle-product-row--swap-button",
        onClick: _cache[1] || (_cache[1] = (...args) => $options.handleSwapClick && $options.handleSwapClick(...args))
      }, [
        createBaseVNode("span", { innerHTML: $props.swapIcon }, null, 8, _hoisted_14$2)
      ])
    ]))
  ], 2);
}
const ProductRow = /* @__PURE__ */ _export_sfc(_sfc_main$7, [["render", _sfc_render$7]]);
const _sfc_main$6 = {
  name: "Banner",
  props: {
    /**
     * Whether to show the banner
     * @type {Boolean}
     */
    showBanner: {
      type: Boolean,
      default: false
    },
    /**
     * Upper heading text
     * @type {String}
     */
    upperHeading: {
      type: String,
      required: true
    },
    /**
     * Main heading text
     * @type {String}
     */
    heading: {
      type: String,
      required: true
    },
    /**
     * Checkmark icon SVG markup
     * @type {String}
     */
    checkmarkIcon: {
      type: String,
      required: true
    }
  }
};
const _hoisted_1$6 = {
  key: 0,
  class: "mini-bundle-samples-banner"
};
const _hoisted_2$5 = { class: "mini-bundle-samples-banner-icon-wrapper" };
const _hoisted_3$5 = ["innerHTML"];
const _hoisted_4$3 = { class: "mini-bundle-samples-banner-content" };
const _hoisted_5$3 = { class: "mini-bundle-samples-banner-upperheading" };
const _hoisted_6$3 = { class: "mini-bundle-samples-banner-heading" };
function _sfc_render$6(_ctx, _cache, $props, $setup, $data, $options) {
  return $props.showBanner ? (openBlock(), createElementBlock("div", _hoisted_1$6, [
    createBaseVNode("div", _hoisted_2$5, [
      createBaseVNode("span", { innerHTML: $props.checkmarkIcon }, null, 8, _hoisted_3$5)
    ]),
    createBaseVNode("div", _hoisted_4$3, [
      createBaseVNode("h5", _hoisted_5$3, toDisplayString($props.upperHeading), 1),
      createBaseVNode("h4", _hoisted_6$3, toDisplayString($props.heading), 1)
    ])
  ])) : createCommentVNode("", true);
}
const Banner = /* @__PURE__ */ _export_sfc(_sfc_main$6, [["render", _sfc_render$6]]);
const _sfc_main$5 = {
  name: "SamplePackPicker",
  components: {
    ProductRow,
    Banner
  },
  computed: {
    ...mapGetters({
      requiredPackages: "getRequiredSamplePackages",
      selectedPackages: "getSelectedSamplePackages",
      discountPercent: "getDiscountPercent",
      textContent: "getTextContent",
      icons: "getIcons",
      hasSelectedSamplePackages: "hasSelectedSamplePackages",
      isSamplePackagesComplete: "isSamplePackagesComplete"
    }),
    /**
     * Heading text for the section
     * @returns {String}
     */
    heading() {
      return this.textContent.samplePackPickerHeading || "";
    },
    /**
     * Empty state message
     * @returns {String}
     */
    emptyMessage() {
      return this.textContent.samplePackEmptyMessage || "";
    },
    /**
     * Plus icon SVG
     * @returns {String}
     */
    plusIcon() {
      return this.icons.plus || "";
    },
    /**
     * Swap icon SVG
     * @returns {String}
     */
    swapIcon() {
      return this.icons.swap || "";
    },
    /**
     * Array of product rows (product objects or null for empty slots)
     * Ensures array length matches required packages count
     * @returns {Array}
     */
    productRows() {
      const rows = [];
      const required = this.requiredPackages || 0;
      const selected = this.selectedPackages || [];
      for (let i = 0; i < required; i++) {
        rows.push(selected[i] || null);
      }
      return rows;
    },
    /**
     * Whether to show the banner
     * Shows when at least one sample package is selected,
     * and hides once all required packages have been selected.
     * @returns {Boolean}
     */
    showBanner() {
      return this.hasSelectedSamplePackages && !this.isSamplePackagesComplete;
    },
    /**
     * Banner upper heading text
     * @returns {String}
     */
    bannerUpperHeading() {
      return this.textContent.bannerUpperheading || "";
    },
    /**
     * Banner main heading text
     * @returns {String}
     */
    bannerHeading() {
      return this.textContent.bannerHeading || "";
    },
    /**
     * Checkmark icon SVG
     * @returns {String}
     */
    checkmarkIcon() {
      return this.icons.checkmark || "";
    }
  },
  methods: {
    /**
     * Handles add button click for a specific row
     * @param {Number} index - Row index
     */
    handleAdd(index) {
      this.$emit("add-sample-package", index);
    },
    /**
     * Handles swap button click for a specific row
     * @param {Number} index - Row index
     */
    handleSwap(index) {
      this.$emit("swap-sample-package", index);
    }
  }
};
const _hoisted_1$5 = { class: "mini-bundle-samples-section" };
const _hoisted_2$4 = { class: "mini-bundle-samples-heading" };
const _hoisted_3$4 = { class: "mini-bundle-samples-container" };
function _sfc_render$5(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_Banner = resolveComponent("Banner");
  const _component_ProductRow = resolveComponent("ProductRow");
  return openBlock(), createElementBlock("div", _hoisted_1$5, [
    createBaseVNode("h4", _hoisted_2$4, toDisplayString($options.heading), 1),
    createVNode(_component_Banner, {
      "show-banner": $options.showBanner,
      "upper-heading": $options.bannerUpperHeading,
      heading: $options.bannerHeading,
      "checkmark-icon": $options.checkmarkIcon
    }, null, 8, ["show-banner", "upper-heading", "heading", "checkmark-icon"]),
    createBaseVNode("div", _hoisted_3$4, [
      (openBlock(true), createElementBlock(Fragment, null, renderList($options.productRows, (product, index) => {
        return openBlock(), createBlock(_component_ProductRow, {
          key: `sample-pack-${index}`,
          "selected-product": product,
          "empty-message": $options.emptyMessage,
          "plus-icon": $options.plusIcon,
          "swap-icon": $options.swapIcon,
          "discount-percent": _ctx.discountPercent,
          onAdd: ($event) => $options.handleAdd(index),
          onSwap: ($event) => $options.handleSwap(index)
        }, null, 8, ["selected-product", "empty-message", "plus-icon", "swap-icon", "discount-percent", "onAdd", "onSwap"]);
      }), 128))
    ])
  ]);
}
const SamplePackPicker = /* @__PURE__ */ _export_sfc(_sfc_main$5, [["render", _sfc_render$5]]);
const _sfc_main$4 = {
  name: "FreeSamplePicker",
  components: {
    ProductRow,
    Banner
  },
  computed: {
    ...mapGetters({
      requiredSamples: "getRequiredFreeSamples",
      selectedSamples: "getSelectedFreeSamples",
      textContent: "getTextContent",
      icons: "getIcons",
      isFreeSamplesComplete: "isFreeSamplesComplete"
    }),
    /**
     * Heading text for the section
     * @returns {String}
     */
    heading() {
      return this.textContent.freeSamplePickerHeading || "";
    },
    /**
     * Empty state message
     * @returns {String}
     */
    emptyMessage() {
      return this.textContent.freeSampleEmptyMessage || "";
    },
    /**
     * Plus icon SVG
     * @returns {String}
     */
    plusIcon() {
      return this.icons.plus || "";
    },
    /**
     * Swap icon SVG
     * @returns {String}
     */
    swapIcon() {
      return this.icons.swap || "";
    },
    /**
     * Array of product rows (product objects or null for empty slots)
     * Ensures array length matches required samples count
     * @returns {Array}
     */
    productRows() {
      const rows = [];
      const required = this.requiredSamples || 0;
      const selected = this.selectedSamples || [];
      for (let i = 0; i < required; i++) {
        rows.push(selected[i] || null);
      }
      return rows;
    },
    /**
     * Whether to show the gifts (free samples) banner.
     * Shows while not all required free samples are selected.
     * @returns {Boolean}
     */
    showBanner() {
      if (!this.requiredSamples) {
        return false;
      }
      const hasSelected = (this.selectedSamples || []).some(
        (sample) => sample !== null && sample !== void 0
      );
      return hasSelected && !this.isFreeSamplesComplete;
    },
    /**
     * Gifts banner upper heading text
     * @returns {String}
     */
    bannerUpperHeading() {
      return this.textContent.giftsBannerUpperheading || "";
    },
    /**
     * Gifts banner main heading text
     * @returns {String}
     */
    bannerHeading() {
      return this.textContent.giftsBannerHeading || "";
    },
    /**
     * Checkmark icon SVG
     * @returns {String}
     */
    checkmarkIcon() {
      return this.icons.checkmark || "";
    }
  },
  methods: {
    /**
     * Handles add button click for a specific row
     * @param {Number} index - Row index
     */
    handleAdd(index) {
      this.$emit("add-free-sample", index);
    },
    /**
     * Handles swap button click for a specific row
     * @param {Number} index - Row index
     */
    handleSwap(index) {
      this.$emit("swap-free-sample", index);
    }
  }
};
const _hoisted_1$4 = { class: "mini-bundle-samples-section" };
const _hoisted_2$3 = { class: "mini-bundle-samples-heading" };
const _hoisted_3$3 = { class: "mini-bundle-samples-container" };
function _sfc_render$4(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_Banner = resolveComponent("Banner");
  const _component_ProductRow = resolveComponent("ProductRow");
  return openBlock(), createElementBlock("div", _hoisted_1$4, [
    createBaseVNode("h4", _hoisted_2$3, toDisplayString($options.heading), 1),
    createVNode(_component_Banner, {
      "show-banner": $options.showBanner,
      "upper-heading": $options.bannerUpperHeading,
      heading: $options.bannerHeading,
      "checkmark-icon": $options.checkmarkIcon
    }, null, 8, ["show-banner", "upper-heading", "heading", "checkmark-icon"]),
    createBaseVNode("div", _hoisted_3$3, [
      (openBlock(true), createElementBlock(Fragment, null, renderList($options.productRows, (product, index) => {
        return openBlock(), createBlock(_component_ProductRow, {
          key: `free-sample-${index}`,
          "selected-product": product,
          "empty-message": $options.emptyMessage,
          "plus-icon": $options.plusIcon,
          "swap-icon": $options.swapIcon,
          "discount-percent": 0,
          "is-gift": true,
          onAdd: ($event) => $options.handleAdd(index),
          onSwap: ($event) => $options.handleSwap(index)
        }, null, 8, ["selected-product", "empty-message", "plus-icon", "swap-icon", "onAdd", "onSwap"]);
      }), 128))
    ])
  ]);
}
const FreeSamplePicker = /* @__PURE__ */ _export_sfc(_sfc_main$4, [["render", _sfc_render$4]]);
const _sfc_main$3 = {
  name: "ProductCard",
  props: {
    /**
     * Product object
     * @type {Object}
     * @required
     */
    product: {
      type: Object,
      required: true
    },
    /**
     * Discount percent to apply
     * @type {Number}
     */
    discountPercent: {
      type: Number,
      default: 0
    },
    /**
     * Whether this is a gift/free sample (100% discount)
     * @type {Boolean}
     */
    isGift: {
      type: Boolean,
      default: false
    }
  },
  emits: ["select", "preview"],
  computed: {
    ...mapGetters({
      icons: "getIcons",
      locales: "getLocales"
    }),
    /**
     * Eye icon SVG
     * @returns {String}
     */
    eyeIcon() {
      return this.icons.eye || "";
    },
    /**
     * Product image URL
     * Prioritizes collectionImage from JSON (which comes from collection_image metafield) if available,
     * otherwise falls back to featured_image
     * @returns {String}
     */
    productImage() {
      var _a, _b, _c;
      if ((_a = this.product) == null ? void 0 : _a.collectionImage) {
        return this.product.collectionImage;
      }
      if (this.product.featured_image) {
        return this.product.featured_image;
      }
      if ((_c = (_b = this.product.featured_media) == null ? void 0 : _b.preview_image) == null ? void 0 : _c.src) {
        return this.product.featured_media.preview_image.src;
      }
      return "";
    },
    /**
     * Product subtitle: same source as quick view tagline.
     * Metafield type is single-line text, so no richtext conversion is needed.
     * @returns {String}
     */
    productSubtitle() {
      var _a, _b, _c, _d, _e;
      const value = ((_a = this.product) == null ? void 0 : _a.productSubtitle) ?? ((_e = (_d = (_c = (_b = this.product) == null ? void 0 : _b.metafields) == null ? void 0 : _c.custom) == null ? void 0 : _d.product_subtitle) == null ? void 0 : _e.value);
      return typeof value === "string" ? value.trim() : "";
    },
    /**
     * Product variant used for display state (availability, pricing fallbacks, etc.)
     * @returns {Object|null}
     */
    productVariant() {
      var _a, _b, _c;
      const direct = (_a = this.product) == null ? void 0 : _a.selected_or_first_available_variant;
      if (direct) return direct;
      const variants = ((_b = this.product) == null ? void 0 : _b.variantsWithMetafields) || ((_c = this.product) == null ? void 0 : _c.variants) || [];
      if (!Array.isArray(variants) || variants.length === 0) return null;
      const firstAvailable = variants.find((v) => v == null ? void 0 : v.available);
      return firstAvailable || variants[0] || null;
    },
    /**
     * Whether product is out of stock based on current variant availability
     * @returns {Boolean}
     */
    isOOS() {
      var _a;
      return ((_a = this.productVariant) == null ? void 0 : _a.available) === false;
    },
    /**
     * OOS label (localized if possible, with safe fallback)
     * @returns {String}
     */
    oosLabel() {
      var _a;
      const label = (_a = this.locales) == null ? void 0 : _a.oos;
      if (!label) return "OOS";
      const normalized = String(label).toLowerCase();
      if (normalized.includes("translation missing") || normalized.trim() === "mini_bundle.oos") {
        return "OOS";
      }
      return label;
    },
    /**
     * Display title with OOS suffix when needed
     * @returns {String}
     */
    displayTitle() {
      var _a;
      const title = ((_a = this.product) == null ? void 0 : _a.title) || "";
      if (!this.isOOS) return title;
      return `${title} (${this.oosLabel})`;
    },
    /**
     * Base price of the product (original price before discount)
     * @returns {Number}
     */
    basePrice() {
      var _a;
      return this.product.price || ((_a = this.productVariant) == null ? void 0 : _a.price) || 0;
    },
    /**
     * Current price with discount applied
     * @returns {Number}
     */
    currentPrice() {
      if (this.isGift) {
        return 0;
      }
      if (this.discountPercent > 0) {
        const discountMultiplier = 1 - this.discountPercent / 100;
        return Math.round(this.basePrice * discountMultiplier);
      }
      return this.basePrice;
    },
    /**
     * Compare at price (original price when discount is applied, or compare_at_price if exists)
     * @returns {Number}
     */
    comparePrice() {
      var _a;
      if (this.isGift) {
        return this.basePrice;
      }
      if (this.discountPercent > 0) {
        return this.basePrice;
      }
      const compareAt = this.product.compare_at_price || ((_a = this.product.selected_or_first_available_variant) == null ? void 0 : _a.compare_at_price) || 0;
      return compareAt > this.basePrice ? compareAt : 0;
    },
    /**
     * Whether to show compare price
     * @returns {Boolean}
     */
    showComparePrice() {
      return this.comparePrice > 0 && this.comparePrice > this.currentPrice;
    }
  },
  methods: {
    /**
     * Formats price into currency string
     * @param {Number} price - Price in cents
     * @returns {String}
     */
    formatPrice(price) {
      var _a, _b, _c;
      if (price === void 0 || price === null) {
        return "";
      }
      const isUKStore = typeof Shopify !== "undefined" && (((_a = Shopify.currency) == null ? void 0 : _a.active) === "GBP" || Shopify.locale === "en-GB");
      const priceValue = price / 100;
      if (priceValue === 0) {
        return this.locales.free ?? "Free";
      }
      if (isUKStore) {
        return `£ ${priceValue.toFixed(2)}`;
      } else {
        const formatted = priceValue.toFixed(2).replace(".", ",");
        const currency = ((_c = (_b = this.$store) == null ? void 0 : _b.state) == null ? void 0 : _c.currency) || "€";
        return `${formatted} ${currency}`;
      }
    },
    /**
     * Handles product selection
     */
    handleSelect() {
      this.$emit("select", this.product);
    },
    /**
     * Handles preview button click
     */
    handlePreview() {
      this.$emit("preview", this.product);
    }
  }
};
const _hoisted_1$3 = { class: "mini-bundle-product-card__image" };
const _hoisted_2$2 = ["src", "alt"];
const _hoisted_3$2 = { class: "mini-bundle-product-card__info" };
const _hoisted_4$2 = { class: "mini-bundle-product-card__copy" };
const _hoisted_5$2 = { class: "mini-bundle-product-card__title" };
const _hoisted_6$2 = ["innerHTML"];
const _hoisted_7$2 = { class: "mini-bundle-product-card__footer" };
const _hoisted_8$2 = {
  key: 0,
  class: "mini-bundle-product-card__variant-picker product-variant-picker no-pointer-events"
};
const _hoisted_9$1 = { class: "product-variant-picker__pill-list" };
const _hoisted_10$1 = { class: "product-card-pill pill selected" };
const _hoisted_11$1 = { class: "pill__label" };
const _hoisted_12$1 = { class: "mini-bundle-product-card__price" };
const _hoisted_13$1 = {
  key: 0,
  class: "mini-bundle-product-card__price-compare"
};
const _hoisted_14$1 = { class: "mini-bundle-product-card__price-current" };
const _hoisted_15$1 = ["innerHTML"];
const _hoisted_16$1 = { class: "mini-bundle-product-card__preview-button_span" };
function _sfc_render$3(_ctx, _cache, $props, $setup, $data, $options) {
  var _a;
  return openBlock(), createElementBlock("div", {
    class: normalizeClass(["mini-bundle-product-card", { "mini-bundle-product-card--oos": $options.isOOS }]),
    onClick: _cache[1] || (_cache[1] = (...args) => $options.handleSelect && $options.handleSelect(...args))
  }, [
    createBaseVNode("div", _hoisted_1$3, [
      createBaseVNode("img", {
        src: $options.productImage,
        alt: $props.product.title,
        loading: "lazy"
      }, null, 8, _hoisted_2$2)
    ]),
    createBaseVNode("div", _hoisted_3$2, [
      createBaseVNode("div", _hoisted_4$2, [
        createBaseVNode("h4", _hoisted_5$2, toDisplayString($options.displayTitle), 1),
        createBaseVNode("div", {
          class: "mini-bundle-product-card__subtitle",
          innerHTML: $options.productSubtitle
        }, null, 8, _hoisted_6$2)
      ]),
      createBaseVNode("div", _hoisted_7$2, [
        ((_a = $props.product) == null ? void 0 : _a.variantContentLabel) ? (openBlock(), createElementBlock("div", _hoisted_8$2, [
          createBaseVNode("div", _hoisted_9$1, [
            createBaseVNode("span", _hoisted_10$1, [
              createBaseVNode("span", _hoisted_11$1, toDisplayString($props.product.variantContentLabel), 1)
            ])
          ])
        ])) : createCommentVNode("", true),
        createBaseVNode("div", _hoisted_12$1, [
          $options.showComparePrice ? (openBlock(), createElementBlock("span", _hoisted_13$1, toDisplayString($options.formatPrice($options.comparePrice)), 1)) : createCommentVNode("", true),
          createBaseVNode("span", _hoisted_14$1, toDisplayString($options.formatPrice($options.currentPrice)), 1)
        ]),
        createBaseVNode("button", {
          class: "mini-bundle-product-card__preview-button",
          onClick: _cache[0] || (_cache[0] = withModifiers((...args) => $options.handlePreview && $options.handlePreview(...args), ["stop"]))
        }, [
          createBaseVNode("span", {
            class: "mini-bundle-product-card__preview-button_icon",
            innerHTML: $options.eyeIcon
          }, null, 8, _hoisted_15$1),
          createBaseVNode("span", _hoisted_16$1, toDisplayString(_ctx.locales.preview), 1)
        ])
      ])
    ])
  ], 2);
}
const ProductCard = /* @__PURE__ */ _export_sfc(_sfc_main$3, [["render", _sfc_render$3], ["__scopeId", "data-v-ae552f0c"]]);
const _sfc_main$2 = {
  name: "QuickViewDrawer",
  props: {
    /**
     * Controls the visibility of the drawer
     * @type {Boolean}
     * @default false
     */
    isOpen: {
      type: Boolean,
      default: false
    },
    /**
     * Product object to display
     * @type {Object}
     */
    product: {
      type: Object,
      default: null
    },
    /**
     * Discount percent to apply
     * @type {Number}
     */
    discountPercent: {
      type: Number,
      default: 0
    },
    /**
     * Whether this is a gift/free sample (100% discount)
     * @type {Boolean}
     */
    isGift: {
      type: Boolean,
      default: false
    }
  },
  emits: ["close", "add-to-row"],
  data() {
    return {
      expandableSections: [],
      sectionDrawerOpen: false,
      sectionDrawerSection: null,
      sectionDrawerCloseTimeout: null,
      SECTION_DRAWER_CLOSE_DURATION_MS: 400,
      originalBodyPaddingRight: "",
      originalBodyOverflow: "",
      scrollbarWidth: 0
    };
  },
  computed: {
    ...mapGetters({
      locales: "getLocales",
      icons: "getIcons"
    }),
    /**
     * Product banner text
     * @returns {String}
     */
    productBanner() {
      var _a, _b, _c, _d;
      return ((_d = (_c = (_b = (_a = this.product) == null ? void 0 : _a.metafields) == null ? void 0 : _b.custom) == null ? void 0 : _c.product_banner) == null ? void 0 : _d.value) || "";
    },
    /**
     * Determines current market/language from URL or Shopify locale
     * @returns {Object} Object with isDutchMarket and isEnglishMarket flags
     */
    currentMarket() {
      var _a, _b, _c;
      const path = (window.location.pathname || "").toLowerCase();
      const shopify = typeof Shopify !== "undefined" ? Shopify : null;
      const locale = String(
        (shopify == null ? void 0 : shopify.locale) || ((_b = (_a = shopify == null ? void 0 : shopify.localization) == null ? void 0 : _a.language) == null ? void 0 : _b.iso_code) || ""
      ).toLowerCase();
      const marketHandle = String(((_c = shopify == null ? void 0 : shopify.market) == null ? void 0 : _c.handle) || "").toLowerCase();
      const lang = locale.slice(0, 2);
      const isDutchMarket = path.includes("/nl/") || path.includes("/nl-nl") || lang === "nl" || marketHandle === "netherlands";
      const isEnglishMarket = path.includes("/en/") || path.includes("/en-") || lang === "en";
      const isFrenchMarket = path.includes("/fr/") || path.includes("/fr-") || lang === "fr" || marketHandle === "france";
      const isGermanMarket = !isDutchMarket && !isEnglishMarket && !isFrenchMarket;
      return {
        isDutchMarket,
        isEnglishMarket,
        isFrenchMarket,
        isGermanMarket
      };
    },
    /**
     * Localized add-to-bundle / ATC label for the Quick View button.
     * Prefers theme locales; falls back by market when keys are missing
     * (e.g. preview theme pull overwrote locales with production files).
     * @returns {String}
     */
    addToCartLabel() {
      var _a, _b, _c, _d;
      const fromLocales = ((_a = this.locales) == null ? void 0 : _a.addToCart) || ((_b = this.locales) == null ? void 0 : _b.add_to_cart) || ((_c = this.locales) == null ? void 0 : _c.add_to_bundle) || ((_d = this.locales) == null ? void 0 : _d.item_add_to_cart);
      if (fromLocales) return fromLocales;
      const market = this.currentMarket;
      if (market.isDutchMarket) return "Toevoegen";
      if (market.isEnglishMarket) return "Add";
      if (market.isFrenchMarket) return "Ajouter";
      return "Hinzufügen";
    },
    /**
     * Product images array filtered by language/market
     * Filters images based on alt text tags (nl_only, en_only, de_only, fr_only)
     * @returns {Array}
     */
    productImages() {
      var _a;
      if (!this.product) return [];
      const market = this.currentMarket;
      let allImages = [];
      if (Array.isArray(this.product.localizedMedia) && this.product.localizedMedia.length > 0) {
        allImages = this.product.localizedMedia.map((media) => {
          var _a2;
          return {
            src: media.src || ((_a2 = media.preview_image) == null ? void 0 : _a2.src) || media,
            alt: media.alt || ""
          };
        });
      } else if (this.product.media && this.product.media.length > 0) {
        allImages = this.product.media.map((media) => {
          var _a2;
          return {
            src: ((_a2 = media.preview_image) == null ? void 0 : _a2.src) || media.src || media,
            alt: media.alt || ""
          };
        });
      } else if (this.product.images && this.product.images.length > 0) {
        allImages = this.product.images.map((img) => {
          if (typeof img === "string") {
            return { src: img, alt: "" };
          }
          return {
            src: img.src || img,
            alt: img.alt || ""
          };
        });
      } else if ((_a = this.product) == null ? void 0 : _a.featured_image) {
        allImages = [
          {
            src: this.product.featured_image,
            alt: ""
          }
        ];
      }
      const getAlt = (img) => (img.alt || "").toLowerCase();
      const hasTag = (img, tag) => getAlt(img).includes(tag);
      const hasAnyLangTag = (img) => ["nl_only", "en_only", "de_only", "fr_only"].some(
        (tag) => hasTag(img, tag)
      );
      const hasNlOnly = allImages.some((img) => hasTag(img, "nl_only"));
      const hasEnOnly = allImages.some((img) => hasTag(img, "en_only"));
      const hasFrOnly = allImages.some((img) => hasTag(img, "fr_only"));
      const hasDeOnly = allImages.some((img) => hasTag(img, "de_only"));
      const images = [];
      allImages.forEach((img, index) => {
        var _a2;
        let shouldShow = false;
        if (market.isDutchMarket) {
          if (hasNlOnly) {
            shouldShow = hasTag(img, "nl_only");
          } else {
            shouldShow = !hasAnyLangTag(img);
          }
        } else if (market.isEnglishMarket) {
          if (hasEnOnly) {
            shouldShow = hasTag(img, "en_only");
          } else {
            shouldShow = !hasAnyLangTag(img);
          }
        } else if (market.isFrenchMarket) {
          if (hasFrOnly) {
            shouldShow = hasTag(img, "fr_only");
          } else {
            shouldShow = !hasAnyLangTag(img);
          }
        } else {
          if (hasDeOnly) {
            shouldShow = hasTag(img, "de_only") || !hasAnyLangTag(img);
          } else {
            shouldShow = !hasTag(img, "nl_only") && !hasTag(img, "en_only") && !hasTag(img, "fr_only");
          }
        }
        if (shouldShow) {
          images.push({
            src: img.src || img,
            alt: img.alt || ((_a2 = this.product) == null ? void 0 : _a2.title) || "",
            quantity: this.getImageQuantity(img, index)
          });
        }
      });
      return images.slice(0, 5);
    },
    /**
     * Numeric rating value from reviews metafields.
     * Uses product.reviewsRating/reviewsCount (from Liquid) or product.metafields.reviews.
     * Supports nested shapes like reviews.rating.value.rating.
     * @returns {Number|null} e.g. 4.5 or null
     */
    numericRating() {
      var _a, _b, _c;
      const p2 = this.product;
      let rating = (p2 == null ? void 0 : p2.reviewsRating) ?? ((_c = (_b = (_a = p2 == null ? void 0 : p2.metafields) == null ? void 0 : _a.reviews) == null ? void 0 : _b.rating) == null ? void 0 : _c.value);
      while (rating != null && typeof rating === "object") {
        if ("rating" in rating) {
          rating = rating.rating;
        } else if ("value" in rating) {
          rating = rating.value;
        } else {
          break;
        }
      }
      if (rating == null || rating === "") return null;
      const ratingNumber = typeof rating === "number" ? rating : Number(String(rating));
      return Number.isFinite(ratingNumber) ? ratingNumber : null;
    },
    /**
     * Review count for rating.
     * @returns {Number} e.g. 618 or 0 if no count
     */
    numericRatingCount() {
      var _a, _b, _c, _d, _e;
      const p2 = this.product;
      const count = (p2 == null ? void 0 : p2.reviewsCount) ?? ((_c = (_b = (_a = p2 == null ? void 0 : p2.metafields) == null ? void 0 : _a.reviews) == null ? void 0 : _b.count) == null ? void 0 : _c.value) ?? ((_e = (_d = p2 == null ? void 0 : p2.metafields) == null ? void 0 : _d.reviews) == null ? void 0 : _e.rating_count);
      if (count == null) return 0;
      const num = Number(count);
      return Number.isFinite(num) && num > 0 ? num : 0;
    },
    /**
     * Generates HTML for rating stars using Reviews.io icon classes.
     * Uses ruk-icon-percentage-star--100 for full stars and ruk-icon-percentage-star--75 for partial stars.
     * @returns {String} HTML string with star icons
     */
    ratingStarsHTML() {
      const rating = this.numericRating;
      if (rating == null) return "";
      const fullStars = Math.floor(rating);
      const decimal = rating % 1;
      const hasPartialStar = decimal >= 0.5;
      let starsHTML = "";
      for (let i = 0; i < fullStars; i++) {
        starsHTML += '<i aria-hidden="true" class="ruk-icon-percentage-star--100"></i>';
      }
      if (hasPartialStar) {
        starsHTML += '<i aria-hidden="true" class="ruk-icon-percentage-star--75"></i>';
      }
      return starsHTML;
    },
    /**
     * Product tagline shown under title.
     * Metafield type is single-line text, so no richtext conversion is needed.
     * @returns {String}
     */
    productTagline() {
      var _a, _b, _c, _d, _e;
      const value = ((_a = this.product) == null ? void 0 : _a.productSubtitle) ?? ((_e = (_d = (_c = (_b = this.product) == null ? void 0 : _b.metafields) == null ? void 0 : _c.custom) == null ? void 0 : _d.product_subtitle) == null ? void 0 : _e.value);
      return typeof value === "string" ? value.trim() : "";
    },
    /**
     * Product description from product.description field.
     * HTML tags are stripped in Liquid, so this is plain text or HTML string.
     * @returns {String}
     */
    productDescription() {
      var _a, _b;
      const desc = ((_a = this.product) == null ? void 0 : _a.productDescription) ?? ((_b = this.product) == null ? void 0 : _b.description);
      if (!desc) return "";
      if (typeof desc === "string") {
        return desc.trim();
      }
      return "";
    },
    /**
     * Base price of the product
     * @returns {Number}
     */
    basePrice() {
      var _a, _b, _c;
      return ((_a = this.product) == null ? void 0 : _a.price) || ((_c = (_b = this.product) == null ? void 0 : _b.selected_or_first_available_variant) == null ? void 0 : _c.price) || 0;
    },
    /**
     * Current price with discount applied
     * @returns {Number}
     */
    currentPrice() {
      if (this.isGift) {
        return 0;
      }
      if (this.discountPercent > 0) {
        const discountMultiplier = 1 - this.discountPercent / 100;
        return Math.round(this.basePrice * discountMultiplier);
      }
      return this.basePrice;
    },
    /**
     * Price per kg
     * @returns {String}
     */
    pricePerKg() {
      var _a, _b, _c, _d;
      const weight = (_d = (_c = (_b = (_a = this.product) == null ? void 0 : _a.metafields) == null ? void 0 : _b.custom) == null ? void 0 : _c.weight) == null ? void 0 : _d.value;
      if (weight && this.currentPrice > 0) {
        const pricePerKg = this.currentPrice / weight * 1e3;
        return `${this.formatPrice(pricePerKg)} je 1 kg`;
      }
      return null;
    },
    /**
     * Product benefits list
     * @returns {Array}
     */
    productBenefits() {
      var _a, _b, _c, _d;
      const benefits = [];
      if ((_d = (_c = (_b = (_a = this.product) == null ? void 0 : _a.metafields) == null ? void 0 : _b.custom) == null ? void 0 : _c.benefits) == null ? void 0 : _d.value) {
        const benefitsValue = this.product.metafields.custom.benefits.value;
        if (Array.isArray(benefitsValue)) {
          return benefitsValue;
        } else if (typeof benefitsValue === "string") {
          return benefitsValue.split("\n").filter((b) => b.trim());
        }
      }
      return benefits;
    },
    /**
     * USP check marks from product.metafields.custom.usp_check_marks (list).
     * Uses product.uspCheckMarks from Liquid or metafield value.
     * @returns {Array<string>}
     */
    uspCheckMarks() {
      var _a, _b, _c, _d, _e;
      const raw = ((_a = this.product) == null ? void 0 : _a.uspCheckMarks) ?? ((_e = (_d = (_c = (_b = this.product) == null ? void 0 : _b.metafields) == null ? void 0 : _c.custom) == null ? void 0 : _d.usp_check_marks) == null ? void 0 : _e.value);
      if (!raw) return [];
      if (Array.isArray(raw)) return raw.filter((item) => item != null && String(item).trim() !== "");
      if (typeof raw === "string") return raw.split("\n").map((s) => s.trim()).filter(Boolean);
      return [];
    }
  },
  watch: {
    product: {
      handler(newProduct) {
        if (newProduct) {
          console.log(
            "variantsWithMetafields --->",
            newProduct.variantsWithMetafields
          );
          console.log("✌️newProduct --->", newProduct);
          this.initializeSections();
        }
      },
      immediate: true
    },
    isOpen(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          if (this.$el && typeof this.$el.querySelector === "function") {
            const backButton = this.$el.querySelector(
              ".mini-bundle-quick-view-drawer__back-button"
            );
            if (backButton) {
              backButton.focus();
            }
          }
        });
      } else {
        this.closeSectionDrawer();
      }
    }
  },
  methods: {
    /**
     * Gets image quantity badge from image alt or metafield
     * @param {Object} image - Image object
     * @param {Number} index - Image index
     * @returns {String|null}
     */
    getImageQuantity(image, index) {
      if (image.alt) {
        const match = image.alt.match(/(\d+)x/i);
        if (match) return match[1] + "x";
      }
      if (index < 2) return "3x";
      return "2x";
    },
    /**
     * Initializes expandable sections from product metafields
     * Dynamically creates sections based on available metafield data
     */
    initializeSections() {
      var _a;
      const sections = [];
      const accordionMetafields = (_a = this.product) == null ? void 0 : _a.accordionMetafields;
      if (!accordionMetafields) {
        this.expandableSections = [];
        return;
      }
      const inhaltsstoffe = accordionMetafields.inhaltsstoffe_neu;
      if (inhaltsstoffe && Array.isArray(inhaltsstoffe) && inhaltsstoffe.length > 0 && inhaltsstoffe.some(
        (entry) => (entry == null ? void 0 : entry.produktbezeichnung) || (entry == null ? void 0 : entry.zutaten)
      )) {
        sections.push({
          title: this.locales.content || this.locales.inhalt || (this.currentMarket.isDutchMarket ? "Inhoud" : this.currentMarket.isFrenchMarket ? "Contenu" : this.currentMarket.isEnglishMarket ? "Contents" : "Inhalt"),
          type: "inhalt",
          content: this.renderInhaltContent(inhaltsstoffe),
          isOpen: false
        });
      }
      const naehrstofftabelle = accordionMetafields.naehrstofftabelle_neu_;
      if (naehrstofftabelle && Array.isArray(naehrstofftabelle) && naehrstofftabelle.length > 0 && naehrstofftabelle.some(
        (entry) => (entry == null ? void 0 : entry.produktbezeichnung) || (entry == null ? void 0 : entry.html_tabelle2) || (entry == null ? void 0 : entry.aminosaurenprofil)
      )) {
        sections.push({
          title: this.locales.nutritionTable || this.locales.nutrition_table || (this.currentMarket.isDutchMarket ? "Voedingswaardetabel" : this.currentMarket.isFrenchMarket ? "Tableau nutritionnel" : this.currentMarket.isEnglishMarket ? "Nutritional table" : "Nährstofftabelle"),
          type: "nahrstofftabelle",
          content: this.renderNahrstofftabelleContent(
            naehrstofftabelle
          ),
          isOpen: false
        });
      }
      const faqs = accordionMetafields.faqs;
      if (faqs && Array.isArray(faqs) && faqs.length > 0 && faqs.some(
        (entry) => (entry == null ? void 0 : entry.faq_frage) && (entry == null ? void 0 : entry.faq_antwort_multiline)
      )) {
        sections.push({
          title: this.locales.faqs || "FAQs",
          type: "faqs",
          content: this.renderFaqsContent(faqs),
          isOpen: false
        });
      }
      this.expandableSections = sections;
    },
    /**
     * Renders content for "Inhalt" section from metafield data
     * @param {Array} inhaltsstoffeData - Array of inhaltsstoffe entries
     * @returns {String} HTML content
     */
    renderInhaltContent(inhaltsstoffeData) {
      if (!inhaltsstoffeData || !Array.isArray(inhaltsstoffeData)) {
        return "";
      }
      let html = '<div style="color: #1d1d1d; font-family: var(--gsc-body-font-family);">';
      inhaltsstoffeData.forEach((entry) => {
        if (!entry) return;
        if (entry.produktbezeichnung) {
          html += `<div style="font-weight: 900; margin-bottom: 16px;">${this.escapeHtml(String(entry.produktbezeichnung))}</div>`;
        }
        if (entry.zutaten) {
          const zutatenHtml = this.renderRichtext(entry.zutaten);
          html += `<div style="padding-bottom: 2rem;">${zutatenHtml}</div>`;
        }
        if (entry.produced_in_eu === true) {
          html += `<div style="padding-bottom: 2rem;">${this.locales.manufacturedInsideEU || this.locales.manufactured_inside_eu || "Hergestellt in der EU"}</div>`;
        } else if (entry.produced_in_eu === false) {
          html += `<div style="padding-bottom: 2rem;">${this.locales.manufacturedOutsideEU || this.locales.manufactured_outside_eu || "Hergestellt außerhalb der EU"}</div>`;
        }
      });
      html += "</div>";
      return html;
    },
    /**
     * Renders content for "Nährstofftabelle" section from metafield data
     * @param {Array} naehrstofftabelleData - Array of nutrition table entries
     * @returns {String} HTML content
     */
    renderNahrstofftabelleContent(naehrstofftabelleData) {
      if (!naehrstofftabelleData || !Array.isArray(naehrstofftabelleData)) {
        return "";
      }
      let html = '<div class="mini-bundle-nutrition">';
      naehrstofftabelleData.forEach((entry) => {
        if (!entry) return;
        if (entry.produktbezeichnung) {
          html += `<div class="mini-bundle-nutrition__title">${this.escapeHtml(String(entry.produktbezeichnung))}</div>`;
        }
        if (entry.html_tabelle2) {
          html += `<div class="mini-bundle-nutrition__table">${this.sanitizeNutritionTableHtml(entry.html_tabelle2)}</div>`;
        }
        if (entry.aminosaurenprofil) {
          html += `<div class="mini-bundle-nutrition__table">${this.sanitizeNutritionTableHtml(entry.aminosaurenprofil)}</div>`;
        }
        if (entry.naehrwerttabelle_zusatzinformation) {
          html += `<div class="mini-bundle-nutrition__note">${String(entry.naehrwerttabelle_zusatzinformation)}</div>`;
        }
      });
      html += "</div>";
      return html;
    },
    /**
     * Strip metafield inline table styling so drawer CSS can match the PDP.
     * @param {String|Object} tableHtml
     * @returns {String}
     */
    sanitizeNutritionTableHtml(tableHtml) {
      return String(tableHtml || "").replace(/<td><\/td>/gi, "").replace(/<th><\/th>/gi, "").replace(
        /\s(style|border|width|height|cellpadding|cellspacing|bgcolor|align)=("[^"]*"|'[^']*'|[^\s>]+)/gi,
        ""
      );
    },
    /**
     * Renders content for "FAQs" section from metafield data
     * @param {Array} faqsData - Array of FAQ entries
     * @returns {String} HTML content
     */
    renderFaqsContent(faqsData) {
      if (!faqsData || !Array.isArray(faqsData)) {
        return "";
      }
      let html = '<div class="faq-accordion">';
      faqsData.forEach((entry) => {
        if (!entry) return;
        const question = entry.faq_frage;
        const answer = entry.faq_antwort_multiline;
        if (question && answer) {
          html += `
                        <div class="faq-item" style="margin-bottom: 16px;">
                            <div class="faq-question" style="font-weight: 700; margin-bottom: 8px;">
                                ${this.escapeHtml(String(question))}
                            </div>
                            <div class="faq-answer" style="color: #1d1d1d;">
                                ${String(answer)}
                            </div>
                        </div>
                    `;
        }
      });
      html += "</div>";
      return html;
    },
    /**
     * Converts Shopify richtext metafield object to HTML
     * @param {Object} richtext - Richtext object with type and children
     * @returns {String} HTML string
     */
    renderRichtext(richtext) {
      if (!richtext || typeof richtext !== "object") {
        return "";
      }
      if (typeof richtext === "string") {
        return richtext;
      }
      if (richtext.type === "root" && Array.isArray(richtext.children)) {
        return this.renderRichtextChildren(richtext.children);
      }
      return "";
    },
    /**
     * Renders richtext children array to HTML
     * @param {Array} children - Array of richtext nodes
     * @returns {String} HTML string
     */
    renderRichtextChildren(children) {
      if (!Array.isArray(children)) {
        return "";
      }
      let html = "";
      children.forEach((node) => {
        if (!node) return;
        switch (node.type) {
          case "paragraph":
            html += "<p>";
            if (Array.isArray(node.children)) {
              html += this.renderRichtextChildren(node.children);
            }
            html += "</p>";
            break;
          case "text":
            let text = this.escapeHtml(String(node.value || ""));
            if (node.bold) {
              text = `<strong>${text}</strong>`;
            }
            html += text;
            break;
          case "line_break":
            html += "<br>";
            break;
          default:
            if (Array.isArray(node.children)) {
              html += this.renderRichtextChildren(node.children);
            } else if (node.value) {
              html += this.escapeHtml(String(node.value));
            }
            break;
        }
      });
      return html;
    },
    /**
     * Escapes HTML to prevent XSS attacks
     * @param {String} text - Text to escape
     * @returns {String} Escaped HTML
     */
    escapeHtml(text) {
      const div = document.createElement("div");
      div.textContent = text;
      return div.innerHTML;
    },
    /**
     * Toggles section open/closed state
     * @param {Number} index - Section index
     */
    toggleSection(index) {
      this.expandableSections[index].isOpen = !this.expandableSections[index].isOpen;
    },
    /**
     * Opens the section content in a fly-in drawer (like theme sidebar).
     * @param {Object} section - Section with title and content
     */
    openSectionDrawer(section) {
      if (this.sectionDrawerCloseTimeout) {
        clearTimeout(this.sectionDrawerCloseTimeout);
        this.sectionDrawerCloseTimeout = null;
      }
      this.sectionDrawerSection = section;
      this.sectionDrawerOpen = true;
    },
    /**
     * Closes the section content drawer.
     * Hides panel immediately for animation; clears content after animation ends.
     */
    closeSectionDrawer() {
      this.sectionDrawerOpen = false;
      if (this.sectionDrawerCloseTimeout) {
        clearTimeout(this.sectionDrawerCloseTimeout);
      }
      this.sectionDrawerCloseTimeout = setTimeout(() => {
        this.sectionDrawerSection = null;
        this.sectionDrawerCloseTimeout = null;
      }, this.SECTION_DRAWER_CLOSE_DURATION_MS);
    },
    /**
     * Formats price into currency string
     * @param {Number} price - Price in cents
     * @returns {String}
     */
    formatPrice(price) {
      var _a, _b, _c;
      if (price === void 0 || price === null) {
        return "";
      }
      const isUKStore = typeof Shopify !== "undefined" && (((_a = Shopify.currency) == null ? void 0 : _a.active) === "GBP" || Shopify.locale === "en-GB");
      const priceValue = price / 100;
      if (priceValue === 0) {
        return this.locales.free ?? "Free";
      }
      if (isUKStore) {
        return `£ ${priceValue.toFixed(2)}`;
      } else {
        const formatted = priceValue.toFixed(2).replace(".", ",");
        const currency = ((_c = (_b = this.$store) == null ? void 0 : _b.state) == null ? void 0 : _c.currency) || "€";
        return `${formatted} ${currency}`;
      }
    },
    /**
     * Handles add to cart click
     * Adds product to mini-bundle row instead of cart
     */
    handleAddToCart() {
      var _a;
      if (!((_a = this.product) == null ? void 0 : _a.available)) return;
      this.$emit("add-to-row", this.product);
      this.close();
    },
    /**
     * Closes the drawer
     */
    close() {
      this.$emit("close");
    },
    /**
     * Handles overlay click
     */
    handleOverlayClick() {
      this.close();
    },
    /**
     * Calculates scrollbar width
     * @returns {number}
     */
    getScrollbarWidth() {
      const outer = document.createElement("div");
      outer.style.visibility = "hidden";
      outer.style.overflow = "scroll";
      document.body.appendChild(outer);
      const inner = document.createElement("div");
      outer.appendChild(inner);
      const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
      outer.parentNode.removeChild(outer);
      return scrollbarWidth;
    },
    /**
     * Locks body scroll
     */
    lockBodyScroll() {
      this.originalBodyPaddingRight = document.body.style.paddingRight || "";
      this.originalBodyOverflow = document.body.style.overflow || "";
      this.scrollbarWidth = this.getScrollbarWidth();
      if (this.scrollbarWidth > 0) {
        document.body.style.paddingRight = `${this.scrollbarWidth}px`;
      }
      document.body.style.overflow = "hidden";
    },
    /**
     * Unlocks body scroll
     */
    unlockBodyScroll() {
      document.body.style.paddingRight = this.originalBodyPaddingRight;
      document.body.style.overflow = this.originalBodyOverflow;
    }
  },
  beforeUnmount() {
    if (this.sectionDrawerCloseTimeout) {
      clearTimeout(this.sectionDrawerCloseTimeout);
      this.sectionDrawerCloseTimeout = null;
    }
    this.unlockBodyScroll();
  },
  unmounted() {
    this.unlockBodyScroll();
  }
};
const _hoisted_1$2 = { class: "mini-bundle-quick-view-drawer__body" };
const _hoisted_2$1 = { class: "mini-bundle-quick-view-drawer__header" };
const _hoisted_3$1 = { class: "mini-bundle-quick-view-drawer__header-title" };
const _hoisted_4$1 = { class: "mini-bundle-quick-view-drawer__viewport" };
const _hoisted_5$1 = {
  key: 0,
  class: "mini-bundle-quick-view-drawer__content"
};
const _hoisted_6$1 = { class: "mini-bundle-quick-view-drawer__images" };
const _hoisted_7$1 = { class: "mini-bundle-quick-view-drawer__product-bars" };
const _hoisted_8$1 = ["src", "alt"];
const _hoisted_9 = { class: "mini-bundle-quick-view-drawer__info" };
const _hoisted_10 = {
  key: 0,
  class: "mini-bundle-quick-view-drawer__rating"
};
const _hoisted_11 = ["title", "aria-label", "innerHTML"];
const _hoisted_12 = {
  key: 0,
  "aria-hidden": "true",
  class: "ruk-rating-snippet-count"
};
const _hoisted_13 = {
  key: 1,
  class: "mini-bundle-quick-view-drawer__title"
};
const _hoisted_14 = ["innerHTML"];
const _hoisted_15 = ["innerHTML"];
const _hoisted_16 = {
  key: 4,
  class: "product-general-block mini-bundle-quick-view-drawer__usp"
};
const _hoisted_17 = { class: "block-usp-check-marks" };
const _hoisted_18 = {
  key: 5,
  class: "mini-bundle-quick-view-drawer__variant-picker product-variant-picker no-pointer-events"
};
const _hoisted_19 = { class: "product-variant-picker__pill-list" };
const _hoisted_20 = { class: "product-card-pill pill selected" };
const _hoisted_21 = { class: "pill__label" };
const _hoisted_22 = { class: "mini-bundle-quick-view-drawer__price" };
const _hoisted_23 = { class: "mini-bundle-quick-view-drawer__price-value" };
const _hoisted_24 = { class: "mini-bundle-quick-view-drawer__shipping" };
const _hoisted_25 = ["innerHTML"];
const _hoisted_26 = {
  key: 0,
  class: "mini-bundle-quick-view-drawer__price-per-kg"
};
const _hoisted_27 = ["data-sold-out"];
const _hoisted_28 = {
  key: 6,
  class: "mini-bundle-quick-view-drawer__benefits"
};
const _hoisted_29 = { class: "mini-bundle-quick-view-drawer__benefit-text" };
const _hoisted_30 = { class: "mini-bundle-quick-view-drawer__sections" };
const _hoisted_31 = ["onClick"];
const _hoisted_32 = { class: "mini-bundle-quick-view-drawer__section-title" };
const _hoisted_33 = ["innerHTML"];
const _hoisted_34 = { class: "mini-bundle-quick-view-drawer__section-drawer-body" };
const _hoisted_35 = { class: "mini-bundle-quick-view-drawer__header" };
const _hoisted_36 = { class: "mini-bundle-quick-view-drawer__header-title" };
const _hoisted_37 = ["innerHTML"];
function _sfc_render$2(_ctx, _cache, $props, $setup, $data, $options) {
  var _a, _b, _c, _d, _e, _f;
  const _component_reviewsio_product_ratings = resolveComponent("reviewsio-product-ratings");
  return openBlock(), createBlock(Teleport, { to: "body" }, [
    createBaseVNode("div", {
      class: normalizeClass(["mini-bundle-quick-view-drawer__overlay", { "is-visible": $props.isOpen }]),
      onClick: _cache[0] || (_cache[0] = (...args) => $options.handleOverlayClick && $options.handleOverlayClick(...args))
    }, null, 2),
    createBaseVNode("div", {
      class: normalizeClass(["mini-bundle-quick-view-drawer", { "is-opened": $props.isOpen }])
    }, [
      createBaseVNode("div", _hoisted_1$2, [
        createBaseVNode("div", _hoisted_2$1, [
          createBaseVNode("button", {
            class: "unstyled-button mini-bundle-quick-view-drawer__back-button",
            type: "button",
            onClick: _cache[1] || (_cache[1] = (...args) => $options.close && $options.close(...args)),
            "aria-label": "Back"
          }, _cache[5] || (_cache[5] = [
            createBaseVNode("span", { class: "mini-bundle-quick-view-drawer__back-icon" }, [
              createBaseVNode("svg", {
                xmlns: "http://www.w3.org/2000/svg",
                width: "24",
                height: "24",
                viewBox: "0 0 24 24",
                fill: "none"
              }, [
                createBaseVNode("path", {
                  d: "M15.3586 18.3618L9 12.0032",
                  stroke: "#592B8A",
                  "stroke-width": "2",
                  "stroke-linecap": "round"
                }),
                createBaseVNode("path", {
                  d: "M9 11.9819L15.3586 5.62329",
                  stroke: "#592B8A",
                  "stroke-width": "2",
                  "stroke-linecap": "round"
                })
              ])
            ], -1)
          ])),
          createBaseVNode("div", _hoisted_3$1, toDisplayString(_ctx.locales.quickView || "Quick View"), 1),
          _cache[6] || (_cache[6] = createBaseVNode("div", null, null, -1))
        ]),
        createBaseVNode("div", _hoisted_4$1, [
          $props.product ? (openBlock(), createElementBlock("div", _hoisted_5$1, [
            createBaseVNode("div", _hoisted_6$1, [
              createBaseVNode("div", _hoisted_7$1, [
                (openBlock(true), createElementBlock(Fragment, null, renderList($options.productImages, (image, index) => {
                  return openBlock(), createElementBlock("div", {
                    key: index,
                    class: "mini-bundle-quick-view-drawer__product-bar"
                  }, [
                    createBaseVNode("img", {
                      src: image.src,
                      alt: image.alt
                    }, null, 8, _hoisted_8$1)
                  ]);
                }), 128))
              ])
            ]),
            createBaseVNode("div", _hoisted_9, [
              $options.numericRating !== null ? (openBlock(), createElementBlock("div", _hoisted_10, [
                createVNode(_component_reviewsio_product_ratings, null, {
                  default: withCtx(() => [
                    createBaseVNode("div", {
                      class: "ruk_rating_snippet",
                      "data-rating-click-handler": true,
                      "rating-snippet-batch": 0,
                      title: `${$options.numericRating.toFixed(2)} Stars - ${$options.numericRatingCount}`,
                      "aria-label": `${$options.numericRating.toFixed(2)} Stars - ${$options.numericRatingCount}`,
                      tabindex: "0",
                      innerHTML: $options.ratingStarsHTML
                    }, null, 8, _hoisted_11),
                    $options.numericRatingCount > 0 ? (openBlock(), createElementBlock("span", _hoisted_12, " (" + toDisplayString($options.numericRatingCount) + ") ", 1)) : createCommentVNode("", true)
                  ]),
                  _: 1
                })
              ])) : createCommentVNode("", true),
              $props.product ? (openBlock(), createElementBlock("h2", _hoisted_13, toDisplayString($props.product.title), 1)) : createCommentVNode("", true),
              $options.productTagline ? (openBlock(), createElementBlock("p", {
                key: 2,
                class: "mini-bundle-quick-view-drawer__tagline",
                innerHTML: $options.productTagline
              }, null, 8, _hoisted_14)) : createCommentVNode("", true),
              $options.productDescription ? (openBlock(), createElementBlock("div", {
                key: 3,
                class: "mini-bundle-quick-view-drawer__description",
                innerHTML: $options.productDescription
              }, null, 8, _hoisted_15)) : createCommentVNode("", true),
              $options.uspCheckMarks.length > 0 ? (openBlock(), createElementBlock("div", _hoisted_16, [
                createBaseVNode("ul", _hoisted_17, [
                  (openBlock(true), createElementBlock(Fragment, null, renderList($options.uspCheckMarks, (item, index) => {
                    return openBlock(), createElementBlock("li", {
                      key: index,
                      class: "block-usp-check-marks__item body1 extra-bold"
                    }, [
                      _cache[7] || (_cache[7] = createBaseVNode("span", { class: "mini-bundle-quick-view-drawer__usp-icon" }, [
                        createBaseVNode("svg", {
                          xmlns: "http://www.w3.org/2000/svg",
                          width: "33",
                          height: "24",
                          viewBox: "0 0 33 24",
                          fill: "none"
                        }, [
                          createBaseVNode("path", {
                            d: "M29.9453 0.512146C30.6288 -0.170456 31.7368 -0.170915 32.4199 0.512146C33.1032 1.19545 33.103 2.30429 32.4199 2.98773L12.1445 23.2631C11.4611 23.9465 10.3524 23.9465 9.66895 23.2631C9.64582 23.24 9.62507 23.2149 9.60352 23.1909L0.512695 14.1C-0.170722 13.4166 -0.170722 12.3088 0.512695 11.6254C1.19612 10.9422 2.30394 10.9421 2.9873 11.6254L10.9092 19.5473L29.9453 0.512146Z",
                            fill: "#8345BA"
                          })
                        ])
                      ], -1)),
                      createTextVNode(" " + toDisplayString(item), 1)
                    ]);
                  }), 128))
                ])
              ])) : createCommentVNode("", true),
              ((_a = $props.product) == null ? void 0 : _a.variantContentLabel) ? (openBlock(), createElementBlock("div", _hoisted_18, [
                createBaseVNode("div", _hoisted_19, [
                  createBaseVNode("span", _hoisted_20, [
                    createBaseVNode("span", _hoisted_21, toDisplayString($props.product.variantContentLabel), 1)
                  ])
                ])
              ])) : createCommentVNode("", true),
              createBaseVNode("div", _hoisted_22, [
                createBaseVNode("span", _hoisted_23, toDisplayString($options.formatPrice($options.currentPrice)), 1)
              ]),
              createBaseVNode("div", _hoisted_24, [
                createBaseVNode("p", {
                  class: "mini-bundle-quick-view-drawer__shipping-text",
                  innerHTML: (_d = (_c = (_b = $props.product) == null ? void 0 : _b.variantsWithMetafields) == null ? void 0 : _c[0]) == null ? void 0 : _d.pricePerKg
                }, null, 8, _hoisted_25),
                $options.pricePerKg ? (openBlock(), createElementBlock("p", _hoisted_26, toDisplayString($options.pricePerKg), 1)) : createCommentVNode("", true)
              ]),
              createBaseVNode("button", {
                class: "mini-bundle-quick-view-drawer__add-to-cart product-form__btn btn btn--lg btn--solid",
                onClick: _cache[2] || (_cache[2] = (...args) => $options.handleAddToCart && $options.handleAddToCart(...args)),
                "data-sold-out": !((_e = $props.product) == null ? void 0 : _e.available)
              }, toDisplayString($options.addToCartLabel), 9, _hoisted_27),
              $options.productBenefits.length > 0 ? (openBlock(), createElementBlock("div", _hoisted_28, [
                (openBlock(true), createElementBlock(Fragment, null, renderList($options.productBenefits, (benefit, index) => {
                  return openBlock(), createElementBlock("div", {
                    key: index,
                    class: "mini-bundle-quick-view-drawer__benefit"
                  }, [
                    _cache[8] || (_cache[8] = createBaseVNode("span", { class: "mini-bundle-quick-view-drawer__benefit-check" }, "✓", -1)),
                    createBaseVNode("span", _hoisted_29, toDisplayString(benefit), 1)
                  ]);
                }), 128))
              ])) : createCommentVNode("", true),
              createBaseVNode("div", _hoisted_30, [
                (openBlock(true), createElementBlock(Fragment, null, renderList($data.expandableSections, (section, index) => {
                  return openBlock(), createElementBlock("button", {
                    key: index,
                    type: "button",
                    class: "unstyled-button mini-bundle-quick-view-drawer__section",
                    onClick: ($event) => $options.openSectionDrawer(section)
                  }, [
                    createBaseVNode("span", _hoisted_32, toDisplayString(section.title), 1),
                    createBaseVNode("span", {
                      class: "mini-bundle-quick-view-drawer__section-arrow",
                      innerHTML: _ctx.icons.arrow
                    }, null, 8, _hoisted_33)
                  ], 8, _hoisted_31);
                }), 128))
              ])
            ])
          ])) : createCommentVNode("", true)
        ])
      ]),
      createBaseVNode("div", {
        class: normalizeClass(["mini-bundle-quick-view-drawer__section-drawer-overlay", { "is-visible": $data.sectionDrawerOpen }]),
        onClick: _cache[3] || (_cache[3] = (...args) => $options.closeSectionDrawer && $options.closeSectionDrawer(...args))
      }, null, 2),
      createBaseVNode("div", {
        class: normalizeClass(["mini-bundle-quick-view-drawer__section-drawer", { "is-opened": $data.sectionDrawerOpen }])
      }, [
        createBaseVNode("div", _hoisted_34, [
          createBaseVNode("div", _hoisted_35, [
            createBaseVNode("button", {
              type: "button",
              class: "unstyled-button mini-bundle-quick-view-drawer__back-button",
              "aria-label": "Back",
              onClick: _cache[4] || (_cache[4] = (...args) => $options.closeSectionDrawer && $options.closeSectionDrawer(...args))
            }, _cache[9] || (_cache[9] = [
              createBaseVNode("span", { class: "mini-bundle-quick-view-drawer__back-icon" }, [
                createBaseVNode("svg", {
                  xmlns: "http://www.w3.org/2000/svg",
                  width: "24",
                  height: "24",
                  viewBox: "0 0 24 24",
                  fill: "none"
                }, [
                  createBaseVNode("path", {
                    d: "M15.3586 18.3618L9 12.0032",
                    stroke: "#592B8A",
                    "stroke-width": "2",
                    "stroke-linecap": "round"
                  }),
                  createBaseVNode("path", {
                    d: "M9 11.9819L15.3586 5.62329",
                    stroke: "#592B8A",
                    "stroke-width": "2",
                    "stroke-linecap": "round"
                  })
                ])
              ], -1)
            ])),
            createBaseVNode("div", _hoisted_36, toDisplayString(((_f = $data.sectionDrawerSection) == null ? void 0 : _f.title) || ""), 1),
            _cache[10] || (_cache[10] = createBaseVNode("div", null, null, -1))
          ]),
          $data.sectionDrawerSection ? (openBlock(), createElementBlock("div", {
            key: 0,
            class: "mini-bundle-quick-view-drawer__section-drawer-content fly-in__content",
            innerHTML: $data.sectionDrawerSection.content
          }, null, 8, _hoisted_37)) : createCommentVNode("", true)
        ])
      ], 2)
    ], 2)
  ]);
}
const QuickViewDrawer = /* @__PURE__ */ _export_sfc(_sfc_main$2, [["render", _sfc_render$2], ["__scopeId", "data-v-0f471a6e"]]);
const _sfc_main$1 = {
  name: "ProductPickerDrawer",
  components: {
    ProductCard,
    QuickViewDrawer
  },
  props: {
    /**
     * Whether the drawer is open
     * @type {Boolean}
     */
    isOpen: {
      type: Boolean,
      default: false
    },
    /**
     * Drawer title
     * @type {String}
     */
    title: {
      type: String,
      required: true
    },
    /**
     * List of collections to display (new format) or products (old format for backward compatibility)
     * @type {Array}
     */
    collections: {
      type: Array,
      default: () => []
    },
    /**
     * Discount percent to apply
     * @type {Number}
     */
    discountPercent: {
      type: Number,
      default: 0
    },
    /**
     * Whether products in this drawer are gifts/free samples (100% discount)
     * @type {Boolean}
     */
    isGift: {
      type: Boolean,
      default: false
    },
    /**
     * Whether to close on overlay click
     * @type {Boolean}
     */
    closeOnOverlay: {
      type: Boolean,
      default: true
    }
  },
  emits: ["close", "select"],
  data() {
    return {
      scrollbarWidth: 0,
      originalBodyPaddingRight: "",
      originalBodyOverflow: "",
      originalBodyPosition: "",
      originalBodyTop: "",
      originalBodyWidth: "",
      originalBodyLeft: "",
      originalBodyRight: "",
      lockedScrollY: 0,
      quickViewOpen: false,
      quickViewProduct: null,
      activeTabIndex: 0,
      tabsScrollbarLeft: 0,
      tabsScrollbarWidth: 0,
      tabsScrollbarVisible: false,
      isDraggingTabsScrollbar: false,
      tabsScrollbarDragStartX: 0,
      tabsScrollbarDragStartLeft: 0
    };
  },
  computed: {
    /**
     * Checks if collections format is used (new format with multiple collections)
     * @returns {Boolean}
     */
    hasMultipleCollections() {
      var _a;
      return this.collections.length > 0 && ((_a = this.collections[0]) == null ? void 0 : _a.collectionId);
    },
    /**
     * Gets visible, in-stock products from the active collection or all products
     * (backward compatibility). Sold-out products and products tagged "hidden"
     * are not rendered.
     * @returns {Array}
     */
    activeProducts() {
      var _a;
      const products = this.hasMultipleCollections ? ((_a = this.collections[this.activeTabIndex]) == null ? void 0 : _a.products) || [] : this.collections;
      return (products || []).filter(
        (product) => !this.isProductHidden(product) && this.isProductInStock(product)
      );
    },
    tabsScrollbarThumbStyle() {
      return {
        width: `${this.tabsScrollbarWidth}px`,
        transform: `translateX(${this.tabsScrollbarLeft}px)`
      };
    }
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        this.activeTabIndex = 0;
      }
      if (newVal) {
        this.lockBodyScroll();
        this.$nextTick(() => {
          this.bindTabsScroll();
          this.updateTabsScrollbar();
          if (this.$el && typeof this.$el.querySelector === "function") {
            const closeButton = this.$el.querySelector(".mini-bundle-product-picker-drawer__header-close-button-wrapper");
            if (closeButton) {
              closeButton.focus();
            }
          }
        });
      } else {
        if (this.quickViewOpen) {
          this.closeQuickView();
        }
        if (!this.quickViewOpen) {
          this.unlockBodyScroll();
        }
      }
    },
    quickViewOpen(newVal) {
      if (!newVal && !this.isOpen) {
        this.unlockBodyScroll();
      }
    },
    /**
     * Resets active tab when collections change
     */
    collections() {
      this.activeTabIndex = 0;
      this.$nextTick(() => {
        this.bindTabsScroll();
        this.updateTabsScrollbar();
      });
    }
  },
  methods: {
    /**
     * Whether a product carries the Shopify "hidden" tag.
     * @param {Object} product
     * @returns {boolean}
     */
    isProductHidden(product) {
      return ((product == null ? void 0 : product.tags) || []).some(
        (tag) => String(tag).trim().toLowerCase() === "hidden"
      );
    },
    /**
     * Whether a product should appear in the picker (not sold out).
     * @param {Object} product
     * @returns {boolean}
     */
    isProductInStock(product) {
      if (!product) return false;
      if (product.available === false) return false;
      const variants = product.variantsWithMetafields || product.variants || [];
      const displayVariant = product.selected_or_first_available_variant || Array.isArray(variants) && (variants.find((v) => v == null ? void 0 : v.available) || variants[0]) || null;
      return (displayVariant == null ? void 0 : displayVariant.available) !== false;
    },
    bindTabsScroll() {
      const tabsContainer = this.$refs.tabsContainer;
      if (!tabsContainer || tabsContainer.dataset.scrollbarBound === "true") {
        return;
      }
      tabsContainer.addEventListener("scroll", this.updateTabsScrollbar, { passive: true });
      tabsContainer.dataset.scrollbarBound = "true";
    },
    unbindTabsScroll() {
      const tabsContainer = this.$refs.tabsContainer;
      if (!tabsContainer || tabsContainer.dataset.scrollbarBound !== "true") {
        return;
      }
      tabsContainer.removeEventListener("scroll", this.updateTabsScrollbar);
      delete tabsContainer.dataset.scrollbarBound;
    },
    updateTabsScrollbar() {
      const tabsContainer = this.$refs.tabsContainer;
      if (!tabsContainer) {
        this.tabsScrollbarVisible = false;
        return;
      }
      const { scrollWidth, clientWidth, scrollLeft } = tabsContainer;
      const hasOverflow = scrollWidth > clientWidth;
      this.tabsScrollbarVisible = hasOverflow;
      if (!hasOverflow) {
        this.tabsScrollbarWidth = 0;
        this.tabsScrollbarLeft = 0;
        return;
      }
      const thumbWidth = Math.max(clientWidth / scrollWidth * clientWidth, 28);
      const maxThumbOffset = clientWidth - thumbWidth;
      const maxScrollLeft = scrollWidth - clientWidth;
      this.tabsScrollbarWidth = thumbWidth;
      this.tabsScrollbarLeft = maxScrollLeft > 0 ? scrollLeft / maxScrollLeft * maxThumbOffset : 0;
    },
    scrollTabsFromScrollbarPosition(nextThumbLeft) {
      const tabsContainer = this.$refs.tabsContainer;
      if (!tabsContainer) {
        return;
      }
      const maxThumbOffset = tabsContainer.clientWidth - this.tabsScrollbarWidth;
      const maxScrollLeft = tabsContainer.scrollWidth - tabsContainer.clientWidth;
      if (maxThumbOffset <= 0 || maxScrollLeft <= 0) {
        return;
      }
      const clampedThumbLeft = Math.min(Math.max(0, nextThumbLeft), maxThumbOffset);
      const nextScrollLeft = clampedThumbLeft / maxThumbOffset * maxScrollLeft;
      tabsContainer.scrollLeft = nextScrollLeft;
    },
    handleTabsScrollbarTrackPointerDown(event) {
      if (event.button !== 0) {
        return;
      }
      const scrollbar = this.$refs.tabsScrollbar;
      if (!scrollbar || !this.tabsScrollbarVisible) {
        return;
      }
      const trackRect = scrollbar.getBoundingClientRect();
      const targetThumbLeft = event.clientX - trackRect.left - this.tabsScrollbarWidth / 2;
      this.scrollTabsFromScrollbarPosition(targetThumbLeft);
    },
    handleTabsScrollbarThumbPointerDown(event) {
      if (event.button !== 0) {
        return;
      }
      event.preventDefault();
      this.isDraggingTabsScrollbar = true;
      this.tabsScrollbarDragStartX = event.clientX;
      this.tabsScrollbarDragStartLeft = this.tabsScrollbarLeft;
      document.addEventListener("pointermove", this.handleTabsScrollbarPointerMove);
      document.addEventListener("pointerup", this.handleTabsScrollbarPointerUp);
    },
    handleTabsScrollbarPointerMove(event) {
      if (!this.isDraggingTabsScrollbar) {
        return;
      }
      const deltaX = event.clientX - this.tabsScrollbarDragStartX;
      this.scrollTabsFromScrollbarPosition(this.tabsScrollbarDragStartLeft + deltaX);
    },
    handleTabsScrollbarPointerUp() {
      this.isDraggingTabsScrollbar = false;
      document.removeEventListener("pointermove", this.handleTabsScrollbarPointerMove);
      document.removeEventListener("pointerup", this.handleTabsScrollbarPointerUp);
    },
    /**
     * Calculates scrollbar width
     * @returns {number} Scrollbar width in pixels
     */
    getScrollbarWidth() {
      const outer = document.createElement("div");
      outer.style.visibility = "hidden";
      outer.style.overflow = "scroll";
      outer.style.msOverflowStyle = "scrollbar";
      document.body.appendChild(outer);
      const inner = document.createElement("div");
      outer.appendChild(inner);
      const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
      outer.parentNode.removeChild(outer);
      return scrollbarWidth;
    },
    /**
     * Locks body scroll and compensates for scrollbar width
     */
    lockBodyScroll() {
      this.originalBodyPaddingRight = document.body.style.paddingRight || "";
      this.originalBodyOverflow = document.body.style.overflow || "";
      this.originalBodyPosition = document.body.style.position || "";
      this.originalBodyTop = document.body.style.top || "";
      this.originalBodyWidth = document.body.style.width || "";
      this.originalBodyLeft = document.body.style.left || "";
      this.originalBodyRight = document.body.style.right || "";
      this.lockedScrollY = window.scrollY || window.pageYOffset || 0;
      this.scrollbarWidth = this.getScrollbarWidth();
      if (this.scrollbarWidth > 0) {
        document.body.style.paddingRight = `${this.scrollbarWidth}px`;
      }
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${this.lockedScrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
    },
    /**
     * Unlocks body scroll and restores original values
     */
    unlockBodyScroll() {
      const scrollY = this.lockedScrollY;
      document.body.style.paddingRight = this.originalBodyPaddingRight;
      document.body.style.overflow = this.originalBodyOverflow;
      document.body.style.position = this.originalBodyPosition;
      document.body.style.top = this.originalBodyTop;
      document.body.style.width = this.originalBodyWidth;
      document.body.style.left = this.originalBodyLeft;
      document.body.style.right = this.originalBodyRight;
      window.scrollTo(0, scrollY);
    },
    /**
     * Closes the drawer
     */
    close() {
      this.$emit("close");
    },
    /**
     * Handles overlay click
     */
    handleOverlayClick() {
      if (this.closeOnOverlay) {
        this.close();
      }
    },
    /**
     * Handles product selection
     * @param {Object} product - Selected product
     */
    handleProductSelect(product) {
      if (!(product == null ? void 0 : product.available)) return;
      this.$emit("select", product);
      this.close();
    },
    /**
     * Handles product preview
     * @param {Object} product - Product to preview
     */
    handleProductPreview(product) {
      this.quickViewProduct = product;
      this.quickViewOpen = true;
    },
    /**
     * Closes quick view drawer
     */
    closeQuickView() {
      this.quickViewProduct = null;
      this.$nextTick(() => {
        this.quickViewOpen = false;
      });
    },
    /**
     * Handles add to row from quick view drawer
     * Emits select event to add product to row
     * @param {Object} product - Product to add to row
     */
    handleQuickViewAddToRow(product) {
      this.closeQuickView();
      this.$emit("select", product);
      this.close();
    },
    /**
     * Sets the active tab index
     * @param {Number} index - Tab index to activate
     */
    setActiveTab(index, event) {
      this.activeTabIndex = index;
      if (this.quickViewOpen) {
        this.closeQuickView();
      }
      this.$nextTick(() => {
        this.scrollTabToStart(event == null ? void 0 : event.currentTarget);
        this.updateTabsScrollbar();
      });
    },
    /**
     * Scrolls the selected tab to the left edge of the visible tabs area
     * @param {HTMLElement | null} tabElement - Selected tab element
     */
    scrollTabToStart(tabElement) {
      const tabsContainer = this.$refs.tabsContainer;
      const content = this.$refs.content;
      if (!tabsContainer || !tabElement || !content) {
        return;
      }
      const contentStyles = window.getComputedStyle(content);
      const contentPaddingLeft = Number.parseFloat(contentStyles.paddingLeft) || 0;
      const maxScrollLeft = tabsContainer.scrollWidth - tabsContainer.clientWidth;
      const targetLeft = Math.min(
        Math.max(0, tabElement.offsetLeft - contentPaddingLeft),
        maxScrollLeft
      );
      tabsContainer.scrollTo({
        left: targetLeft,
        behavior: "smooth"
      });
    }
  },
  mounted() {
    window.addEventListener("resize", this.updateTabsScrollbar, { passive: true });
  },
  beforeUnmount() {
    this.unbindTabsScroll();
    window.removeEventListener("resize", this.updateTabsScrollbar);
    this.handleTabsScrollbarPointerUp();
    this.unlockBodyScroll();
  }
};
const _hoisted_1$1 = { class: "mini-bundle-product-picker-drawer__body" };
const _hoisted_2 = { class: "mini-bundle-product-picker-drawer__header" };
const _hoisted_3 = { class: "mini-bundle-product-picker-drawer__header-title" };
const _hoisted_4 = { class: "mini-bundle-product-picker-drawer__viewport" };
const _hoisted_5 = {
  ref: "content",
  class: "mini-bundle-product-picker-drawer__content"
};
const _hoisted_6 = {
  key: 0,
  ref: "tabsContainer",
  class: "mini-bundle-product-picker-drawer__tabs"
};
const _hoisted_7 = ["onClick"];
const _hoisted_8 = { class: "mini-bundle-product-picker-drawer__products" };
function _sfc_render$1(_ctx, _cache, $props, $setup, $data, $options) {
  var _a, _b;
  const _component_ProductCard = resolveComponent("ProductCard");
  const _component_QuickViewDrawer = resolveComponent("QuickViewDrawer");
  return openBlock(), createBlock(Teleport, { to: "body" }, [
    createBaseVNode("div", {
      class: normalizeClass(["mini-bundle-product-picker-drawer__overlay", { "is-visible": $props.isOpen }]),
      onClick: _cache[0] || (_cache[0] = (...args) => $options.handleOverlayClick && $options.handleOverlayClick(...args))
    }, null, 2),
    createBaseVNode("div", {
      class: normalizeClass(["mini-bundle-product-picker-drawer", { "is-opened": $props.isOpen }])
    }, [
      createBaseVNode("div", _hoisted_1$1, [
        createBaseVNode("div", _hoisted_2, [
          createBaseVNode("div", _hoisted_3, toDisplayString($props.title), 1),
          createBaseVNode("button", {
            class: "unstyled-button mini-bundle-product-picker-drawer__header-close-button-wrapper close-button-with-scale-hover-wrapper",
            type: "button",
            onClick: _cache[1] || (_cache[1] = (...args) => $options.close && $options.close(...args)),
            "aria-label": "Close"
          }, _cache[4] || (_cache[4] = [
            createBaseVNode("span", { class: "close-button-with-scale-hover mini-bundle-product-picker-drawer__close-button-with-scale-hover" }, null, -1)
          ]))
        ]),
        createBaseVNode("div", _hoisted_4, [
          createBaseVNode("div", _hoisted_5, [
            $options.hasMultipleCollections && ((_a = $props.collections) == null ? void 0 : _a.length) > 1 ? (openBlock(), createElementBlock("div", _hoisted_6, [
              (openBlock(true), createElementBlock(Fragment, null, renderList($props.collections, (collection, index) => {
                return openBlock(), createElementBlock("button", {
                  key: collection.collectionId,
                  class: normalizeClass(["mini-bundle-product-picker-drawer__tab", { "is-active": $data.activeTabIndex === index }]),
                  onClick: ($event) => $options.setActiveTab(index, $event)
                }, toDisplayString(collection.collectionTitle), 11, _hoisted_7);
              }), 128))
            ], 512)) : createCommentVNode("", true),
            $options.hasMultipleCollections && ((_b = $props.collections) == null ? void 0 : _b.length) > 1 ? (openBlock(), createElementBlock("div", {
              key: 1,
              ref: "tabsScrollbar",
              class: normalizeClass(["mini-bundle-product-picker-drawer__tabs-scrollbar", { "is-hidden": !$data.tabsScrollbarVisible, "is-dragging": $data.isDraggingTabsScrollbar }]),
              "aria-hidden": "true",
              onPointerdown: _cache[3] || (_cache[3] = (...args) => $options.handleTabsScrollbarTrackPointerDown && $options.handleTabsScrollbarTrackPointerDown(...args))
            }, [
              createBaseVNode("div", {
                ref: "tabsScrollbarThumb",
                class: "mini-bundle-product-picker-drawer__tabs-scrollbar-thumb",
                style: normalizeStyle($options.tabsScrollbarThumbStyle),
                onPointerdown: _cache[2] || (_cache[2] = withModifiers((...args) => $options.handleTabsScrollbarThumbPointerDown && $options.handleTabsScrollbarThumbPointerDown(...args), ["stop"]))
              }, null, 36)
            ], 34)) : createCommentVNode("", true),
            createBaseVNode("div", _hoisted_8, [
              (openBlock(true), createElementBlock(Fragment, null, renderList($options.activeProducts, (product) => {
                return openBlock(), createBlock(_component_ProductCard, {
                  key: product.id,
                  product,
                  "discount-percent": $props.discountPercent,
                  "is-gift": $props.isGift,
                  onSelect: $options.handleProductSelect,
                  onPreview: $options.handleProductPreview
                }, null, 8, ["product", "discount-percent", "is-gift", "onSelect", "onPreview"]);
              }), 128))
            ])
          ], 512)
        ])
      ])
    ], 2),
    createVNode(_component_QuickViewDrawer, {
      "is-open": $data.quickViewOpen,
      product: $data.quickViewProduct,
      "discount-percent": $props.discountPercent,
      "is-gift": $props.isGift,
      onClose: $options.closeQuickView,
      onAddToRow: $options.handleQuickViewAddToRow
    }, null, 8, ["is-open", "product", "discount-percent", "is-gift", "onClose", "onAddToRow"])
  ]);
}
const ProductPickerDrawer = /* @__PURE__ */ _export_sfc(_sfc_main$1, [["render", _sfc_render$1], ["__scopeId", "data-v-b509e956"]]);
const _sfc_main = {
  name: "MiniBundleApp",
  components: {
    SamplePackPicker,
    FreeSamplePicker,
    ProductPickerDrawer
  },
  data() {
    return {
      drawerOpen: false,
      drawerType: null,
      // 'sample-package' or 'free-sample'
      drawerIndex: null,
      drawerMode: null,
      // 'add' or 'swap'
      drawerCloseTimeout: null,
      DRAWER_CLOSE_DURATION_MS: 400
    };
  },
  computed: {
    ...mapGetters({
      requiredSamplePackages: "getRequiredSamplePackages",
      requiredFreeSamples: "getRequiredFreeSamples",
      discountPercent: "getDiscountPercent",
      samplePackagesProducts: "getSamplePackagesProducts",
      freeSamplesProducts: "getFreeSamplesProducts"
    }),
    /**
     * Current parent variant ID from store
     * @returns {Number|null}
     */
    currentParentVariantId() {
      return this.$store.state.currentParentVariantId;
    },
    /**
     * Drawer title based on type and mode
     * @returns {String}
     */
    drawerTitle() {
      const textContent = this.$store.getters.getTextContent;
      if (this.drawerType === "sample-package") {
        return textContent.samplePackPickerHeading || "";
      } else if (this.drawerType === "free-sample") {
        return textContent.freeSamplePickerHeading || "";
      }
      return "";
    },
    /**
     * Collections to show in drawer
     * @returns {Array}
     */
    drawerCollections() {
      if (this.drawerType === "sample-package") {
        return this.samplePackagesProducts;
      } else if (this.drawerType === "free-sample") {
        return this.freeSamplesProducts;
      }
      return [];
    }
  },
  watch: {
    /**
     * Watches for variant changes and reinitializes arrays
     */
    currentParentVariantId() {
      this.initializeArrays();
    },
    /**
     * Watches for required quantities changes and reinitializes arrays
     */
    requiredSamplePackages() {
      this.initializeArrays();
    },
    requiredFreeSamples() {
      this.initializeArrays();
    }
  },
  methods: {
    ...mapActions(["setSamplePackage", "setFreeSample"]),
    getAvailableProductIds(collections) {
      var _a;
      if (!Array.isArray(collections)) {
        return /* @__PURE__ */ new Set();
      }
      if (collections.length > 0 && ((_a = collections[0]) == null ? void 0 : _a.collectionId)) {
        return new Set(
          collections.flatMap((collection) => (collection.products || []).map((product) => product.id))
        );
      }
      return new Set(collections.map((product) => product.id));
    },
    /**
     * Initializes selected arrays based on required quantities
     * Preserves existing selections when quantities change
     */
    initializeArrays() {
      const requiredPackages = this.requiredSamplePackages || 0;
      const requiredSamples = this.requiredFreeSamples || 0;
      const currentPackages = [...this.$store.state.selectedSamplePackages || []];
      const currentSamples = [...this.$store.state.selectedFreeSamples || []];
      const availableSamplePackageIds = this.getAvailableProductIds(this.samplePackagesProducts);
      const availableFreeSampleIds = this.getAvailableProductIds(this.freeSamplesProducts);
      this.$store.commit("CLEAR_SAMPLE_PACKAGES");
      this.$store.commit("CLEAR_FREE_SAMPLES");
      for (let i = 0; i < requiredPackages; i++) {
        const existingProduct = currentPackages[i] && availableSamplePackageIds.has(currentPackages[i].id) ? currentPackages[i] : null;
        this.$store.commit("SET_SAMPLE_PACKAGE", {
          index: i,
          product: existingProduct
        });
      }
      for (let i = 0; i < requiredSamples; i++) {
        const existingProduct = currentSamples[i] && availableFreeSampleIds.has(currentSamples[i].id) ? currentSamples[i] : null;
        this.$store.commit("SET_FREE_SAMPLE", {
          index: i,
          product: existingProduct
        });
      }
    },
    /**
     * Opens the product picker drawer
     * @param {String} type - 'sample-package' or 'free-sample'
     * @param {Number} index - Row index
     * @param {String} mode - 'add' or 'swap'
     */
    openDrawer(type, index, mode = "add") {
      if (this.drawerCloseTimeout) {
        clearTimeout(this.drawerCloseTimeout);
        this.drawerCloseTimeout = null;
      }
      this.drawerType = type;
      this.drawerIndex = index;
      this.drawerMode = mode;
      this.drawerOpen = true;
    },
    /**
     * Closes the product picker drawer.
     * Hides drawer immediately for animation; clears content after animation ends.
     */
    closeDrawer() {
      this.drawerOpen = false;
      if (this.drawerCloseTimeout) {
        clearTimeout(this.drawerCloseTimeout);
      }
      this.drawerCloseTimeout = setTimeout(() => {
        this.drawerType = null;
        this.drawerIndex = null;
        this.drawerMode = null;
        this.drawerCloseTimeout = null;
      }, this.DRAWER_CLOSE_DURATION_MS);
    },
    /**
     * Handles product selection from drawer
     * @param {Object} product - Selected product
     */
    handleProductSelect(product) {
      if (this.drawerType === "sample-package") {
        this.setSamplePackage({
          index: this.drawerIndex,
          product
        });
      } else if (this.drawerType === "free-sample") {
        this.setFreeSample({
          index: this.drawerIndex,
          product
        });
      }
      this.closeDrawer();
    },
    /**
     * Handles adding a sample package
     * @param {Number} index - Row index
     */
    handleAddSamplePackage(index) {
      this.openDrawer("sample-package", index, "add");
    },
    /**
     * Handles swapping a sample package
     * @param {Number} index - Row index
     */
    handleSwapSamplePackage(index) {
      this.openDrawer("sample-package", index, "swap");
    },
    /**
     * Handles adding a free sample
     * @param {Number} index - Row index
     */
    handleAddFreeSample(index) {
      this.openDrawer("free-sample", index, "add");
    },
    /**
     * Handles swapping a free sample
     * @param {Number} index - Row index
     */
    handleSwapFreeSample(index) {
      this.openDrawer("free-sample", index, "swap");
    }
  },
  created() {
    this.initializeArrays();
  },
  beforeUnmount() {
    if (this.drawerCloseTimeout) {
      clearTimeout(this.drawerCloseTimeout);
    }
  }
};
const _hoisted_1 = { class: "mini-bundle-samples" };
function _sfc_render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_SamplePackPicker = resolveComponent("SamplePackPicker");
  const _component_FreeSamplePicker = resolveComponent("FreeSamplePicker");
  const _component_ProductPickerDrawer = resolveComponent("ProductPickerDrawer");
  return openBlock(), createElementBlock("div", _hoisted_1, [
    createVNode(_component_SamplePackPicker, {
      onAddSamplePackage: $options.handleAddSamplePackage,
      onSwapSamplePackage: $options.handleSwapSamplePackage
    }, null, 8, ["onAddSamplePackage", "onSwapSamplePackage"]),
    createVNode(_component_FreeSamplePicker, {
      onAddFreeSample: $options.handleAddFreeSample,
      onSwapFreeSample: $options.handleSwapFreeSample
    }, null, 8, ["onAddFreeSample", "onSwapFreeSample"]),
    createVNode(_component_ProductPickerDrawer, {
      "is-open": $data.drawerOpen,
      title: $options.drawerTitle,
      collections: $options.drawerCollections,
      "discount-percent": _ctx.discountPercent,
      "is-gift": $data.drawerType === "free-sample",
      onClose: $options.closeDrawer,
      onSelect: $options.handleProductSelect
    }, null, 8, ["is-open", "title", "collections", "discount-percent", "is-gift", "onClose", "onSelect"])
  ]);
}
const App = /* @__PURE__ */ _export_sfc(_sfc_main, [["render", _sfc_render]]);
function getSelectedVariant(productData, optionInputs) {
  if (!productData || !productData.variantsWithMetafields) {
    return null;
  }
  const selectedOptions = {};
  optionInputs.forEach((input) => {
    if (input.checked) {
      const nameMatch = input.name.match(/-(\d+)$/);
      if (nameMatch) {
        const position = parseInt(nameMatch[1], 10);
        selectedOptions[position] = input.value;
      }
    }
  });
  const variant = productData.variantsWithMetafields.find((v) => {
    const option1Match = !selectedOptions[1] || v.option1 === selectedOptions[1];
    const option2Match = !selectedOptions[2] || v.option2 === selectedOptions[2];
    const option3Match = !selectedOptions[3] || v.option3 === selectedOptions[3];
    return option1Match && option2Match && option3Match;
  });
  return variant || null;
}
function updateURLParameter(variantId) {
  if (!variantId) return;
  const url = new URL(window.location.href);
  url.searchParams.set("variant", variantId.toString());
  window.history.replaceState({}, "", url.toString());
}
function getVariantIdFromURL$1() {
  const urlParams = new URLSearchParams(window.location.search);
  const variantParam = urlParams.get("variant");
  return variantParam ? parseInt(variantParam, 10) : null;
}
function getFirstAvailableVariantId$1(productData) {
  if (!productData || !productData.variantsWithMetafields) {
    return null;
  }
  const firstAvailable = productData.variantsWithMetafields.find((v) => v.available);
  if (firstAvailable) {
    return firstAvailable.id;
  }
  if (productData.variantsWithMetafields.length > 0) {
    return productData.variantsWithMetafields[0].id;
  }
  return null;
}
function updateUnderheading(variant) {
  var _a, _b;
  const underheadingElement = document.getElementById("mini-bundle-options-underheading");
  if (!underheadingElement) {
    return;
  }
  const underheading = (_b = (_a = variant == null ? void 0 : variant.metafields) == null ? void 0 : _a.miniBundle) == null ? void 0 : _b.underheading;
  if (underheading) {
    underheadingElement.textContent = underheading;
  } else {
    underheadingElement.textContent = "";
  }
}
function initVariantSync(productData) {
  if (!productData) {
    console.warn("Mini Bundle: Product data not available for variant sync");
    return;
  }
  const optionInputs = document.querySelectorAll('.mini-bundle-option-input[type="radio"]');
  if (optionInputs.length === 0) {
    console.warn("Mini Bundle: No option inputs found");
    return;
  }
  function handleVariantChange() {
    const selectedVariant = getSelectedVariant(productData, optionInputs);
    if (!selectedVariant) {
      console.warn("Mini Bundle: Could not determine selected variant");
      return;
    }
    updateURLParameter(selectedVariant.id);
    updateUnderheading(selectedVariant);
    const event = new CustomEvent("mini-bundle:variant-changed", {
      detail: {
        variantId: selectedVariant.id,
        variant: selectedVariant
      },
      bubbles: true
    });
    document.dispatchEvent(event);
  }
  optionInputs.forEach((input) => {
    input.addEventListener("change", handleVariantChange);
  });
  const urlVariantId = getVariantIdFromURL$1();
  if (!urlVariantId) {
    const firstAvailableId = getFirstAvailableVariantId$1(productData);
    if (firstAvailableId) {
      updateURLParameter(firstAvailableId);
    }
  }
  const currentVariantId = getVariantIdFromURL$1() || getFirstAvailableVariantId$1(productData);
  if (currentVariantId && productData.variantsWithMetafields) {
    const currentVariant = productData.variantsWithMetafields.find((v) => v.id === currentVariantId);
    if (currentVariant) {
      updateUnderheading(currentVariant);
    }
  }
  return {
    getCurrentVariantId: () => getVariantIdFromURL$1() || getFirstAvailableVariantId$1(productData),
    getVariantIdFromURL: getVariantIdFromURL$1
  };
}
function formatPrice(priceInCents) {
  var _a, _b, _c;
  if (typeof priceInCents !== "number" || priceInCents < 0) {
    return "";
  }
  const isUKStore = typeof Shopify !== "undefined" && (((_a = Shopify.currency) == null ? void 0 : _a.active) === "GBP" || Shopify.locale === "en-GB");
  const priceValue = priceInCents / 100;
  if (isUKStore) {
    return `£ ${priceValue.toFixed(2)}`;
  } else {
    const formatted = priceValue.toFixed(2).replace(".", ",");
    const currency = ((_c = (_b = window.__MINI_BUNDLE_STORE__) == null ? void 0 : _b.state) == null ? void 0 : _c.currency) || "€";
    return `${formatted} ${currency}`;
  }
}
function getProductVariant(product) {
  if (!product) return null;
  if (product.selected_or_first_available_variant) {
    return product.selected_or_first_available_variant;
  }
  const variants = product.variantsWithMetafields || product.variants || [];
  if (!Array.isArray(variants) || variants.length === 0) return null;
  const firstAvailable = variants.find((v) => v == null ? void 0 : v.available);
  return firstAvailable || variants[0] || null;
}
function getProductPrice$1(product) {
  if (!product) return 0;
  const variant = getProductVariant(product);
  if (variant == null ? void 0 : variant.price) return variant.price;
  return product.price || 0;
}
function hasAnySelectedProducts(selectedProducts) {
  if (!Array.isArray(selectedProducts)) return false;
  return selectedProducts.some((p2) => p2 !== null && p2 !== void 0);
}
function calculateTotalSavings(selectedPackages, selectedGifts, discountPercent) {
  let discountSavings = 0;
  let freeSampleSavings = 0;
  (selectedPackages || []).forEach((product) => {
    if (product && discountPercent > 0) {
      const basePrice = getProductPrice$1(product);
      const discountMultiplier = 1 - discountPercent / 100;
      const discountedPrice = Math.round(basePrice * discountMultiplier);
      discountSavings += basePrice - discountedPrice;
    }
  });
  (selectedGifts || []).forEach((product) => {
    if (product) {
      const basePrice = getProductPrice$1(product);
      freeSampleSavings += basePrice;
    }
  });
  return discountSavings + freeSampleSavings;
}
function calculateTotalPrice(selectedPackages, selectedGifts, discountPercent) {
  let packagesBasePrice = 0;
  let packagesFinalPrice = 0;
  let giftsBasePrice = 0;
  (selectedPackages || []).forEach((product) => {
    if (product) {
      const basePrice = getProductPrice$1(product);
      packagesBasePrice += basePrice;
      if (discountPercent > 0) {
        const discountMultiplier = 1 - discountPercent / 100;
        packagesFinalPrice += Math.round(basePrice * discountMultiplier);
      } else {
        packagesFinalPrice += basePrice;
      }
    }
  });
  (selectedGifts || []).forEach((product) => {
    if (product) {
      const basePrice = getProductPrice$1(product);
      giftsBasePrice += basePrice;
    }
  });
  const hasSelection = hasAnySelectedProducts(selectedPackages) || hasAnySelectedProducts(selectedGifts);
  if (!hasSelection) {
    return {
      totalPrice: 0,
      totalComparePrice: 0,
      hasSelection: false
    };
  }
  const giftsFinalPrice = 0;
  const totalPrice = packagesFinalPrice + giftsFinalPrice;
  const totalComparePrice = packagesBasePrice + giftsBasePrice;
  const shouldShowCompare = discountPercent > 0 && packagesBasePrice > packagesFinalPrice || giftsBasePrice > giftsFinalPrice;
  return {
    totalPrice,
    totalComparePrice: shouldShowCompare ? totalComparePrice : 0,
    hasSelection: true
  };
}
function updateSavingsBadge(totalSavings, totalComparePrice, savingsText = "") {
  const priceBlock = document.querySelector(".product-general-info-block.product-general-price-block");
  if (!priceBlock) {
    return;
  }
  const priceCost = priceBlock.querySelector(".product-price__cost");
  if (!priceCost) {
    return;
  }
  let savingsBadge = priceCost.querySelector(".mini-bundle-savings-badge");
  if (totalSavings > 0 && totalComparePrice > 0) {
    if (!savingsBadge) {
      savingsBadge = document.createElement("div");
      savingsBadge.className = "mini-bundle-savings-badge";
      priceCost.appendChild(savingsBadge);
    }
    const savingsPercent = Math.round(totalSavings / totalComparePrice * 100);
    const formattedSavings = `${savingsPercent}%`;
    savingsBadge.textContent = savingsText ? `${savingsText} ${formattedSavings}` : formattedSavings;
    savingsBadge.style.display = "";
  } else if (savingsBadge) {
    savingsBadge.style.display = "none";
  }
}
function updatePriceElement({ totalPrice, totalComparePrice, priceWillBeCalculatedText, hasSelection, totalSavings = 0, savingsText = "", shippingNotice = "" }) {
  const priceContainer = document.querySelector(".product-price[data-product-block-container]");
  if (!priceContainer) {
    return;
  }
  const priceContent = priceContainer.querySelector(".product-price__content[data-product-block-item]");
  if (!priceContent) {
    return;
  }
  const priceCost = priceContent.querySelector(".product-price__cost");
  if (!priceCost) {
    return;
  }
  priceCost.innerHTML = "";
  if (!hasSelection) {
    const messageDiv = document.createElement("div");
    messageDiv.className = "product-price__current-price price-info";
    messageDiv.textContent = priceWillBeCalculatedText || "";
    priceCost.appendChild(messageDiv);
    updateSavingsBadge(0, totalComparePrice, savingsText);
    const priceBlock = priceContainer.closest(".product-general-price-block");
    if (priceBlock) {
      const shippingNoticeElement = priceBlock.querySelector(".product-price__per-kg.mini-bundle-shipping-notice");
      if (shippingNoticeElement) {
        shippingNoticeElement.style.display = "none";
      }
    }
    return;
  }
  if (totalComparePrice > totalPrice) {
    const comparePriceDiv = document.createElement("div");
    comparePriceDiv.className = "product-price__old-price product-price__old-price--highlight-sale";
    comparePriceDiv.textContent = formatPrice(totalComparePrice);
    priceCost.appendChild(comparePriceDiv);
  }
  const currentPriceDiv = document.createElement("div");
  currentPriceDiv.className = "product-price__current-price";
  if (totalComparePrice > totalPrice) {
    currentPriceDiv.classList.add("product-price__current-price--highlight-sale");
  }
  currentPriceDiv.textContent = formatPrice(totalPrice);
  priceCost.appendChild(currentPriceDiv);
  updateSavingsBadge(totalSavings, totalComparePrice, savingsText);
  if (shippingNotice) {
    const priceBlock = priceContainer.closest(".product-general-price-block");
    const shippingNoticeText = shippingNotice.replace("&amp;", "&");
    if (priceBlock) {
      let shippingNoticeElement = priceBlock.querySelector(".product-price__per-kg.mini-bundle-shipping-notice");
      if (!shippingNoticeElement) {
        shippingNoticeElement = document.createElement("div");
        shippingNoticeElement.className = "product-price__per-kg mini-bundle-shipping-notice";
        const savingsBadge = priceBlock.querySelector(".mini-bundle-savings-badge");
        if (savingsBadge && savingsBadge.parentNode) {
          savingsBadge.parentNode.insertBefore(shippingNoticeElement, savingsBadge.nextSibling);
        } else {
          const priceWrapper = priceBlock.querySelector(".product-price-wrapper");
          if (priceWrapper && priceWrapper.parentNode) {
            priceWrapper.parentNode.insertBefore(shippingNoticeElement, priceWrapper.nextSibling);
          } else {
            priceBlock.appendChild(shippingNoticeElement);
          }
        }
      }
      shippingNoticeElement.textContent = shippingNoticeText;
      shippingNoticeElement.style.display = "";
    }
  } else {
    const priceBlock = priceContainer.closest(".product-general-price-block");
    if (priceBlock) {
      const shippingNoticeElement = priceBlock.querySelector(".product-price__per-kg.mini-bundle-shipping-notice");
      if (shippingNoticeElement) {
        shippingNoticeElement.style.display = "none";
      }
    }
  }
}
function initPriceUpdater(store2, priceWillBeCalculatedText, savingsText = "", shippingNotice = "") {
  const selectedPackages = store2.getters.getSelectedSamplePackages || [];
  const selectedGifts = store2.getters.getSelectedFreeSamples || [];
  const discountPercent = store2.getters.getDiscountPercent || 0;
  const { totalPrice, totalComparePrice, hasSelection } = calculateTotalPrice(
    selectedPackages,
    selectedGifts,
    discountPercent
  );
  const totalSavings = calculateTotalSavings(
    selectedPackages,
    selectedGifts,
    discountPercent
  );
  updatePriceElement({
    totalPrice,
    totalComparePrice,
    hasSelection,
    priceWillBeCalculatedText,
    totalSavings,
    savingsText,
    shippingNotice
  });
  store2.watch(
    (state, getters) => [
      getters.getSelectedSamplePackages,
      getters.getSelectedFreeSamples,
      getters.getDiscountPercent
    ],
    ([selectedPackages2, selectedGifts2, discountPercent2]) => {
      const { totalPrice: totalPrice2, totalComparePrice: totalComparePrice2, hasSelection: hasSelection2 } = calculateTotalPrice(
        selectedPackages2 || [],
        selectedGifts2 || [],
        discountPercent2 || 0
      );
      const totalSavings2 = calculateTotalSavings(
        selectedPackages2 || [],
        selectedGifts2 || [],
        discountPercent2 || 0
      );
      updatePriceElement({
        totalPrice: totalPrice2,
        totalComparePrice: totalComparePrice2,
        hasSelection: hasSelection2,
        priceWillBeCalculatedText,
        totalSavings: totalSavings2,
        savingsText,
        shippingNotice
      });
    },
    { deep: true }
  );
}
const MINI_BUNDLE_ROOT_SELECTOR = ".mini-bundle-builder";
const MINI_BUNDLE_FORM_SELECTOR = `${MINI_BUNDLE_ROOT_SELECTOR} product-form form[data-type="add-to-cart-form"]`;
function getMiniBundleRoot() {
  return document.querySelector(MINI_BUNDLE_ROOT_SELECTOR);
}
function getMiniBundleForms() {
  return document.querySelectorAll(MINI_BUNDLE_FORM_SELECTOR);
}
function getCartItemFullHeight(item) {
  if (!item) {
    return 0;
  }
  const body = item.querySelector("[data-cart-item-body]");
  if (!body) {
    return Math.ceil(item.scrollHeight);
  }
  const itemStyles = window.getComputedStyle(item);
  const itemVerticalExtras = Number.parseFloat(itemStyles.paddingTop || "0") + Number.parseFloat(itemStyles.paddingBottom || "0") + Number.parseFloat(itemStyles.borderTopWidth || "0") + Number.parseFloat(itemStyles.borderBottomWidth || "0");
  return Math.ceil(body.getBoundingClientRect().height + itemVerticalExtras);
}
function getVuexStore() {
  const appElement = document.querySelector("#mini-bundle-samples");
  if (!appElement || !appElement.__vue_app__) {
    return null;
  }
  const app = appElement.__vue_app__;
  if (app && app.config && app.config.globalProperties && app.config.globalProperties.$store) {
    return app.config.globalProperties.$store;
  }
  if (window.__MINI_BUNDLE_STORE__) {
    return window.__MINI_BUNDLE_STORE__;
  }
  return null;
}
function getProductPrice(product) {
  var _a;
  if (!product) return 0;
  if ((_a = product.selected_or_first_available_variant) == null ? void 0 : _a.price) {
    return product.selected_or_first_available_variant.price;
  }
  if (product.variants && product.variants.length > 0) {
    return product.variants[0].price || 0;
  }
  return product.price || 0;
}
function calculateCompareAtPrice(selectedPackages, selectedGifts) {
  let totalBasePrice = 0;
  (selectedPackages || []).forEach((product) => {
    if (product) {
      const basePrice = getProductPrice(product);
      totalBasePrice += basePrice;
    }
  });
  (selectedGifts || []).forEach((product) => {
    if (product) {
      const basePrice = getProductPrice(product);
      totalBasePrice += basePrice;
    }
  });
  return totalBasePrice;
}
function extractVariantIds(selectedProducts) {
  if (!Array.isArray(selectedProducts)) {
    return [];
  }
  const variantIds = [];
  selectedProducts.forEach((product) => {
    if (product && product.selected_or_first_available_variant) {
      const variantId = product.selected_or_first_available_variant.id;
      if (variantId) {
        variantIds.push(variantId);
      }
    } else if (product && product.variants && product.variants.length > 0) {
      const variantId = product.variants[0].id;
      if (variantId) {
        variantIds.push(variantId);
      }
    }
  });
  return variantIds;
}
function addPropertiesToForm(form, samplePackagesVariantIds, freeSamplesVariantIds, compareAtPrice) {
  const existingProps = form.querySelectorAll('input[name^="properties["]');
  existingProps.forEach((input) => {
    if (input.name === "properties[_nested_variants_ids]" || input.name === "properties[_nested_variants_gifts_ids]" || input.name === "properties[_timestamp]" || input.name === "properties[_compare_at_price]") {
      input.remove();
    }
  });
  const timestampInput = document.createElement("input");
  timestampInput.type = "hidden";
  timestampInput.name = "properties[_timestamp]";
  timestampInput.value = Date.now().toString();
  form.appendChild(timestampInput);
  if (compareAtPrice > 0) {
    const compareAtPriceInput = document.createElement("input");
    compareAtPriceInput.type = "hidden";
    compareAtPriceInput.name = "properties[_compare_at_price]";
    compareAtPriceInput.value = compareAtPrice.toString();
    form.appendChild(compareAtPriceInput);
  }
  if (samplePackagesVariantIds.length > 0) {
    const samplePackagesInput = document.createElement("input");
    samplePackagesInput.type = "hidden";
    samplePackagesInput.name = "properties[_nested_variants_ids]";
    samplePackagesInput.value = JSON.stringify(samplePackagesVariantIds);
    form.appendChild(samplePackagesInput);
  }
  if (freeSamplesVariantIds.length > 0) {
    const freeSamplesInput = document.createElement("input");
    freeSamplesInput.type = "hidden";
    freeSamplesInput.name = "properties[_nested_variants_gifts_ids]";
    freeSamplesInput.value = JSON.stringify(freeSamplesVariantIds);
    form.appendChild(freeSamplesInput);
  }
}
function getFormQuantity(form) {
  const quantityInput = form == null ? void 0 : form.querySelector('input[name="quantity"]');
  const parsed = Number.parseInt((quantityInput == null ? void 0 : quantityInput.value) || "1", 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}
function getFormDataQuantity(formData) {
  const parsed = Number.parseInt((formData == null ? void 0 : formData.get("quantity")) || "1", 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}
function summarizeProductForSentry(product) {
  if (!product) {
    return null;
  }
  const variant = product.selected_or_first_available_variant || (Array.isArray(product.variants) ? product.variants[0] : null);
  const variantOptions = variant ? [variant.option1, variant.option2, variant.option3].filter(Boolean).join(" / ") : "";
  return {
    product_id: product.id,
    handle: product.handle,
    title: product.title,
    variant_id: (variant == null ? void 0 : variant.id) ?? null,
    variant_title: (variant == null ? void 0 : variant.title) ?? null,
    variant_options: variantOptions || null,
    sku: (variant == null ? void 0 : variant.sku) ?? null
  };
}
function formatVariantHumanLine(variant) {
  if (!variant) {
    return "—";
  }
  const opts = [variant.option1, variant.option2, variant.option3].filter(Boolean).join(" / ");
  const label = variant.title || opts || "variant";
  const sku = variant.sku ? ` | sku ${variant.sku}` : "";
  return `${label} [variant_id ${variant.id}]${sku}`;
}
function buildMiniBundleHumanReadable(store2) {
  var _a, _b, _c, _d;
  if (!(store2 == null ? void 0 : store2.state)) {
    return "Mini-bundle: Vuex store was not available when the error was reported.";
  }
  const lines = [];
  const pp = store2.state.parentProduct;
  const variants = (pp == null ? void 0 : pp.variantsWithMetafields) || (pp == null ? void 0 : pp.variants) || [];
  const current = variants.find((v) => v.id === store2.state.currentParentVariantId) || null;
  lines.push("—— Mini-bundle configuration ——");
  lines.push(
    `Parent product: ${(pp == null ? void 0 : pp.title) || (pp == null ? void 0 : pp.handle) || "(unknown)"} (handle: ${(pp == null ? void 0 : pp.handle) || "n/a"}, product_id: ${(pp == null ? void 0 : pp.id) ?? "n/a"})`
  );
  lines.push(`Selected parent variant: ${formatVariantHumanLine(current)}`);
  const samples = ((_a = store2.getters) == null ? void 0 : _a.getSelectedSamplePackages) || [];
  lines.push("Sample packages (one slot per line):");
  samples.forEach((product, i) => {
    if (!product) {
      lines.push(`  Slot ${i + 1}: (empty)`);
      return;
    }
    const v = product.selected_or_first_available_variant || (Array.isArray(product.variants) ? product.variants[0] : null);
    lines.push(`  Slot ${i + 1}: ${product.title} — ${formatVariantHumanLine(v)}`);
  });
  const gifts = ((_b = store2.getters) == null ? void 0 : _b.getSelectedFreeSamples) || [];
  lines.push("Gifts / free samples (one slot per line):");
  gifts.forEach((product, i) => {
    if (!product) {
      lines.push(`  Slot ${i + 1}: (empty)`);
      return;
    }
    const v = product.selected_or_first_available_variant || (Array.isArray(product.variants) ? product.variants[0] : null);
    lines.push(`  Slot ${i + 1}: ${product.title} — ${formatVariantHumanLine(v)}`);
  });
  lines.push(
    `Completeness: sample_packages_complete=${Boolean((_c = store2.getters) == null ? void 0 : _c.isSamplePackagesComplete)}, free_samples_complete=${Boolean((_d = store2.getters) == null ? void 0 : _d.isFreeSamplesComplete)}`
  );
  return lines.join("\n");
}
function buildMiniBundleAdditionalContext(store2, formData) {
  var _a, _b, _c, _d, _e, _f;
  const context = {
    bundle_type: "mini-bundle"
  };
  if (store2 == null ? void 0 : store2.state) {
    context.parent_product_id = ((_a = store2.state.parentProduct) == null ? void 0 : _a.id) ?? null;
    context.parent_product_handle = ((_b = store2.state.parentProduct) == null ? void 0 : _b.handle) ?? null;
    context.current_parent_variant_id = store2.state.currentParentVariantId ?? null;
    context.sample_packages_complete = Boolean((_c = store2.getters) == null ? void 0 : _c.isSamplePackagesComplete);
    context.free_samples_complete = Boolean((_d = store2.getters) == null ? void 0 : _d.isFreeSamplesComplete);
    context.selected_sample_packages = (((_e = store2.getters) == null ? void 0 : _e.getSelectedSamplePackages) || []).map(
      summarizeProductForSentry
    );
    context.selected_free_samples = (((_f = store2.getters) == null ? void 0 : _f.getSelectedFreeSamples) || []).map(
      summarizeProductForSentry
    );
  }
  if (formData instanceof FormData) {
    context.form_quantity = formData.get("quantity");
    context.form_variant_id = formData.get("id");
    context.form_sections = formData.get("sections");
    context.form_sections_url = formData.get("sections_url");
  }
  return context;
}
function formDataToPlainObject(formData) {
  const out = {};
  try {
    formData.forEach((value, key) => {
      if (typeof value === "string") {
        out[key] = value;
      }
    });
  } catch (_) {
  }
  return out;
}
async function addMultipleBundlesFromFormData(formData) {
  const quantity = getFormDataQuantity(formData);
  const variantId = Number.parseInt(formData.get("id"), 10);
  const compareAtPrice = formData.get("properties[_compare_at_price]") || "";
  const nestedVariantsIds = formData.get("properties[_nested_variants_ids]") || "";
  const nestedGiftVariantsIds = formData.get("properties[_nested_variants_gifts_ids]") || "";
  const sections = formData.get("sections");
  const sectionsUrl = formData.get("sections_url") || window.location.pathname;
  if (!variantId || quantity <= 1) {
    throw new Error("Invalid multi bundle payload");
  }
  const baseTimestamp = Date.now();
  const items = Array.from({ length: quantity }, (_, index) => {
    const properties = {
      _timestamp: String(baseTimestamp + index)
    };
    if (compareAtPrice) {
      properties._compare_at_price = compareAtPrice;
    }
    if (nestedVariantsIds) {
      properties._nested_variants_ids = nestedVariantsIds;
    }
    if (nestedGiftVariantsIds) {
      properties._nested_variants_gifts_ids = nestedGiftVariantsIds;
    }
    return {
      id: variantId,
      quantity: 1,
      properties
    };
  });
  const requestPayload = {
    items,
    ...sections ? { sections, sections_url: sectionsUrl } : {}
  };
  const response = await fetch("/cart/add.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest"
    },
    body: JSON.stringify(requestPayload)
  });
  const responseText = await response.text();
  if (!response.ok) {
    const store2 = getVuexStore();
    await reportBundleCartAddFailure({
      feature: "mini-bundle",
      httpStatus: response.status,
      responseBody: responseText,
      requestPayload,
      error: new Error(responseText || `HTTP ${response.status}`),
      humanReadableBundle: buildMiniBundleHumanReadable(store2),
      additionalContext: {
        ...buildMiniBundleAdditionalContext(store2, formData),
        flow: "addMultipleBundlesFromFormData",
        bundle_quantity: quantity
      }
    });
    return responseText;
  }
  try {
    const parsedState = JSON.parse(responseText);
    if (Array.isArray(parsedState.items) && parsedState.items.length > 0) {
      parsedState.id = variantId;
      parsedState.quantity = 1;
      parsedState._added_bundle_count = quantity;
    }
    return JSON.stringify(parsedState);
  } catch (_) {
    return responseText;
  }
}
function patchThemeProductForms() {
  const miniBundleRoot = getMiniBundleRoot();
  if (!miniBundleRoot) {
    return;
  }
  const productForms = miniBundleRoot.querySelectorAll("product-form");
  productForms.forEach((productForm) => {
    var _a;
    if (productForm.dataset.miniBundleCartApiPatched === "true") {
      return;
    }
    const originalAdd = (_a = productForm.cartApi) == null ? void 0 : _a.add;
    if (typeof originalAdd !== "function") {
      return;
    }
    productForm.cartApi.add = function patchedAdd(config) {
      const formData = config == null ? void 0 : config.body;
      const isMiniBundle = formData instanceof FormData && formData.has("properties[_nested_variants_ids]") && formData.has("properties[_timestamp]");
      const quantity = isMiniBundle ? getFormDataQuantity(formData) : 1;
      if (!isMiniBundle || quantity <= 1) {
        if (!isMiniBundle) {
          return originalAdd.call(this, config);
        }
        return originalAdd.call(this, config).then((responseText) => {
          const { isFailure, parsed, httpStatusFromBody } = parseCartAddJsonResponse(responseText);
          if (isFailure) {
            const store2 = getVuexStore();
            void reportBundleCartAddFailure({
              feature: "mini-bundle",
              httpStatus: httpStatusFromBody ?? void 0,
              responseBody: responseText,
              requestPayload: formDataToPlainObject(formData),
              error: new Error(
                (parsed == null ? void 0 : parsed.description) || (parsed == null ? void 0 : parsed.message) || "Mini-bundle cart add rejected (JSON error body)"
              ),
              humanReadableBundle: buildMiniBundleHumanReadable(store2),
              additionalContext: {
                ...buildMiniBundleAdditionalContext(store2, formData),
                flow: "productFormCartApi_single_quantity",
                parsed_cart_response: parsed,
                quantity: 1
              }
            }).catch(() => {
            });
          }
          return responseText;
        });
      }
      return addMultipleBundlesFromFormData(formData).then((responseText) => {
        scheduleBundleCartItemHeightRefresh();
        return responseText;
      });
    };
    productForm.dataset.miniBundleCartApiPatched = "true";
  });
}
function refreshBundleCartItemHeights() {
  const cartItems = document.querySelectorAll("cart-drawer .cart-item--bundle");
  cartItems.forEach((item) => {
    const nextHeight = getCartItemFullHeight(item);
    if (nextHeight > 0) {
      item.style.maxHeight = `${nextHeight}px`;
    }
  });
}
function scheduleBundleCartItemHeightRefresh() {
  const refresh = () => refreshBundleCartItemHeights();
  const cartDrawer = document.querySelector("cart-drawer");
  const delayedRefreshes = [0, 100, 250, 500];
  requestAnimationFrame(() => {
    requestAnimationFrame(refresh);
  });
  delayedRefreshes.forEach((delayMs) => {
    setTimeout(refresh, delayMs);
  });
  cartDrawer == null ? void 0 : cartDrawer.querySelectorAll(".cart-item--bundle img").forEach((image) => {
    if (image.complete) {
      return;
    }
    image.addEventListener("load", refresh, { once: true });
    image.addEventListener("error", refresh, { once: true });
  });
}
function initFormProperties() {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      setupFormInterception();
    });
  } else {
    setupFormInterception();
  }
}
function updateFormVariantId(form, variantId) {
  if (!form || !variantId) {
    return;
  }
  const variantInput = form.querySelector('input[name="id"]');
  if (variantInput) {
    const variantIdString = variantId.toString();
    variantInput.value = variantIdString;
    if (variantInput.hasAttribute("data-product-form-variant")) {
      variantInput.setAttribute("data-product-form-variant", variantIdString);
    }
    variantInput.dispatchEvent(new Event("change", { bubbles: true }));
  }
}
function updateFormProperties(store2) {
  if (!store2) {
    return;
  }
  const productForms = getMiniBundleForms();
  if (productForms.length === 0) {
    return;
  }
  try {
    const currentParentVariantId = store2.state.currentParentVariantId;
    const selectedSamplePackages = store2.getters.getSelectedSamplePackages || [];
    const selectedFreeSamples = store2.getters.getSelectedFreeSamples || [];
    const samplePackagesVariantIds = extractVariantIds(selectedSamplePackages);
    const freeSamplesVariantIds = extractVariantIds(selectedFreeSamples);
    const compareAtPrice = calculateCompareAtPrice(selectedSamplePackages, selectedFreeSamples);
    productForms.forEach((form) => {
      if (currentParentVariantId) {
        updateFormVariantId(form, currentParentVariantId);
      }
      addPropertiesToForm(form, samplePackagesVariantIds, freeSamplesVariantIds, compareAtPrice);
    });
  } catch (error) {
    console.error("Error updating form properties:", error);
  }
}
function setupStoreWatchers(store2) {
  if (!store2) {
    return;
  }
  store2.watch(
    (state, getters) => getters.getSelectedSamplePackages,
    () => {
      updateFormProperties(store2);
    },
    { deep: true }
  );
  store2.watch(
    (state, getters) => getters.getSelectedFreeSamples,
    () => {
      updateFormProperties(store2);
    },
    { deep: true }
  );
  store2.watch(
    (state) => state.currentParentVariantId,
    () => {
      updateFormProperties(store2);
    }
  );
}
function setupFormInterception() {
  patchThemeProductForms();
  if (document.documentElement.dataset.miniBundleSubmitHandled !== "true") {
    document.documentElement.dataset.miniBundleSubmitHandled = "true";
    document.addEventListener("submit", function(event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) {
        return;
      }
      if (!form.matches(MINI_BUNDLE_FORM_SELECTOR)) {
        return;
      }
      const store3 = getVuexStore();
      if (!store3) {
        return;
      }
      const packagesComplete = store3.getters.isSamplePackagesComplete;
      const samplesComplete = store3.getters.isFreeSamplesComplete;
      if (!packagesComplete || !samplesComplete) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      const quantity = getFormQuantity(form);
      updateFormProperties(store3);
      if (quantity <= 1) {
        return;
      }
    }, true);
  }
  const store2 = getVuexStore();
  if (store2) {
    setupStoreWatchers(store2);
    updateFormProperties(store2);
    patchThemeProductForms();
  } else {
    setTimeout(() => {
      const store3 = getVuexStore();
      if (store3) {
        setupStoreWatchers(store3);
        updateFormProperties(store3);
        patchThemeProductForms();
      }
    }, 1e3);
  }
}
const FORM_SELECTOR = '.mini-bundle-builder product-form form[data-type="add-to-cart-form"]';
const BTN_SELECTOR = 'button.product-form__btn[type="submit"]';
const BTN_TEXT_SELECTOR = "[data-button-text]";
const CLASS_INCOMPLETE = "mini-bundle-atc-incomplete";
function isBundleComplete(store2) {
  if (!store2 || !store2.getters) return false;
  const packagesComplete = store2.getters.isSamplePackagesComplete;
  const samplesComplete = store2.getters.isFreeSamplesComplete;
  return Boolean(packagesComplete && samplesComplete);
}
function isCurrentVariantAvailable(store2) {
  if (!store2 || !store2.getters) return true;
  const variant = store2.getters.getCurrentParentVariant;
  return variant == null || variant.available !== false;
}
function getAtcButtons() {
  const forms = document.querySelectorAll(FORM_SELECTOR);
  const pairs = [];
  forms.forEach((form) => {
    const buttons = form.querySelectorAll(BTN_SELECTOR);
    buttons.forEach((button) => {
      const textSpan = button.querySelector(BTN_TEXT_SELECTOR);
      if (textSpan) {
        pairs.push({ button, textSpan });
      }
    });
  });
  return pairs;
}
function updateButtonElement(store2, { complete, variantAvailable, chooseProductsText, addToCartText, soldOutText }) {
  const pairs = getAtcButtons();
  const incomplete = !complete;
  const label = !complete ? chooseProductsText : variantAvailable ? addToCartText : addToCartText;
  pairs.forEach(({ button, textSpan }) => {
    button.disabled = complete && !variantAvailable;
    button.classList.toggle(CLASS_INCOMPLETE, incomplete);
    button.setAttribute("aria-disabled", incomplete ? "true" : "false");
    textSpan.textContent = label;
    button.setAttribute("aria-label", label);
  });
}
function initButtonUpdater(store2, chooseProductsText, addToCartText, soldOutText) {
  function runUpdate() {
    const complete = isBundleComplete(store2);
    const variantAvailable = isCurrentVariantAvailable(store2);
    updateButtonElement(store2, {
      complete,
      variantAvailable,
      chooseProductsText: chooseProductsText || "Choose your products",
      addToCartText: addToCartText || "Add to cart",
      soldOutText: null
    });
  }
  runUpdate();
  store2.watch(
    (state, getters) => [
      getters.isSamplePackagesComplete,
      getters.isFreeSamplesComplete,
      getters.getCurrentParentVariant
    ],
    () => runUpdate(),
    { deep: true }
  );
}
function getVariantIdFromURL() {
  const urlParams = new URLSearchParams(window.location.search);
  const variantParam = urlParams.get("variant");
  return variantParam ? parseInt(variantParam, 10) : null;
}
function getFirstAvailableVariantId(productData) {
  if (!productData || !productData.parentProduct || !productData.parentProduct.variantsWithMetafields) {
    return null;
  }
  const firstAvailable = productData.parentProduct.variantsWithMetafields.find((v) => v.available);
  if (firstAvailable) {
    return firstAvailable.id;
  }
  if (productData.parentProduct.variantsWithMetafields.length > 0) {
    return productData.parentProduct.variantsWithMetafields[0].id;
  }
  return null;
}
const miniBundleInit = (selectors) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i, _j, _k;
  const jsonData = (_a = document.querySelector(selectors.json)) == null ? void 0 : _a.textContent;
  let data;
  try {
    data = jsonData ? JSON.parse(jsonData) : null;
  } catch (error) {
    console.log("✌️jsonData --->", jsonData);
    console.error(error);
    return;
  }
  if (!data) return;
  const urlVariantId = getVariantIdFromURL();
  const firstAvailableId = getFirstAvailableVariantId(data);
  if (urlVariantId) {
    data.currentParentVariantId = urlVariantId;
  } else if (firstAvailableId && !data.currentParentVariantId) {
    data.currentParentVariantId = firstAvailableId;
  }
  console.log("✌️data --->", data);
  const vuexStore = store(data);
  initVariantSync(data.parentProduct);
  document.addEventListener("mini-bundle:variant-changed", (event) => {
    const { variantId } = event.detail;
    if (variantId) {
      vuexStore.dispatch("setCurrentParentVariant", variantId);
    }
  });
  const priceWillBeCalculatedText = ((_c = (_b = data.textContent) == null ? void 0 : _b.locales) == null ? void 0 : _c.price_will_be_calculated) || "";
  const savingsText = ((_e = (_d = data.textContent) == null ? void 0 : _d.locales) == null ? void 0 : _e.savings) || "";
  const shippingNotice = ((_g = (_f = data.textContent) == null ? void 0 : _f.locales) == null ? void 0 : _g.shipping_notice) || "";
  initPriceUpdater(vuexStore, priceWillBeCalculatedText, savingsText, shippingNotice);
  const chooseProductsText = ((_i = (_h = data.textContent) == null ? void 0 : _h.locales) == null ? void 0 : _i.choose_products) || "";
  const addToCartText = ((_k = (_j = data.textContent) == null ? void 0 : _j.locales) == null ? void 0 : _k.add_to_cart) || "";
  initButtonUpdater(vuexStore, chooseProductsText, addToCartText);
  window.__MINI_BUNDLE_STORE__ = vuexStore;
  initFormProperties();
  const app = createApp(App);
  app.use(vuexStore);
  app.config.devtools = true;
  app.config.errorHandler = (err, vm, info) => {
    console.error("Error in component!");
    console.log("✌️info --->", info);
    console.log("✌️vm --->", vm);
    console.log("✌️err --->", err);
    console.log("\n\n\n");
  };
  app.mount(selectors.app);
};
class SfWhatsInside extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute("data-initialized")) return;
    this.setAttribute("data-initialized", "true");
    this.triggers = Array.from(
      this.querySelectorAll("[data-whats-inside-trigger]")
    );
    this.panels = Array.from(
      this.querySelectorAll("[data-whats-inside-panel]")
    );
    this.triggers.forEach((trigger2) => {
      trigger2.addEventListener("click", () => this.toggle(trigger2));
      trigger2.addEventListener(
        "keydown",
        (event) => this.handleKeydown(event, trigger2)
      );
    });
  }
  toggle(activeTrigger) {
    const shouldOpen = activeTrigger.getAttribute("aria-expanded") !== "true";
    const activePanelId = activeTrigger.getAttribute("aria-controls");
    this.triggers.forEach(
      (trigger2) => trigger2.setAttribute("aria-expanded", "false")
    );
    this.panels.forEach((panel) => {
      panel.hidden = true;
    });
    if (!shouldOpen) return;
    activeTrigger.setAttribute("aria-expanded", "true");
    const activePanel = this.panels.find(
      (panel) => panel.id === activePanelId
    );
    if (activePanel) activePanel.hidden = false;
  }
  handleKeydown(event, currentTrigger) {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    const currentIndex = this.triggers.indexOf(currentTrigger);
    const direction = event.key === "ArrowRight" ? 1 : -1;
    const nextIndex = (currentIndex + direction + this.triggers.length) % this.triggers.length;
    this.triggers[nextIndex].focus();
  }
}
const whatsInside = () => {
  if (!customElements.get("sf-whats-inside")) {
    customElements.define("sf-whats-inside", SfWhatsInside);
  }
};
function shouldShowBanner(id) {
  if (sessionStorage.getItem("banner-" + id) !== "no") {
    return true;
  }
  return false;
}
function setBannerShouldShow(id, shouldShow) {
  sessionStorage.setItem("banner-" + id, "no");
}
function getCookie(name) {
  var value = "; " + document.cookie;
  var parts = value.split("; " + name + "=");
  if (parts.length == 2) {
    const raw = parts.pop().split(";").shift();
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
}
function showBanner(banner, discountCode) {
  if (banner.querySelector(".discount-bar-text")) {
    const discountBarText = banner.querySelector(".discount-bar-text");
    const originalHTML = discountBarText.getAttribute("data-original-text");
    discountBarText.innerHTML = originalHTML.replace("#DISCOUNTCODE", discountCode);
  }
  setTimeout(() => {
    banner.classList.remove("hidden");
  }, 100);
  setTimeout(() => {
    banner.classList.remove("discount-bar-section-hidden");
  }, 600);
}
function getCartUpdateUrl() {
  var _a;
  if ((_a = window.routes) == null ? void 0 : _a.cart_update_url) {
    const base = String(window.routes.cart_update_url);
    return base.endsWith(".js") ? base : `${base}.js`;
  }
  return "/cart/update.js";
}
function hasAnyDiscount(cart) {
  if (typeof cart.total_discount === "number" && cart.total_discount > 0) return true;
  if (Array.isArray(cart.cart_level_discount_applications) && cart.cart_level_discount_applications.length) return true;
  if (Array.isArray(cart.items) && cart.items.some(
    (i) => Array.isArray(i.discounts) && i.discounts.length || Array.isArray(i.discount_allocations) && i.discount_allocations.length
  )) return true;
  return false;
}
async function isDiscountCookieApplicable(discountCookie) {
  if (!discountCookie) return false;
  try {
    const res = await fetch(getCartUpdateUrl(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ discount: discountCookie })
    });
    if (!res.ok) throw new Error("Failed to update discount");
    const cart = await res.json();
    if (!cart.item_count) {
      return true;
    }
    return hasAnyDiscount(cart);
  } catch (e) {
    console.warn("Discount applicability check failed:", e);
    return false;
  }
}
async function handleBanner() {
  const discountBarSections = document.querySelectorAll(".discount-bar-section");
  if (discountBarSections.length === 0) return;
  const discountCookie = getCookie("discount_code");
  if (!discountCookie) return;
  const applicable = await isDiscountCookieApplicable(discountCookie);
  if (!applicable) return;
  discountBarSections.forEach((discountBarSection) => {
    if (!shouldShowBanner(discountBarSection.dataset.id)) return;
    showBanner(discountBarSection, discountCookie);
    if (discountBarSection.querySelector(".discount-bar-close")) {
      discountBarSection.querySelector(".discount-bar-close").addEventListener("click", () => {
        discountBarSection.classList.add("hidden");
        setBannerShouldShow(discountBarSection.dataset.id);
      });
    }
  });
}
document.addEventListener("DOMContentLoaded", () => {
  handleBanner();
});
function handleVariantSelectCollection() {
  const variantPills = document.querySelectorAll(".product-card-pill");
  if (variantPills.length > 0) {
    variantPills.forEach((variantPill) => {
      variantPill.addEventListener("click", (e) => {
        const pill = e.currentTarget;
        const variantList = pill.closest(".product-variant-picker__pill-list");
        if (variantList) {
          variantList.querySelectorAll(".product-card-pill.selected").forEach((el) => el.classList.remove("selected"));
        }
        pill.classList.add("selected");
        const priceCents = pill.dataset.variantPrice;
        const compareCents = pill.dataset.variantCompareAtPrice;
        const card = pill.closest("product-card");
        if (!card) return;
        const priceWraps = card.querySelectorAll(".product-card__price-cost");
        if (priceWraps.length > 0) {
          priceWraps.forEach((priceWrap) => {
            if (compareCents) {
              priceWrap.innerHTML = `
                <div class="product-card--price product-card--price-compare">${compareCents}</div>
                <div class="product-card--price product-card--price--highlight product-card--price-sale">${priceCents}</div>
              `;
            } else {
              priceWrap.innerHTML = `<div class="product-card--price product-card--price--highlight">${priceCents}</div>`;
            }
          });
        }
        const savingsText = pill.dataset.savingsText;
        if (!savingsText) {
          const existingSaleWrap = card.querySelector(".product-card__on-image-badges-sale");
          if (existingSaleWrap) existingSaleWrap.remove();
        } else {
          let saleWrap = card.querySelector(".product-card__on-image-badges-sale");
          if (!saleWrap) {
            saleWrap = document.createElement("div");
            saleWrap.className = "product-card__on-image-badges product-card__on-image-badges-sale";
            card.appendChild(saleWrap);
          }
          let saleBadge = saleWrap.querySelector(".product-badge--sale");
          if (!saleBadge) {
            saleBadge = document.createElement("div");
            saleBadge.className = "product-card__badge product-badge product-badge--sale";
            saleWrap.appendChild(saleBadge);
          }
          saleBadge.textContent = savingsText;
        }
        const form = card.querySelector(".product-card__btn-wrapper");
        const fromSubmitButton = form == null ? void 0 : form.querySelector(".product-card__atc");
        if (pill.dataset.variantAvailable === "true") {
          if (fromSubmitButton) fromSubmitButton.removeAttribute("disabled");
          if (form) form.querySelector('[name="id"]').value = pill.dataset.variantId;
        } else {
          if (fromSubmitButton) fromSubmitButton.setAttribute("disabled", "true");
        }
      });
    });
  }
}
function handleHint() {
  if (screen.width <= 1024) {
    document.querySelectorAll(".product-badge-hint").forEach((badge) => {
      badge.addEventListener("click", (e) => {
        if (e.target.closest(".product-badge-hint-close")) return;
        badge.classList.remove("hide-hint");
        badge.classList.add("show-hint");
      });
      const closeBtn = badge.querySelector(".product-badge-hint-close");
      if (closeBtn) {
        closeBtn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          badge.classList.remove("show-hint");
          badge.classList.add("hide-hint");
        });
      }
    });
  }
}
window.addEventListener("resize", () => {
  handleHint();
});
document.addEventListener("DOMContentLoaded", () => {
  handleVariantSelectCollection();
  handleHint();
});
document.addEventListener("collection:refresh", () => {
  handleVariantSelectCollection();
  handleHint();
  if (typeof ratingSnippet === "function" && window.theme.template === "collection") {
    ratingSnippet("ruk_rating_snippet", {
      store: "aiheadnutrition.com",
      mode: "default",
      linebreak: false,
      usePolaris: true,
      showEmptyStars: false,
      showNumericalAverageRating: false
    });
  }
});
(function flagBrowser() {
  const ua = navigator.userAgent;
  const isChrome = /Chrome/.test(ua) && /Google Inc/.test(navigator.vendor) && !/Edg|OPR|Brave/.test(ua);
  const isSafari = /Safari/.test(ua) && /Apple Computer/.test(navigator.vendor) && !/Chrome|CriOS|FxiOS/.test(ua);
  document.documentElement.classList.toggle("is-chrome", isChrome);
  document.documentElement.classList.toggle("is-safari", isSafari);
})();
function setActiveIndex(state, index) {
  if (index < 0 || index >= state.total) return;
  state.items.forEach((item, i) => {
    item.classList.toggle("is-active", i === index);
  });
  state.tabs.forEach((tab) => {
    const tabIndex = parseInt(tab.getAttribute("data-cc-tab"), 10);
    tab.classList.toggle("is-active", tabIndex === index);
  });
  if (state.tabCarousel && state.tabCarousel.embla) {
    state.tabCarousel.embla.scrollTo(index, true);
  }
}
function activateCard(state, index) {
  if (index < 0 || index >= state.total) return;
  setActiveIndex(state, index);
  if (state.scrollToIndex) state.scrollToIndex(index, "auto");
}
function bindTab(state, tab) {
  const goToTab = () => {
    activateCard(state, parseInt(tab.getAttribute("data-cc-tab"), 10));
  };
  tab.addEventListener("click", goToTab);
  tab.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      goToTab();
    }
  });
}
function bindDot(state, dot) {
  dot.addEventListener("click", () => {
    activateCard(state, parseInt(dot.getAttribute("data-cc-dot"), 10));
  });
}
function bindScrollSync(state, track2) {
  let rafId = null;
  const nearestIndex = () => {
    const width = track2.clientWidth || 1;
    return Math.round(track2.scrollLeft / width);
  };
  track2.addEventListener(
    "scroll",
    () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        setActiveIndex(state, nearestIndex());
      });
    },
    { passive: true }
  );
  state.scrollToIndex = (index, behavior = "smooth") => {
    track2.scrollTo({ left: index * track2.clientWidth, behavior });
  };
}
function initComparisonCards(wrap2) {
  const state = {
    tabs: Array.from(wrap2.querySelectorAll("[data-cc-tab]")),
    items: Array.from(wrap2.querySelectorAll("[data-cc-item]")),
    dots: Array.from(wrap2.querySelectorAll("[data-cc-dot]")),
    tabCarousel: wrap2.querySelector(".sf-comparison-cards__tabs")
  };
  state.total = state.items.length;
  if (!state.total) return;
  state.tabs.forEach((tab) => bindTab(state, tab));
  state.dots.forEach((dot) => bindDot(state, dot));
  const track2 = wrap2.querySelector(".sf-comparison-cards__track");
  if (track2 && state.total > 1) bindScrollSync(state, track2);
}
function initAllComparisonCards() {
  document.querySelectorAll("[data-cc-wrap]").forEach(initComparisonCards);
}
document.addEventListener("DOMContentLoaded", initAllComparisonCards);
initBundleCartSentry();
const template = window.theme ? window.theme.template : null;
window.theme ? window.theme.pageType : null;
if (template.includes("mix-match")) {
  const selectors = {
    json: "#box-builder-json",
    app: "#box-builder"
  };
  mixMatchInit(selectors);
}
rukRating();
whatsInside();
variantPicker();
overlayModification();
stockBarometer();
parallax();
miniBundleInit({
  json: "#mini-bundle-samples-json",
  app: "#mini-bundle-samples"
});
